// ============================================
// AgentSession — 도구 버퍼 + 도구 실행 핸들러 통합 클래스
// aiDataBuffer.js + aiTools.js를 단일 세션 객체로 결합
// ============================================

import moment from 'moment';
import { FIELD_REGISTRY, getFieldNames, buildAliasMap, getFieldLabel, isFieldMetaObject, sanitizeFieldLabel, getDisplayLabel, resolveToItemKey } from './aiFieldConfig';
import { LOOKUP_TERM_INTENTION, lookupTermDefinition, USER_TERM_DICTIONARY, detectTermAndModifier, detectAxisFromInput, detectTermOnly, getPrimaryMetricForTerm } from './aiUserTermDictionary';
import { TOOL_CALLING_LOG } from '../Model/aiConfig';
import FetchModule from '../../Model/Share/Network/FetchModule';
import {
  parseEndpoint,
  resolveFetchMethod,
  buildDynamicEndpoint,
  PARSE_RESPONSE_OVERRIDES,
  STATUS_FILTER_MAP,
} from '../Transaction/aiTransaction';
import { executeGuideSearch } from '../Transaction/aiGuide';
import { substituteDateTemplates, ABS_PERIOD_KW_RE, resolveQuarterOverride, resolveDayOverride, detectStalePeriodRelabel } from '../Transaction/aiDateResolver';
import { applyAutoFallbackParams, applyDefaultPeriodOverride, checkVisualizationOnlyConflict, getLocalIntentionsByCategory } from '../Transaction/aiIntentionMeta';
import { AVAILABLE_FIELDS_OVERRIDES, DEFAULT_DISPLAY_OVERRIDES, TOOL_DEFINITIONS } from '../Transaction/aiToolConfig';
import { resolvePrompt } from '../Transaction/promptStore';
// [시임2] 백엔드 호출 계측 — rawResponse/params cap 직렬화 (텔레메트리 전용, 동작 무영향)
import { safeJsonCapped } from './aiLlmCollector';
// Architecture B 일원화 (2026-05-19) — visualization 도구의 frontend transformation utility
import { classifyMemberStatus, computeMemberStatusRevenue, sortClassAttendance, rankMemberAttendance, aggregateAttendanceChart, aggregateEngagement, computeLtv } from './visualizationTransforms';
// 회원권 선호도 — 통계 페이지(MembershipPreferenceBox)와 동일 집계 로직 공용 소스
import { computeMembershipPreference } from '../../Model/Individual/StatisticsModel/membershipPreferenceCompute';
// 기간별 매출(월별 매출 순위) — 통계 페이지(SalesPeriodBox)와 동일 집계 로직 공용 소스
import { computeSalesPeriod } from '../../Model/Individual/StatisticsModel/salesPeriodCompute';
// [P1-2 anchor 6] call_api intention cache lazy-fill (INTENTION_DIMENSIONS source + separator normalize)
import { resolveIntentionKey, getDimensionsByIntentionKey } from '../../Agent/Routing/intentionAdapter';
// [anchor 16] DETAIL fail → LIST fallback 오도 차단 가이드 (3-layer harness)
import { applyDetailNotFoundGuide, applyListFallbackWarning } from '../../Agent/Tools/detailNotFoundGuide.js';
// [anchor 17] extreme value ("최고/최저 + entity") 1건 limit 강제
// [PLACEHOLDER-SUMMARY-SCOPE] explicit top-N intent ("상위 5명만") detection for scope-aware {{COUNT}}
import { evaluateExtremeShowDisplay, detectExtremeIntent, detectExplicitTopNIntent } from '../../Agent/Tools/extremeIntentGuide.js';
// [1-A] MEMBER STATUS GUIDANCE의 multi-period 분기 부재 — wrong intention 자동 치환
import { detectMultiPeriodMemberStatus, formatReplacementMessage as formatMpmsReplacement, detectNewMemberMisroute, formatNewMemberMisrouteMessage } from '../../Agent/Tools/multiPeriodMemberStatusGuide.js';
// [L5-MEMBERSHIP-TOPATTEND-001 cycle 2] ATTENDANCE RANKING GUIDANCE의 multi-period 분기 부재 — visualizationOnly MONTHLY → non-MONTHLY variant 자동 치환
import { detectMultiPeriodAttendanceRanking, formatReplacementMessage as formatMparReplacement } from '../../Agent/Tools/multiPeriodAttendanceRankingGuide.js';
// [1-C] merge_slots matchKey row-level timestamp/date 차단 + entity-level identifier auto-swap
import { evaluateMergeMatchKey } from '../../Agent/Tools/mergeMatchKeyGuide.js';
// [1-D] show_display columns buffer field 부재 차단 + valid 후보 retry
import { evaluateShowDisplayColumns, detectAxisIntentMismatch } from '../../Agent/Tools/showDisplayColumnGuide.js';
// [1-U] call_api args 한국어 단위 suffix 정규화 (backend strict match guard)
import { normalizeCallApiArgs } from '../../Agent/Tools/callApiArgNormalize.js';
// [L13-API-ERROR-003] history-only intention + 미래 date guard — backend 호출 skip + finish() 안내 강제
import { evaluateFutureDateGuard, formatFutureDateBlockedMessage } from '../../Agent/Tools/futureDateGuide.js';
import { LABEL_OVERRIDES } from '../../Agent/Config/columnLabels.js';
import { classifyBackendError, getBackoffDelay, previewDisplayColumns, findKeywordMatchedFields, findKeywordAliasGroups, stripPoliteReproposal, stripMarkdownBold, stripPlaceholderArtifacts, stripRawNumberArtifacts, detectIntroOutroNumericHallucination, detectEntityCatalogMismatch, detectVagueMemberSearchName, detectVagueDetailIntent, detectNationalityCountIntent, detectUnmetHistoryCondition, aggregateNationalityCount, substituteOutroPlaceholders, isAmbiguousCountKey, selectActionGuide, buildMemberExistsActionGuide, buildSinglePeriodAvgHint, isLongTextField, stripIntroOverlap, formatValue, stripColumnEnumLeak, stripFalseChangeDirectionIntro, stripIntroDateHallucination, stripRawIsoDatetime, stripAssistantHarnessLeak, normalizeAttendanceFilterArgs, normalizeLessonMetricFilterArgs, normalizeEnumFilterValue, stripPeriodCountMismatchSentences, ensurePeriodCountsInIntro, normalizeGroupedUnits, detectUserSortIntent, parseMonthRangeFromInput, PRICE_FIELDS_RE, detectExplicitListRequest, matchMembershipNamesInSegment, computeConversionFunnel , computeNewToPaidConversion, computeMemberTrend, computeMemberFlowTrend, computeNewMemberRevenueTrend, correctReRegScopeNarrative, VIEW_FORMAT_LIST_RE, detectChangeDirection } from './aiUtils';
import { ERROR_CONFIG } from '../Model/aiConfig';
import { autoGenerateDisplay } from './aiUtils';
import { detectRequestedMemberDetailAspects } from './aiUtils';

const MAX_SLOTS = 16;  // 코호트 교집합(전환/추가구매 등) 팬아웃이 8슬롯 초과 시 merge 대상 슬롯이 LRU 축출돼 merge_slots SLOT NOT FOUND 실패 → 상향(리포트 11버퍼도 무축출로 안전). dual sync with nodeAgent.mjs

// [Q] 사용자 입력의 numeric subset 의도 감지 (한국어 표준 키워드 — FILTER_GUIDE와 동일) //
// "N 이상/이하/초과/미만/넘는/넘은" 패턴을 추출. 단위(만원/원/회/명/건/점/%)는 LLM이 환산 //
function detectNumericSubsetIntent(input) {
  if (!input || typeof input !== 'string') return null;
  const re = /(\d[\d,]*)\s*(만원|원|개|회|명|건|점|살|%|시간|분|일|kg|cm)?\s*(이상|이하|초과|미만|넘는|넘은|초과한|미만인)/;
  const m = input.match(re);
  if (!m) return null;
  const op = m[3];
  const isGte = /이상|초과|넘/.test(op);
  const numStr = (m[1] || '').replace(/,/g, '');
  const value = Number.isFinite(parseFloat(numStr)) ? parseFloat(numStr) : null;
  // 단위별 base unit 환산 (만원→원 곱하기 10000 등)
  let convertedValue = value;
  if (value !== null && m[2] === '만원') convertedValue = value * 10000;
  // 매치 위치 + 이전 텍스트 (~30자) — field 추론 context로 사용 (generic, 키워드 hardcode X)
  const matchIndex = typeof m.index === 'number' ? m.index : input.indexOf(m[0]);
  const contextBefore = matchIndex > 0 ? input.slice(Math.max(0, matchIndex - 30), matchIndex) : '';
  return {
    raw: m[0],
    type: isGte ? 'numeric_gte' : 'numeric_lte',
    unit: m[2] || null,
    value,
    convertedValue,
    contextBefore,
  };
}

// [Q] subsetIntent + buffer.availableFields로 numeric field 후보 추론 (LLM 자유 의도가 fail-safe 시 fallback)
//   generic: availableFields의 한국어 라벨에 사용자 input의 단위(회/원/%/명) + context 단어가 포함되는 field를 score.
//   본 함수는 hardcode 없이 buffer metadata + 사용자 입력의 단어 overlap만 사용.
function inferNumericFieldFromBuffer(intent, buffer) {
  if (!intent || !buffer || !buffer.items?.[0]) return null;
  const sample = buffer.items[0];
  const fields = buffer.availableFields || {};
  // numeric 후보 키: items[0]에 number 값 있는 키
  const numericKeys = Object.entries(sample)
    .filter(([k, v]) => typeof v === 'number' && Number.isFinite(v))
    .map(([k]) => k);
  if (numericKeys.length === 0) return null;
  // contextBefore에서 한글 단어 추출 (2자 이상)
  const ctxTokens = Array.from(
    new Set((intent.contextBefore || '').match(/[가-힣]{2,}/g) || [])
  );
  const unit = intent.unit || '';
  let best = null;
  let bestScore = 0;
  for (const key of numericKeys) {
    // [비문자열 라벨 방어] availableFields 값이 객체형({label,...})인 intention에서 label.includes TypeError → string 강제 (L31-COMPLEX-002 실측, dual sync with nodeAgent.mjs)
    const _rawLabel = fields[key];
    const label = typeof _rawLabel === 'string' ? _rawLabel : (_rawLabel && typeof _rawLabel.label === 'string' ? _rawLabel.label : '');
    // summary-only 필드 (label === key 또는 raw key 그대로) → skip (per-row 필드 아님)
    if (!label || label === key) continue;
    // [PK/식별자 배제] memberId/userId 등 id류 키 + PK/고유/식별 라벨은 numeric threshold 대상 아님.
    // 단위 "회"가 "회원"(memberId 라벨 "회원 PK(long)")에 부분매칭되어 PK가 오선택되는 collision 차단. dual sync with nodeAgent.mjs
    if (/(?:^id|Id|ID)$/.test(key) || /\bPK\b|고유|식별/.test(label)) continue;
    let score = 0;
    // unit 매치: label이 unit 단어를 포함하면 +2
    if (unit && label.includes(unit)) score += 2;
    // context token 매치: label과 token 일부 매치마다 +2
    for (const tok of ctxTokens) {
      if (label.includes(tok)) score += 2;
      // 부분 매치: 2자 token의 첫 글자 또는 끝 글자가 label에 (출석 → 출석률/출석 횟수)
      else if (tok.length >= 2 && (label.includes(tok.slice(0, 2)) || label.includes(tok.slice(-2)))) {
        score += 1;
      }
    }
    // per-row 가산: key가 sample에 numeric으로 존재 (above filter 통과). 추가 가산점 없음.
    if (score > bestScore) {
      bestScore = score;
      best = { field: key, label, score };
    }
  }
  return bestScore > 0 ? best : null;
}

// dateRange (startDate, endDate)를 사용자 표시용 한국어 라벨로 변환 //
// 단월(MM-01~말일) → "M월", 단년(YYYY-01-01~12-31) → "YYYY년", 그 외 → raw 표기 //
function formatPeriodLabel(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const sm = String(startDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const em = String(endDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!sm || !em) return null;
  const [, sy, sM, sd] = sm;
  const [, ey, eM, ed] = em;
  if (sy === ey && sM === eM) {
    const lastDay = new Date(parseInt(sy, 10), parseInt(sM, 10), 0).getDate();
    if (sd === '01' && parseInt(ed, 10) === lastDay) return `${parseInt(sM, 10)}월`;
    return `${parseInt(sM, 10)}월 ${parseInt(sd, 10)}일~${parseInt(ed, 10)}일`;
  }
  if (sy === ey && sM === '01' && eM === '12' && sd === '01' && ed === '31') return `${sy}년`;
  return `${startDate}~${endDate}`;
}

// [P3 anchor 11] date field value-form detect — derive 산술 거부용 (birthDay/startDate/endDate 등)
// pattern: YYYY-MM-DD / ISO datetime / YYYYMMDD compact (year 1900-2099 validation)
// 산술 4 op (add/subtract/multiply/divide) 은 parseFloat("19970527")=19970527 으로 통과되어 의미없는 결과 산출 — entry 가드로 본질 차단
function _isDateString(s) {
  if (typeof s !== 'string') return false;
  return /^(19|20)\d{2}-\d{2}-\d{2}/.test(s)
      || /^(19|20)\d{2}\d{2}\d{2}$/.test(s);
}

// [1-F] period buffer authoritative count source — multi-row-per-entity (MESSAGE 한 row=한 발송 등) 케이스에서
// entity-merge 후 items.length가 unique recipient count로 misleading. buf.summary의 total* 우선 사용.
// 본질: NEWMEMBER 같은 single-row-per-entity (한 row=한 member)는 summary에 total* 없어 fallback items.length 정상 유지.
function _extractPeriodSourceCount(buf) {
  const summary = buf?.summary;
  if (summary && typeof summary === 'object') {
    const totalKeys = Object.entries(summary).filter(([k, v]) =>
      typeof v === 'number' && Number.isFinite(v) && /^total[A-Z]/.test(k)
    );
    const nonFailed = totalKeys.filter(([k]) => !/(Failed|Cancel|Reject|Error)/i.test(k));
    const pool = nonFailed.length > 0 ? nonFailed : totalKeys;
    if (pool.length > 0) {
      const totalCount = pool.find(([k]) => k === 'totalCount');
      const totalSuccess = pool.find(([k]) => /^total.*Success.*Count$/i.test(k));
      const picked = totalCount || totalSuccess || pool[0];
      return { count: picked[1], source: `summary.${picked[0]}` };
    }
  }
  return { count: buf?.items?.length || 0, source: 'items.length' };
}

// [L10-DETAIL-SCHEDULE-001] CHAIN CONTEXT MEMBER_DETAIL REDIRECT registry
// Maps generic LIST/PERIOD intentions (whole-roster scope) to their per-member MEMBER_DETAIL
// counterparts. Used by executeCallApi guard when a chain T1+ bare-keyword input is detected with
// an active MEMBER_DETAIL buffer context — preserves the individual-member focus of the chain.
// Registry entries must satisfy: (a) source intention is roster-wide (no memberId param),
// (b) target intention is in MEMBER_DETAIL category and requires memberId.
// dual sync with nodeAgent.mjs
const MEMBER_DETAIL_REDIRECT_MAP = {
  'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD': 'CHECK_MEMBER+ATTENDANCE_DETAIL',
  'CHECK_MEMBER+LESSON+PARTICIPATION_LIST_BY+PERIOD': 'CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL',
};

// Module-level helper: domain noun set used to filter out non-name Korean tokens when detecting
// whether user input contains a NEW member name candidate. Words here are treated as topic/feature
// keywords (출석/현황/회원권/이력 etc.), not personal names.
const _CHAIN_CONTEXT_DOMAIN_NOUNS = new Set([
  '회원', '회원권', '이력', '상세', '내역', '연장', '홀딩', '목록', '전체', '확인', '조회',
  '출석', '현황', '수업', '레슨', '참여', '참석', '결제', '환불', '포인트', '상품', '락커',
  '프로필', '정보', '이번', '저번', '지난', '이번달', '저번달', '지난달', '오늘', '어제', '내일',
  '월간', '연간', '주간', '일간', '비율', '횟수', '평균', '통계', '분석', '리포트',
]);

export default class AgentSession {
  constructor() {
    // --- from aiDataBuffer ---
    this.buffers = new Map();   // slotName → buffer object
    this.activeSlot = null;
    this._slotSeqCounter = 0;   // [SLOT LRU] 슬롯 사용 순번 — 슬롯 생성/전환 시 buf._lru 스탬프(고갈 시 최소값 회수)
    this._comparisonActive = false;  // 비교 세션 진행 중 플래그
    this._mergeOutputSlot = null;    // merge 결과 슬롯명 추적
    // [#585-FOLLOWUP-D] 직전 merge 모드/intention 추적 — chain follow-up에서 detail-list 뷰 일관성 유지용.
    // turn N이 outer-merge로 detail-list 비교를 produce했고, turn N+1에서 LLM이 same-intention + inner mode를 선택하면
    // SUMMARY-OVERRIDE가 period-stack으로 silent coerce하여 뷰 form이 turn 사이에 (detail → summary)로 비일관되게 변동.
    // 본 추적으로 SUMMARY-OVERRIDE 진입 직전에 mode='inner'를 'outer'로 coerce하여 직전 뷰 form 보존.
    this._lastMergeMode = null;      // 'outer' | 'inner' | 'intersect' | 'summary-series'
    this._lastMergeIntention = null; // 직전 merge에 참여한 슬롯의 공통 intention (same-intention merge 시)

    this._userInput = '';
    this._lastMember = null;  // 마지막 회원 검색 결과 (멀티턴 컨텍스트 보존, {memberId, name})
    // [L10-DETAIL-SCHEDULE-001 CHAIN_CONTEXT_MULTI_MATCH_BLOCK] 직전 MEMBER_SEARCH가 2명+ 반환 시 동명이인 disambiguation 필요 상태
    // T0 MEMBER_SEARCH 다중 매치 → LLM이 한 명을 picking 못함 → _lastMember 미저장 → 후속 T1 bare keyword 입력 시
    // CHAIN_CONTEXT_MEMBER_DETAIL_REDIRECT 미발동 → whole-roster fallback (사용자 의도 무관 데이터)
    // 본 flag로 차단 + finish() 자연어 되묻기 강제
    this._lastMemberSearchMultiMatch = null;  // null | { name, count, query } — disambiguation pending

    // --- from aiTools ---
    this.intentionCache = {};
  }

  // 멀티턴 복원용 영속 상태 직렬화 — 턴-스코프 가드 플래그(setUserInput에서 리셋)는 제외 //
  // buffers(Map)는 plain object로 변환. session-state 저장 시 buffersSnapshot에 들어감.
  serialize() {
    return {
      buffers: Object.fromEntries(this.buffers),
      activeSlot: this.activeSlot,
      _slotSeqCounter: this._slotSeqCounter,
      intentionCache: this.intentionCache,
      _comparisonActive: this._comparisonActive,
      _mergeOutputSlot: this._mergeOutputSlot,
      _lastMergeMode: this._lastMergeMode,
      _lastMergeIntention: this._lastMergeIntention,
      _lastMember: this._lastMember,
      _lastMemberSearchMultiMatch: this._lastMemberSearchMultiMatch,
    };
  }

  // 직렬화 상태 재주입 — 세션 복원 시 buffers/intentionCache/멀티턴 컨텍스트 rehydrate //
  rehydrate(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return;
    if (snapshot.buffers && typeof snapshot.buffers === 'object') {
      this.buffers = new Map(Object.entries(snapshot.buffers));
    }
    if (snapshot.intentionCache && typeof snapshot.intentionCache === 'object') {
      this.intentionCache = snapshot.intentionCache;
    }
    if (snapshot.activeSlot !== undefined) this.activeSlot = snapshot.activeSlot;
    if (snapshot._slotSeqCounter !== undefined) this._slotSeqCounter = snapshot._slotSeqCounter;
    if (snapshot._comparisonActive !== undefined) this._comparisonActive = snapshot._comparisonActive;
    if (snapshot._mergeOutputSlot !== undefined) this._mergeOutputSlot = snapshot._mergeOutputSlot;
    if (snapshot._lastMergeMode !== undefined) this._lastMergeMode = snapshot._lastMergeMode;
    if (snapshot._lastMergeIntention !== undefined) this._lastMergeIntention = snapshot._lastMergeIntention;
    if (snapshot._lastMember !== undefined) this._lastMember = snapshot._lastMember;
    if (snapshot._lastMemberSearchMultiMatch !== undefined) this._lastMemberSearchMultiMatch = snapshot._lastMemberSearchMultiMatch;
  }

  setUserInput(input) {
    this._userInput = input;
    this._vizListRequested = false;  // [B-1 과적합 수술] viz 렌더 턴의 명시적 표/리스트 요구 플래그 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._conversionFunnelIntro = null;  // [CONVERSION-FUNNEL] 결정적 intro 강제용 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._reRegMetaThisTurn = null;  // [REREG-SCOPE GUARD] 이번 턴 재등록 메타 실측 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._listRequestFinishBlockAttempted = false;  // [B-1] LIST REQUESTED finish 차단 1회 한정 (turn별 리셋)
    this._showDisplayFilterBlocked = 0;  // 새 턴마다 차단 카운터 리셋 (0=미차단, 1=1차 retry, 2=2차 retry, 3+=auto-apply 단계)
    this._filterAutoAppliedNote = null;  // 자동 필터 적용 시 표시용 안내 (turn별 리셋)
    this._showDisplayHallucinationBlocked = false;
    this._showDisplayCountQuestionBlocked = false;
    this._nationalityCountBlocked = false;
    this._showDisplayChangeClaimBlocked = false;
    this._showDisplayBufferMismatchBlocked = false;
    this._showDisplayCenterInfoBlocked = false;  // [CENTER-INFO STALE RENDER] 체육관 정보 요청 시 stale 버퍼 거짓 렌더 차단 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._centerInfoPrepPassCount = 0;  // [CENTER-INFO PREP PASS] POST-GUARD 준비도구 통과 횟수 (iteration당 1, cap 5, serialize 제외, dual sync with nodeAgent.mjs)
    this._centerInfoPrepLastIter = -1;  // [CENTER-INFO PREP PASS] 같은 iteration 중복 증가 방지
    this._unmetHistoryCondApplied = null;  // [UNMET HISTORY-COND] disclosure 적용 마커 turn별 리셋 (dual sync with nodeAgent.mjs)
    this._bufferMismatchInfo = null;  // [C-#6] BUFFER MISMATCH 발동 시 {columns, activeSlot} 저장 — finishPolicy GUARD 14 retryGuide용 (turn별 리셋)
    this._bufferMismatchFinishRetryAttempted = false;  // [C-#6] BUFFER MISMATCH 미해결 finish 차단 GUARD 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._bareCountFinishRetryAttempted = false;  // [BARE-COUNT AMBIGUITY GUARD 15] dual sync with nodeAgent.mjs
    this._turnFilterApplied = false;  // [BARE-COUNT AMBIGUITY] 본 turn LLM filter 적용 = active 앵커링 신호 (dual sync with nodeAgent.mjs)
    this._showDisplayColMismatchBlocked = false;  // COLUMN MISMATCH 가드 1회 한정 플래그
    this._showDisplayEmptyRowsBlocked = false;  // EMPTY DISPLAY 가드 1회 한정 플래그 (turn별 리셋)
    this._showDisplayRecoveryAttempted = false;  // EMPTY DISPLAY → switch_slot 회복 안내 1회 한정
    this._contextReuseRedirectAttempted = false;  // CONTEXT REUSE call_api redirect 1회 한정 (turn별 리셋)
    this._multiPeriodAutoChainFiredThisTurn = false;  // "X말고 Y" 멀티슬롯 자동 chain 1회 한정 플래그
    this._vizOnlyFallbackRerouted = false;  // [#655] visualizationOnly fallback re-route 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._calledApiThisTurn = false;  // 본 턴에 call_api 호출 여부 (merge_slots 우선 차단용)
    this._turnCallApiSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 call_api 적재 슬롯 (dual sync with nodeAgent.mjs)
    this._turnMergedSourceSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 성공 merge 소스 슬롯 — append 후보 제외
    this._turnDisplayedSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 표시 성공 슬롯 — 다축 모두-표시 시 append 후보 제외
    this._undisplayedCitedThisTurn = false;  // [UNDISPLAYED METRIC AUTO-APPEND] turn당 1회 보강
    this._memberDetailAspectAugmentedThisTurn = false;  // [#10 MEMBER-DETAIL MULTI-ASPECT COMPLETION] turn당 1회 (dual sync with nodeAgent.mjs)
    this._madForceFetchingThisTurn = false;  // [#10 EARLY COMPLETION] force-fetch 재귀 가드 (dual sync with nodeAgent.mjs)
    this._perAxisSlotCorrected = false;  // [PER-AXIS ACTIVE SLOT CORRECTION] turn당 1회 (show_display 진입부)
    this._dateParamGuardBlockedCount = 0;  // [DATE PARAM GUARD] 절대 기간 표현 + dates 누락 차단 횟수 (max 2, turn별 리셋)
    this._getIntentionsCalledThisTurn = false;  // [INTENTION GATE] 본 턴에 LLM이 get_intentions 호출 여부 (call_api 선행 검증용, dual sync with nodeAgent.mjs)
    this._intentionGateBlocked = false;  // [INTENTION GATE] 1회 한정 차단 플래그 — 2번째 call_api는 통과 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._getGuideFailedThisTurn = false;  // [HOWTO/ADVICE GATE] 본 턴에 get_guide가 not_found로 실패했는지 (call_api 차단 검증용, dual sync with nodeAgent.mjs)
    this._howtoAdviceGateBlockedCount = 0;  // [HOWTO/ADVICE GATE] 차단 횟수 (max 3회 — LLM이 stochastic 재시도해도 끝까지 finish 강제, turn별 리셋, dual sync with nodeAgent.mjs)
    this._howtoAdviceFinishRefusalRetryCount = 0;  // [HOWTO/ADVICE FINISH] LLM이 finish 응답에 "안내 어렵" 등 거부 패턴 사용 시 retry 카운터 (max 2회, turn별 리셋, dual sync)
    this._autoChainRequiresDisplay = false;  // auto-chain 후 finish 전 show_display 필수 플래그
    this._outroRetryAttempted = false;  // OUTRO raw 수치 leak retry 1회 한정 플래그 (turn별 리셋)
    this._showDisplayDimensionRedirectAttempted = false;  // show_display period mismatch redirect 1회 한정 플래그 (turn별 리셋)
    this._showDisplayPeriodMismatchAttempted = false;  // [PERIOD MISMATCH → CALL_API] 절대월 명시 + 매칭 슬롯 부재 시 call_api 유도 1회 한정 (turn별 리셋)
    this._showDisplayNumericRetryCount = 0;  // [B1] intro/outro 수치 hallucination retry 카운터 (최대 2회, turn별 리셋)
    this._finishNumericRetryCount = 0;  // [B1] finish response 수치 hallucination retry 카운터 (최대 2회, turn별 리셋)
    this._showDisplayEntityRetryCount = 0;  // [B4-2] catalog entity mismatch retry 카운터 (최대 2회, turn별 리셋)
    this._detailNotFoundContext = null;  // [anchor 16] DETAIL fail + specifier 감지 시 {intentionPrefix, specifier} 저장 (turn별 리셋)
    this._detailFallbackRetryAttempted = false;  // [anchor 16] finishPolicy GUARD 5 retry 1회 한정 플래그 (turn별 리셋)
    this._detailFallbackShowDisplayBlocked = false;  // [anchor 16] show_display Layer 3a 1회 한정 (turn별 리셋)
    this._extremeLimitRetryAttempted = false;  // [anchor 17] extreme value 1건 limit retry 1회 한정 (turn별 리셋)
    this._zeroCountByIntention = new Map();  // [1-E] intention별 count=0 multi-call 추적 (session 누적)
    this._catastrophicNameDropRetryAttempted = false;  // [1-K Layer X] CATASTROPHIC NAME DROP GUARD 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._multiPeriodMergeAnchorDropRetryAttempted = false;  // [F11 Issue A — L9c-COMPARE-006 cycle 1] MULTI-PERIOD MERGE ANCHOR DROP GUARD 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._entityDetailMissingRetryAttempted = false;  // [1-K GUARD 10] ENTITY DETAIL MISSING 1회 한정 (turn별 리셋, dual sync with useAiChat.js)
    this._axisIntentRetryAttempted = false;  // [AXIS INTENT MISMATCH] show_display axis 불일치 retry 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._showDisplayFormMismatchBlocked = false;  // [1-M β] DATA FORM MISMATCH 차단 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._axisAutoSwitchedThisTurn = false;  // [AXIS AUTO-SWITCH] sibling axis 슬롯 자동전환 1회/turn (turn별 리셋, dual sync with nodeAgent.mjs)
    this._autoUserTermAppliedThisTurn = null;  // [AUTO USER TERM PROTECTION] 본 turn AUTO USER TERM 적용 컨텍스트 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._smsRowScopeThisTurn = null;  // [SMS-INDIVIDUAL-SUBSET fix] individual SMS recipients가 표 부분집합일 때 show_display를 recipients로 narrowing (turn별 리셋, dual sync with nodeAgent.mjs)
    this._smsRowScopeOriginalItems = null;  // [SMS-INDIVIDUAL-SUBSET fix] row-scope swap 원본 1회 보존 (재진입 안전, turn별 리셋, dual sync with nodeAgent.mjs)
    this._lastDisplayedOrder = null;  // [rid60 SMS-RECIPIENT-ORDER fix] 마지막 show_display 표시 item order(memberId/name) — recommend_sms targetIndex resolve용. ⚠️ turn 간 보존 (리셋 X), dual sync with nodeAgent.mjs
    this._lastStatusScope = null;  // [rid188 SMS-DEICTIC-STATUS-SCOPE fix] 직전 show_member_status 분류 표시 대상(신규/재등록/이탈 memberIds/names/label) — recommend_sms bulk deictic("이 사람들") 대상 한정용. ⚠️ turn 간 보존, call_api(initBuffer) 시 무효화. dual sync with nodeAgent.mjs
    this._speechRegisterPref = null;  // [TONE PERSIST rid114/rid200] 명시적 톤 요청("존댓말로 해"/"반말로 해") 영속 pref('formal'|'informal'|null) — ⚠️ turn 간 보존(리셋 X), 다음 명시 요청까지 유지. dual sync with nodeAgent.mjs
    this._periodRestrictionRedisplayNudged = false;  // [L9a-MEMBER-003 cycle 1] PERIOD-RESTRICTION FOLLOW-UP nudge 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._entityNotFoundContext = null;  // [L9b-CROSS-009 cycle 1] prev buffer entity list + user mentioned name not in set → outro auto-prepend context (turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
    this._entityNotFoundCallApiBlocked = false;  // [L9b-CROSS-009 cycle 2] call_api block 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
    this._showDisplayPeriodNarrowBlocked = false;  // [L21-COMPARE-FOLLOWUP-001 cycle 1] NARROW PERIOD CHAIN FOLLOW-UP — HONEST INFORM 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._dataToolPeriodNarrowBlocked = false;  // [L21-COMPARE-FOLLOWUP-001 cycle 2] NARROW PERIOD CHAIN FOLLOW-UP — data-processing tool (filter/sort/compute/aggregate/derive/merge_slots) HONEST INFORM 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._compareIntentDriftNarrowBlocked = false;  // [L21-COMPARE-FOLLOWUP-001 cycle 3] NARROW PERIOD CHAIN FOLLOW-UP — INTENT-DRIFT: 본 turn call_api 후 fresh single-period buffer가 prior compare chain framing 손실 시 show_display block (turn별 리셋, dual sync with nodeAgent.mjs)
    this._adviceFollowupGateBlockedCount = 0;  // [ADVICE FOLLOW-UP GATE] 자문 후속턴 데이터 도구 차단 횟수 (max 3, turn별 리셋, dual sync with nodeAgent.mjs)

    this._guardFirings = [];  // [OB-1 가드 트레이스] 가드 발동 구조화 링버퍼 (cap 50, turn별 리셋, dual sync with nodeAgent.mjs)
  }
  getUserInput() { return this._userInput || ''; }

  // [OB-1 가드 트레이스] 가드 발동 구조화 기록 — 드라이버가 executeTool 실패 반환 지점에서 호출 (계측 전용·행동 0변경, dual sync with nodeAgent.mjs) //
  //   hadLiveItems: 발동 시점 items>0 버퍼 실존 — aiUtils computeFalseNoDataRisk의 거짓 no-data 통계 재료
  recordGuardFiring(guardKey, detail = {}) {
    try {
      if (!Array.isArray(this._guardFirings)) this._guardFirings = [];

      let hadLiveItems = false;

      for (const [, b] of this.buffers) {
        if ((b?.items?.length || 0) > 0) { hadLiveItems = true; break; }
      }

      this._guardFirings.push({ guard: String(guardKey), hadLiveItems, ...detail });

      if (this._guardFirings.length > 50) this._guardFirings.shift();
    }
    catch (_e) { /* 계측 실패는 동작에 영향 없음 */ }
  }

  // [ADVICE FOLLOW-UP GATE] 정밀 자문 의도 탐지 — 전략/조언 자문만(bare "어떻게 돼?" 데이터 질문 제외). dual sync AgentSession.js <-> nodeAgent.mjs.
  //   modal "어떻게 하면 ~ㄹ까/수있을까" + retention/grow 동사+방법/전략/추천 + 자문명사+추천/알려. 운영 5차 rid100/101.
  _isAdviceFollowupIntent(input) {
    const _ui = (typeof input === 'string' ? input : (this.getUserInput?.() || '')).trim();
    if (!_ui) return false;
    const _modal = /어떻게\s*하면.{0,22}(까|을지|ㄹ지|좋|돼|되|할\s*수|을\s*수|ㄹ\s*수|있)/;
    const _verb = /(올리|개선|향상|증가|성장|늘리|줄이|낮추|높이|키우|확대|확장|유치|유지|이탈\s*방지|붙잡|유인|관리)\S*\s*(방법|법|전략|방안|추천|어떻게|제안|아이디어|팁|조언|비결|꿀팁|노하우)/;
    const _noun = /(전략|방안|아이디어|팁|조언|제안|비결|꿀팁|노하우)\s*(추천|줘|알려|있을까|없을까|좋을까)/;
    return _modal.test(_ui) || _verb.test(_ui) || _noun.test(_ui);
  }

  // [L10-DETAIL-SCHEDULE-001] CHAIN CONTEXT helpers
  // _isChainContextMemberDetail: true if any persistent buffer's intention belongs to MEMBER_DETAIL category.
  //   Uses INTENTION_DIMENSIONS static source (getDimensionsByIntentionKey) so it works regardless of
  //   intentionCache population state at the time of guard evaluation.
  _isChainContextMemberDetail() {
    if (!this.buffers || typeof this.buffers.values !== 'function') return false;
    for (const buf of this.buffers.values()) {
      const dims = buf?.intention ? getDimensionsByIntentionKey(buf.intention) : null;
      if (dims?.category === 'MEMBER_DETAIL') return true;
    }
    return false;
  }

  // _hasNewMemberNameInInput: true if current user input contains a Korean 2-4 char or Latin 2-20 char
  //   token that is NOT a domain noun (출석/회원권/현황 등). False = bare-keyword input → chain context inheritance OK.
  //   Also returns true if the input token matches _lastMember.name exactly (explicit re-mention is still
  //   "no new name" because the chain context already references the same member — handled separately).
  _hasNewMemberNameInInput() {
    const userInput = this.getUserInput?.() || '';
    if (!userInput) return false;
    const koTokens = (userInput.match(/[가-힣]{2,4}/g) || []).filter(w => !_CHAIN_CONTEXT_DOMAIN_NOUNS.has(w));
    const enTokens = userInput.match(/[A-Za-z]{2,20}/g) || [];
    const candidates = [...koTokens, ...enTokens];
    if (candidates.length === 0) return false;
    // Same-name re-mention (e.g., user repeats existing member's name) does not count as "new name".
    const lastName = this._lastMember?.name || null;
    const onlyLastName = candidates.length === 1 && lastName && candidates[0] === lastName;
    if (onlyLastName) return false;
    return true;
  }

  // CONTEXT REUSE redirect — buffers에서 _change_* 보유 슬롯(active 외) 중 동일 카테고리 매칭 슬롯 검색
  // intentionPrefix: 새 call_api가 호출하려는 인텐션의 카테고리 prefix (예: CHECK_SALES_, CHECK_MEMBER_)
  // 매칭 우선순위: (1) intention prefix 일치 (2) intention==='MERGED' 또는 _merged (3) items.length
  _findContextReuseSlot(intentionPrefix) {
    if (!this.buffers || typeof this.buffers.keys !== 'function') return null;
    const candidates = [];
    for (const slotName of this.buffers.keys()) {
      if (slotName === this.activeSlot) continue;
      const buf = this.buffers.get(slotName);
      if (!buf?.items?.length) continue;
      const hasChange = buf.items.some(it => it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_')));
      if (!hasChange) continue;
      const slotIntention = String(buf.intention || '');
      const isMerged = slotIntention === 'MERGED' || slotName === '_merged';
      const sameCategory = intentionPrefix && slotIntention && slotIntention.startsWith(intentionPrefix);
      // merged 슬롯은 intention='MERGED'로 카테고리가 소실 → mergeSlots가 보존한 _mergedSourcePrefix로 검증.
      //   (이전: isMerged면 무조건 후보 → 재등록 merge가 매출 call_api를 hijack하는 cross-metric 오류. rid49)
      const mergedCategoryMatch = isMerged && !!intentionPrefix && buf._mergedSourcePrefix === intentionPrefix;
      if (!sameCategory && !mergedCategoryMatch) continue;
      candidates.push({ slotName, isMerged, sameCategory, itemsLen: buf.items.length });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => (b.sameCategory - a.sameCategory) || (b.isMerged - a.isMerged) || (b.itemsLen - a.itemsLen));
    return candidates[0].slotName;
  }

  // EMPTY DISPLAY 발동 시 buffers 다른 슬롯 중 명시 columns이 100% 매칭 + 값 채워진 슬롯 검색 (회복 안내용)
  // 매칭 우선순위: (1) intention==='MERGED' 또는 slot 이름이 '_merged' (2) 매칭 columns 개수 최다 (3) items.length 최다
  // false positive 차단: columns ≥ 2 + active slot이 명시 columns 절반 이상 부재일 때만 회복 의미
  _findRecoverySlotForColumns(columns) {
    if (!Array.isArray(columns) || columns.length < 2) return null;
    const activeBuf = this.getBuffer?.();
    if (activeBuf?.items?.length > 0) {
      const aKeys = new Set(Object.keys(activeBuf.items[0] || {}));
      const aFieldKeys = new Set(Object.keys(activeBuf.availableFields || {}));
      const activeMatched = columns.filter(c => aKeys.has(c) || aFieldKeys.has(c));
      if (activeMatched.length / columns.length > 0.5) return null;
    }
    if (!this.buffers || typeof this.buffers.keys !== 'function') return null;
    const candidates = [];
    for (const slotName of this.buffers.keys()) {
      if (slotName === this.activeSlot) continue;
      const buf = this.buffers.get(slotName);
      if (!buf?.items?.length) continue;
      const sampleKeys = new Set(Object.keys(buf.items[0] || {}));
      const fieldKeys = new Set(Object.keys(buf.availableFields || {}));
      const matchedColumns = columns.filter(c => sampleKeys.has(c) || fieldKeys.has(c));
      if (matchedColumns.length !== columns.length) continue;
      const hasRealData = buf.items.some(it =>
        columns.some(c => {
          const v = it?.[c];
          return v != null && v !== '' && !(Array.isArray(v) && v.length === 0);
        })
      );
      if (!hasRealData) continue;
      const isMerged = (buf.intention === 'MERGED') || (slotName === '_merged');
      candidates.push({ slotName, isMerged, matchedCount: matchedColumns.length, itemsLen: buf.items.length });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => (b.isMerged - a.isMerged) || (b.matchedCount - a.matchedCount) || (b.itemsLen - a.itemsLen));
    return candidates[0].slotName;
  }

  // PERIOD MATCH 슬롯 검색 — user input의 "X월" 명시 vs active buffer/buffers의 period 매칭
  // - userInput의 월(\d+월) 추출 → requestedMonths Set
  // - active buffer items가 requestedMonths 전부 보유 시 null (이미 매칭, redirect 불필요)
  // - 그 외 buffers iterate: _change_*/_changeRate_* 보유 또는 _merged/MERGED 슬롯만 후보 → requestedMonths 전수 매칭
  // - 우선순위: (1) _merged/MERGED (2) requestedMonths 매칭 개수 (3) items.length
  // - false positive 차단: requestedMonths.size === 0 → null
  _findPeriodMatchSlot(userInput) {
    const requestedMonths = this._extractMonthSet(userInput);
    if (requestedMonths.size === 0) return null;
    const activeBuf = this.getBuffer?.();
    if (activeBuf?.items?.length > 0) {
      const activeMonths = this._extractMonthSetFromItems(activeBuf.items);
      const allMatched = [...requestedMonths].every(m => activeMonths.has(m));
      if (allMatched) return null;
    }
    if (!this.buffers || typeof this.buffers.keys !== 'function') return null;
    const candidates = [];
    for (const slotName of this.buffers.keys()) {
      if (slotName === this.activeSlot) continue;
      const buf = this.buffers.get(slotName);
      if (!buf?.items?.length) continue;
      const hasChange = buf.items.some(it => it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_') || k.startsWith('_changeRate_')));
      const isMerged = (buf.intention === 'MERGED') || (slotName === '_merged');
      if (!hasChange && !isMerged) continue;
      const slotMonths = this._extractMonthSetFromItems(buf.items);
      const matchCount = [...requestedMonths].filter(m => slotMonths.has(m)).length;
      if (matchCount !== requestedMonths.size) continue;  // 전수 매칭만 허용
      candidates.push({ slotName, isMerged, matchCount, itemsLen: buf.items.length });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => (b.isMerged - a.isMerged) || (b.matchCount - a.matchCount) || (b.itemsLen - a.itemsLen));
    return candidates[0].slotName;
  }

  // [L11-SWITCH-SLOT-001 cycle 1] MULTI-SLOT PER-ENTITY COMPARE 슬롯 검색 — dual sync with nodeAgent.mjs
  //   목적/조건/false positive 차단 nodeAgent.mjs _findMultiSlotMergeCandidates 동일.
  _findMultiSlotMergeCandidates() {
    if (!this.buffers || typeof this.buffers.size !== 'number' || this.buffers.size < 2) return null;
    if (!this.activeSlot) return null;
    const activeBuf = this.buffers.get(this.activeSlot);
    if (!activeBuf || !activeBuf.intention || activeBuf.intention === 'MERGED') return null;
    if (this.activeSlot === '_merged') return null;
    if (Array.isArray(activeBuf.items) && activeBuf.items.length > 0) {
      const sampleKeys = Object.keys(activeBuf.items[0] || {});
      const hasChangeField = sampleKeys.some(k => k.startsWith('_change_') || k.startsWith('_changeRate_'));
      if (hasChangeField) return null;
    }
    if (!Array.isArray(activeBuf.items) || activeBuf.items.length === 0) return null;
    const activeIntention = activeBuf.intention;
    const otherSlots = [];
    for (const slotName of this.buffers.keys()) {
      if (slotName === this.activeSlot) continue;
      const buf = this.buffers.get(slotName);
      if (!buf || !buf.intention || buf.intention !== activeIntention) continue;
      if (!Array.isArray(buf.items) || buf.items.length === 0) continue;
      otherSlots.push(slotName);
    }
    if (otherSlots.length === 0) return null;
    return { activeSlot: this.activeSlot, otherSlots };
  }

  // text에서 "N월" 추출 후 정규화 ("02월"→"2월") Set 반환
  _extractMonthSet(text) {
    const set = new Set();
    if (typeof text !== 'string') return set;
    for (const m of text.matchAll(/(\d{1,2})\s*월/g)) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 12) set.add(`${n}월`);
    }
    return set;
  }

  // items의 period 관련 필드(period/periodStart/periodEnd/_label/month/yearMonth/date/startDate/endDate)에서 월 추출
  // 지원: "2월" / "2026-02-15" / "2026-02"
  _extractMonthSetFromItems(items) {
    const set = new Set();
    const PERIOD_KEYS = ['period', 'periodStart', 'periodEnd', '_label', 'month', 'yearMonth', 'date', 'startDate', 'endDate'];
    if (!Array.isArray(items)) return set;
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      for (const k of PERIOD_KEYS) {
        const v = it[k];
        if (v == null) continue;
        const s = String(v);
        const m1 = s.match(/(\d{1,2})\s*월/);
        if (m1) {
          const n = parseInt(m1[1], 10);
          if (n >= 1 && n <= 12) set.add(`${n}월`);
          continue;
        }
        const m2 = s.match(/^\d{4}-(\d{2})/);
        if (m2) {
          const n = parseInt(m2[1], 10);
          if (n >= 1 && n <= 12) set.add(`${n}월`);
        }
      }
    }
    return set;
  }

  // 슬롯 버퍼의 기간(월 Set) 추출 — dateRange / summary.startDate~endDate / items 날짜필드 종합 //
  //   코치 통계처럼 items에 날짜 필드가 없고 summary에만 startDate/endDate가 있는 집계 슬롯도 커버 //
  _getSlotMonths(buf) {
    const set = new Set();
    if (!buf || typeof buf !== 'object') return set;

    const addRange = (sd, ed) => {
      const sm = String(sd == null ? '' : sd).match(/^(\d{4})-(\d{2})/);
      const em = String(ed == null ? '' : ed).match(/^(\d{4})-(\d{2})/);
      if (sm && em) {
        let y = parseInt(sm[1], 10);
        let mo = parseInt(sm[2], 10);
        const ey = parseInt(em[1], 10);
        const emo = parseInt(em[2], 10);
        let guard = 0;

        while ((y < ey || (y === ey && mo <= emo)) && guard < 24) {
          if (mo >= 1 && mo <= 12) set.add(`${mo}월`);
          mo += 1;
          if (mo > 12) { mo = 1; y += 1; }
          guard += 1;
        }
      }
      else if (sm) {
        const mo = parseInt(sm[2], 10);
        if (mo >= 1 && mo <= 12) set.add(`${mo}월`);
      }
    };

    if (buf.dateRange) addRange(buf.dateRange.startDate, buf.dateRange.endDate);
    if (buf.summary && typeof buf.summary === 'object') addRange(buf.summary.startDate, buf.summary.endDate);

    for (const m of this._extractMonthSetFromItems(buf.items || [])) set.add(m);

    return set;
  }

  // ============================================
  // destroy — 세션 전체 상태 초기화
  // ============================================
  destroy() {
    this.buffers.clear();
    this.activeSlot = null;
    Object.keys(this.intentionCache).forEach(k => delete this.intentionCache[k]);
  }

  // alias → canonical 필드명 정규화 //
  normalizeFieldNames(items) {
    const aliasMap = buildAliasMap();
    if (Object.keys(aliasMap).length === 0) return items;

    return items.map(item => {
      const normalized = {};
      for (const [key, value] of Object.entries(item)) {
        const canonical = aliasMap[key];

        if (canonical && !(canonical in item)) {
          normalized[canonical] = value;
        }
        else if (!canonical) {
          normalized[key] = value;
        }
      }
      return normalized;
    });
  }

  // [USER-VISIBLE DEDUP HELPER] availableFields 기반 content-hash dedup //
  //   availableFields scalar leaf 키만 hash key로 사용 — raw PK(productId/userId 등은 availableFields에 보통 없음) 자동 제외
  //   nested availableFields(axis grouping)는 isFieldMetaObject로 구분해 scalar 필드만 추출
  dedupByVisibleFields(items, availableFields) {
    if (!Array.isArray(items) || items.length < 2) {
      return { items, removed: 0 };
    }
    if (!availableFields || typeof availableFields !== 'object') {
      return { items, removed: 0 };
    }

    // availableFields scalar leaf 키 추출 — nested axis group / enum-meta object는 그 자체로는 hash key X
    // (nested의 경우 sample item에 실제 field가 평탄화돼 있어 nested key가 아닌 leaf key를 봐야 함)
    const visibleKeys = [];
    for (const [k, v] of Object.entries(availableFields)) {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        // nested availableFields 그룹 vs enum-meta 구분
        if (isFieldMetaObject(v)) {
          // enum-meta {label, enums} → leaf key
          visibleKeys.push(k);
        }
        else {
          // axis grouping {axis1: {field1: '라벨'}} — group 안의 leaf 키들 추가
          for (const sub of Object.keys(v)) visibleKeys.push(sub);
        }
      }
      else {
        // 단순 라벨 string → leaf key
        visibleKeys.push(k);
      }
    }

    if (visibleKeys.length === 0) {
      return { items, removed: 0 };
    }

    // 모든 row에서 visibleKeys 적어도 1개 이상이 실제로 존재해야 dedup 의미 있음
    const sample = items[0];
    // [NESTED ROSTER DEDUP fix / 운영 5차 rid193-196] dot-notation availableFields 키("reserves.name")는
    //   autoFlatten 후 flat 키("name")가 됨 → suffix 해소해 dedup 해시에 회원 구분 필드 포함 (같은 세션 복수 회원 roster 보존).
    //   미해소 시 회원 구분 필드가 해시에서 누락 → 같은 세션 N명이 reserves[0] 1명으로 collapse. dual sync with nodeAgent.mjs.
    const resolveSampleKey = (k) => {
      if (sample && Object.prototype.hasOwnProperty.call(sample, k)) return k;
      const dot = k.lastIndexOf('.');
      if (dot >= 0) {
        const suffix = k.slice(dot + 1);
        if (sample && Object.prototype.hasOwnProperty.call(sample, suffix)) return suffix;
      }
      return null;
    };
    const presentKeys = [...new Set(visibleKeys.map(resolveSampleKey).filter(Boolean))];
    if (presentKeys.length === 0) {
      return { items, removed: 0 };
    }

    const seen = new Map();
    const deduped = [];
    let removed = 0;

    for (const item of items) {
      if (!item || typeof item !== 'object') {
        deduped.push(item);
        continue;
      }
      // hash: presentKeys 값들을 안정 순서로 직렬화
      const hashParts = [];
      for (const key of presentKeys) {
        const v = item[key];
        // null/undefined → 명시적 sentinel (false positive 차단)
        hashParts.push(v == null ? ' ' : JSON.stringify(v));
      }
      const hash = hashParts.join('');

      if (seen.has(hash)) {
        removed++;
        continue;
      }
      seen.set(hash, true);
      deduped.push(item);
    }

    return { items: deduped, removed };
  }

  // ============================================
  // 1. 데이터 버퍼 (멀티슬롯 구조) — from aiDataBuffer
  // ============================================

  createEmptyBuffer() {
    return {
      raw: null,
      items: null,
      originalItems: null,
      intention: null,
      availableFields: null,
      summary: null,
      appliedFilters: [],
      appliedSort: null,
      columnPrefs: null,
      resolvedColumns: null,
      resolvedColumnLabels: null,
    };
  }

  // 활성 버퍼 참조 (없으면 null) //
  getActiveBuf() {
    if (!this.activeSlot) return null;
    return this.buffers.get(this.activeSlot) || null;
  }

  // availableFields object의 모든 라벨(string + {label} object)을 sanitize — //
  //   structure 보존 (nested 그룹/메타 구조 유지), label만 dev annotation strip //
  //   normalizeAvailableFields와 달리 결과 shape을 입력과 동일하게 유지(string→string / object→object) //
  _sanitizeAvailableFieldsLabels(af) {
    if (!af || typeof af !== 'object') return af;
    const result = {};
    for (const [k, v] of Object.entries(af)) {
      if (typeof v === 'string') {
        result[k] = sanitizeFieldLabel(v);
      } else if (isFieldMetaObject(v)) {
        const cleaned = { ...v };
        if (typeof cleaned.label === 'string') cleaned.label = sanitizeFieldLabel(cleaned.label);
        result[k] = cleaned;
      } else if (v && typeof v === 'object' && !Array.isArray(v)) {
        // 그룹 nested object — 재귀 적용 (axis sibling schema 등)
        result[k] = this._sanitizeAvailableFieldsLabels(v);
      } else {
        result[k] = v;
      }
    }
    return result;
  }

  // 버퍼 초기화 (call_api 호출 시) //
  initBuffer(data, intention, availableFields, slotName = 'default') {
    // [rid188 SMS-DEICTIC-STATUS-SCOPE fix] 새 데이터 로드 시 status 분류 scope 무효화 — 단 같은 분류 데이터(sourceIntention) 재로드는 유지. //
    //   deictic 흐름에서 LLM이 같은 HISTORY를 재로드해도 scope 보존(운영 rid188 run1: T1 buffer 재로드가 scope 날려 274 over). 다른 데이터(미수금 등)만 무효화(오염 차단). dual sync with nodeAgent.mjs //
    if (this._lastStatusScope) {
      const _normIntent = (s) => (typeof s === 'string' ? s.replace(/[+_]/g, '') : '');
      if (_normIntent(intention) !== _normIntent(this._lastStatusScope.sourceIntention)) this._lastStatusScope = null;
    }

    // MAX_SLOTS 초과 체크 (기존 슬롯 덮어쓰기는 허용)
    // [SLOT LRU EVICTION] 신규 슬롯인데 8개 도달 → 에러 대신 가장 오래 안 쓴 비보호 슬롯 회수 후 진행.
    //   회수 대상 전무(전부 보호 슬롯)일 때만 기존 에러 fallback.
    if (!this.buffers.has(slotName) && this.buffers.size >= MAX_SLOTS) {
      const evicted = this._evictLruSlot();
      if (!evicted) {
        return {
          items: null, summary: null,
          error: `최대 ${MAX_SLOTS}개의 슬롯까지 사용 가능합니다. 기존 슬롯명으로 덮어쓰거나 새 세션을 시작하세요.`,
        };
      }
    }

    let { items, summary } = this.extractArray(data);

    // summary는 buf.summary에 별도 보존 — items에 주입하지 않음
    // (주입 시 merge_slots에서 모든 행에 동일한 통계값이 복사되는 P2 버그 유발)

    // availableFields 중첩 구조 해소: 값이 그룹 nested object이면 실제 데이터와 매칭하여 해당 그룹 추출
    // (enum 메타 object { label, enums }는 그룹 nested 아님 — isFieldMetaObject로 구분)
    // 혼합 구조 (nested 그룹 + root 스칼라 메타) 지원: nested 그룹이 1개 이상이면 sample 기반 best match 선택
    let resolvedFields = availableFields;
    // [AXIS SIBLING SCHEMAS] sibling axis 보존 — multi-axis intention(예: _groupBy=lesson/coach/day/time) 의
    //   resolved 외 형제 axis 스키마를 buffer에 보존. show_display COLUMN GUARD가 invalid column이
    //   sibling axis 소속인지 판단하여 axis-switch (call_api with different groupBy) 제안 가능.
    //   generic: groupBy/granularity/mode 등 prefix-keyed sibling group 전반에 적용.
    let axisSchemas = null;
    let currentAxisKey = null;
    if (availableFields && items && items.length > 0) {
      const nestedEntries = Object.entries(availableFields)
        .filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && !isFieldMetaObject(v));
      if (nestedEntries.length > 0) {
        const sampleKeys = new Set(Object.keys(items[0]));
        let bestKey = null, bestGroup = null, bestMatch = 0, bestExtra = Infinity;

        for (const [groupKey, groupFields] of nestedEntries) {
          const groupFieldKeys = Object.keys(groupFields);
          const matchCount = groupFieldKeys.filter(k => sampleKeys.has(k)).length;
          // tie-break: 동일 match일 때 schema의 extra field가 적은 (더 specific한) axis 우선
          //   예: items=9 fields(time axis 정확). lesson(15)과 time(9) 모두 9 match면 → time(extra=0) 선택
          //   axis identifier 컬럼(lessonName/teacherNames vs classTime)으로 mis-resolve 방지
          const extra = groupFieldKeys.length - matchCount;
          if (matchCount > bestMatch || (matchCount === bestMatch && extra < bestExtra)) {
            bestMatch = matchCount; bestGroup = groupFields; bestKey = groupKey; bestExtra = extra;
          }
        }

        if (bestMatch > 0) {
          resolvedFields = bestGroup;
          currentAxisKey = bestKey;
          // 형제 axis 스키마 보존 (resolved 포함 전체) — sibling axis-switch 감지용
          axisSchemas = {};
          for (const [k, v] of nestedEntries) axisSchemas[k] = v;
          if (TOOL_CALLING_LOG) console.log(`[initBuffer] availableFields 중첩 해소: ${bestMatch}개 필드 매칭 (axis=${bestKey}, extra=${bestExtra}, siblings=${nestedEntries.length})`);
        }
      }
    }

    // availableFields 기반 자동 평탄화: items에 기대 필드가 없으면 하위 배열 flatMap
    if (items && items.length > 0 && resolvedFields) {
      items = this.autoFlatten(items, resolvedFields);
    }

    // resolvedFields를 저장용으로 사용 (autoGenerateDisplay 등에서 참조)
    availableFields = resolvedFields || availableFields;

    // [DEV_COMMENT_LEAK guard] availableFields 라벨에서 developer annotation strip — //
    //   LLM context, column label, intro/outro 모두 노출되는 진입점이라 buffer 저장 직전 정화. //
    //   ~26 cluster (#014/#124/#150/#172/#173/#178/#179/#207~217/#226/#244-247/#253-254 등) 동시 cover. //
    availableFields = this._sanitizeAvailableFieldsLabels(availableFields);
    if (axisSchemas) {
      const cleaned = {};
      for (const [k, v] of Object.entries(axisSchemas)) {
        cleaned[k] = this._sanitizeAvailableFieldsLabels(v);
      }
      axisSchemas = cleaned;
    }

    // alias → canonical 필드명 정규화 (normalize: true인 필드만)
    if (items && items.length > 0) {
      items = this.normalizeFieldNames(items);
    }

    // [USER-VISIBLE DEDUP] availableFields에 정의된 user-visible field만 추출해서 content-hash dedup //
    //   배경: backend가 productId/userId 등 raw PK만 다르고 표시 필드(name/price/stock)는 동일한 row를
    //         N개 반환할 수 있음. 사용자 입장에서 같은 row가 N번 = 노이즈/혼란.
    //   범용성: availableFields는 intention 별로 LLM에 노출되는 display 필드 정의 (raw PK는 제외) →
    //         availableFields의 키만 hash 산정에 사용하면 자동으로 user-visible level dedup.
    //         intention-agnostic: PRODUCT/INFRA/COMMUNICATION 등 모든 list intention에 적용.
    //   안전성: dedup은 동일 row 합치는 것 — 정보 손실 X. 모든 row가 unique면 변화 X.
    //   skip 조건: items.length < 2 / availableFields 없음 / availableFields 키가 items에 1개도 없음
    if (items && items.length > 1 && availableFields && typeof availableFields === 'object') {
      const dedupResult = this.dedupByVisibleFields(items, availableFields);
      if (dedupResult.removed > 0) {
        if (TOOL_CALLING_LOG) {
          console.log(`[initBuffer] user-visible dedup: ${items.length} → ${dedupResult.items.length} (removed=${dedupResult.removed})`);
        }
        items = dedupResult.items;
      }
    }

    // [ABSENCE-RATE AUTO-DERIVE / 운영 rid198] 출석 데이터(attendanceRate=예약 출석률)에 결석률(absenceRate) 파생 자동 등록 (nodeAgent.mjs dual sync) //
    //   결석률 = 100 - 예약출석률. 사용자 "결석률" 요청 시 LLM이 무관 필드(attendanceRate 등)를 거짓 라벨/오해하지 않도록
    //   정확한 결석률 필드를 사전 제공. availableFields 등록 + item 값 추가만 (columnPrefs 무변경 → 평소 표 미표출,
    //   결석률 요청 시 LLM이 sort/show_display에 선택). 구조 기반(attendanceRate 필드 존재) — intention/키워드 무참조.
    if (items && items.length > 0 && availableFields && typeof availableFields === 'object'
        && items[0] && ('attendanceRate' in items[0]) && !('absenceRate' in availableFields) && !('absenceRate' in items[0])) {
      const _toAbsence = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round((100 - n) * 10) / 10)) : null;
      };
      items = items.map(it => {
        const ar = _toAbsence(it?.attendanceRate);
        return (ar === null || !it) ? it : { ...it, absenceRate: ar };
      });
      availableFields = { ...availableFields, absenceRate: '결석률(%)' };
    }

    // 같은 슬롯에 같은 인텐션이면 컬럼 프리퍼런스 유지
    const prev = this.buffers.get(slotName);
    const sameIntention = prev?.intention === intention;
    const sameFields = prev?.availableFields && JSON.stringify(prev.availableFields) === JSON.stringify(availableFields);
    const prevPrefs = sameIntention ? prev?.columnPrefs : null;
    const prevColumns = (sameIntention && sameFields) ? prev?.resolvedColumns : null;

    if (TOOL_CALLING_LOG) {
      console.log(`[initBuffer] slot: ${slotName}, intention: ${intention}, sameIntention: ${sameIntention}, items: ${items?.length}, summary:`, summary);
    }

    this.buffers.set(slotName, {
      raw: data,
      items: items ? [...items] : null,
      originalItems: items ? [...items] : null,
      intention,
      availableFields,
      _axisSchemas: axisSchemas,  // sibling axis 스키마 전체 (multi-axis intention 한정)
      _currentAxisKey: currentAxisKey,  // 현재 resolved axis key (예: "_groupBy=lesson")
      summary,
      dateRange: null,  // call_api에서 세팅 { startDate, endDate }
      appliedFilters: [],
      appliedSort: null,
      columnPrefs: prevPrefs,
      resolvedColumns: prevColumns,
      _lru: ++this._slotSeqCounter,  // [SLOT LRU] 생성/덮어쓰기 시점 사용 순번 스탬프
    });

    this.activeSlot = slotName;

    const buf = this.buffers.get(slotName);
    return { items: buf.items, summary };
  }

  // 버퍼 상태 조회 //
  getBuffer(slotName = null) {
    const target = slotName ? this.buffers.get(slotName) : this.getActiveBuf();
    if (!target) return this.createEmptyBuffer();
    return { ...target };
  }

  // 버퍼 상태 스냅샷 (도구 result에 공통 첨부) //
  getBufferSnapshot() {
    const buf = this.getActiveBuf();
    if (!buf?.items?.[0]) return null;
    return {
      itemCount: buf.items.length,
      fields: Object.keys(buf.items[0]).filter(k => !k.startsWith('_')),
      sort: buf.appliedSort,
      filters: buf.appliedFilters?.length || 0,
      columnPrefs: buf.columnPrefs
        ? { include: buf.columnPrefs.include || [], exclude: buf.columnPrefs.exclude || [] }
        : null,
    };
  }

  // 슬롯 날짜 범위 세팅 (call_api에서 호출) //
  setSlotDateRange(slotName, startDate, endDate) {
    const buf = this.buffers.get(slotName);
    if (buf) buf.dateRange = { startDate, endDate };
  }

  // 기존 슬롯들의 날짜 범위 조회 (겹침 감지용) //
  getSlotDateRanges() {
    const ranges = {};
    for (const [name, buf] of this.buffers) {
      if (buf.dateRange) ranges[name] = buf.dateRange;
    }
    return ranges;
  }

  // [Architecture B 일원화 2026-05-19] intention key로 buffer slot lookup — visualization 도구가 multi-endpoint 데이터 access //
  // slotPreference: 명시된 slot 이름이 있으면 우선 (multi-period 시 'current' / 'previous' 구분용)
  findBufferByIntention(intentionKey, slotPreference) {
    if (!this.buffers || typeof this.buffers.keys !== 'function' || !intentionKey) return null;
    // slot 이름 명시된 경우 우선 매칭
    if (slotPreference && this.buffers.has(slotPreference)) {
      const b = this.buffers.get(slotPreference);
      if (b?.intention === intentionKey) return b;
    }
    // intention 일치 첫 buffer 반환
    for (const buf of this.buffers.values()) {
      if (buf?.intention === intentionKey) return buf;
    }
    return null;
  }

  // 확정된 컬럼 저장 (autoGenerateDisplay에서 호출) //
  saveResolvedColumns(columns) {
    const buf = this.getActiveBuf();
    if (buf) buf.resolvedColumns = columns ? [...columns] : null;
  }

  // [P1-D3] 확정된 컬럼 라벨 map 저장 — outro {{TABLE_COLS}} placeholder 치환용
  saveResolvedColumnLabels(columnLabels) {
    const buf = this.getActiveBuf();
    if (buf) buf.resolvedColumnLabels = columnLabels ? { ...columnLabels } : null;
  }

  // 버퍼 전체 리셋 //
  resetBuffer() {
    this.buffers.clear();
    this.activeSlot = null;
    this._comparisonActive = false;
    this._mergeOutputSlot = null;
    this._lastMergeMode = null;
    this._lastMergeIntention = null;
  }

  // 비교 슬롯 정리 (base, compare_*, merge 결과 삭제) //
  clearComparisonSlots() {
    for (const name of [...this.buffers.keys()]) {
      if (name === 'base' || name.startsWith('compare_') || name === this._mergeOutputSlot) {
        this.buffers.delete(name);
      }
    }
    this._mergeOutputSlot = null;
    this._lastMergeMode = null;
    this._lastMergeIntention = null;
    if (this.activeSlot && !this.buffers.has(this.activeSlot)) {
      this.activeSlot = this.buffers.keys().next().value || null;
    }
  }

  // 새 run 시작 시 비교 플래그 리셋 //
  resetComparisonFlag() {
    this._comparisonActive = false;
  }

  // [L5-CROSS-004 fix 2026-05-25] turn 경계에서 1회 한정 가드 플래그 reset
  // 이전 turn에서 set된 _finishAllowedByGuard 등이 다음 turn 첫 도구 호출을 false-positive 차단하는 문제 차단.
  // chain (L5/L9/L11 multi-turn) case에서 state leak 방지.
  resetTurnFlags() {
    this._finishAllowedByGuard = false;
    this._finishGuardSuggestedText = null;
    this._calledApiThisTurn = false;
    this._turnCallApiSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 call_api 적재 슬롯 (dual sync with nodeAgent.mjs)
    this._turnMergedSourceSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 성공 merge 소스 슬롯 — append 후보 제외
    this._turnDisplayedSlots = new Set();  // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 표시 성공 슬롯 — 다축 모두-표시 시 append 후보 제외
    this._undisplayedCitedThisTurn = false;  // [UNDISPLAYED METRIC AUTO-APPEND] turn당 1회 보강
    this._memberDetailAspectAugmentedThisTurn = false;  // [#10 MEMBER-DETAIL MULTI-ASPECT COMPLETION] turn당 1회 (dual sync with nodeAgent.mjs)
    this._madForceFetchingThisTurn = false;  // [#10 EARLY COMPLETION] force-fetch 재귀 가드 (dual sync with nodeAgent.mjs)
    this._perAxisSlotCorrected = false;  // [PER-AXIS ACTIVE SLOT CORRECTION] turn당 1회 (show_display 진입부)
    this._dateParamGuardBlockedCount = 0;  // [DATE PARAM GUARD] 절대 기간 표현 + dates 누락 차단 횟수 (max 2, turn별 리셋)
    this._getIntentionsCalledThisTurn = false;  // [INTENTION GATE] dual sync with nodeAgent.mjs
    this._intentionGateBlocked = false;  // [INTENTION GATE] dual sync with nodeAgent.mjs
    this._getGuideFailedThisTurn = false;  // [HOWTO/ADVICE GATE] dual sync with nodeAgent.mjs
    this._howtoAdviceGateBlockedCount = 0;  // [HOWTO/ADVICE GATE] dual sync with nodeAgent.mjs
    this._howtoAdviceFinishRefusalRetryCount = 0;  // [HOWTO/ADVICE FINISH] dual sync with nodeAgent.mjs
    this._adviceFollowupGateBlockedCount = 0;  // [ADVICE FOLLOW-UP GATE] dual sync with nodeAgent.mjs
    this._multiPeriodAutoChainFiredThisTurn = false;
    this._vizOnlyFallbackRerouted = false;  // [#655] dual sync with nodeAgent.mjs
    this._contextReuseRedirectAttempted = false;
    this._showDisplayEmptyRowsBlocked = false;
    this._showDisplayRecoveryAttempted = false;
    this._showDisplayCountQuestionBlocked = false;
    this._nationalityCountBlocked = false;
    this._showDisplayChangeClaimBlocked = false;
    this._showDisplayBufferMismatchBlocked = false;
    this._showDisplayCenterInfoBlocked = false;  // [CENTER-INFO STALE RENDER] dual sync with nodeAgent.mjs
    this._centerInfoPrepPassCount = 0;  // [CENTER-INFO PREP PASS] POST-GUARD 준비도구 통과 횟수 (iteration당 1, cap 5, serialize 제외, dual sync with nodeAgent.mjs)
    this._centerInfoPrepLastIter = -1;  // [CENTER-INFO PREP PASS] 같은 iteration 중복 증가 방지
    this._unmetHistoryCondApplied = null;  // [UNMET HISTORY-COND] dual sync with nodeAgent.mjs
    this._bufferMismatchInfo = null;  // [C-#6] dual sync with nodeAgent.mjs
    this._bufferMismatchFinishRetryAttempted = false;  // [C-#6] dual sync with nodeAgent.mjs
    this._bareCountFinishRetryAttempted = false;  // [BARE-COUNT AMBIGUITY GUARD 15] dual sync with nodeAgent.mjs
    this._turnFilterApplied = false;  // [BARE-COUNT AMBIGUITY] 본 turn LLM filter 적용 = active 앵커링 신호 (dual sync with nodeAgent.mjs)
    this._showDisplayColMismatchBlocked = false;
    this._autoChainRequiresDisplay = false;
    this._outroRetryAttempted = false;
    this._showDisplayDimensionRedirectAttempted = false;
    this._showDisplayPeriodMismatchAttempted = false;
    this._showDisplayNumericRetryCount = 0;
    this._finishNumericRetryCount = 0;
    this._showDisplayEntityRetryCount = 0;
    this._detailNotFoundContext = null;
    this._detailFallbackRetryAttempted = false;
    this._detailFallbackShowDisplayBlocked = false;
    this._extremeLimitRetryAttempted = false;
    this._catastrophicNameDropRetryAttempted = false;
    this._multiPeriodMergeAnchorDropRetryAttempted = false;  // [F11 Issue A] turn별 리셋
    this._entityDetailMissingRetryAttempted = false;
    this._showDisplaySucceededThisTurn = false;
    this._columnEnumRetryAttempted = false;
    this._axisIntentRetryAttempted = false;  // [AXIS INTENT MISMATCH] turn별 리셋
    this._showDisplayFormMismatchBlocked = false;  // [1-M β] DATA FORM MISMATCH 차단 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._axisAutoSwitchedThisTurn = false;  // [AXIS AUTO-SWITCH] sibling axis 슬롯 자동전환 1회/turn (turn별 리셋, dual sync with nodeAgent.mjs)
    this._autoUserTermAppliedThisTurn = null;  // [AUTO USER TERM PROTECTION] 본 turn AUTO USER TERM 적용 컨텍스트 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._smsRowScopeThisTurn = null;  // [SMS-INDIVIDUAL-SUBSET fix] turn별 리셋 (dual sync with nodeAgent.mjs)
    this._smsRowScopeOriginalItems = null;  // [SMS-INDIVIDUAL-SUBSET fix] turn별 리셋 (dual sync with nodeAgent.mjs)
    this._periodRestrictionRedisplayNudged = false;  // [L9a-MEMBER-003 cycle 1] PERIOD-RESTRICTION FOLLOW-UP nudge 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._entityNotFoundContext = null;  // [L9b-CROSS-009 cycle 1] prev buffer entity list + user mentioned name not in set → outro auto-prepend context (turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
    this._entityNotFoundCallApiBlocked = false;  // [L9b-CROSS-009 cycle 2] call_api block 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
    this._showDisplayPeriodNarrowBlocked = false;  // [L21-COMPARE-FOLLOWUP-001 cycle 1] NARROW PERIOD CHAIN FOLLOW-UP — HONEST INFORM 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._dataToolPeriodNarrowBlocked = false;  // [L21-COMPARE-FOLLOWUP-001 cycle 2] NARROW PERIOD CHAIN FOLLOW-UP — data-processing tool HONEST INFORM 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._compareIntentDriftNarrowBlockCount = 0;  // [L21-COMPARE-FOLLOWUP-001 cycle 3] NARROW PERIOD CHAIN FOLLOW-UP — INTENT-DRIFT block 카운터 (turn별 리셋, max 3회 발동 후 해제, dual sync with nodeAgent.mjs)
    this._periodChangeRefetchBlocked = false;  // [STALE PERIOD RELABEL rid63/65/68] show_display 경로 stale 재라벨 차단 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs)
    this._periodChangeFinishBlockCount = 0;  // [STALE PERIOD RELABEL rid63/65/68] finish-tool/text-only-stop 경로 stale 재라벨 차단 카운터 (cap 3, turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
    this._postMergeShowDisplayAllowedOnce = false;  // [L11-SWITCH-SLOT-001 cycle 2] merge_slots 성공 후 1회 한정 show_display dup-block bypass (turn별 리셋, dual sync with nodeAgent.mjs)
    this._multiAxisShowDisplayAllowedOnce = false;  // [MULTI-AXIS COMPOSITION] 다축 구성(나이대 AND 성별 등) — 다른 축 compute(group) 후 1회 한정 show_display dup-block bypass (turn별 리셋, dual sync with nodeAgent.mjs)
    this._wodEmptyParticipantBlocked = false;  // [L10-SCHEDULE-WORKOUT-001 cycle 2] WOD empty → PARTICIPANT cascade 차단 1회 한정 (turn별 리셋, dual sync with nodeAgent.mjs + useAiChat.js)
  }

  // 턴 경계 슬롯 정리: 비교 슬롯만 제거, 일반 슬롯 보존 //
  clearStaleSlots() {
    if (this._mergeOutputSlot) return;

    for (const name of [...this.buffers.keys()]) {
      if (name === 'base' || name.startsWith('compare_')) {
        this.buffers.delete(name);
      }
    }

    if (this.activeSlot && !this.buffers.has(this.activeSlot)) {
      this.activeSlot = this.buffers.keys().next().value || null;
    }
  }

  // ============================================
  // 1-1. 멀티슬롯 관리
  // ============================================

  // [SLOT LRU EVICTION] buffers가 MAX_SLOTS 도달 + 신규 슬롯 필요 시, 보호 슬롯 제외 가장 오래 안 쓴 슬롯 1개 회수 //
  //   배경: 긴 운영 세션에서 LLM이 call_api마다 새 슬롯명(may/jun/day/alive/...)을 부여 → 8개 도달 후 모든 신규 //
  //         call_api가 "최대 8개의 슬롯" 에러로 거부 → 세션 후반 마비(운영 store398 trace T12~T19 cascade). //
  //   보호: active / _merged(merge 결과 = _mergeOutputSlot) / 진행 중 비교 슬롯(base/compare_*, _comparisonActive 시). //
  //   대상: 보호 외 슬롯 중 _lru(마지막 생성/전환 순번) 최소 슬롯. 전무 시 null → 호출부가 기존 에러 fallback. //
  //   안전성: MAX_SLOTS 미도달 세션은 호출 자체가 안 됨(완전 no-op). 범용 — case-ID/입력 문자열 하드코딩 X. //
  _evictLruSlot() {
    let victim = null;
    let victimLru = Infinity;
    for (const [name, buf] of this.buffers) {
      if (name === this.activeSlot) continue;
      if (name === '_merged' || name === this._mergeOutputSlot) continue;
      if (this._comparisonActive && (name === 'base' || name.startsWith('compare_'))) continue;
      const lru = buf?._lru || 0;
      if (lru < victimLru) { victimLru = lru; victim = name; }
    }
    if (victim) {
      this.buffers.delete(victim);
      if (TOOL_CALLING_LOG) console.log(`[initBuffer] SLOT LRU EVICTION: "${victim}" 회수 (_lru=${victimLru}, ${this.buffers.size}/${MAX_SLOTS} 후)`);
      return victim;
    }
    return null;
  }

  // 활성 슬롯 전환 //
  setActiveSlot(slotName) {
    if (!this.buffers.has(slotName)) {
      return { success: false, message: `Slot "${slotName}" not found. Available: [${this.getSlotNames().join(', ')}]` };
    }
    this.activeSlot = slotName;
    const buf = this.buffers.get(slotName);
    if (buf) buf._lru = ++this._slotSeqCounter;  // [SLOT LRU] 전환 = 사용 → 순번 갱신(회수 보호)
    return {
      success: true,
      activeSlot: slotName,
      intention: buf.intention,
      count: buf.items?.length || 0,
      message: `활성 슬롯을 "${slotName}"으로 전환했습니다. (${buf.items?.length || 0}건)`,
    };
  }

  // 슬롯 이름 목록 //
  getSlotNames() {
    return Array.from(this.buffers.keys());
  }

  // [AUTO-RECOVER] 활성 버퍼 schema 불일치 시 전환할 정상 item-row 슬롯 탐색 //
  //   items 키가 자기 availableFields와 ≥50% 일치하는 첫 슬롯 반환(없으면 null). _merged(비교 결과)/제외 슬롯은 skip.
  //   intent 판단/키워드 0 — 순수 구조(스키마 매칭)만 사용. dual sync with nodeAgent.mjs.
  _findRenderableSiblingSlot(excludeName) {
    for (const name of this.getSlotNames()) {
      if (name === excludeName || name === '_merged') continue;

      const buf = this.buffers.get(name);

      if (!buf?.items?.length || !buf.availableFields || typeof buf.availableFields !== 'object') continue;

      const fieldKeys = Object.keys(buf.availableFields);

      if (fieldKeys.length === 0) continue;

      const itemKeys = new Set();

      for (let i = 0; i < Math.min(buf.items.length, 10); i++) {
        const it = buf.items[i];
        if (it && typeof it === 'object') for (const k of Object.keys(it)) itemKeys.add(k);
      }

      if (fieldKeys.filter(k => itemKeys.has(k)).length / fieldKeys.length >= 0.5) return name;
    }

    return null;
  }

  // columns가 가리키는 sibling axis(_groupBy=X 등)로 로드된 슬롯 탐색 — AXIS AUTO-SWITCH용 (dual sync with nodeAgent.mjs) //
  _findSlotByAxisKey(axisKey, excludeName) {
    for (const name of this.getSlotNames()) {
      if (name === excludeName || name === '_merged') continue;

      const buf = this.buffers.get(name);

      if (buf && buf._currentAxisKey === axisKey && buf.items?.length) return name;
    }

    return null;
  }

  // 슬롯별 요약 정보 //
  getSlotSummaries() {
    const summaries = {};
    for (const [name, buf] of this.buffers) {
      summaries[name] = {
        intention: buf.intention,
        count: buf.items?.length || 0,
        fields: buf.availableFields ? Object.keys(buf.availableFields) : [],
        // [P1-D4] availableFields 전체를 노출하여 summary serialize 시 단위 토큰 lookup 가능
        availableFields: buf.availableFields || null,
        dateRange: buf.dateRange || null,
        // [B1] backend response scalar 메타값(예: activeRate) 노출 — intro/outro hallucination 차단
        summary: buf.summary && typeof buf.summary === 'object' && Object.keys(buf.summary).length > 0
          ? buf.summary
          : null,
        appliedFilters: buf.appliedFilters?.length > 0
          ? buf.appliedFilters.map(f => ({ field: f.field, condition: f.condition }))
          : null,
      };
    }
    return summaries;
  }

  // [MULTI-SLOT] {slotName: summaryObj} 맵 — substitutePlaceholders 3rd arg용.
  //   슬롯 인지 {{SUMMARY:<slot>:<key>}} resolve + bare key 멀티슬롯 ambiguity 안전망 (ISSUE #1).
  getSlotSummaryMap() {
    const m = {};
    for (const [name, e] of Object.entries(this.getSlotSummaries())) {
      if (e && e.summary && typeof e.summary === 'object') m[name] = e.summary;
    }
    return m;
  }

  // [BARE-COUNT AMBIGUITY] bare {{COUNT}} 참조 모호 turn 판정 — finishPolicy GUARD 15 meta + substitute backstop 공용.
  //   본질: 멀티슬롯 turn의 text-only finish에서 bare {{COUNT}}는 활성 슬롯 count로만 치환 — LLM이 붙인 참조 명사가
  //         타 슬롯이면 거짓 (운영 L9a-CENTERINFO run3: 활성 center(1)로 "유효회원은 총 1명").
  //   신호(전부 구조적, 사용자 키워드 0): ① 본 turn 사용자 call_api 슬롯(내부 오케스트레이션 `_` prefix 제외) ≥2
  //   ② 그 슬롯들(buffers 실재 + items ≥1만) count divergent ③ 본 turn 표시 성공 0(text-only)
  //   ④ filter 미적용(적용 시 active 앵커링 = filtered count 정당) ⑤ 활성이 merge/퍼널 산출 아님(merge count는 결정적 정답).
  //   적대검증 반영: FP-1 `_src_` prefetch 제외 / FP-2 스칼라 0건 제외 / FP-3 filter skip(compute는 원버그 turn 실재로 제외) / FP-4 merge skip.
  //   dual sync with nodeAgent.mjs //
  getBareCountAmbiguity() {
    try {
      if (this._turnDisplayedSlots && this._turnDisplayedSlots.size > 0) return null;
      if (this._turnFilterApplied) return null;
      const _userTurnSlots = [...(this._turnCallApiSlots || [])].filter(n => typeof n === 'string' && !n.startsWith('_'));
      if (_userTurnSlots.length < 2) return null;
      const _activeBufBC = this.buffers.get(this.activeSlot);
      const _activeIntentionBC = _activeBufBC?.intention || '';
      if (this.activeSlot === '_merged' || _activeIntentionBC === 'MERGED' || _activeIntentionBC === 'CONVERSION_FUNNEL') return null;
      const _counted = [];
      for (const _n of _userTurnSlots) {
        const _b = this.buffers.get(_n);
        if (!_b || !Array.isArray(_b.items) || _b.items.length < 1) continue;
        _counted.push([_n, _b.items.length]);
      }
      if (_counted.length < 2) return null;
      if (new Set(_counted.map(([, c]) => c)).size < 2) return null;
      return { slotCounts: _counted.map(([n, c]) => `${n}(${c})`).join(' / ') };
    } catch (e) {
      return null;
    }
  }

  // 후속 turn에 시간 표현이 없으면 SQL의 default {today-N}~{today} 토큰을 가장 최근 buffer dateRange로 치환 //
  // L5-CROSS-004 등 referential 멀티턴에서 T0 기간 컨텍스트 보존 (LLM 비결정성 방어) //
  applyBufferDateRangeToSqlString(sqlQuery, userInput) {
    if (!sqlQuery || typeof sqlQuery !== 'string') return sqlQuery;
    const TIME_KW = /오늘|어제|내일|지난주|이번주|다음주|지난달|이번달|다음달|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+일\s*전|작년|올해/;
    if (TIME_KW.test(userInput || '')) return sqlQuery;

    const candidates = [...this.buffers.values()].filter(b => b.dateRange?.startDate && b.dateRange?.endDate);
    if (candidates.length === 0) return sqlQuery;
    const r = candidates[candidates.length - 1].dateRange;

    const replaced = sqlQuery.replace(
      /\(\s*['"]\{today(?:-\d+)?\}['"]\s*,\s*['"]\{today(?:[+-]\d+)?\}['"]\s*\)/g,
      `('${r.startDate}','${r.endDate}')`
    );
    if (replaced !== sqlQuery) {
      console.log(`[SQL] buffer dateRange inherited: ${r.startDate}~${r.endDate}`);
    }
    return replaced;
  }

  // 비교 플로우 상태 반환 — 슬롯 존재 여부 기반 //
  getComparisonStatus() {
    const slots = this.getSlotNames();
    return {
      active: this._comparisonActive,
      hasBase: slots.includes('base'),
      compareSlots: slots.filter(s => s.startsWith('compare_')),
      hasMerged: this._mergeOutputSlot !== null && slots.includes(this._mergeOutputSlot),
    };
  }

  // 버퍼 상태 요약 — 도구 결과 message에 append용 //
  getSlotContext() {
    const parts = [];
    let hasExtra = false;
    for (const [name, buf] of this.buffers) {
      const count = buf.items?.length || 0;
      const tags = [];
      if (name === this.activeSlot) tags.push('active');
      if (buf.intention === 'MERGED') tags.push('merged');
      if (buf.dateRange) {
        tags.push(`${buf.dateRange.startDate}~${buf.dateRange.endDate}`);
        hasExtra = true;
      }
      const sample = buf.items?.[0];
      const fields = sample && typeof sample === 'object'
        ? Object.keys(sample)
        : (buf.availableFields ? Object.keys(buf.availableFields) : []);
      const fieldsStr = fields.length > 0 ? `,fields:[${fields.join(',')}]` : '';
      parts.push(`${name}(${count}${tags.length ? ',' + tags.join(',') : ''}${fieldsStr})`);
    }
    if (!hasExtra && this.buffers.size <= 1) return '';
    return ` [slots: ${parts.join(', ')}]`;
  }

  // [CROSS-MERGE OPPORTUNITY] entity-level cross 가능성 자동 감지 (사전 0, 데이터 기반)
  // 트리거: active 외 entity-level buffer 2개+ + 공통 식별자 후보(unique 비율 >= 70%) 있음
  getCrossMergeHint() {
    const otherBufs = [...this.buffers.entries()]
      .filter(([n, b]) => n !== this.activeSlot && (b.items?.length || 0) >= 2 && b.intention !== 'MERGED');
    if (otherBufs.length < 2) return '';

    const samples = otherBufs.map(([, b]) => b.items[0] || {});
    let common = new Set(
      Object.keys(samples[0]).filter(k => samples[0][k] != null && typeof samples[0][k] !== 'object')
    );
    for (let i = 1; i < samples.length; i++) {
      const next = new Set(
        Object.keys(samples[i]).filter(k => samples[i][k] != null && typeof samples[i][k] !== 'object')
      );
      common = new Set([...common].filter(k => next.has(k)));
    }
    if (common.size === 0) return '';

    const idCandidates = [...common].filter(f => {
      return otherBufs.every(([, b]) => {
        const vals = b.items.map(it => it[f]).filter(v => v != null);
        if (vals.length === 0) return false;
        return new Set(vals).size / vals.length >= 0.7;
      });
    });
    if (idCandidates.length === 0) return '';

    const [bufA, bufB] = otherBufs.slice(0, 2);
    const keyEx = idCandidates[0];
    const activeName = this.activeSlot || '<active>';
    const sourceList = otherBufs.slice(0, 3).map(([n, b]) => `${n}(${b.items.length}건)`).join(', ');
    return `\n[CROSS-MERGE OPPORTUNITY] 사용자 입력이 직전 데이터의 다른 차원 조건과 현재 차원의 cross/교집합을 요청한다면 entity-level cross-merge 시도 가능:\n- source 후보: ${sourceList}\n- 공통 식별자 후보(데이터 unique 비율 ≥70%): [${idCandidates.slice(0, 5).join(', ')}]\n- 시도 예시:\n  1) merge_slots([{slotName:"${bufA[0]}"},{slotName:"${bufB[0]}"}], mode:"compare", matchKey:"${keyEx}", outputSlot:"_byEntity")\n  2) (변화/감소/증가 조건이 있으면) filter({field:"_change_<value field>", condition:{type:"numeric_lt", value:0}})\n  3) merge_slots([{slotName:"_byEntity"},{slotName:"${activeName}"}], mode:"intersect", matchKey:"${keyEx}")\n  4) show_display`;
  }

  // 단일 슬롯 삭제 //
  clearSlot(slotName) {
    if (!this.buffers.has(slotName)) {
      return { success: false, message: `Slot "${slotName}" not found.` };
    }
    this.buffers.delete(slotName);
    if (this.activeSlot === slotName) {
      this.activeSlot = this.buffers.size > 0 ? this.buffers.keys().next().value : null;
    }
    return { success: true, message: `슬롯 "${slotName}" 삭제 완료.`, activeSlot: this.activeSlot };
  }

  // [AUTO USER TERM] 사전 등록된 어휘 + 모디파이어 매칭 시 자동 도구 chain (call_api + filter + sort) 실행 //
  // 반환: { success, intention, field, threshold, modifier, before, after } | null
  async autoApplyTermDefinition(input) {
    const detected = detectTermAndModifier(input);
    if (!detected) return null;

    const def = lookupTermDefinition(detected.term, detected.modifier);
    if (!def?.relatedIntention || !def?.metricField || !def?.averageKey) return null;

    // [AXIS GUARD] input에 명시 axis가 USER TERM의 defaultAxis와 다르고 alternativeAxes에 포함되면 skip (LLM 위임)
    const detectedAxis = detectAxisFromInput(input);
    const entry = USER_TERM_DICTIONARY[detected.term];
    if (detectedAxis && entry?.defaultAxis && detectedAxis !== entry.defaultAxis
        && entry.alternativeAxes?.includes(detectedAxis)) {
      if (TOOL_CALLING_LOG) console.log(`[autoApplyTermDefinition] AXIS GUARD: input axis="${detectedAxis}" ≠ term defaultAxis="${entry.defaultAxis}" → skip`);
      return null;
    }

    // intentionCache 적재 보장
    if (def.domain && !this.intentionCache[def.domain]) {
      try { await this.executeGetIntentions({ category: def.domain }); }
      catch (e) { return null; }
    }

    let apiResult;
    try {
      apiResult = await this.executeCallApi({ intention: def.relatedIntention });
    } catch (e) {
      return null;
    }
    if (!apiResult?.success) return null;

    const activeBuf = this.getBuffer();
    const summary = activeBuf?.summary || {};
    const threshold = summary[def.averageKey];
    if (typeof threshold !== 'number') return null;

    const filterType = detected.modifier === '낮은' ? 'numeric_lte' : 'numeric_gte';
    const sortDir = detected.modifier === '낮은' ? 'asc' : 'desc';

    const filterResult = this.applyFilter({
      field: def.metricField,
      condition: { type: filterType, value: threshold }
    });

    if (!filterResult.success || filterResult.after === 0) {
      // 0건 또는 fail → 직접 롤백
      const original = activeBuf?.originalItems || [];
      if (original.length > 0) activeBuf.items = [...original];
      activeBuf.appliedFilters = (activeBuf.appliedFilters || []).filter(f => f.field !== def.metricField);
      activeBuf.resolvedColumns = null;
      return null;
    }

    this.applySort({ field: def.metricField, direction: sortDir });

    if (TOOL_CALLING_LOG) console.log(`[runAgent] AUTO USER TERM: ${def.relatedIntention} → filter ${def.metricField} ${filterType} ${threshold} → ${filterResult.before}→${filterResult.after}건 + sort ${sortDir}`);

    // [AUTO USER TERM PROTECTION] 본 turn 한정 — 동일 intention call_api 재호출 차단용 plate (dual sync with nodeAgent.mjs)
    //   본질: LLM이 show_display 실패 후 get_intentions/call_api로 fallback하여 filtered buffer를 다시 raw N건으로 덮어쓴 다음
    //         "전체 평균보다 낮은 N건" 같은 HOWTO criterion intro를 적는데 N=raw count (filter 안 됨) → HOWTO_GOT_DATA 본질.
    //   guard: executeCallApi 진입점에서 본 intention 매칭 시 BLOCK + show_display 안내 (turn별 리셋)
    this._autoUserTermAppliedThisTurn = {
      intention: def.relatedIntention,
      field: def.metricField,
      threshold,
      modifier: detected.modifier,
      term: detected.term,
      before: filterResult.before,
      after: filterResult.after,
    };

    return {
      success: true,
      intention: def.relatedIntention,
      field: def.metricField,
      threshold,
      modifier: detected.modifier,
      term: detected.term,
      before: filterResult.before,
      after: filterResult.after,
    };
  }

  // [AUTO CROSS-REFERENCE] 직전 buffer가 작은 entity list + input demonstrative+보유 패턴 시 자동 cross-merge //
  // 반환: { success, enumField, enums, memberCount, slot } | null
  async autoCrossReferenceIfMatched(input) {
    // [NUMERIC SUBSET SKIP] "N회/원 이상/이하" 수치 필터 의도는 회원권명 cross-reference가 아니라
    //   직전 buffer에 대한 직접 수치 필터 의도다. 이를 cross-merge로 오인하면 출석 수치 field가 없는
    //   ALIVE(cross_member) 슬롯을 active로 만들어 무관 필드(viewCount=잔여 횟수 등) 오필터를 유발한다
    //   (운영 4차 #A rc2: "주 3회 이상"을 cross-ref로 오발동 → viewCount 필터 → 거짓 31명).
    //   수치 의도면 LLM 직접 처리에 위임. dual sync with nodeAgent.mjs.
    if (detectNumericSubsetIntent(input)) return null;

    const prevBuf = this.getBuffer?.();
    if (!prevBuf?.items?.length || prevBuf.items.length > 30) return null;

    const demonstrative = /해당|이|그|저|위|방금|같은|위에서|직전/;
    const entityVerb = /보유|속한|가진|있는|쓰는|사용/;
    const memberWord = /회원|명/;
    if (!demonstrative.test(input) || !entityVerb.test(input) || !memberWord.test(input)) return null;

    const sample = prevBuf.items[0] || {};
    const enumField = ['memberShipName', 'membershipName', 'viewMembershipName', 'lessonName', 'coachName'].find(f => f in sample);
    if (!enumField) return null;

    // [MEMBER-SOURCE SELF-EXPANSION GUARD] enumField가 회원권명 "속성"(memberShipName/viewMembershipName)이고
    //   소스 buffer가 이미 회원 목록이면 "이 회원(들)이 가진 회원권"은 그 회원 본인의 drill-down이지 같은
    //   회원권 가진 타회원으로의 확장이 아니다. self-확장은 소스보다 늘리기만 해 의도를 위배한다
    //   (운영 5차 rid59: 하채린 1명→같은 "횟수 10회권" Kim 혼입 2명 / 곧이탈 4명→같은 회원권명 전체 43명).
    //   cross-ref legit 용도는 비-회원 엔티티(회원권 종류/수업)→회원 확장(L9b-CROSS-010)이며 그 경우 enumField는
    //   membershipName/lessonName 등 엔티티 정체다. dual sync with nodeAgent.mjs.
    const isMemberSource = ('memberId' in sample) || (('name' in sample) && (('phone' in sample) || ('userId' in sample)));
    if (isMemberSource && (enumField === 'memberShipName' || enumField === 'viewMembershipName')) return null;

    const enums = [...new Set(prevBuf.items.map(i => i[enumField]).filter(v => v != null && typeof v === 'string'))];
    if (!enums.length) return null;

    const targetSlot = 'cross_member';
    const targetCategory = 'MEMBER';
    const targetIntention = 'CHECK_MEMBER+ALIVE_LIST';

    // intentionCache 적재 보장
    if (!this.intentionCache[targetCategory]) {
      try { await this.executeGetIntentions({ category: targetCategory }); }
      catch (e) { return null; }
    }

    let apiResult;
    try {
      apiResult = await this.executeCallApi({ intention: targetIntention, slot: targetSlot });
    } catch (e) {
      return null;
    }
    if (!apiResult?.success) return null;

    const prevActive = this.activeSlot;
    this.activeSlot = targetSlot;

    const targetBuf = this.buffers.get(targetSlot);
    const targetField = ['viewMembershipName', 'memberShipName', 'membershipName'].find(f => targetBuf?.items?.[0] && f in targetBuf.items[0]) || 'viewMembershipName';

    const filterResult = this.applyFilter({
      field: targetField,
      condition: { type: 'exact', value: enums },
    });

    if (!filterResult.success || filterResult.after === 0) {
      this.activeSlot = prevActive;
      return null;
    }

    if (TOOL_CALLING_LOG) console.log(`[runAgent] AUTO CROSS-REFERENCE: ${enums.length} enums (${enumField}) → ${filterResult.after} members on "${targetField}"`);

    return {
      success: true,
      enumField,
      enums,
      targetField,
      memberCount: filterResult.after,
      slot: targetSlot,
    };
  }

  // [DEICTIC EMPTY BUFFER GUARD] deictic reference + active buffer 0건 감지 //
  // 반환: true (가드 발동) | false (정상)
  detectDeicticEmptyBuffer(input) {
    const prevBuf = this.getBuffer?.();
    const itemCount = prevBuf?.items?.length || 0;

    if (itemCount > 0) return false;

    // [Cat8-REPEAT fix] 감정/일반 문맥 false positive 방지 — GREETING/emotional input은 deictic 아님 (dual sync with nodeAgent.mjs)
    const EMOTIONAL_CONTEXT = /힘들|걱정|고민|불안|어렵|어떡|화나|짜증|속상|슬프|지치|피곤|스트레스|우울|힘내|격려|응원|위로/;
    if (EMOTIONAL_CONTEXT.test(input)) return false;

    // [L11-SMS-FULL-001 fix] SMS 발송 요청은 deictic 아님 — 신규 조회+SMS 복합 요청 (dual sync with nodeAgent.mjs)
    const SMS_SEND_REQUEST = /문자|메시지|SMS|문구/;
    if (SMS_SEND_REQUEST.test(input)) return false;

    // [L30-CONCEPT-002 fix] "~것 같은 회원" = 추상 형용 (신규 조회), deictic 아님 — "같은 회원" false positive 방지 (dual sync with nodeAgent.mjs)
    const ABSTRACT_QUALIFIER = /(?:것|거|듯|듯한|것처럼)\s*같은/;
    if (ABSTRACT_QUALIFIER.test(input)) return false;

    // [DEICTIC NAME FALSE-POSITIVE fix] bare 명은 뒤에 한글 미연속일 때만 — 성씨 이+명○ 실명("이명희 연장 내역")이 "이 명" 오매칭돼 첫턴 조회를 0건 거부하던 결함. 명단은 명시 토큰 승격(기존엔 명+단 우연 매칭 의존) (dual sync with nodeAgent.mjs) //
    const DEICTIC = /(?:^|[\s,])(?:이|해당|그|저|위|방금|같은|위에서|직전|이런|그런|저런)\s*(?:회원|분들?|사람|것들?|명단|명(?![가-힣])|목록|리스트|데이터|결과|내역|항목)/;
    // [Cat8-REPEAT fix] DEICTIC_SUFFIX에 data-action verb 요구 — "회원도 줄고"(emotional)/"회원들은 평균"(신규 분석질문) vs "회원을 보여줘"(deictic) 구분 (dual sync with nodeAgent.mjs)
    const DEICTIC_SUFFIX = /(?:회원들?|분들?|사람들?|것들?)\s*(?:을|를|에게|한테)\s*.{0,10}(?:보여|알려|정리|조회|확인|보내|추려|뽑아|찾아|검색)/;

    if (DEICTIC.test(input) || DEICTIC_SUFFIX.test(input)) return true;

    return false;
  }

  // [rid192 SMS-RECOMMEND-MISSING GUARD] SMS 추천/발송 요청인데 recommend_sms 미호출 + 대상(phone) member buffer 보유 감지 //
  //   본질: "미수금 회원에게 문자 추천"처럼 대상 조회 선행 SMS 요청에서 LLM이 call_api+show_display(명단 표)로 끝내고 recommend_sms 미호출
  //         → SMS 문구 누락(Cat3-SMS_MISSING, 운영 rid192). show_display 사전 가드가 본 detector로 recommend_sms 선행 유도 (dual sync with nodeAgent.mjs).
  //   범용: case ID/리터럴 hardcode X. SMS 추천 의도 regex + member buffer(phone) signal로만 발동. SMS 이력 조회("문자 발송 내역")·HOWTO 제외.
  //   반환: true (recommend_sms 누락 — 가드 발동 대상) | false (정상)
  detectSmsRecommendMissing(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;

    // [1] SMS 추천/발송 의도 — 발송+이력명사(내역/건수 등)는 negative lookahead로 제외 (이력 조회 false positive 차단)
    const SMS_RECOMMEND_INTENT = /(?:문자|메시지|메세지|문구|sms)\s*(?:를|을|로|으로|도)?\s*(?:추천|보내|발송|작성|만들|준비|써|쓸|돌려|날려)(?!\s*(?:내역|이력|건수|통계|결과|현황|성공률|기록|취소|실패))|(?:추천|보내|발송|작성|만들)\s*(?:해|해줘|해주세요|할|하는)?\s*(?:문자|메시지|메세지|문구|sms)/i;
    if (!SMS_RECOMMEND_INTENT.test(input)) return false;

    // [2] HOWTO("문자 보내는 법"/"사용법") 제외 — 발송 방법 질문은 recommend_sms 대상 아님
    const HOWTO = /어떻게|방법|[가-힣]+는\s*법|사용법|설정/;
    if (HOWTO.test(input)) return false;

    // [3] 대상 buffer items 보유 (조회 선행 완료) — 없으면 SMS 대상 미적재
    const buf = this.getBuffer?.();
    const items = buf?.items || [];
    if (items.length < 1) return false;

    // [4] SMS 이력 buffer 제외 — CHECK_MESSAGE_LIST_BY+PERIOD / CHECK_BIZ_LIST_BY+PERIOD (COMMUNICATION 이력 조회)
    if (/MESSAGE_LIST|BIZ_LIST/i.test(buf?.intention || '')) return false;

    // [5] phone 보유 — recommend_sms 가능 대상. 정식 PHONE_KEYS와 동일 (executeRecommendSms)
    const PHONE_KEYS = ['phone', 'phoneNumber', 'tel', 'mobile', 'contactNumber'];
    const hasPhone = items.some(it => it && typeof it === 'object' && PHONE_KEYS.some(k => it[k]));
    if (!hasPhone) return false;

    return true;
  }

  // [LTV-ORCHESTRATION] LTV "값/데이터" 요청 감지 — 개념/방법 질문은 제외 (dual sync with nodeAgent.mjs + useAiChat.js) //
  //   본질: gpt 모델이 "회원 LTV 얼마야?" 입력에 show_display로 빠지거나(VIZ-LTV-001) 도구를 0개 호출(VIZ-LTV-002)하여
  //         LTV 산정에 실패. soft metadata(requiredSources)만으로는 0/10 → LTV는 다른 도메인과 격리된 단일 지표이므로
  //         사용자 승인 하에 코드 결정성 short-circuit(detect → prefetch → executeShowLtv) 도입.
  //   범용성 caveat: 본 detector는 LTV 한정 hardcode (사용자 승인). detector가 유일한 gate — 다른 도메인 무영향.
  //   CRITICAL: 개념/방법(HOWTO) 질문("LTV가 뭐야", "LTV 어떻게 계산해", "LTV 뜻")은 FALSE 반환 → 일반 flow/get_guide가 설명 처리.
  //             VALUE/DATA 요청("우리 LTV 얼마야", "회원 1명당 평균 매출")에만 발동.
  _detectLtvDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;

    // [1] CONCEPT/HOWTO 마커 — 개념/정의/계산방법 질문은 LTV orchestration 대상 아님 (먼저 제외)
    const CONCEPT_HOWTO = /(뭐야|뭔데|뭐임|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|계산법|구하는\s*법|구하는\s*방법|산정\s*방법|what\s+is|how\s+(?:do|to|can)\s+(?:i\s+|you\s+)?calculate|how\s+is\s+.*calculated|explain|definition|meaning)/i;
    if (CONCEPT_HOWTO.test(input)) return false;

    // [2] bare concept + 물음표 ("LTV?" / "고객 생애가치?") — 개념 질문으로 간주, 제외
    const BARE_CONCEPT_QUESTION = /^(?:LTV|생애\s*가치|고객\s*가치|customer\s+lifetime\s+value|lifetime\s+value)\s*\??$/i;
    if (BARE_CONCEPT_QUESTION.test(input)) return false;

    // [3] POSITIVE 키워드 (다국어) — 값/데이터 요청 신호
    const LTV_POSITIVE = /(LTV|생애\s*가치|고객\s*가치|고객가치|customer\s+lifetime\s+value|lifetime\s+value|1명당\s*평균\s*매출|1인당\s*평균\s*매출|회원\s*1명이\s*남기는|회원당\s*매출)/i;
    return LTV_POSITIVE.test(input);
  }

  // [ATTENDANCE-RANKING-ORCHESTRATION] 회원 출석 순위 "데이터" 요청 감지 (dual sync: useAiChat.js + nodeAgent.mjs) //
  //   본질: gpt prior가 plain per-member 출석 랭킹을 non-MONTHLY+sort+show_display로 격하(타깃 5/5↔4/5 진동, VIZ-ATTENDANCE-RANKING-001 0/5).
  //         프롬프트 강화(663/667/3080)로 천장 확인 → 사용자 승인 하 코드 결정성 short-circuit(detect → prefetch → executeShowAttendanceRanking).
  //   detector가 유일 gate — 매우 보수적. threshold(N회 이상/이하)·축별(수업별/코치별 등)·성별 prefilter·비교(멀티기간)·표/리스트 명시·개념질문은 모두 제외 → 일반 flow.
  _detectAttendanceRankingDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;
    // [제외1] 개념/방법 질문
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|what\s+is|how\s+to|explain|definition)/i.test(input)) return false;
    // [제외2] 표/리스트/목록 명시 → show_display soft default 존중 (가이드 EXCEPTION)
    if (VIEW_FORMAT_LIST_RE.test(input)) return false;
    // [제외3] 숫자 threshold (N회 이상/이하/초과/미만) → filter 경로 (ATTENDANCE COUNT ROUTING)
    if (/\d+\s*회\s*(이상|이하|초과|미만)/.test(input)) return false;
    // [제외4] 축별 그룹 (수업별/코치별/강사별/시간대별/요일별) → 해당 축 집계 도구(show_class_attendance 등)
    if (/(수업별|레슨별|클래스별|코치별|강사별|시간대별|시간별|요일별)/.test(input)) return false;
    // [제외4b] 회원권(상품) 축 랭킹 — 멤버십 명사 존재 + standalone '회원' 부재면 per-member 아님 → CHECK_MEMBERSHIP+TOP+ATTENDANCE 경로 ("회원권 보유 회원 중 출석 순위" 같은 per-member 문형은 '회원' 동반이라 유지)
    if (/(회원권|이용권|멤버십|맴버십)/.test(input) && !/회원(?!권)/.test(input)) return false;
    // [제외5] 성별 등 attribute pre-filter → non-MONTHLY filter+display 경로 (가이드 METRIC DEFINITION)
    if (/(성별|남녀|여성\s*회원|남성\s*회원|남자\s*회원|여자\s*회원)/.test(input)) return false;
    // [제외6] 비교/추이 (멀티기간) → COMPARISON / multiPeriod 경로
    if (/(비교|추이|변화|대비|변동)/.test(input)) return false;
    // [POSITIVE] 회원 출석 + 랭킹 신호
    const ATTEND = /(출석|출석률|나오는|나온|방문)/;
    const RANK = /(순위|랭킹|제일\s*많이|가장\s*많이|많이\s*한|많은\s*회원|상위|출석왕|출석\s*top|높은\s*순|잘\s*나오는\s*회원|제일\s*잘\s*나오는)/i;
    return ATTEND.test(input) && RANK.test(input);
  }

  // [CONVERSION-FUNNEL-ORCHESTRATION] "X 쓴/산 사람 중 Y 구매" 전환 퍼널 — 코드 결정 계산 (dual sync nodeAgent.mjs, 진입부 useAiChat/nodeAgent 루프) //
  //   gate: 기점 구절(사용/구매한 사람·회원 중) + 회원권 도메인 신호 + from 상품명이 usages 실카탈로그와 매칭될 때만 발동(비조작).
  //   bare "전환율"(상품 미지정)은 from 미매칭으로 자연 비발동 → 기존 RE+REGISTRATION 경로 보존.
  //   계산은 aiUtils.computeConversionFunnel(단일소스) — 회원별 usages 타임라인에서 기간 내 from 구매(t0) 모수 → t0 이후 첫 타상품 구매 = 전환.
  async autoConversionFunnelIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    const COHORT_RE = /(사용|이용|쓴|쓰던|산|구매|구입|끊은|등록)[^.\n]{0,8}(사람|회원|분|고객)[^.\n]{0,6}중/;
    const cohortM = input.match(COHORT_RE);
    if (!cohortM) return null;
    if (!/(체험|이용권|회원권|멤버십|맴버십|\d+\s*(일|회|개월))/.test(input)) return null;
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|what\s+is|how\s+to)/i.test(input)) return null;
    // [v2b 양보] 추가구매 "명단" 의도(추가+구매류 && 명시 roster-noun)는 autoConversionRosterIfMatched 모드B-fresh가 담당 — 집계표 선점 차단 //
    if (/추가[^.\n]{0,15}(구매|구입|샀|산|등록|끊|결제)/.test(input) && /(명단|목록|리스트|누구|누가|뽑아)/.test(input)) return null;

    // 기간 — 명시월(parseMonthRangeFromInput, 미래월이면 작년) > 최근 30일 //
    const now = new Date();
    const _fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let startDate = null;
    let endDate = null;
    const mr = parseMonthRangeFromInput(input, now.getFullYear());
    if (mr) {
      const loM = Number(mr.startMonth.slice(5, 7));
      const yr = loM > now.getMonth() + 1 ? now.getFullYear() - 1 : now.getFullYear();
      const hiM = Number(mr.endMonth.slice(5, 7));
      startDate = `${yr}-${String(loM).padStart(2, '0')}-01`;
      endDate = `${yr}-${String(hiM).padStart(2, '0')}-${String(new Date(yr, hiM, 0).getDate()).padStart(2, '0')}`;
    }
    else if (/(지난|저번|이?전)\s*달/.test(input)) {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate = _fmt(pm);
      endDate = _fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    else {
      startDate = _fmt(new Date(now.getTime() - 30 * 86400000));
      endDate = _fmt(now);
    }

    // 데이터 — 회원별 usages 타임라인 (기존 버퍼 재사용 → 부재 시 프리페치) //
    const _findBufCF = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufCF.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_funnel_members' });
      if (r?.success) memberBuf = _findBufCF.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    // from/to 상품명 — 기점 구절 앞 구간=from(최장 매칭), 뒤 구간=to (usages 실카탈로그 대조) //
    const catalog = [...new Set(memberBuf.items.flatMap(m => (m?.usages || []).map(u => u?.memberShipName)).filter(Boolean))];
    const splitIdx = input.indexOf(cohortM[0]);
    const fromCands = matchMembershipNamesInSegment(input.slice(0, splitIdx + cohortM[0].length), catalog);
    if (!fromCands.length) return null;
    const fromName = fromCands[0];
    const toNames = matchMembershipNamesInSegment(input.slice(splitIdx + cohortM[0].length), catalog).filter(n => n !== fromName);

    const funnel = computeConversionFunnel({ members: memberBuf.items, fromName, toNames, startDate, endDate, today: _fmt(now) });
    if (!funnel) return null;
    if (funnel.cohortCount === 0) return { success: false, fromName, startDate, endDate, cohortCount: 0 };

    const _cfFields = { conversionTarget: '전환 회원권', convertedCount: '인원(명)', share: '비율(%)', medianDays: '전환 소요일(중앙값)' };
    this.initBuffer(funnel.rows, 'CONVERSION_FUNNEL', _cfFields, '_conversion_funnel');
    const _cfBuf = this.buffers.get('_conversion_funnel');
    if (_cfBuf) _cfBuf.dateRange = { startDate, endDate };

    this._lastConversionFunnel = { fromName, toNames, startDate, endDate, funnel };  // [v2] 모드B 추가구매 follow-up 컨텍스트 (세션 스코프 — turn 리셋 X)
    const introSentence = `${startDate}~${endDate} '${fromName}' 구매 회원 ${funnel.cohortCount}명 중 이후 다른 회원권으로 전환한 회원은 ${funnel.convertedCount}명(${funnel.conversionRate}%)이야.`;
    this._conversionFunnelIntro = introSentence;  // executeShowDisplay에서 결정적 강제 (LLM 거짓 disclaimer 차단)
    return { success: true, fromName, toNames, startDate, endDate, cohortCount: funnel.cohortCount, convertedCount: funnel.convertedCount, conversionRate: funnel.conversionRate, rowCount: funnel.rows.length, introSentence, slot: '_conversion_funnel' };
  }

  // [CONVERSION-ROSTER-ORCHESTRATION v2] 전환 퍼널 명단 모드 — 코드 결정 명단 (dual sync nodeAgent.mjs, 진입부 useAiChat/nodeAgent 루프) //
  //   모드B(추가구매 follow-up): "이 사람들 중 추가로 구매한 사람 목록" — 직전 퍼널/명단 컨텍스트(_lastConversionFunnel)의 전환자 명단 (운영 store445 rid204 T6/T7 재현: NL-to-SQL 하이재킹 61명 오답 → 정답 24명).
  //   모드A(코호트 명단): "6월에 X 쓴 사람들 목록" (중 구절 없음 — 퍼널 COHORT_RE와 배타) — 기간 내 from 구매 회원 명단.
  //   명단/수치 전부 aiUtils.computeConversionFunnel(단일소스) 산출 — LLM 날조 여지 0. 컨텍스트는 퍼널/명단 슬롯이 active일 때만 사용(stale 바인딩 차단, rid127 동형 방지).
  async autoConversionRosterIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|what\s+is|how\s+to)/i.test(input)) return null;
    if (/(문자|메시지|메세지|sms|보내)/i.test(input)) return null;  // 메시징 흐름 비개입
    if (/재등록/.test(input)) return null;  // RE+REGISTRATION 명단 경로 보존

    // ── 모드B: 추가구매 follow-up — deictic("이 사람들 중")+구매동사 또는 "추가" 강앵커, 직전 퍼널 컨텍스트 활성 필수 ──
    const DEICTIC = /(이|그|해당|위)\s*(사람|회원|분)\s*들?\s*(중에서|중에|중|에서|가운데)/.test(input);
    const ADD_STRONG = /추가[^.\n]{0,15}(구매|구입|샀|산|등록|끊|결제)/.test(input);
    const ADD_ANY = ADD_STRONG || /(다른|또|더|새로)\s*[^.\n]{0,10}(구매|구입|샀|산|등록|끊|결제)/.test(input);
    if ((DEICTIC && ADD_ANY) || ADD_STRONG) {
      if (!/(명단|목록|리스트|누구|누가|뽑아|몇\s*명|몇명|인원|얼마나|알려|보여)/.test(input)) return null;
      const _ctx = this._lastConversionFunnel;
      const _ctxActive = !!_ctx?.funnel && (this.activeSlot === '_conversion_funnel' || this.activeSlot === '_conversion_roster');
      if (_ctxActive) {
        const list = _ctx.funnel.convertedMembers || [];
        const _crFields = { name: '이름', phone: '전화번호', fromDate: '기존 구매일', conversionTarget: '추가 구매 회원권', conversionDate: '추가 구매일' };
        this.initBuffer(list, 'CONVERSION_FUNNEL', _crFields, '_conversion_roster');
        const _crBuf = this.buffers.get('_conversion_roster');
        if (_crBuf) {
          _crBuf.dateRange = { startDate: _ctx.startDate, endDate: _ctx.endDate };
          _crBuf.resolvedColumns = /(전화|연락처|폰|번호)/.test(input) ? ['name', 'phone', 'fromDate', 'conversionTarget', 'conversionDate'] : ['name', 'fromDate', 'conversionTarget', 'conversionDate'];  // phone은 EXCLUDED_FIELDS 기본 비노출 — 명시 요청 시만 포함 (결정적)
        }
        const introSentence = `${_ctx.startDate}~${_ctx.endDate} '${_ctx.fromName}' 구매 회원 ${_ctx.funnel.cohortCount}명 중 이후 다른 회원권을 추가 구매한 회원은 ${_ctx.funnel.convertedCount}명(${_ctx.funnel.conversionRate}%)이야.`;
        this._conversionFunnelIntro = introSentence;
        return { success: true, mode: 'converted-roster', fromName: _ctx.fromName, startDate: _ctx.startDate, endDate: _ctx.endDate, cohortCount: _ctx.funnel.cohortCount, convertedCount: _ctx.funnel.convertedCount, rowCount: list.length, introSentence, slot: '_conversion_roster' };
      }

      // ── 모드B-fresh(v2b): 신규 단일턴 — 컨텍스트 없이 입력 자체에 코호트 구절("X 산 사람 중")+추가구매 강앵커+명시 roster-noun 보유 시 fresh 계산 //
      //    v1 autoConversionFunnelIfMatched의 [v2b 양보] 게이트와 정규식 쌍 — 집계표 대신 convertedMembers 명단. deictic stale 바인딩(구매동사 없는 "이 사람들 중")은 여전히 차단(rid127 동형). //
      const COHORT_FRESH_RE = /(사용|이용|쓴|쓰던|산|구매|구입|끊은|등록)[^.\n]{0,8}(사람|회원|분|고객)[^.\n]{0,6}중/;
      const cohortFreshM = ADD_STRONG && /(명단|목록|리스트|누구|누가|뽑아)/.test(input) ? input.match(COHORT_FRESH_RE) : null;
      if (!cohortFreshM) return null;
      if (!/(체험|이용권|회원권|멤버십|맴버십|\d+\s*(일|회|개월))/.test(input)) return null;

      const nowB = new Date();
      const _fmtB = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      let startDateB = null;
      let endDateB = null;
      const mrB = parseMonthRangeFromInput(input, nowB.getFullYear());
      if (mrB) {
        const loMB = Number(mrB.startMonth.slice(5, 7));
        const yrB = loMB > nowB.getMonth() + 1 ? nowB.getFullYear() - 1 : nowB.getFullYear();
        const hiMB = Number(mrB.endMonth.slice(5, 7));
        startDateB = `${yrB}-${String(loMB).padStart(2, '0')}-01`;
        endDateB = `${yrB}-${String(hiMB).padStart(2, '0')}-${String(new Date(yrB, hiMB, 0).getDate()).padStart(2, '0')}`;
      }
      else if (/(지난|저번|이?전)\s*달/.test(input)) {
        const pmB = new Date(nowB.getFullYear(), nowB.getMonth() - 1, 1);
        startDateB = _fmtB(pmB);
        endDateB = _fmtB(new Date(nowB.getFullYear(), nowB.getMonth(), 0));
      }
      else {
        startDateB = _fmtB(new Date(nowB.getTime() - 30 * 86400000));
        endDateB = _fmtB(nowB);
      }

      const _findBufCB = this.findBufferByIntention || this._findBufferByIntention;
      let memberBufB = _findBufCB.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
      if (!memberBufB?.items?.length) {
        const rB = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_funnel_members' });
        if (rB?.success) memberBufB = _findBufCB.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
      }
      if (!memberBufB?.items?.length) return null;

      const catalogB = [...new Set(memberBufB.items.flatMap(m => (m?.usages || []).map(u => u?.memberShipName)).filter(Boolean))];
      const splitIdxB = input.indexOf(cohortFreshM[0]);
      const fromCandsB = matchMembershipNamesInSegment(input.slice(0, splitIdxB + cohortFreshM[0].length), catalogB);
      if (!fromCandsB.length) return null;
      const fromNameB = fromCandsB[0];
      const toNamesB = matchMembershipNamesInSegment(input.slice(splitIdxB + cohortFreshM[0].length), catalogB).filter(n => n !== fromNameB);

      const funnelB = computeConversionFunnel({ members: memberBufB.items, fromName: fromNameB, toNames: toNamesB, startDate: startDateB, endDate: endDateB, today: _fmtB(nowB) });
      if (!funnelB) return null;
      if (funnelB.cohortCount === 0) return { success: false, fromName: fromNameB, startDate: startDateB, endDate: endDateB, cohortCount: 0 };

      this._lastConversionFunnel = { fromName: fromNameB, toNames: toNamesB, startDate: startDateB, endDate: endDateB, funnel: funnelB };  // follow-up 컨텍스트 (세션 스코프)
      const listB = funnelB.convertedMembers || [];
      const _cbFields = { name: '이름', phone: '전화번호', fromDate: '기존 구매일', conversionTarget: '추가 구매 회원권', conversionDate: '추가 구매일' };
      this.initBuffer(listB, 'CONVERSION_FUNNEL', _cbFields, '_conversion_roster');
      const _cbBuf = this.buffers.get('_conversion_roster');
      if (_cbBuf) {
        _cbBuf.dateRange = { startDate: startDateB, endDate: endDateB };
        _cbBuf.resolvedColumns = /(전화|연락처|폰|번호)/.test(input) ? ['name', 'phone', 'fromDate', 'conversionTarget', 'conversionDate'] : ['name', 'fromDate', 'conversionTarget', 'conversionDate'];  // phone은 EXCLUDED_FIELDS 기본 비노출 — 명시 요청 시만 포함 (결정적)
      }
      const introSentenceB = `${startDateB}~${endDateB} '${fromNameB}' 구매 회원 ${funnelB.cohortCount}명 중 이후 다른 회원권을 추가 구매한 회원은 ${funnelB.convertedCount}명(${funnelB.conversionRate}%)이야.`;
      this._conversionFunnelIntro = introSentenceB;
      return { success: true, mode: 'converted-roster', fromName: fromNameB, startDate: startDateB, endDate: endDateB, cohortCount: funnelB.cohortCount, convertedCount: funnelB.convertedCount, rowCount: listB.length, introSentence: introSentenceB, slot: '_conversion_roster' };
    }

    // ── 모드A: 코호트 명단 — 명시적 명단 요청 + 구매동사(사용중/이용중 현재형 제외)+사람 명사 + 도메인 신호 + from 실카탈로그 매칭 시만 발동 ──
    if (!/(명단|목록|리스트|누구|누가|뽑아)/.test(input)) return null;
    if (!/(사용(?!\s*중)|이용(?!\s*중|권)|쓴|쓰던|산|구매|구입|끊은|등록|결제)[^.\n]{0,8}(사람|회원|분|고객)/.test(input)) return null;
    if (!/(체험|이용권|회원권|멤버십|맴버십|\d+\s*(일|회|개월))/.test(input)) return null;

    const now = new Date();
    const _fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let startDate = null;
    let endDate = null;
    const mr = parseMonthRangeFromInput(input, now.getFullYear());
    if (mr) {
      const loM = Number(mr.startMonth.slice(5, 7));
      const yr = loM > now.getMonth() + 1 ? now.getFullYear() - 1 : now.getFullYear();
      const hiM = Number(mr.endMonth.slice(5, 7));
      startDate = `${yr}-${String(loM).padStart(2, '0')}-01`;
      endDate = `${yr}-${String(hiM).padStart(2, '0')}-${String(new Date(yr, hiM, 0).getDate()).padStart(2, '0')}`;
    }
    else if (/(지난|저번|이?전)\s*달/.test(input)) {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate = _fmt(pm);
      endDate = _fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    else {
      startDate = _fmt(new Date(now.getTime() - 30 * 86400000));
      endDate = _fmt(now);
    }

    const _findBufCR = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufCR.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_funnel_members' });
      if (r?.success) memberBuf = _findBufCR.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    const catalog = [...new Set(memberBuf.items.flatMap(m => (m?.usages || []).map(u => u?.memberShipName)).filter(Boolean))];
    const fromCands = matchMembershipNamesInSegment(input, catalog);
    if (!fromCands.length) return null;
    const fromName = fromCands[0];

    const funnel = computeConversionFunnel({ members: memberBuf.items, fromName, toNames: [], startDate, endDate, today: _fmt(now) });
    if (!funnel) return null;
    if (funnel.cohortCount === 0) return { success: false, fromName, startDate, endDate, cohortCount: 0 };

    this._lastConversionFunnel = { fromName, toNames: [], startDate, endDate, funnel };  // 모드B follow-up 컨텍스트 (세션 스코프)
    const _caFields = { name: '이름', phone: '전화번호', purchaseDate: '구매일' };
    this.initBuffer(funnel.cohortMembers, 'CONVERSION_FUNNEL', _caFields, '_conversion_roster');
    const _caBuf = this.buffers.get('_conversion_roster');
    if (_caBuf) {
      _caBuf.dateRange = { startDate, endDate };
      _caBuf.resolvedColumns = /(전화|연락처|폰|번호)/.test(input) ? ['name', 'phone', 'purchaseDate'] : ['name', 'purchaseDate'];  // phone은 EXCLUDED_FIELDS 기본 비노출 — 명시 요청 시만 포함 (결정적)
    }
    const introSentence = `${startDate}~${endDate} '${fromName}' 구매 회원은 총 ${funnel.cohortCount}명이야.`;
    this._conversionFunnelIntro = introSentence;
    return { success: true, mode: 'cohort-roster', fromName, startDate, endDate, cohortCount: funnel.cohortCount, rowCount: funnel.cohortMembers.length, introSentence, slot: '_conversion_roster' };
  }

  // [NEW-TO-PAID CONVERSION] bare "전환율/신규 전환율/정규 전환" (특정 상품 미지정) — 신규 유료 전환율 코드 결정 계산 (dual sync nodeAgent.mjs, 진입부 useAiChat/nodeAgent 루프) //
  //   gate: 일반 전환 신호 + 회원/회원권 맥락, 특정 상품 코호트("X 쓴 사람 중")·재등록/갱신·월별/연령/성별 세분·개념질문·명시 체험전환 제외.
  //   계산은 aiUtils.computeNewToPaidConversion(단일소스) — 회원별 최초 회원권(기간 내)=신규 모수, 그 중 유료 usage 보유=전환.
  //   full 조기포기(정의 없음→포기, store445 5run 중 3run)·mini 모수 혼합 모순(16 중 19명) 둘 다 원천 차단(루프 진입 전 결정 finish). 미스=LLM 폴백=무회귀.
  async autoNewToPaidConversionIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    // [POSITIVE] 일반 전환율/전환 신호 (특정 상품 미지정) — gym 회원관리 도메인에서 '전환율'은 회원 전환 단일 의미라 별도 도메인어 요구 안 함(bare "전환율 얼마야" 포함, 조기포기 방지). 오탐 방지는 아래 제외들이 담당 //
    if (!/전환율|전환\s*(비율|율)|전환되는\s*(비율|율|회원|비중)|정규\s*회원권?[^.\n]{0,6}전환|정식\s*(등록|가입|전환)|정규\s*(등록|가입|전환)/.test(input)) return null;
    // [제외] 개념/방법 질문 → LOOKUP //
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|어떻게\s*계산|공식|산식|정의\s*(가|는|를|해)?)/.test(input) || /what\s+is|how\s+to/i.test(input)) return null;
    // [제외] 재등록/갱신 — renewalRate 별도 지표 //
    if (/재등록|재가입|갱신|만료\s*후/.test(input)) return null;
    // [제외] 특정 상품 코호트("X 쓴/산 사람 중") → autoConversionFunnel 담당 //
    if (/(사용|이용|쓴|쓰던|산|구매|구입|끊은|등록)[^.\n]{0,8}(사람|회원|분|고객)[^.\n]{0,6}중/.test(input)) return null;
    // [제외] 월별/연령/성별 등 세분 → 스칼라 게이트 밖 (멀티섹션 리포트 영역) //
    if (/월별|월간|분기별|연령|나이|성별|남녀|요일|추이|비교|대비/.test(input)) return null;
    // [제외] 명시 '체험 전환' — 라벨 상이(신규 유료 전환율 아님), 별도 경로 양보 //
    if (/체험\s*전환/.test(input)) return null;

    // 기간 — 명시월(미래월이면 작년) > 지난달 > 올해 1/1~오늘 (리포트 04 기본과 동일) //
    const now = new Date();
    const _fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let startDate = null;
    let endDate = null;
    const mr = parseMonthRangeFromInput(input, now.getFullYear());
    if (mr) {
      const loM = Number(mr.startMonth.slice(5, 7));
      const yr = loM > now.getMonth() + 1 ? now.getFullYear() - 1 : now.getFullYear();
      const hiM = Number(mr.endMonth.slice(5, 7));
      startDate = `${yr}-${String(loM).padStart(2, '0')}-01`;
      endDate = `${yr}-${String(hiM).padStart(2, '0')}-${String(new Date(yr, hiM, 0).getDate()).padStart(2, '0')}`;
    }
    else if (/(지난|저번|이?전)\s*달/.test(input)) {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate = _fmt(pm);
      endDate = _fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    else {
      startDate = `${now.getFullYear()}-01-01`;
      endDate = _fmt(now);
    }

    // 데이터 — 회원별 usages 전체 이력 (기간 미전달 = 전체 이력; 최초 회원권 판정 필수) //
    const _findBufNP = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufNP.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_conversion_members' });
      if (r?.success) memberBuf = _findBufNP.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    const conv = computeNewToPaidConversion({ members: memberBuf.items, startDate, endDate, today: _fmt(now) });
    if (!conv) return null;
    if (conv.newCount === 0) return { success: false, startDate, endDate, newCount: 0 };

    const _npRows = [
      { _ord: 0, label: '신규 진입 회원', count: conv.newCount, ratio: 100 },
      { _ord: 1, label: '유료 전환 회원', count: conv.convertedCount, ratio: conv.conversionRate },
      { _ord: 2, label: '미전환 회원', count: conv.newCount - conv.convertedCount, ratio: conv.newCount > 0 ? Math.round((conv.newCount - conv.convertedCount) / conv.newCount * 1000) / 10 : 0 },
    ];
    const _npFields = { label: '구분', count: '인원(명)', ratio: '비율(%)' };
    this.initBuffer(_npRows, 'NEW_TO_PAID_CONVERSION', _npFields, '_conversion_funnel');
    const _npBuf = this.buffers.get('_conversion_funnel');
    if (_npBuf) {
      _npBuf.dateRange = { startDate, endDate };
      _npBuf.appliedSort = { field: '_ord', direction: 'asc' };
      _npBuf.resolvedColumns = ['label', 'count', 'ratio'];
    }

    this._lastNewToPaidConversion = { startDate, endDate, conv };  // 미전환 명단 follow-up 컨텍스트 (세션 스코프)
    const introSentence = `${startDate}~${endDate} 신규 진입 회원 ${conv.newCount}명 중 유료 회원권으로 전환한 회원은 ${conv.convertedCount}명(신규 유료 전환율 ${conv.conversionRate}%)이야.`;
    this._conversionFunnelIntro = introSentence;  // executeShowDisplay 결정적 intro 강제 (재사용 slot _conversion_funnel, 9265 게이트)
    return { success: true, startDate, endDate, newCount: conv.newCount, convertedCount: conv.convertedCount, conversionRate: conv.conversionRate, rowCount: _npRows.length, introSentence, slot: '_conversion_funnel' };
  }

  // [MEMBER TREND] "(유효회원|활성회원) + (증가|추이|추세…)" — 월별 유효회원 추이 결정적 게이트: HISTORY_LIST 로드 후 show_member_trend 유도 (dual sync nodeAgent.mjs, 진입부 useAiChat/nodeAgent 루프) //
  //   "유효회원"이 ALIVE_LIST 인텐션으로 유인 → vizTools 래치 체크에그 → ALIVE_SCOPE 가드 데드락(store445 원본 실패). HISTORY_LIST 결정 로드로 래치 ON + 도구 노출 + LLM 스티어. 미스=LLM 폴백=무회귀.
  async autoMemberTrendIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    // [POSITIVE-1] (유효|활성)회원 + 증가/추이/추세/흐름/변화 (양방향) or 월별 유효/활성회원 //
    const _pos1 = /(유효|활성)\s*회원[^.\n]{0,10}(증가|추이|추세|흐름|변화|늘어|줄어)|(증가|추이|추세)[^.\n]{0,10}(유효|활성)\s*회원|(월별|월간)[^.\n]{0,8}(유효|활성)\s*회원/.test(input);
    // [POSITIVE-2] 무료체험 포함/제외 계열 분석 후속 ("제외한것도 분석" 등 유효회원 추이 문맥의 follow-up) — 도메인 이탈 negative 가드 후 //
    const _pos2 = /(무료\s*체험|무료체험|체험)[^.\n]{0,12}(포함|제외)|(포함|제외)[^.\n]{0,12}(무료\s*체험|무료체험|체험)/.test(input)
      && /(분석|보여|알려|추이|증가|추세|현황|따로)/.test(input)
      && !/(매출|결제|출석|수업|예약|랭킹|순위|재등록|신규|이탈)/.test(input);
    // [POSITIVE-3] 맥락 게이트 — 직전 턴이 회원추이 차트일 때만 발동하는 약한 후속 문구("무료체험만/체험만/제외만/포함만/제외한것도"). //
    //   sticky 아님: _lastMemberTrendTurn은 매 턴 재계산(직전 한 턴만 반영) → 중간에 다른 주제 얘기하면 false로 덮여 오발화 차단. + 도메인 negative 가드. //
    const _pos3 = (this._lastMemberTrendTurn === true)
      && /((무료\s*체험|무료체험|체험)\s*(회원)?\s*만)|((제외|포함)\s*만)|(제외|포함)한것도/.test(input)
      && !/(매출|결제|출석|수업|예약|랭킹|순위|재등록|신규|이탈)/.test(input);
    if (!_pos1 && !_pos2 && !_pos3) return null;
    // [제외] 개념/방법 질문 → LOOKUP //
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|어떻게\s*계산|공식|산식|정의)/.test(input)) return null;
    // [제외] 신규/재등록/이탈 분류 → show_member_status 영역 //
    if (/신규|재등록|재가입|이탈|만료\s*회원/.test(input)) return null;

    // 계열 판정 — 포함/제외/무료체험만 신호로 막대 계열 결정. 둘 다/각각/양쪽 or 포함·제외 동시 언급 = both(막대=포함/점선=제외) //
    const _hasExclude = /제외/.test(input);
    const _hasInclude = /포함/.test(input);
    const _trialOnly = /(무료\s*체험|무료체험|체험)\s*(회원)?\s*만/.test(input) && !_hasExclude && !_hasInclude; // "무료체험만" (제외/포함 없이) = trial only
    const _bothSignal = /둘\s*다|둘다|각각|모두|전부|양쪽/.test(input) || (_hasExclude && _hasInclude);
    let series = 'both';
    if (_trialOnly) series = 'trialOnly';                         // 무료체험만 → 무료체험 회원(포함−제외)을 막대로
    else if (!_bothSignal) {
      if (_hasExclude && !_hasInclude) series = 'paidOnly';      // 무료체험 제외만 → 제외를 막대로
      else if (_hasInclude && !_hasExclude) series = 'withTrial'; // 무료체험 포함만 → 포함만 막대(점선 제거)
    }

    // 데이터 — 회원별 usages 전체 이력 (기간 미전달 = 전체; 월별 활성 판정 필수) //
    const _findBufMT = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufMT.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_member_trend_src' });
      if (r?.success) memberBuf = _findBufMT.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    return { success: true, series };
  }

  // [MEMBER FLOW TREND] "(신규|재등록|이탈|만료) + (추이|증감|월별…)" — 월별 회원 증감 추이 결정적 게이트: HISTORY_LIST 로드 후 show_member_flow_trend 유도 (dual sync nodeAgent.mjs, 진입부 useAiChat/nodeAgent 루프) //
  //   show_member_status(특정 유형 목록)로 오유인 방지 + vizTools 래치 체크에그 차단. A(유효회원 스톡 추이, autoMemberTrendIfMatched)와 배타 게이트. 미스=LLM 폴백=무회귀.
  async autoMemberFlowTrendIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    // [POSITIVE] (신규|재등록|재가입|이탈|만료) + 추이/증감/월별/월간/흐름/변화 (양방향) //
    if (!/(신규|재등록|재가입|이탈|만료)[^.\n]{0,12}(추이|증감|월별|월간|흐름|변화)|(추이|증감|월별|월간|흐름)[^.\n]{0,12}(신규|재등록|재가입|이탈|만료)/.test(input)) return null;
    // [제외] 개념/방법 질문 → LOOKUP //
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|어떻게\s*계산|공식|산식|정의)/.test(input)) return null;
    // [제외] 매출 → show_member_revenue_trend(autoMemberRevenueTrendIfMatched) 담당 (인원 증감이 아닌 금액 추이) //
    if (/매출/.test(input)) return null;

    // 데이터 — 회원별 usages 전체 이력 (기간 미전달 = 전체; 월별 신규/재등록/이탈 판정 필수) //
    const _findBufMF = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufMF.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_member_flow_trend_src' });
      if (r?.success) memberBuf = _findBufMF.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    return { success: true };
  }

  // [MEMBER REVENUE TREND] "(신규|재등록) + 매출 + (추이|월별|증감…)" — 월별 신규/재등록 매출 추이 결정적 게이트: HISTORY_LIST 로드 후 show_member_revenue_trend(RevenueChart 막대=신규매출/점선=재등록매출) 유도 (dual sync nodeAgent.mjs) //
  //   flow-trend(인원 증감)와 배타 — 매출 신호로 분기. usages 자체 결제필드(cardPrice+cashPrice) 사용 → SALES 조인 불필요. 미스=LLM 폴백=무회귀.
  async autoMemberRevenueTrendIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    // [POSITIVE] (신규|재등록|재가입) + 매출 + (추이|월별|월간|증감|흐름|변화) //
    if (!/신규|재등록|재가입/.test(input)) return null;
    if (!/매출/.test(input)) return null;
    if (!/(추이|월별|월간|증감|흐름|변화)/.test(input)) return null;
    // [제외] 개념/방법 질문 → LOOKUP //
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|어떻게\s*계산|공식|산식|정의)/.test(input)) return null;

    // 데이터 — 회원별 usages 전체 이력 (월별 신규/재등록 결제액 집계 필수) //
    const _findBufMR = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBufMR.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_member_revenue_trend_src' });
      if (r?.success) memberBuf = _findBufMR.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    return { success: true };
  }

  // [MEMBER STATUS] "(신규|재등록|이탈) 회원 현황/목록/매출" — 신규/재등록/이탈 분류 목록 결정적 게이트: HISTORY_LIST(+SALES 매출조인) 로드 후 executeShowMemberStatus 유도 (show_display 범용표 오유인 차단, dual sync nodeAgent.mjs) //
  //   추이/증감/월별은 flow-trend 게이트 담당(배타). 카운트/SMS/개념 질문은 부정 가드로 제외 → LLM 폴백=무회귀. new/reregistered는 SALES 조인해 매출총액 표시(이탈은 매출 부적합). //
  async autoMemberStatusIfMatched(userInput) {
    const input = String(userInput || '').trim();
    if (!input) return null;
    // [POSITIVE] (신규|재등록|재가입|이탈|잔존) + (회원|가입자|고객) — "신규등록 회원"처럼 1~3자 개재 허용 //
    if (!/(신규|재등록|재가입|이탈|잔존)[가-힣\s]{0,3}(회원|가입자|고객)/.test(input)) return null;
    // [제외] 개념/방법 질문 → LOOKUP //
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|어떻게\s*계산|공식|산식|정의)/.test(input)) return null;
    // [제외] 단순 카운트(몇 명/인원수/개수) → LLM 카운트 경로 //
    if (/(몇\s*명|몇명|인원\s*수|명이야|명인가|개수|카운트)/.test(input)) return null;
    // [제외] SMS/문자 발송 → recommend_sms 경로 //
    if (/(문자|메시지|메세지|보내|발송|전송|안내\s*문)/.test(input)) return null;
    // [제외] 추이/증감/월별 → flow-trend/revenue-trend 게이트 담당(배타) //
    if (/(추이|증감|월별|월간|흐름|변화)/.test(input)) return null;

    // memberType 판정 — 이탈/만료 → churned, 재등록/재가입 → reregistered, else 신규 → new //
    let memberType = 'new';
    if (/이탈|만료/.test(input)) memberType = 'churned';
    else if (/재등록|재가입/.test(input)) memberType = 'reregistered';

    // 기간 — 명시월(parseMonthRangeFromInput, 미래월이면 작년) > 지난달 > 기본 이번 달. 형제 게이트(autoConversionFunnel:1663·autoNewToPaidConversion:1895)와 동일 정규 블록. 현황=현재라 명시 없으면 이번 달 유지(기존 동작·_resolvePeriod 기본값·SALES 조인 기간 정합). dual sync nodeAgent.mjs //
    const _now = new Date();
    const _fmtMS = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let _sD;
    let _eD;
    const _mrMS = parseMonthRangeFromInput(input, _now.getFullYear());
    if (_mrMS) {
      const _loM = Number(_mrMS.startMonth.slice(5, 7));
      const _yr = _loM > _now.getMonth() + 1 ? _now.getFullYear() - 1 : _now.getFullYear();
      const _hiM = Number(_mrMS.endMonth.slice(5, 7));
      _sD = `${_yr}-${String(_loM).padStart(2, '0')}-01`;
      _eD = `${_yr}-${String(_hiM).padStart(2, '0')}-${String(new Date(_yr, _hiM, 0).getDate()).padStart(2, '0')}`;
    }
    else if (/(지난|저번|이?전)\s*달/.test(input)) {
      const _pm = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
      _sD = _fmtMS(_pm);
      _eD = _fmtMS(new Date(_now.getFullYear(), _now.getMonth(), 0));
    }
    else {
      _sD = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-01`;
      const _lastDay = new Date(_now.getFullYear(), _now.getMonth() + 1, 0).getDate();
      _eD = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_lastDay).padStart(2, '0')}`;
    }

    const _findBufMS = this.findBufferByIntention || this._findBufferByIntention;

    // 매출 조인용 SALES 버퍼 먼저 로드 (new/reregistered만) — 이후 HISTORY 로드로 activeSlot=HISTORY 유지(rid179 가드 통과) //
    if (memberType !== 'churned') {
      const _salesBuf0 = _findBufMS.call(this, 'CHECK_SALES_STATISTICS_BY+PERIOD');
      if (!_salesBuf0?.items?.length) {
        await this.executeCallApi({ intention: 'CHECK_SALES_STATISTICS_BY+PERIOD', startDate: _sD, endDate: _eD, slot: '_member_status_sales' });
      }
    }

    // 데이터 — 회원별 usages 전체 이력 (신규/재등록/이탈 분류 필수). HISTORY 마지막 로드 = activeSlot 유지 //
    let memberBuf = _findBufMS.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_member_status_src' });
      if (r?.success) memberBuf = _findBufMS.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }
    if (!memberBuf?.items?.length) return null;

    return { success: true, memberType, startDate: _sD, endDate: _eD };
  }

  // 회원권 선호도/인기/랭킹 요청 감지 — MEMBERSHIP-PREFERENCE-ORCHESTRATION의 유일 gate. dual sync with nodeAgent.mjs //
  _detectMembershipPreferenceDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;
    // [제외1] 개념/방법 질문
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|what\s+is|how\s+to|explain|definition)/i.test(input)) return false;
    // [제외2] 표/리스트/목록 명시 → show_display soft default 존중 (detectExplicitListRequest 단일소스 정렬 — 명단/목록 뽑아줘/list 등 커버)
    if (detectExplicitListRequest(input)) return false;
    // [제외3] 연령/참여도 분석(연령대 지정 포함) → show_engagement_chart 담당
    if (/(\d0\s*대|연령|나이|참여도|engagement)/i.test(input)) return false;
    // [POSITIVE] 회원권 + 선호/인기/랭킹/많이(끊|팔)/가입인원/재등록률/매출기여 신호
    if (!/회원권/.test(input)) return false;
    const PREF = /(선호도|선호|인기|랭킹|순위|많이\s*(끊|파|팔)|가입\s*인원|가입자\s*수|재등록률|매출\s*기여|매출\s*많은|매출\s*높은)/;
    return PREF.test(input);
  }

  // 월별 매출 순위(기간별 매출) 요청 감지 — PERIOD-SALES-ORCHESTRATION의 유일 gate. dual sync with nodeAgent.mjs //
  _detectPeriodSalesDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;
    // [제외1] 개념/방법 질문
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|what\s+is|how\s+to|explain|definition)/i.test(input)) return false;
    // [제외2] 표/리스트/목록 명시 → show_display soft default 존중 (detectExplicitListRequest 단일소스 정렬)
    if (detectExplicitListRequest(input)) return false;
    // [제외3] 회원권 관련 → show_membership_preference 담당
    if (/회원권/.test(input)) return false;
    // [제외4] 추이/변화/작년 대비 → show_revenue_chart 담당
    if (/(추이|변화|증감|작년\s*대비|전년\s*대비|꺾은선)/.test(input)) return false;
    // [POSITIVE] 매출 + (순위/랭킹/많은 순 | 기간 매출)
    if (!/매출/.test(input)) return false;
    return /(순위|랭킹|많은\s*순|높은\s*순|기간\s*(별\s*)?매출|월별\s*매출\s*(순위|랭킹|많))/.test(input);
  }

  // 포인트 관리 현황 요청 감지 — POINT-MANAGE-ORCHESTRATION의 유일 gate. dual sync with nodeAgent.mjs //
  _detectPointManagementDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;
    // [제외1] 개념/방법 질문
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|what\s+is|how\s+to|explain|definition)/i.test(input)) return false;
    // [제외2] 표/리스트/목록 명시 → show_display soft default 존중 (detectExplicitListRequest 단일소스 정렬)
    if (detectExplicitListRequest(input)) return false;
    // [제외3] 특정 회원 포인트 발행/차감 등 액션 → 통계 뷰 아님
    if (/(발행\s*해|지급\s*해|적립\s*해|차감\s*해|추가\s*해)/.test(input)) return false;
    // [POSITIVE] 포인트 + (관리/현황/통계/순위/랭킹/발행·사용 내역/보유 회원)
    if (!/포인트/.test(input)) return false;
    return /(관리|현황|통계|순위|랭킹|많은\s*순|높은\s*순|발행\s*내역|사용\s*내역|보유\s*(현황|순위|랭킹|고객|회원|많))/.test(input);
  }

  // 월간 경영 리포트 요청 감지 — MANAGEMENT-REPORT-ORCHESTRATION의 유일 gate. dual sync with nodeAgent.mjs //
  _detectReportDataIntent(userInput) {
    const input = (typeof userInput === 'string' ? userInput : (this.getUserInput?.() || '')).trim();
    if (!input) return false;
    // [제외1] 개념/방법 질문
    if (/(뭐야|뭔데|무엇|뜻|의미|개념|설명|정의|어떻게\s*계산|계산\s*방법|what\s+is|how\s+to|explain|definition)/i.test(input)) return false;
    // [POSITIVE] 경영/월간/월별/종합 리포트 명시 또는 "리포트 + 동작 동사"
    if (/(경영\s*리포트|월간\s*리포트|월별\s*리포트|종합\s*리포트|management\s*report)/i.test(input)) return true;
    if (!/(리포트|report)/i.test(input)) return false;
    return /(뽑아|만들|보여|작성|생성|정리)/.test(input);
  }

  // ============================================
  // 1-2. 멀티슬롯 병합 (merge_slots)
  // ============================================

  // matchKey 결정 — LLM 요청 키 우선(강한 키 검증) → 자동탐색(강한 키 → 약한 키 폴백) //
  resolveMatchKey(slots, requestedKey) {
    const firstBuf = this.buffers.get(slots[0].slotName);
    const sampleItem = firstBuf.items[0] || {};

    // 1. LLM 요청 키 우선 — 데이터 존재 + 슬롯 내 unique > 1이면 사용 (LLM 의도 존중)
    if (requestedKey && sampleItem[requestedKey] != null) {
      const buffers = slots.map(s => this.buffers.get(s.slotName));
      const minSlotUnique = Math.min(
        ...buffers.map(buf => new Set(
          buf.items.map(i => i[requestedKey]).filter(v => v != null)
        ).size)
      );
      if (minSlotUnique > 1) {
        const items = firstBuf.items;
        const uniqueCount = new Set(items.map(i => i[requestedKey]).filter(v => v != null)).size;
        const duplicateWarning = uniqueCount < items.length
          ? `matchKey "${requestedKey}"에 중복 값 존재 (${items.length}건 중 ${uniqueCount}개 고유값). 동명이인 등으로 일부 데이터가 손실될 수 있습니다.`
          : null;
        if (duplicateWarning) {
          const candidateFields = Object.keys(sampleItem).filter(k =>
            !k.startsWith('_') && k !== requestedKey &&
            sampleItem[k] != null && typeof sampleItem[k] !== 'object' && !Array.isArray(sampleItem[k])
          );
          for (const field of candidateFields) {
            const isPerfectUnique = buffers.every(buf => {
              const vals = buf.items.map(i => i[field]).filter(v => v != null);
              return vals.length > 0 && new Set(vals).size === vals.length;
            });
            if (isPerfectUnique && this._calcOverlap(field, buffers) > 0) {
              if (TOOL_CALLING_LOG) console.log(`[merge] matchKey 자동 승격: "${requestedKey}" → "${field}" (LLM 키 중복 → 완전 고유 키 우선)`);
              return { key: field, auto: true };
            }
          }
        }
        if (TOOL_CALLING_LOG) console.log(`[merge] matchKey: "${requestedKey}" (LLM 지정 우선, ${uniqueCount}/${items.length} 고유)${duplicateWarning ? ' ⚠️ 중복' : ''}`);
        return { key: requestedKey, auto: false, duplicateWarning };
      }
    }

    // 2. 자동탐색 (강한 키 → 약한 키 폴백)
    const candidate = this.findBestMatchKey(slots);
    if (candidate) {
      if (TOOL_CALLING_LOG) console.log(`[merge] matchKey 자동 감지: "${candidate.key}" (score=${candidate.score})${requestedKey && requestedKey !== candidate.key ? ` ← LLM 요청 "${requestedKey}" 부적합(unique=1) 대체` : ''}`);
      return { key: candidate.key, auto: candidate.key !== requestedKey };
    }

    // 3. LLM 키가 데이터에 없음
    if (requestedKey && sampleItem[requestedKey] == null) {
      return { key: null, error: `matchKey "${requestedKey}"가 데이터에 존재하지 않습니다.` };
    }

    return { key: null, error: 'matchKey를 자동 감지할 수 없습니다. 슬롯 간 공통 값을 가진 필드가 없습니다.' };
  }

  // 슬롯 간 교집합 계산 유틸 //
  _calcOverlap(field, buffers) {
    const valueSets = buffers.map(buf =>
      new Set(buf.items.map(i => String(i[field])).filter(v => v !== 'null' && v !== 'undefined'))
    );

    let overlap = new Set(valueSets[0]);

    for (let i = 1; i < valueSets.length; i++) {
      overlap = new Set([...overlap].filter(v => valueSets[i].has(v)));
    }

    return overlap.size;
  }

  // 슬롯 간 교집합 × 타입 가중치 스코어링으로 최적 matchKey 탐색 //
  // 강한 키(슬롯 내 unique > 1) 우선, 없으면 약한 키 폴백 (자동 aggregate용) //
  findBestMatchKey(slots) {
    const buffers = slots.map(s => this.buffers.get(s.slotName));
    const sample = buffers[0].items[0] || {};

    const candidateFields = Object.keys(sample).filter(k =>
      !k.startsWith('_') &&
      sample[k] != null &&
      typeof sample[k] !== 'object' &&
      !Array.isArray(sample[k])
    );

    let bestStrong = null;  // 강한 키: 슬롯 내 unique > 1 + 슬롯 간 overlap > 0
    let bestWeak = null;    // 약한 키: 슬롯 간 overlap > 0 (unique 무관, 폴백용)

    for (const field of candidateFields) {
      const overlapCount = this._calcOverlap(field, buffers);
      if (overlapCount === 0) continue;

      const typeWeight = typeof sample[field] === 'number' ? 2 : 1;
      const score = overlapCount * typeWeight;

      // 슬롯 내 모든 행이 동일값이면 매치키 부적절 → 약한 키로 분류
      const minSlotUnique = Math.min(
        ...buffers.map(buf => new Set(
          buf.items.map(i => i[field]).filter(v => v != null)
        ).size)
      );

      if (minSlotUnique > 1) {
        if (!bestStrong || score > bestStrong.score) bestStrong = { key: field, score };
      } else {
        if (!bestWeak || score > bestWeak.score) bestWeak = { key: field, score };
      }
    }

    const winner = bestStrong || bestWeak;
    if (TOOL_CALLING_LOG && winner) {
      const tier = bestStrong ? '강한 키' : '약한 키 폴백';
      console.log(`[merge] matchKey 스코어링 ${tier}: "${winner.key}" (score=${winner.score})`);
    }
    return winner;
  }

  // matchKey가 1:N일 때 groupBy + count로 자동 집계 //
  _autoAggregate(items, groupField) {
    const groups = {};

    for (const item of items) {
      const key = String(item[groupField] ?? '');
      if (!groups[key]) groups[key] = { count: 0, numericSums: {}, representative: { ...item } };
      groups[key].count++;

      // 숫자 필드는 합산 (groupField 자체와 ID성 필드 제외)
      for (const [k, v] of Object.entries(item)) {
        if (k === groupField || typeof v !== 'number') continue;
        if (k.endsWith('Id') || k.endsWith('Version') || k === 'workoutId') continue;
        groups[key].numericSums[k] = (groups[key].numericSums[k] || 0) + v;
      }
    }

    return Object.entries(groups).map(([key, { count, numericSums, representative }]) => {
      const result = {};
      for (const [k, v] of Object.entries(representative)) {
        if (typeof v !== 'number') result[k] = v;
      }
      result[groupField] = key;
      result.count = count;
      Object.assign(result, numericSums);
      return result;
    });
  }

  // [L31-COMPLEX-005] visualizationOnly source intention 감지 — merge로 wrapping 시 visualizationOnly 플래그 전파용
  //   본질: visualizationOnly intention(MONTHLY+ATTENDANCE 등)을 다른 slot과 merge한 후 show_display 호출 시
  //   원본 intention buffer가 active가 아니므로 show_display visualization-only guard가 우회됨.
  //   merged buffer에 _sourceVizIntention/visualizationOnly 플래그를 전파해서 show_display에서 동일하게 차단.
  //   구조 기반(intention 키 hardcode 없음) — buf.visualizationOnly 플래그만 검사. dual sync with nodeAgent.mjs
  _detectMergeVizSource(slots) {
    if (!Array.isArray(slots)) return null;
    for (const s of slots) {
      const buf = this.buffers.get(s?.slotName);
      if (buf?.visualizationOnly === true) {
        return { visualizationOnly: true, _sourceVizIntention: buf.intention || null };
      }
    }
    return null;
  }

  // merge 결과 버퍼가 상속할 사전 필터 — 참여 슬롯들의 appliedFilters를 field 기준 dedup union //
  // intersect/except는 이미 필터된 데이터를 합치므로, numeric-subset 가드(detectNumericSubsetIntent)가
  // "필터 누락"으로 오판하지 않도록 메타를 보존해야 한다. slots[0]만 상속 시 두번째+ 슬롯에 걸린
  // 필터(예: ATTENDANCE WHERE attendedCount<=10)가 소실됨. (series/summary-stack 파생 row는 호출 안 함) //
  // dual sync with nodeAgent.mjs
  _collectSlotFilters(slots) {
    const out = [];
    const seen = new Set();
    for (const s of slots || []) {
      const buf = this.buffers.get(s.slotName);
      for (const f of (buf?.appliedFilters || [])) {
        if (f?.field && !seen.has(f.field)) { seen.add(f.field); out.push({ ...f }); }
      }
    }
    return out;
  }

  // N개 슬롯을 matchKey 기준으로 병합 //
  mergeSlots({ slots, matchKey, mode = 'inner', outputSlot = '_merged' }) {
    // 유효성 검사
    if (!slots || slots.length < 2) {
      return { success: false, message: 'Merge requires 2+ slots.' };
    }

    // [CONTEXT REUSE GUARD — cross-metric merged hijack 차단] merged 버퍼는 intention='MERGED'로
    //   source category가 소실됨. _findContextReuseSlot이 reuse 후보 판별 시 카테고리 검증할 수 있도록
    //   source 슬롯의 CHECK_* prefix를 보존 태그(아래 모든 출력버퍼에 _mergedSourcePrefix). 중첩 merge는
    //   source의 기존 태그 승계. (rid49: 재등록 merge가 매출 call_api를 hijack하던 것 차단. dual sync with nodeAgent.mjs)
    let _mergedSourcePrefix = null;
    for (const _s of slots) {
      const _sb = this.buffers.get(_s.slotName);
      const _si = String(_sb?.intention || '');
      if (_si === 'MERGED') { _mergedSourcePrefix = _mergedSourcePrefix || _sb?._mergedSourcePrefix || null; continue; }
      const _m = _si.match(/^(CHECK_[A-Z]+)/);
      if (_m) { _mergedSourcePrefix = _m[1]; break; }
    }

    // [X말고 Y 패턴 우선 차단] 새 차원(Y)을 call_api 없이 기존 슬롯 재merge로 우회하는 시도 차단
    // → LLM이 새 인텐션의 call_api 호출하면 _executeMultiPeriodAutoChain이 직전 period 구조 자동 적용
    if (!this._multiPeriodAutoChainFiredThisTurn && !this._calledApiThisTurn) {
      const userInput = this.getUserInput?.() || '';
      if (/(\S+)\s*(말고|대신|아니고|아니라)\s+(\S+)/.test(userInput)) {
        const periods = this._extractPeriodStructure();
        if (periods && periods.length >= 2) {
          return {
            success: false,
            message: `[SWITCH PATTERN BLOCK] 사용자 input "${userInput.slice(0, 60)}"가 "X말고 Y" 패턴입니다 — 새 차원(Y)을 위해 기존 슬롯 재merge가 아니라 call_api(Y intention)를 먼저 호출하세요. 코드가 직전 turn 기간 구조(${periods.map(p => p.start + '~' + p.end).join(', ')})를 자동 적용합니다. (get_intentions로 Y에 맞는 인텐션 찾기 → call_api → 자동 chain → show_display)`,
          };
        }
      }
    }

    // [SELF-INTERSECT GUARD] 동일 slotName 중복 입력 차단 — 자기 자신과 merge는 의미 없음
    const slotNames = slots.map(s => s?.slotName).filter(Boolean);
    if (new Set(slotNames).size < slotNames.length) {
      const available = [...this.buffers.keys()].filter(k => k !== outputSlot).join(', ');
      return { success: false, message: `merge_slots: 동일한 slotName(${slotNames.join(', ')})이 중복됩니다. 서로 다른 슬롯을 지정하세요. 현재 사용 가능 슬롯: [${available}]. cross-merge는 default(원본)와 새로 로드한 슬롯(예: alive, attendance)을 함께 지정해야 합니다.` };
    }

    // [SLOT NOT FOUND 2026-07-06] 미존재 슬롯(LLM 슬롯명 오기재)을 "0건"과 동일 취급하면 실데이터 적재 상태에서
    //   "조회된 데이터가 모두 0건" 거짓 no-data verbatim finish 강제 (FN-1) — no-data 경로는 전 슬롯 실재+0건일 때만.
    //   미존재 슬롯 포함 시 가용 슬롯 목록 반환으로 재바인딩 유도, finish 강제 없음. (dual sync with nodeAgent.mjs)
    const notFoundSlotNames = slots.filter(s => !this.buffers.get(s.slotName)).map(s => s.slotName);
    if (notFoundSlotNames.length > 0) {
      const availableList = [...this.buffers.entries()].map(([k, b]) => `${k}(${b?.items?.length || 0} items)`).join(', ');
      return {
        success: false,
        message: `[SLOT NOT FOUND] Slot(s) "${notFoundSlotNames.join(', ')}" do not exist — this is a slot-name mistake, NOT empty data. Existing slots: [${availableList}]. Re-call merge_slots with existing slot names, or switch_slot/show_display the slot that already holds the requested data.`,
      };
    }

    // 슬롯 존재 확인 — 모든 슬롯 비어있으면 데이터 부재 안내 (finish 허용)
    const emptySlotNames = slots
      .filter(s => {
        const b = this.buffers.get(s.slotName);
        return !b.items || b.items.length === 0;
      })
      .map(s => s.slotName);
    if (emptySlotNames.length === slots.length) {
      this._finishAllowedByGuard = true;
      const userInput = this.getUserInput?.() || '';
      const suggestedText = userInput
        ? `'${userInput.slice(0, 50)}' 요청에 대한 결과가 없어요. 조회된 데이터가 모두 0건입니다. 다른 기간이나 조건으로 다시 시도해 주세요.`
        : '요청하신 기간/조건의 데이터가 없어 비교 결과를 보여드릴 수 없어요. 다른 기간이나 조건으로 다시 시도해 주세요.';
      this._finishGuardSuggestedText = suggestedText;
      return {
        success: false,
        message: `[NO DATA — ALL SLOTS EMPTY] All ${slots.length} slots have 0 items. Call this EXACT finish — copy verbatim, no modifications, no other tool calls:\n\nfinish({response: "${suggestedText}"})`,
      };
    }
    for (const { slotName } of slots) {
      const buf = this.buffers.get(slotName);
      if (!buf || !buf.items || buf.items.length === 0) {
        const activeData = this.buffers.get(this.activeSlot);
        const hasFilters = buf?.appliedFilters?.length > 0;
        const originalCount = buf?.originalItems?.length || 0;
        const filterHint = hasFilters && originalCount > 0
          ? ` Slot "${slotName}" has filter applied (${buf.appliedFilters.map(f => f.field).join(', ')}) reducing ${originalCount} items to 0. Call switch_slot({slot:"${slotName}"}) → filter({reset:true}) to restore, then retry merge.`
          : '';
        const hint = activeData?.items?.length > 0
          ? ` Active slot "${this.activeSlot}" has ${activeData.items.length} items — use show_display to display it directly without merging.`
          : '';
        return { success: false, message: `Slot "${slotName}" has no data.${filterHint}${hint}` };
      }
    }

    // [#585-FOLLOWUP-E / PERIOD CHANGE AUTO-UNION] L9c-COMPARE-006 T3 — prior compare set drop 차단 (2026-05-30)
    // 사용자가 "X 기준으로 다시 비교 / X 추가해서 / X도 같이" 등 PERIOD CHANGE follow-up 입력 시
    // LLM이 prior compare 슬롯 일부 누락 (예: T0=3월/4월 후 T1=1월기준 → 4월 drop)하면 자동 합집합:
    // - 조건: user input == period-change keyword + 직전 outer-merge 같은 intention 컨텍스트 + 누락된 prior same-intention dateRange 슬롯 존재
    // - 동작: 누락된 prior compare 슬롯을 slots 배열에 compare_{next} label="<N월>"으로 자동 추가
    // 구조 기반 (case-ID 없음), language-agnostic month label, regression 안전 (same-intention multi-period 한정).
    // dual sync with nodeAgent.mjs.
    {
      const userInputForUnion = this.getUserInput?.() || '';
      const PERIOD_CHANGE_KW = /기준으로\s*다시|기준으로\s*비교|로\s*다시\s*비교|에\s*맞춰서?\s*(?:다시|비교)|추가해서\s*비교|도\s*같이\s*비교|도\s*포함해서\s*(?:다시|비교)/;
      if (PERIOD_CHANGE_KW.test(userInputForUnion)) {
        const slotIntentionsForUnion = slots.map(s => this.buffers.get(s.slotName)?.intention);
        const sameIntentionForUnion =
          new Set(slotIntentionsForUnion.filter(Boolean)).size === 1 && slotIntentionsForUnion[0];
        if (sameIntentionForUnion) {
          const currentSlotNamesSet = new Set(slots.map(s => s.slotName));
          // dateRange 월 collect (현재 slots) + prior 같은 intention 슬롯의 dateRange 월
          const monthFromDr = (dr) => {
            const m = dr?.startDate && String(dr.startDate).match(/^\d{4}-(\d{2})/);
            return m ? `${parseInt(m[1], 10)}월` : null;
          };
          const currentMonths = new Set();
          for (const { slotName } of slots) {
            const lbl = monthFromDr(this.buffers.get(slotName)?.dateRange);
            if (lbl) currentMonths.add(lbl);
          }
          // 누락 prior slots: same-intention + dateRange 보유 + 현재 slots에 없는 슬롯
          const missingPriorSlots = [];
          for (const [slotName, buf] of this.buffers.entries()) {
            if (currentSlotNamesSet.has(slotName)) continue;
            if (slotName.startsWith('_merged') || slotName === outputSlot) continue;
            if (buf?.intention !== sameIntentionForUnion) continue;
            const lbl = monthFromDr(buf?.dateRange);
            if (!lbl || currentMonths.has(lbl)) continue;
            missingPriorSlots.push({ slotName, label: lbl });
          }
          if (missingPriorSlots.length > 0) {
            // 다음 compare 인덱스 결정 (기존 compare_N 중 가장 큰 N + 1부터 시작)
            let nextIdx = 1;
            for (const s of slots) {
              const m = String(s.slotName).match(/^compare_(\d+)$/);
              if (m) nextIdx = Math.max(nextIdx, parseInt(m[1], 10) + 1);
            }
            for (const ms of missingPriorSlots) {
              // 이미 compare_*/base/named slot 형태 그대로 사용 — slotName은 buffer key, label만 보강
              slots.push({ slotName: ms.slotName, label: ms.label });
            }
            if (TOOL_CALLING_LOG) {
              console.log(`[merge] PERIOD CHANGE AUTO-UNION (#585-FOLLOWUP-E): user="${userInputForUnion.slice(0, 50)}" → added ${missingPriorSlots.length} prior slot(s) [${missingPriorSlots.map(x => `${x.slotName}(${x.label})`).join(', ')}] to merge set`);
            }
          }
        }
      }
    }

    // [PERIOD COVERAGE GUARD] — P26 차단 (multi-period 비교 case에서 LLM이 일부 기간만 load 후 조기 merge하는 패턴 차단)
    // 구조 기반: user input에 explicit "N월" 2개 이상 + slots가 same-intention multi-period + 슬롯 dateRange month set이 user input month set과 다름 → merge 거절 후 누락 월 load 유도
    // L11-COMPARE-FULL-001: wrong-offset case (LLM이 같은 개수의 month 로드했지만 month set 자체가 user 요청과 다름 — 예: 1월/2월/3월 vs 요청 2월/3월/4월) 포함 cover.
    // dual sync with nodeAgent.mjs. cross-intention/cross-category merge는 isSameIntentionMerge=false라 미발동 (regression 안전).
    {
      const userInputForCoverage = this.getUserInput?.() || '';
      const requestedMonths = this._extractMonthSet(userInputForCoverage);
      if (requestedMonths.size >= 2) {
        const slotIntentionsForCoverage = slots.map(s => this.buffers.get(s.slotName)?.intention);
        const isSameIntentionMergeForCoverage =
          new Set(slotIntentionsForCoverage.filter(Boolean)).size === 1 && slotIntentionsForCoverage[0];
        if (isSameIntentionMergeForCoverage) {
          const coveredMonths = new Set();
          for (const { slotName } of slots) {
            const dr = this.buffers.get(slotName)?.dateRange;
            if (!dr?.startDate) continue;
            const m = String(dr.startDate).match(/^\d{4}-(\d{2})/);
            if (m) {
              const n = parseInt(m[1], 10);
              if (n >= 1 && n <= 12) coveredMonths.add(`${n}월`);
            }
          }
          const missing = [...requestedMonths].filter(m => !coveredMonths.has(m));
          const extra = [...coveredMonths].filter(m => !requestedMonths.has(m));
          if (missing.length > 0) {
            const covered = [...coveredMonths].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const requested = [...requestedMonths].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const usedCompareSlots = [...this.buffers.keys()].filter(k => k.startsWith('compare_'));
            const nextCompareIdx = usedCompareSlots.length + 1;
            const hasBaseSlot = this.buffers.has('base');
            const missingCount = missing.length;
            const slotPlan = missing.map((m, i) => {
              const isLast = (i === missingCount - 1);
              const slotForThis = (isLast && !hasBaseSlot) ? 'base' : `compare_${nextCompareIdx + i}`;
              return `${m} → slot:"${slotForThis}"`;
            }).join(', ');
            const extraClause = extra.length > 0
              ? `\n\n현재 merge 대상에 사용자가 요청하지 않은 월이 포함되어 있어요: ${extra.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).join('/')}. 재호출 시 이 슬롯들은 제외하고 요청 월(${requested.join('/')})에 해당하는 슬롯만 merge_slots slots에 포함하세요.`
              : '';
            return {
              success: false,
              message: `[PERIOD COVERAGE BLOCK] 사용자가 ${requested.join('/')} (${requestedMonths.size}개 기간)을 비교 요청했는데 현재 merge 대상 슬롯의 month set은 ${covered.join('/')}이에요. 누락 월: ${missing.join('/')}.${extraClause}\n\nmerge_slots 호출 차단. 먼저 call_api로 누락 월 데이터 load:\n${slotPlan}\n\n그 다음 merge_slots을 요청한 ${requestedMonths.size}개 월(${requested.join('/')})에 해당하는 슬롯만 포함해서 재호출하세요. (마지막 기간은 slot:"base" 권장 — COMPARISON_GUIDE 규약.) 사용자가 명시한 월과 다른 월로 비교하는 건 의도 불일치 → 차단.`,
            };
          }
        }
      }
    }

    // [#585-FOLLOWUP-D / VIEW-FORM CONTINUITY] chain follow-up 일관성 가드
    // 직전 turn(같은 세션)에서 same-intention outer-merge로 detail-list 비교 뷰가 produce된 직후,
    // 현재 turn에서 LLM이 same-intention + mode='inner' 선택 시 SUMMARY-OVERRIDE가 entry → period-stack
    // 뷰로 silent 변경되어 사용자 입장에선 "다시 비교"인데 뷰 form 자체가 바뀌어 비교 자체가 안 보이는 mismatch 발생.
    // 구조 검사만 — keyword/입력 hardcode 없음. _lastMergeMode === 'outer' + same intention이 트리거 조건.
    // 직전 detail-list 컨텍스트를 유지하기 위해 mode='inner' → 'outer'로 coerce. (LLM은 즉시 다음 step 진행, 뷰는 turn 0과 동일 form)
    {
      const slotIntentionsForContinuity = slots.map(s => this.buffers.get(s.slotName)?.intention);
      const sameIntentionForContinuity =
        new Set(slotIntentionsForContinuity.filter(Boolean)).size === 1 && slotIntentionsForContinuity[0];
      if (
        mode === 'inner' &&
        this._lastMergeMode === 'outer' &&
        sameIntentionForContinuity &&
        this._lastMergeIntention &&
        sameIntentionForContinuity === this._lastMergeIntention
      ) {
        if (TOOL_CALLING_LOG) {
          console.log(`[merge] VIEW-FORM CONTINUITY: prior outer-merge same-intention(${this._lastMergeIntention}) → coerce mode 'inner' → 'outer' (block SUMMARY-OVERRIDE silent view drift)`);
        }
        mode = 'outer';
      }
    }

    // [SUMMARY-OVERRIDE] same-intention + 사용자 input에 회원별/개별 키워드 X
    // → matchKey join (회원별 비교) 대신 summary-level vertical stack (총액/요약 비교 의도가 default)
    // 슬롯 summary 메타 우선, 부재 시 items numeric 자동 합산으로 fallback 합성 (구조 기반)
    // [1-C] mode === 'intersect' 도 포함 — multi-period same-intention RATE_STATISTICS 등에서 LLM이 intersect 선택 시도 시 entity overlap 결과 신뢰성 ↓ + show_display 누락 차단 (1-B β 대칭). cross-category/cross-intention intersect는 isSameIntentionMerge=false라 자동 우회 (regression X).
    if ((mode === 'inner' || mode === 'intersect') && slots.length >= 2) {
      const slotIntentions = slots.map(s => this.buffers.get(s.slotName)?.intention);
      const isSameIntentionMerge = new Set(slotIntentions.filter(Boolean)).size === 1 && slotIntentions[0];
      const userInputForOverride = this.getUserInput?.() || '';
      // 각각/따로/별도/개별로 — per-slot 분리 표시 의도. 사용자가 두 기간/슬롯의 결과를
      // 합친 summary 한 줄이 아닌 entity-level 단위(회원 list 등)로 따로따로 보고 싶다는 신호.
      // SUMMARY-OVERRIDE 차단 후 matchKey resolution → entity join 경로로 진입하도록 처리.
      const PER_ENTITY_KW = /회원별|사람별|개별|각각|따로|별도|각자|누가|누구|어떤\s*회원|회원\s*중|회원\s*마다|사람\s*마다|(?:줄어든|늘어난|증가한|감소한|많은|적은|높은|낮은|올라간|떨어진|새로|신규|이탈한)\s*(?:회원|사람|유저|고객|학생)/;
      const hasPerEntityIntent = PER_ENTITY_KW.test(userInputForOverride);

      // 슬롯별 summary 결정 — buf.summary 우선, 없으면 items numeric 자동 합산
      const slotSummaries = slots.map(s => {
        const buf = this.buffers.get(s.slotName);
        if (buf?.summary && typeof buf.summary === 'object' && Object.keys(buf.summary).length > 0) {
          return { source: 'meta', summary: { ...buf.summary } };
        }
        if (buf?.items && buf.items.length > 0) {
          const synth = { _count: buf.items.length };
          for (const item of buf.items) {
            for (const [k, v] of Object.entries(item)) {
              if (typeof v !== 'number') continue;
              if (k.endsWith('Id') || k.endsWith('Version') || k === 'workoutId') continue;
              synth[k] = (synth[k] || 0) + v;
            }
          }
          if (Object.keys(synth).length > 1) return { source: 'synthesized', summary: synth };
        }
        return null;
      });
      const canSummaryOverride = isSameIntentionMerge && !hasPerEntityIntent && slotSummaries.every(ss => ss !== null);

      if (canSummaryOverride) {
        const stackedItems = slots.map((s, i) => {
          const buf = this.buffers.get(s.slotName);
          const item = { ...slotSummaries[i].summary };
          const labelValue = s.label || s.slotName;
          const dr = buf.dateRange;
          const periodDisplay = formatPeriodLabel(dr?.startDate, dr?.endDate) || labelValue;
          item._label = labelValue; // 내부 식별/호환용 (기존 그대로)
          item.period = periodDisplay; // 사용자 표시용 (한국어 기간 라벨)
          if (dr?.startDate && dr?.endDate) {
            item.periodStart = dr.startDate;
            item.periodEnd = dr.endDate;
          }
          return item;
        });

        // 누락 키 null 채움 (컬럼 일관성)
        const allKeys = new Set();
        for (const it of stackedItems) for (const k of Object.keys(it)) allKeys.add(k);
        for (const it of stackedItems) for (const k of allKeys) if (!(k in it)) it[k] = null;

        // 2슬롯 비교 시 숫자 필드 _change_/_changeRate_ 자동 파생 (첫=이전, 둘째=최근 기준)
        if (slots.length === 2) {
          const numericKeys = [...allKeys].filter(k =>
            !k.startsWith('_') && typeof stackedItems[0][k] === 'number' && typeof stackedItems[1][k] === 'number'
          );
          for (const key of numericKeys) {
            const prevVal = stackedItems[0][key];
            const currVal = stackedItems[1][key];
            stackedItems[1][`_change_${key}`] = Math.round((currVal - prevVal) * 100) / 100;
            stackedItems[1][`_changeRate_${key}`] = prevVal !== 0
              ? Math.round(((currVal - prevVal) / Math.abs(prevVal)) * 1000) / 10
              : null;
          }
        }

        // mergedAvailableFields: 백엔드 라벨만 spread. 합성 필드(period/_change_*) 라벨은
        // buildColumnLabels의 동적 패턴 + LABEL_OVERRIDES fallback + LLM columnLabels로 처리.
        const mergedAvailableFields = {};
        for (const { slotName } of slots) {
          const buf = this.buffers.get(slotName);
          if (buf?.availableFields) Object.assign(mergedAvailableFields, buf.availableFields);
        }

        // 비교 표는 시간순(이전→최근)이 자연 — periodStart 기준 asc 정렬 + appliedSort 명시
        // (autoGenerateDisplay의 autoSort 진입 차단, _change_* 위치 일관 보장)
        const allHavePeriodStart = stackedItems.every(it => it.periodStart);
        const orderedItems = allHavePeriodStart
          ? [...stackedItems].sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)))
          : stackedItems;
        const defaultSort = allHavePeriodStart ? { field: 'periodStart', direction: 'asc' } : null;

        this.buffers.set(outputSlot, {
          raw: null, items: [...orderedItems],
          originalItems: [...orderedItems.map(item => ({ ...item }))],
          intention: 'MERGED', availableFields: mergedAvailableFields, summary: null, _mergedSourcePrefix,
          dateRange: null,
          appliedFilters: [], appliedSort: defaultSort,
          columnPrefs: null, resolvedColumns: null,
          _aggregateMetaFields: [],
        });
        this.activeSlot = outputSlot;
        this._mergeOutputSlot = outputSlot;
        this._comparisonActive = false;
        // [#585-FOLLOWUP-D] 직전 merge 모드/intention 기록 — chain follow-up view continuity guard 용
        this._lastMergeMode = 'summary-series';
        this._lastMergeIntention = isSameIntentionMerge || null;
        // [DEMO-T4-REREG / 운영4차 #5] SUMMARY-OVERRIDE 소스 슬롯을 merge 소스로 등록 (SUMMARY-SERIES fallback과 동일) —
        //   누락 시 buildUndisplayedSlotSummaryOutro(aiUtils)가 period-stack 소스(예: 4월 슬롯)를 "미표시 슬롯"으로 보고
        //   단일월 summary("함께 조회한 요약 4월 기준")를 outro 부가 → 전체 흐름 답변에 한 달치만 붙어 혼란. dual sync with nodeAgent.mjs
        if (this._turnMergedSourceSlots) for (const s of slots) if (s?.slotName) this._turnMergedSourceSlots.add(s.slotName);

        const periodEntries = slots
          .map(s => {
            const dr = this.buffers.get(s.slotName)?.dateRange;
            return dr?.startDate && dr?.endDate ? `${s.label || s.slotName}=${dr.startDate}~${dr.endDate}` : null;
          })
          .filter(Boolean)
          .join(', ');
        const periodHint = periodEntries ? ` 기간: ${periodEntries}.` : '';

        if (TOOL_CALLING_LOG) {
          console.log(`[merge] SUMMARY-OVERRIDE: 총액 비교 의도 → summary-level stack (${stackedItems.length}건)`);
        }

        return {
          success: true,
          mode: 'summary-series',
          mergedCount: stackedItems.length,
          message: `[SUMMARY-OVERRIDE] 사용자가 회원별/개별 비교를 명시하지 않아 ${slots.length}개 슬롯 summary를 stack (${stackedItems.length}건, period 필드로 구분).${periodHint}

show_display 호출 필수 규칙:
(1) columns 첫 번째에 "period" 포함. columnLabels에 한국어 라벨 명시 권장 (예: { period: "기간" }).
(2) ${slots.length === 2 ? `비교 표는 [period, <지표들>] 순서로 base 컬럼만 포함. 변화량/변화율은 표 컬럼으로 두지 마세요 — _change_*/_changeRate_* 필드는 자동 파생되어 있지만 columns에 포함하면 표가 과도하게 넓어집니다. 변화량은 아래 outro 자연어 규칙으로 표현하세요.` : ''}
(3) intro: 비교 기간을 자연어로 명시 (예: "2월과 3월 매출 비교").

finish 호출 outro 필수 규칙:
- 변화량과 변화율을 자연어로 명시. 수치 자리에 placeholder 토큰을 사용하세요 — 코드가 buffer 정답으로 결정적 치환합니다 (raw 수치 직접 작성 시 hallucination 위험).
    예) "3월은 2월 대비 결제 금액 {{CHANGE:totalPrice}} ({{CHANGE_RATE:totalPrice}}) 증가, 원가 {{CHANGE:originalPrice}} ({{CHANGE_RATE:originalPrice}}) 증가했어요."
    → 코드 치환: "3월은 2월 대비 결제 금액 +7,913,500원 (+65.3%) 증가, 원가 +4,440,500원 (+21.9%) 증가했어요."
  placeholder 형식: {{CHANGE:fieldName}} (변화량) / {{CHANGE_RATE:fieldName}} (변화율). fieldName은 _change_/_changeRate_ prefix 제외한 base 필드명 (예: _change_totalPrice → totalPrice). 자연어 문장 구조(어순/연결사/종결어)는 자유.
- "비교 가능합니다" / "확인하실 수 있어요" 같이 사용자에게 계산 떠넘기는 표현 금지.
- 표 columns에 이미 포함된 차원(cardPrice, cashPrice, originalPrice 등)을 outro에서 "결제수단별로 더 보여드릴까요" 식으로 재제안 절대 금지.
[ACTIVE SLOT] 합쳐진 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능. switch_slot 불필요 (이미 목표 슬롯이 active).${this.getSlotContext()}`,
        };
      }
    }

    // matchKey 자동 감지 (memberId > userId > LLM 지정 키)
    let resolved = this.resolveMatchKey(slots, matchKey);

    // [1-C] resolved matchKey가 row-level timestamp/date + multi-period outer/compare 시 entity-level identifier 자동 치환
    if (resolved.key) {
      const _mmkEval = evaluateMergeMatchKey({
        matchKey: resolved.key,
        mode,
        slots,
        buffers: this.buffers,
        swapAttempted: !!this._mmkSwapAttempted,
      });
      if (_mmkEval.swap) {
        this._mmkSwapAttempted = true;
        const _mmkRetry = this.mergeSlots({ slots, matchKey: _mmkEval.suggestedMatchKey, mode, outputSlot });
        this._mmkSwapAttempted = false;
        if (_mmkRetry && typeof _mmkRetry === 'object') {
          return {
            ..._mmkRetry,
            message: `${_mmkEval.message}\n${_mmkRetry.message || ''}`,
          };
        }
      }
    }

    if (!resolved.key) {
      // [SUMMARY-SERIES FALLBACK] 슬롯마다 1건씩 + matchKey 부재 → 기간 시리즈로 vertical stack
      // 구조 기반: entity-level join 불가한 summary 데이터에서 _label로 기간 라벨링 후 표 생성
      const allSingleItem = slots.every(s => this.buffers.get(s.slotName)?.items?.length === 1);
      if (allSingleItem && (mode === 'inner' || mode === 'intersect')) {
        const stackedItems = slots.map(s => {
          const buf = this.buffers.get(s.slotName);
          const item = { ...buf.items[0] };
          item._label = s.label || s.slotName;
          return item;
        });
        const allKeys = new Set();
        for (const it of stackedItems) for (const k of Object.keys(it)) allKeys.add(k);
        for (const it of stackedItems) for (const k of allKeys) if (!(k in it)) it[k] = null;

        // mergedAvailableFields: 백엔드 라벨만 spread. 합성 필드 라벨은 LABEL_OVERRIDES + LLM columnLabels로 처리.
        const mergedAvailableFields = {};
        for (const { slotName } of slots) {
          const buf = this.buffers.get(slotName);
          if (buf?.availableFields) Object.assign(mergedAvailableFields, buf.availableFields);
        }

        const _vizProp_series = this._detectMergeVizSource(slots);
        this.buffers.set(outputSlot, {
          raw: null, items: [...stackedItems],
          originalItems: [...stackedItems.map(item => ({ ...item }))],
          intention: 'MERGED', availableFields: mergedAvailableFields, summary: null, _mergedSourcePrefix,
          dateRange: null,
          appliedFilters: [], appliedSort: null,
          columnPrefs: null, resolvedColumns: null,
          _aggregateMetaFields: [],
          ...(_vizProp_series || {}),
        });
        this.activeSlot = outputSlot;
        this._mergeOutputSlot = outputSlot;
        this._comparisonActive = false;
        // [#585-FOLLOWUP-D] view-form continuity tracking
        {
          const _intsForSeries = slots.map(s => this.buffers.get(s.slotName)?.intention);
          const _sameIntForSeries = new Set(_intsForSeries.filter(Boolean)).size === 1 && _intsForSeries[0];
          this._lastMergeMode = 'series';
          this._lastMergeIntention = _sameIntForSeries || null;
        }

        // [UNDISPLAYED METRIC AUTO-APPEND] 시리즈 stack 합류 슬롯도 표시 데이터 — 보강 후보 제외 //
        if (this._turnMergedSourceSlots) for (const _ms of slots) if (_ms?.slotName) this._turnMergedSourceSlots.add(_ms.slotName);

        const labels = slots.map(s => s.label || s.slotName).join('/');
        return {
          success: true, mode: 'series', mergedCount: stackedItems.length,
          message: `[SUMMARY-SERIES] ${slots.length}개 슬롯이 각 1건씩이라 entity-level join 불가 → 기간 시리즈로 stack (${labels}, _label 필드 추가). ${stackedItems.length}건이 "${outputSlot}"에 저장됨. show_display로 비교 표시하세요.\n[ACTIVE SLOT] 합쳐진 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능. switch_slot 불필요 (이미 목표 슬롯이 active).${this.getSlotContext()}`,
        };
      }
      // [CHAIN HINT — raw slot fallback] 매칭 실패 시 사용 가능한 raw slots + 공통 식별자 fields + 다음 호출 예시 자동 명시
      const _failedSlotNames = new Set(slots.map(s => s.slotName));
      const _rawCandidates = [...this.buffers.entries()]
        .filter(([n, buf]) => !_failedSlotNames.has(n) && (buf.items?.length || 0) > 0);
      let _chainHint = '';
      if (_rawCandidates.length >= 2) {
        const _samples = _rawCandidates.map(([, buf]) => buf.items[0] || {});
        let _common = new Set(
          Object.keys(_samples[0]).filter(k => _samples[0][k] != null && typeof _samples[0][k] !== 'object')
        );
        for (let i = 1; i < _samples.length; i++) {
          const _next = new Set(
            Object.keys(_samples[i]).filter(k => _samples[i][k] != null && typeof _samples[i][k] !== 'object')
          );
          _common = new Set([..._common].filter(k => _next.has(k)));
        }
        if (_common.size > 0) {
          const _idFields = [..._common].slice(0, 8);
          const [_rawA] = _rawCandidates[0];
          const [_rawB] = _rawCandidates[1];
          const _exKey = _idFields[0];
          const _targetSlot = [...this.buffers.keys()].find(n => _failedSlotNames.has(n) && n !== '_merged' && !/^sql_/.test(n)) || '<target_slot>';
          _chainHint = `\n[CHAIN HINT — raw slot fallback] 매칭 실패한 슬롯이 aggregated 결과일 수 있습니다. raw source slots에서 entity-level chain 시도:\n- raw slots: ${_rawCandidates.slice(0, 3).map(([n, b]) => `${n}(${b.items.length}건)`).join(', ')}\n- 공통 식별자 fields: [${_idFields.join(', ')}]\n- 호출 예시:\n  1) merge_slots([{slotName:"${_rawA}"},{slotName:"${_rawB}"}], mode:"compare", matchKey:"${_exKey}", outputSlot:"_byEntity")\n  2) (변화 조건 있으면) filter({field:"_change_<value field>", condition:{type:"numeric_lt", value:0}})\n  3) merge_slots([{slotName:"_byEntity"},{slotName:"${_targetSlot}"}], mode:"intersect", matchKey:"${_exKey}")\n  4) show_display`;
        }
      }
      // [NO-JOIN RECOVERY] 차원이 다른 두 슬롯(공통 식별자 0) — merge 재시도 대신 결정적 회복 경로 명시 //
      // 축 슬롯 표시 + 반대 슬롯 summary 수치 finish 인용. 본질: per-axis 다지표 질문(DEMO-37)에서 merge 실패 후
      // LLM 회복이 stochastic하게 갈림 (표 미렌더 / 두번째 지표 누락 / 내역 dump). 축 판정은 row 수 휴리스틱이 아닌
      // userInput "X별" 토큰 해소(_resolveAxisFieldInSlot) 우선 — 내역형 슬롯(94행 결제내역 등)이 행 수로 축을 가로채는 오판 차단.
      // 발동 조건 좁음 — CHAIN HINT(공통 식별자 보유) 비발동 시에만. dual sync with nodeAgent.mjs //
      let _noJoinRecovery = '';

      if (slots.length === 2 && !_chainHint) {
        const _bufA = this.buffers.get(slots[0].slotName);
        const _bufB = this.buffers.get(slots[1].slotName);

        // 축 선정 ①: userInput "X별" 토큰이 한쪽 슬롯에서만 해소되면 그 슬롯이 축
        let _axisName = null;
        const _inputNJ = this.getUserInput?.() || '';
        const _axisTokensNJ = [..._inputNJ.matchAll(/([가-힣A-Za-z0-9]{1,12}?)\s?별(?:로|은|는|을|를)?(?=\s|$|[^가-힣])/g)].map(m => m[1]).filter(Boolean);

        for (const _tk of _axisTokensNJ) {
          const _inA = this._resolveAxisFieldInSlot(_tk, slots[0].slotName);
          const _inB = this._resolveAxisFieldInSlot(_tk, slots[1].slotName);
          if (_inA && !_inB) { _axisName = slots[0].slotName; break; }
          if (_inB && !_inA) { _axisName = slots[1].slotName; break; }
        }

        // 축 선정 ② fallback: 한쪽만 multi-row (반대쪽 0~1행 summary성)
        if (!_axisName) {
          const _rowsA = _bufA?.items?.length || 0;
          const _rowsB = _bufB?.items?.length || 0;
          if (_rowsA > 1 && _rowsB <= 1) _axisName = slots[0].slotName;
          else if (_rowsB > 1 && _rowsA <= 1) _axisName = slots[1].slotName;
        }

        const _axisBuf = _axisName ? this.buffers.get(_axisName) : null;

        if (_axisName && (_axisBuf?.items?.length || 0) > 1) {
          const _otherName = _axisName === slots[0].slotName ? slots[1].slotName : slots[0].slotName;
          const _otherBuf = this.buffers.get(_otherName);
          const _otherSummary = _otherBuf?.summary;
          const _sumEntries = (_otherSummary && typeof _otherSummary === 'object')
            ? Object.entries(_otherSummary)
              .filter(([k, v]) => (typeof v === 'number' || typeof v === 'string') && !isAmbiguousCountKey(k))
              .slice(0, 8)
              .map(([k, v]) => `${k}=${v}`)
            : [];
          const _citePart = _sumEntries.length > 0
            ? `keep it short — the system AUTO-APPENDS the second metric summary from "${_otherName}" backend scalars ({${_sumEntries.join(', ')}}) to the final answer. Do NOT write these numbers yourself and do NOT use {{SUMMARY:...}} for them`
            : `the second metric "${_otherName}" holds ${_otherBuf?.items?.length || 0} raw rows with NO backend summary — do NOT fabricate totals from rows. If the user needs period totals, call_api a summary-form intention of that category (period statistics) BEFORE step 1 — the system will auto-append its backend scalars; otherwise mention the row count only`;

          _noJoinRecovery = `\n[NO-JOIN RECOVERY] These two slots hold DIFFERENT dimensions (no shared join key) — that is EXPECTED, do NOT retry merge_slots. Recover in 3 steps:\n1) switch_slot({slot:"${_axisName}"})\n2) show_display with "${_axisName}" columns (the multi-row table the user asked for)\n3) finish() — ${_citePart}.`;
        }
      }

      return { success: false, message: resolved.error + _chainHint + _noJoinRecovery };
    }
    matchKey = resolved.key;

    // [UNDISPLAYED METRIC AUTO-APPEND] matchKey 해소 = merge 진행 확정 — 소스 슬롯은 표시 데이터에 합류하므로 보강 후보 제외 //
    if (this._turnMergedSourceSlots) for (const _ms of slots) if (_ms?.slotName) this._turnMergedSourceSlots.add(_ms.slotName);

    // matchKey 고유성 검사 → 1:N이면 자동 aggregate 후 merge //
    const firstBufCheck = this.buffers.get(slots[0].slotName);
    const uniqueCount = new Set(firstBufCheck.items.map(i => i[matchKey]).filter(v => v != null)).size;
    const isOneToMany = uniqueCount < firstBufCheck.items.length;

    if (isOneToMany) {
      if (TOOL_CALLING_LOG) console.log(`[merge] matchKey "${matchKey}" 1:N 감지 (${uniqueCount} 고유 / ${firstBufCheck.items.length} 건) → 자동 aggregate`);
      for (const { slotName } of slots) {
        const buf = this.buffers.get(slotName);
        buf.items = this._autoAggregate(buf.items, matchKey);
        buf.originalItems = [...buf.items.map(item => ({ ...item }))];
        // _autoAggregate가 추가한 'count' 메타 필드 추적 (자동 _change_count 파생 시 메타로 인식되도록)
        buf._aggregateMetaFields = [...(buf._aggregateMetaFields || []), 'count'].filter((v, i, arr) => arr.indexOf(v) === i);
      }
    }

    // 각 슬롯별 인덱스 맵 생성: matchKey값 → item
    const slotMaps = slots.map(({ slotName }) => {
      const buf = this.buffers.get(slotName);
      const map = new Map();
      for (const item of buf.items) {
        const key = item[matchKey];
        if (key != null) map.set(String(key), item);
      }
      return map;
    });

    // 키 유니버스 결정
    let keyUniverse;
    if (mode === 'inner' || mode === 'intersect') {
      // 교집합: 모든 슬롯에 있는 키만
      keyUniverse = new Set(slotMaps[0].keys());
      for (let i = 1; i < slotMaps.length; i++) {
        for (const key of keyUniverse) {
          if (!slotMaps[i].has(key)) keyUniverse.delete(key);
        }
      }
    } else if (mode === 'except') {
      // 차집합: 첫 슬롯에 있는 키 중 나머지 슬롯에 없는 키만 (base - subtract)
      keyUniverse = new Set(slotMaps[0].keys());
      for (let i = 1; i < slotMaps.length; i++) {
        for (const key of slotMaps[i].keys()) {
          if (keyUniverse.has(key)) keyUniverse.delete(key);
        }
      }
    } else if (mode === 'left') {
      // 첫 슬롯 기준
      keyUniverse = new Set(slotMaps[0].keys());
    } else {
      // outer: 합집합
      keyUniverse = new Set();
      for (const map of slotMaps) {
        for (const key of map.keys()) keyUniverse.add(key);
      }
    }

    // 숫자 필드 vs 비숫자 필드 판별 (전체 슬롯의 필드 스캔)
    const numericFields = new Set();
    const identityFields = new Set();
    // [L5-MEMBERSHIP-TOPREREG-001] same-intention multi-period outer/left merge에서 entity-attribute string 필드 (memberShipName/purchaseDate/reRegistration 등)는
    //   같은 entity의 시간별 attribute로 prefix해서 월별 비교 컬럼 생성 (PERIOD_COLS_MISSING 본질 차단).
    //   → IDENTITY_FIELDS (person identifier)는 raw 유지, 나머지 non-numeric/non-identity는 attribute로 분리.
    const attributeFields = new Set();
    // 이미 MERGED 슬롯에 존재하는 prefixed 필드 (예: "4월_totalPrice"). 재-prefix 방지용으로 slot index별 추적.
    // → 라벨 leak (예: "기존_4월_totalPrice") 발생 차단. MERGED 슬롯의 prefixed 필드는 prefix 없이 그대로 복제.
    const preMergedPassthrough = new Map(); // slotIdx → Set<fieldName>
    // 각 slot이 실제로 보유한 numeric raw field set (재-prefix loop에서 empty-column 생성 방지용).
    // → 한 slot이 numericField field "totalPrice"를 가지지 않으면 (예: MERGED 슬롯) "기존_totalPrice" 같은 null column 생성 X.
    const slotNumericPresence = []; // slotIdx → Set<fieldName>
    // 각 slot이 실제로 보유한 attribute raw field set (재-prefix loop에서 empty-column 생성 방지용).
    const slotAttributePresence = []; // slotIdx → Set<fieldName>

    // identity로 강제 분류할 필드 패턴 (비교 무의미한 식별자/연락처)
    const IDENTITY_FIELDS = new Set([
      'userId', 'memberId', 'name', 'memberName', 'userName',
      'phone', 'phoneNumber', 'tel', 'mobile', 'contactNumber',
      'countryCode', 'profile', 'email',
    ]);

    // [L5-MEMBERSHIP-TOPREREG-001] same-intention multi-period outer/left merge 시 entity-attribute string prefix 활성화 조건.
    //   - cross-intention merge (isSameIntention=false): 미발동 (cross-merge string field는 첫 slot 우선 raw 유지가 자연 — name/memberShipName이 동일 entity referent)
    //   - inner/intersect/except mode: 별 branch에서 처리되므로 본 flag 영향 X
    //   - outer/left + same-intention only: 같은 entity의 시간별 attribute 비교가 명백한 의도
    const _earlyIntentions = slots.map(s => this.buffers.get(s.slotName)?.intention);
    const _earlyIsSameIntention = _earlyIntentions.every(i => i && i === _earlyIntentions[0]);
    const _enableAttributePrefix = _earlyIsSameIntention && (mode === 'outer' || mode === 'left');
    // [L11-CONSECUTIVE-COMPARE-001 Pattern B] attribute field 타입 추적 — outer/left merge 시 missing slot의 boolean attribute는 false로 정규화하여 NULL_VS_FALSE 일관성 보존.
    //   - rationale: numeric NULL→0 normalize (위 _missingNumericDefault) 와 동일 철학 — "이 기간 record 없음" = boolean false 의미 자연 (refund 없으면 isRefund=false와 같음).
    //   - boolean만 normalize — string/date는 placeholder('-') 강제하면 라벨 leak 위험 + 의미 왜곡 가능성 (구매일 "-" 등 mock value).
    //   - 타입 검출은 raw sampleItem 기반 (`typeof v === 'boolean'`) — case-specific 필드명 hardcode X, 모든 boolean attribute에 generic 적용.
    const attributeFieldTypes = new Map();

    for (let i = 0; i < slots.length; i++) {
      const { slotName } = slots[i];
      const buf = this.buffers.get(slotName);
      const sampleItem = buf.items[0] || {};
      const isPreMerged = buf?.intention === 'MERGED';
      const slotPassthrough = new Set();
      const slotNumeric = new Set();
      const slotAttribute = new Set();
      // pre-merged 슬롯의 prefixed 필드 detect: availableFields에 raw key가 없지만 사용 중인 prefixed 패턴 ("<label>_<rawKey>") 식별
      // availableFields = backend label dict (raw key 기반). prefixed 필드는 availableFields에 등재되지 않음.
      const availFieldKeys = new Set(Object.keys(buf?.availableFields || {}));
      for (const key of Object.keys(sampleItem)) {
        if (key === matchKey) continue;
        if (key.startsWith('_')) continue; // 파생 필드(_change_, _count 등) 프리픽스 방지

        // pre-merged passthrough: 이 슬롯이 MERGED + key가 availableFields raw key가 아님 (즉 이미 prefixed)
        //   → label leak 방지 위해 별도 카테고리로 격리. identity/numeric 분류 skip.
        if (isPreMerged && !availFieldKeys.has(key) && !IDENTITY_FIELDS.has(key)) {
          slotPassthrough.add(key);
          continue;
        }

        if (typeof sampleItem[key] === 'number' && !IDENTITY_FIELDS.has(key)) {
          slotNumeric.add(key);
        }

        // [L5-MEMBERSHIP-TOPREREG-001] same-intention outer/left merge 시 entity-attribute string presence track
        if (_enableAttributePrefix && !IDENTITY_FIELDS.has(key) && typeof sampleItem[key] !== 'number' && sampleItem[key] != null) {
          slotAttribute.add(key);
          // [L11-CONSECUTIVE-COMPARE-001 Pattern B] attribute field 타입 첫 검출 — boolean field만 NULL→false normalize.
          //   - first non-null sample 기준. boolean 외 (string/date) 타입은 'other'로 표시하여 null 유지 (기존 동작 보존).
          if (!attributeFieldTypes.has(key)) {
            const v = sampleItem[key];
            attributeFieldTypes.set(key, typeof v === 'boolean' ? 'boolean' : 'other');
          }
        }

        if (identityFields.has(key) || numericFields.has(key) || attributeFields.has(key)) continue;

        if (IDENTITY_FIELDS.has(key)) {
          identityFields.add(key);
        }
        else if (typeof sampleItem[key] === 'number') {
          numericFields.add(key);
        }
        // [L5-MEMBERSHIP-TOPREREG-001] same-intention outer/left + non-IDENTITY/non-number string → attribute field로 분리 (월별 prefix 적용)
        else if (_enableAttributePrefix) {
          attributeFields.add(key);
        }
        else {
          identityFields.add(key);
        }
      }
      slotNumericPresence.push(slotNumeric);
      slotAttributePresence.push(slotAttribute);
      if (slotPassthrough.size > 0) {
        preMergedPassthrough.set(i, slotPassthrough);
      }
    }

    // uniform value 감지: 모든 슬롯의 전 항목에서 동일값인 필드는 summary로 격리
    const uniformFields = new Map();
    for (const field of [...numericFields]) {
      let isUniform = true;
      const uniformValues = {};
      for (const { slotName } of slots) {
        const buf = this.buffers.get(slotName);
        const vals = buf.items.map(i => i[field]).filter(v => v != null);
        if (vals.length > 1 && new Set(vals).size === 1) {
          uniformValues[slotName] = vals[0];
        }
        else {
          isUniform = false;
          break;
        }
      }
      if (isUniform) {
        numericFields.delete(field);
        uniformFields.set(field, uniformValues);
        if (TOOL_CALLING_LOG) console.log(`[merge] uniform field "${field}" → summary로 격리`);
      }
    }

    // 병합 실행
    const mergedItems = [];
    const unmatchedCounts = {};
    slots.forEach(({ slotName }) => { unmatchedCounts[slotName] = 0; });

    // except 모드: 첫 슬롯(base)에서 나머지 슬롯(subtract)에 매칭되는 entity 제외
    // base 슬롯의 row 구조 그대로 유지 (subtract slot의 필드 mix-in X — "Y 없는" 의미상 Y의 metric은 row에 X)
    if (mode === 'except') {
      for (const key of keyUniverse) {
        const item0 = slotMaps[0].get(key);
        if (item0) mergedItems.push({ ...item0 });
      }

      // base slot의 unmatched 카운트 (subtract slot에 의해 제거된 row)
      const removedCount = slotMaps[0].size - keyUniverse.size;
      unmatchedCounts[slots[0].slotName] = removedCount;
      for (let i = 1; i < slots.length; i++) {
        unmatchedCounts[slots[i].slotName] = slotMaps[i].size;
      }

      // base slot의 availableFields만 사용 (subtract slot 필드는 의미상 row에 없음)
      const baseBuf = this.buffers.get(slots[0].slotName);
      const baseAvailableFields = baseBuf?.availableFields ? { ...baseBuf.availableFields } : {};

      const _vizProp_except = this._detectMergeVizSource(slots);
      this.buffers.set(outputSlot, {
        raw: null,
        items: [...mergedItems],
        originalItems: [...mergedItems.map(item => ({ ...item }))],
        intention: baseBuf?.intention || 'MERGED',
        availableFields: baseAvailableFields,
        summary: null,
        dateRange: baseBuf?.dateRange || null,
        appliedFilters: this._collectSlotFilters(slots),
        appliedSort: baseBuf?.appliedSort || null,
        columnPrefs: null,
        resolvedColumns: null,
        _aggregateMetaFields: baseBuf?._aggregateMetaFields || [],
        ...(_vizProp_except || {}),
      });

      this.activeSlot = outputSlot;

      const allFields = mergedItems.length > 0 ? Object.keys(mergedItems[0]) : [];

      if (TOOL_CALLING_LOG) {
        console.log(`[merge] ${slots.map(s => s.slotName).join(' - ')} → ${outputSlot} (${mergedItems.length}건, mode: except, removed: ${removedCount})`);
      }

      this._pendingCrossMerge = null;
      return {
        success: true,
        matchKey,
        mode: 'except',
        mergedCount: mergedItems.length,
        unmatchedCounts,
        fields: allFields,
        message: `차집합 완료. ${slots[0].slotName}의 ${slotMaps[0].size}건 중 ${slots.slice(1).map(s => s.slotName).join('/')}에 매칭된 ${removedCount}건 제외 → ${mergedItems.length}건 유지. (의미: "${slots[0].slotName}이지만 ${slots.slice(1).map(s => s.slotName).join('/')}에는 해당 X")\n[ACTIVE SLOT] 결과 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능.${this.getSlotContext()}`,
      };
    }

    // intersect 모드: 첫 번째 슬롯 데이터 우선, 추가 슬롯의 새 필드(첫 slot에 없는 것)도 spread
    // (cross-category INTERSECT 케이스: ALIVE × ATTENDANCE → attendedCount 등 ATTENDANCE 필드 보존 필수)
    if (mode === 'intersect') {
      for (const key of keyUniverse) {
        const item0 = slotMaps[0].get(key);
        const merged = item0 ? { ...item0 } : {};
        for (let i = 1; i < slots.length; i++) {
          const item = slotMaps[i].get(key);
          if (!item) continue;
          for (const [k, v] of Object.entries(item)) {
            if (!(k in merged)) merged[k] = v;
          }
        }
        mergedItems.push(merged);
      }

      // unmatched 카운트
      for (let i = 0; i < slots.length; i++) {
        let unmatched = 0;
        for (const key of slotMaps[i].keys()) {
          if (!keyUniverse.has(key)) unmatched++;
        }
        unmatchedCounts[slots[i].slotName] = unmatched;
      }

      // availableFields도 모든 slot 합침 (첫 slot 우선)
      const firstBufForFields = this.buffers.get(slots[0].slotName);
      const mergedAvailableFields = {};
      for (let i = slots.length - 1; i >= 0; i--) {
        const buf = this.buffers.get(slots[i].slotName);
        if (buf?.availableFields) Object.assign(mergedAvailableFields, buf.availableFields);
      }

      const _vizProp_intersect = this._detectMergeVizSource(slots);
      this.buffers.set(outputSlot, {
        raw: null,
        items: [...mergedItems],
        originalItems: [...mergedItems.map(item => ({ ...item }))],
        intention: 'MERGED',
        availableFields: mergedAvailableFields,
        summary: null,
        _mergedSourcePrefix,
        dateRange: firstBufForFields?.dateRange || null,
        appliedFilters: this._collectSlotFilters(slots),
        appliedSort: firstBufForFields?.appliedSort || null,
        columnPrefs: null,
        // intersect는 cross-reference/cross-category 용도가 대부분 — resolvedColumns 상속하면
        // 첫 slot 컬럼이 고정되어 사용자 입력 키워드 매칭이 무력화됨. null로 reset해 selectColumns 재실행.
        resolvedColumns: null,
        _aggregateMetaFields: firstBufForFields?._aggregateMetaFields || [],
        ...(_vizProp_intersect || {}),
      });

      this.activeSlot = outputSlot;

      const allFields = mergedItems.length > 0 ? Object.keys(mergedItems[0]) : [];

      if (TOOL_CALLING_LOG) {
        console.log(`[merge] ${slots.map(s => s.slotName).join(' ∩ ')} → ${outputSlot} (${mergedItems.length}건, mode: intersect)`);
        console.log('[merge] fields:', allFields);
        console.log('[merge] unmatched:', unmatchedCounts);
      }

      // 자동 변화 필터 (cross-filter with change 의도) //
      const autoFilterMsg_intersect = this._applyAutoChangeFilter(outputSlot, mergedItems);
      this._pendingCrossMerge = null;
      if (autoFilterMsg_intersect) {
        const updatedItems = this.buffers.get(outputSlot)?.items || mergedItems;
        return {
          success: true,
          matchKey,
          mode: 'intersect',
          mergedCount: updatedItems.length,
          unmatchedCounts,
          fields: allFields,
          message: `교차 필터링 완료. ${slots[0].slotName}의 ${mergedItems.length}건 중 ${slots[1].slotName}에도 존재하는 항목만 유지.${autoFilterMsg_intersect}\n[ACTIVE SLOT] 합쳐진 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능. switch_slot 불필요 (이미 목표 슬롯이 active).${this.getSlotContext()}`,
        };
      }

      return {
        success: true,
        matchKey,
        mode: 'intersect',
        mergedCount: mergedItems.length,
        unmatchedCounts,
        fields: allFields,
        message: `교차 필터링 완료. ${slots[0].slotName}의 ${mergedItems.length}건 중 ${slots[1].slotName}에도 존재하는 항목만 유지.\n[ACTIVE SLOT] 합쳐진 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능. switch_slot 불필요 (이미 목표 슬롯이 active).${this.getSlotContext()}`,
      };
    }

    for (const key of keyUniverse) {
      const merged = { [matchKey]: key };

      // 비숫자(identity) 필드: 첫 번째로 발견된 슬롯에서 복사
      for (const field of identityFields) {
        for (let i = 0; i < slotMaps.length; i++) {
          const item = slotMaps[i].get(key);
          if (item && item[field] != null) {
            merged[field] = item[field];
            break;
          }
        }
      }

      // 숫자 필드: 슬롯별 접두사 복제 (단, slot이 실제 raw field 보유한 경우만 — empty-column leak 방지)
      // [L11-CONSECUTIVE-COMPARE-001] outer/left mode 시 missing slot의 numeric field는 0으로 정규화.
      //   - rationale: outer-merge로 disjoint set 병합 시 한쪽 slot에만 entity 존재 → 다른 slot field=null이 finalText에서 blank로 표시.
      //   - "값 없음" 의미인데 한 행은 blank, 다른 행은 0 (실제 0값)으로 표시되어 NULL_VS_ZERO 일관성 깨짐.
      //   - numeric field (sum/count metric: totalPrice, attendedCount 등) 절대다수에서 "데이터 없음 = 0" 의미 보존 안전.
      //   - inner/intersect mode는 별 branch에서 처리되므로 본 loop 영향 X. except mode도 별 branch.
      const _missingNumericDefault = (mode === 'outer' || mode === 'left') ? 0 : null;
      for (const field of numericFields) {
        for (let i = 0; i < slots.length; i++) {
          // 이 slot이 field를 보유하지 않으면 (예: MERGED 슬롯이 raw "totalPrice" 없음) prefixedKey 생성 skip → "기존_totalPrice" null column leak 차단
          if (slotNumericPresence[i] && !slotNumericPresence[i].has(field)) continue;
          const item = slotMaps[i].get(key);
          const prefixedKey = `${slots[i].label}_${field}`;
          merged[prefixedKey] = item ? (item[field] ?? _missingNumericDefault) : _missingNumericDefault;
        }
      }

      // [L5-MEMBERSHIP-TOPREREG-001] attribute 필드: same-intention multi-period outer/left merge 시 entity-attribute string도 슬롯별 접두사 복제
      //   - rationale: 같은 entity (matchKey resolved)의 시간별 attribute (이번 구매 회원권/이전 회원권/구매일/재등록 여부 등)는
      //     identity (person identifier)와 달리 월별로 다른 값 가질 수 있음 → 비교 시 prefix 필요.
      //   - [L11-CONSECUTIVE-COMPARE-001 Pattern B] boolean attribute의 missing slot은 false로 normalize (numeric의 0과 동일 철학 — "이 기간 record 없음" = boolean false 의미 자연).
      //     ex) 1월 isRefund=false ("아니오")인데 2월 isRefund=null (blank) → 같은 의미 "환불 없음"이 다르게 표시되어 시각 불일치 → 둘 다 false로 통일.
      //   - 그 외 attribute (string/date)는 null 유지 (placeholder "-" 강제 시 라벨 leak 위험 + mock value 의미 왜곡 가능성)
      //   - cross-intention merge / inner / intersect / except에서는 _enableAttributePrefix=false라 attributeFields=빈 set이므로 본 loop 미발동
      for (const field of attributeFields) {
        const _fieldType = attributeFieldTypes.get(field);
        const _missingAttrDefault = (_fieldType === 'boolean' && (mode === 'outer' || mode === 'left')) ? false : null;
        for (let i = 0; i < slots.length; i++) {
          if (slotAttributePresence[i] && !slotAttributePresence[i].has(field)) continue;
          const item = slotMaps[i].get(key);
          const prefixedKey = `${slots[i].label}_${field}`;
          merged[prefixedKey] = item ? (item[field] ?? _missingAttrDefault) : _missingAttrDefault;
        }
      }

      // pre-merged passthrough 필드: 해당 slot에서 prefix 없이 그대로 복제 (라벨 leak 방지) //
      for (const [slotIdx, passthroughSet] of preMergedPassthrough) {
        const item = slotMaps[slotIdx].get(key);
        if (!item) continue;
        for (const pField of passthroughSet) {
          if (!(pField in merged) && item[pField] != null) {
            merged[pField] = item[pField];
          }
        }
      }

      // _change_/_changeRate_ 자체 파생 필드: 첫 슬롯에서 raw 그대로 보존 (prefix 없음, identity 패턴) //
      for (let i = 0; i < slots.length; i++) {
        const item = slotMaps[i].get(key);
        if (!item) continue;
        for (const k of Object.keys(item)) {
          if ((k.startsWith('_change_') || k.startsWith('_changeRate_')) && !(k in merged)) {
            merged[k] = item[k];
          }
        }
      }

      mergedItems.push(merged);
    }

    // 미매칭 수 계산
    for (let i = 0; i < slots.length; i++) {
      let unmatched = 0;
      for (const key of slotMaps[i].keys()) {
        if (!keyUniverse.has(key)) unmatched++;
      }
      unmatchedCounts[slots[i].slotName] = unmatched;
    }

    // 2개 슬롯 병합 시 자동 파생 필드 (_change_, _changeRate_)
    // base = 기준(최근), compare = 비교(이전) → _change = base - compare
    // 같은 인텐션(기간 비교)일 때만 파생. 다른 인텐션(교차 조인)이면 _change_ 무의미.
    const derivedFields = [];
    const slotIntentions = slots.map(s => this.buffers.get(s.slotName)?.intention);
    const isSameIntention = slotIntentions.every(i => i === slotIntentions[0]);

    if (slots.length === 2 && isSameIntention) {
      // base 슬롯을 기준(최근)으로, compare 슬롯을 비교(이전)으로 판별
      const baseSlot = slots.find(s => s.slotName === 'base') || slots[1];
      const compareSlot = slots.find(s => s.slotName !== 'base') || slots[0];

      for (const field of numericFields) {
        const baseKey = `${baseSlot.label}_${field}`;
        const compareKey = `${compareSlot.label}_${field}`;
        const changeKey = `_change_${field}`;
        const rateKey = `_changeRate_${field}`;

        // 첫 아이템의 값이 실제 숫자인 경우에만 _change 파생 (날짜/문자열 제외)
        const sampleVal = mergedItems[0]?.[baseKey] ?? mergedItems[0]?.[compareKey];
        if (typeof sampleVal !== 'number') continue;

        for (const item of mergedItems) {
          const bRaw = item[baseKey];
          const cRaw = item[compareKey];
          const baseVal = typeof bRaw === 'number' ? bRaw : 0;
          const compareVal = typeof cRaw === 'number' ? cRaw : 0;

          if (typeof bRaw === 'number' || typeof cRaw === 'number') {
            item[changeKey] = Math.round((baseVal - compareVal) * 100) / 100;
            item[rateKey] = compareVal !== 0
              ? Math.round(((baseVal - compareVal) / Math.abs(compareVal)) * 1000) / 10
              : null;
          }
          else {
            item[changeKey] = null;
            item[rateKey] = null;
          }
        }

        derivedFields.push(changeKey, rateKey);
      }
    }

    // 각 슬롯의 summary를 라벨 접두사로 병합
    const mergedSummary = {};
    for (const { slotName, label } of slots) {
      const buf = this.buffers.get(slotName);
      if (buf.summary) {
        for (const [key, val] of Object.entries(buf.summary)) {
          mergedSummary[`${label}_${key}`] = val;
        }
      }
    }

    // uniform fields도 summary에 병합 (행별 복사 방지, summary에서 통합 관리)
    for (const [field, slotValues] of uniformFields) {
      for (const { slotName, label } of slots) {
        if (slotValues[slotName] != null) {
          mergedSummary[`${label}_${field}`] = slotValues[slotName];
        }
      }
    }

    // 2개 슬롯 시 summary도 _change 파생
    if (slots.length === 2 && Object.keys(mergedSummary).length > 0) {
      const [first, second] = slots;
      const firstBufSummary = this.buffers.get(first.slotName).summary || {};
      const secondBufSummary = this.buffers.get(second.slotName).summary || {};

      for (const key of Object.keys(firstBufSummary)) {
        const prev = firstBufSummary[key];
        const curr = secondBufSummary[key];
        if (typeof prev === 'number' && typeof curr === 'number' && !isNaN(prev) && !isNaN(curr)) {
          mergedSummary[`_change_${key}`] = Math.round((curr - prev) * 100) / 100;
          mergedSummary[`_changeRate_${key}`] = prev !== 0
            ? Math.round(((curr - prev) / Math.abs(prev)) * 1000) / 10
            : null;
        }
      }
    }

    // 병합 결과를 새 슬롯에 저장
    // availableFields: 원본 슬롯의 availableFields만 합침 (라벨 매핑용)
    // 프리픽스 필드(최근_attendedCount)와 _change_ 필드는 buildColumnLabels에서 자동 해석
    const mergedAvailableFields = {};
    for (const { slotName } of slots) {
      const buf = this.buffers.get(slotName);
      if (buf?.availableFields) {
        Object.assign(mergedAvailableFields, buf.availableFields);
      }
    }

    // 첫 슬롯의 _aggregateMetaFields 전파 (메타 필드 추적 유지)
    const inheritedMetaFields = this.buffers.get(slots[0].slotName)?._aggregateMetaFields || [];

    const _vizProp_inner = this._detectMergeVizSource(slots);
    this.buffers.set(outputSlot, {
      raw: null,
      items: [...mergedItems],
      originalItems: [...mergedItems.map(item => ({ ...item }))],
      intention: 'MERGED',
      availableFields: mergedAvailableFields,
      summary: Object.keys(mergedSummary).length > 0 ? mergedSummary : null,
      _mergedSourcePrefix,
      matchKey,
      appliedFilters: [],
      appliedSort: null,
      columnPrefs: null,
      resolvedColumns: null,
      _aggregateMetaFields: inheritedMetaFields,
      ...(_vizProp_inner || {}),
    });

    this.activeSlot = outputSlot;

    // 결과 필드 목록 (all-null 필드 제외 — LLM이 유효 필드만 볼 수 있도록)
    const allFields = mergedItems.length > 0
      ? Object.keys(mergedItems[0]).filter(k => mergedItems.some(item => item[k] != null))
      : [];

    if (TOOL_CALLING_LOG) {
      console.log(`[merge] ${slots.map(s => s.slotName).join(' + ')} → ${outputSlot} (${mergedItems.length}건, mode: ${mode})`);
      console.log('[merge] fields:', allFields);
      console.log('[merge] derived:', derivedFields);
      console.log('[merge] unmatched:', unmatchedCounts);
    }

    const unmatchedMsg = Object.entries(unmatchedCounts)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k} ${v}건`)
      .join('·');

    const autoMsg = resolved.auto ? ` (matchKey: "${matchKey}" 자동 감지)` : '';
    const dupMsg = resolved.duplicateWarning ? ` ⚠️ ${resolved.duplicateWarning}` : '';

    // 비교 세션 완료 → 플래그 해제 + merge 결과 슬롯 기록
    this._mergeOutputSlot = outputSlot;
    this._comparisonActive = false;
    // [#585-FOLLOWUP-D] view-form continuity tracking — main outer/inner merge 성공 시 mode + intention 기록
    {
      const _intsForMain = slots.map(s => this.buffers.get(s.slotName)?.intention);
      const _sameIntForMain = new Set(_intsForMain.filter(Boolean)).size === 1 && _intsForMain[0];
      this._lastMergeMode = mode;  // 'outer' or 'inner' (intersect는 SUMMARY-OVERRIDE branch에서 이미 처리됨)
      this._lastMergeIntention = _sameIntForMain || null;
    }

    // 자동 변화 필터 (cross-filter with change 의도) //
    const autoFilterMsg_inner = this._applyAutoChangeFilter(outputSlot, mergedItems);
    const finalMergedCount = this.buffers.get(outputSlot)?.items?.length || mergedItems.length;

    // [1-B β + 1-F 확장] PERIOD COUNTS HINT — multi-period merge 구조 기반 감지 시 per-slot count hint inject //
    // SUMMARY-OVERRIDE 우회 path (mode!=='inner' 또는 mixed-intention)에서 LLM이 outro에 per-period count 누락 차단.
    // language-agnostic — keyword regex X, 구조 검사만: slots[].dateRange가 2+ distinct (LLM이 period 분리해 호출했다는 사실 자체가 비교 의도).
    // [1-F] count source는 buf.summary 우선 (MESSAGE/SALES 류 multi-row-per-entity에서 entity-merge 후 items.length가 unique recipient count로 misleading 차단).
    let periodCountHint_inner = '';
    const perPeriodCandidates = slots.map(s => {
      const buf = this.buffers.get(s.slotName);
      if (!buf?.items || !buf.dateRange?.startDate || !buf.dateRange?.endDate) return null;
      const labelValue = s.label || s.slotName;
      const dr = buf.dateRange;
      const periodLabel = formatPeriodLabel(dr.startDate, dr.endDate) || labelValue;
      const sourceCount = _extractPeriodSourceCount(buf);
      return { label: labelValue, periodLabel, count: sourceCount.count, source: sourceCount.source, dateRange: dr };
    }).filter(Boolean);
    const distinctPeriodKeys = new Set(perPeriodCandidates.map(p => `${p.dateRange.startDate}_${p.dateRange.endDate}`));
    if (perPeriodCandidates.length >= 2 && distinctPeriodKeys.size >= 2) {
      const hintEntries = perPeriodCandidates.map(p => `${p.periodLabel}=${p.count}`).join(', ');
      const sampleOutro = perPeriodCandidates.map(p => `${p.periodLabel}: ${p.count}`).join(' / ');
      const usingSummary = perPeriodCandidates.some(p => p.source !== 'items.length');
      const sourceNote = usingSummary
        ? ` Counts sourced from per-slot ${[...new Set(perPeriodCandidates.map(p => p.source))].join('/')} (authoritative API tally — NOT _merged buffer의 derived "{period}_count" entity-aggregate field).`
        : '';
      periodCountHint_inner = `\n\n[PERIOD COUNTS HINT] ${hintEntries} — multi-period merge detected (each slot has distinct dateRange).${sourceNote} MUST mention EACH period count in BOTH show_display() intro AND finish() outro using the user's input language (e.g. "${sampleOutro}"). Intro lead with period count enumeration (one-glance overview) AND outro echo the same per-period counts (summary line) — both placements are mandatory because readers look at intro first for context AND outro for closing summary. Mentioning only one side (intro-only or outro-only) is incomplete. FORBIDDEN intro shortcuts: generic "총 N건" (single global count), "각 기간별로 정리했어" (no counts), period names without counts. Use direct numeric values (NOT placeholder like {{SUMMARY:...}}) — substitute resolver may not have the matching key.`;
      // γ GUARD용 meta 저장 (finishPolicy에서 외부 검증, language-agnostic)
      this._periodCountHintMeta = { perPeriod: perPeriodCandidates, totalSlots: slots.length };
    }

    // [1-N P26] PERIOD MONTH MISMATCH HINT — user input의 N월 enumeration vs 실제 call_api dateRange month set 검증
    // 본질: 사용자가 "2월 3월 4월 비교" 요청했는데 LLM이 stochastic하게 (a) 부족 (2개만) (b) wrong offset (1월/2월/3월 또는 3월/4월/5월) load 후 merge → 잘못된 월 표출 finalText 차단.
    //   - 부족 case: missing > 0 AND called.size < input.size (기존 cover)
    //   - wrong-offset case: missing > 0 AND called.size == input.size (set 차이 — extra month 존재) → L11-COMPARE-FULL-001 본질
    //   - 둘 다 set 차이 (missing.length > 0) 단일 조건으로 통합 검출 (구조적, 카테고리 무관 generic guard)
    // language-agnostic — "N월" 패턴만 추출 (이미 export된 _extractMonthSet 사용). dual sync with nodeAgent.mjs.
    // call_api dateRange의 startDate/endDate에서 month 추출 → user input month set과 비교 → set 차이 발견 시 retry hint 저장.
    let periodCountMismatchHint_inner = '';
    if (perPeriodCandidates.length >= 2) {
      const userInputForMonths = this.getUserInput?.() || '';
      const inputMonthSet = this._extractMonthSet(userInputForMonths);
      if (inputMonthSet.size >= 2) {
        // call_api dateRange에서 month 추출 (startDate/endDate 양쪽 — month 경계 걸친 range 보호)
        const calledMonthSet = new Set();
        for (const p of perPeriodCandidates) {
          const dr = p?.dateRange;
          if (!dr?.startDate) continue;
          const startMonth = parseInt(String(dr.startDate).slice(5, 7), 10);
          if (Number.isFinite(startMonth) && startMonth >= 1 && startMonth <= 12) {
            calledMonthSet.add(`${startMonth}월`);
          }
          if (dr.endDate) {
            const endMonth = parseInt(String(dr.endDate).slice(5, 7), 10);
            if (Number.isFinite(endMonth) && endMonth >= 1 && endMonth <= 12) {
              calledMonthSet.add(`${endMonth}월`);
            }
          }
        }
        const missingMonths = [...inputMonthSet].filter(m => !calledMonthSet.has(m));
        const extraMonths = [...calledMonthSet].filter(m => !inputMonthSet.has(m));
        // mismatch 조건: set 차이만 (missing 또는 extra) — count equality는 무관 (wrong-offset case 포함)
        if (missingMonths.length > 0) {
          const sourceIntention = perPeriodCandidates.find(p => p?.dateRange)?.dateRange
            ? [...this.buffers.entries()].find(([n, b]) => n !== outputSlot && b.intention && b.intention !== 'MERGED' && b.dateRange)?.[1]?.intention || null
            : null;
          const inputMonthsSorted = [...inputMonthSet].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
          const calledMonthsSorted = [...calledMonthSet].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
          const missingMonthsSorted = missingMonths.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
          const extraMonthsSorted = extraMonths.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
          const extraClause = extraMonthsSorted.length > 0
            ? ` Extra months loaded but NOT requested: [${extraMonthsSorted.join(', ')}] — these MUST be excluded from merge_slots slots and show_display columns.`
            : '';
          periodCountMismatchHint_inner = `\n\n[PERIOD MONTH MISMATCH] User input requested months [${inputMonthsSorted.join(', ')}] but call_api loaded [${calledMonthsSorted.join(', ')}]. Missing: [${missingMonthsSorted.join(', ')}].${extraClause} BEFORE finish(): (1) call call_api(intention="${sourceIntention || '<same as loaded slots>'}", startDate=<missing month start>, endDate=<missing month end>, slot="compare_${slots.length + 1}") for each missing month; (2) re-merge_slots using ONLY the slots whose dateRange matches the requested months [${inputMonthsSorted.join(', ')}] — DROP any slot whose month is in the Extra list; (3) show_display columns must reference ONLY the requested months [${inputMonthsSorted.join(', ')}]. The user explicitly named those months — do NOT substitute adjacent calendar months.`;
          // finishPolicy GUARD용 meta 저장 (language-agnostic — month set 비교만)
          this._periodCountMismatchMeta = {
            inputMonths: inputMonthsSorted,
            calledMonths: calledMonthsSorted,
            missingMonths: missingMonthsSorted,
            extraMonths: extraMonthsSorted,
            sourceIntention,
          };
        }
      }
    }

    return {
      success: true,
      matchKey,
      matchKeyAuto: resolved.auto || false,
      mergedCount: finalMergedCount,
      unmatchedCounts,
      fields: allFields,
      derivedFields,
      summary: Object.keys(mergedSummary).length > 0 ? mergedSummary : undefined,
      message: `${slots.length}개 슬롯 병합 완료: ${mergedItems.length}건 매칭${unmatchedMsg ? `, 미매칭: ${unmatchedMsg}` : ''}${autoMsg}${dupMsg}. [SHOW_DISPLAY COLUMNS] 병합 후 buffer fields = [${allFields.join(', ')}]. show_display columns에 위 fields만 사용 — 'N월_unknownField' 같은 합성 column 추정 금지 (buffer에 없는 prefix×field 조합은 빈 셀 반환). period 비교 시 "{slotLabel}_{baseField}" 패턴은 slotLabel과 baseField 모두 위 fields에 존재할 때만 valid.${autoFilterMsg_inner}${periodCountHint_inner}${periodCountMismatchHint_inner}\n[ACTIVE SLOT] 합쳐진 데이터("${outputSlot}")가 지금 활성 슬롯입니다. 바로 filter/sort/show_display 호출 가능. switch_slot 불필요 (이미 목표 슬롯이 active).${this.getSlotContext()}`,
    };
  }

  // 사용자 입력에서 변화 방향 표현 감지 (단방향만, 양방향 "변화/추이/비교" 제외) //
  // 동사 stem `줄/늘`은 어미 -고/-어/-었/-어든/-어들/-어드/-어진/-어졌 등 활용 다수 → 어미 alternation으로 catch
  // (이전 좁은 regex `/줄어|...`은 "줄고" "늘고" "줄었" 등 빈출 활용 미스 — L16-SQL-3WAY-001 본질)
  _detectChangeDirection(userInput) {
    // [게이트 단일소스 ⑤] aiUtils.detectChangeDirection 위임 (dual sync with nodeAgent.mjs) //
    return detectChangeDirection(userInput);
  }

  // merge 결과에 자동 변화 필터 적용 (사용자 입력 + _change_* 존재 시) //
  _applyAutoChangeFilter(outputSlot, mergedItems) {
    if (!mergedItems || mergedItems.length === 0) return '';
    // [L5-CROSS-002] AUTO_CROSS_MERGE path에서 호출된 경우 skip — 사용자가 이미 explicit `_change_*` filter 지정했고
    // 곧 recursive applyFilter가 그것을 적용하므로, 다른 `_change_*` field로 heuristic 자동 filter 적용하면 cascade로 SELF_CONTRADICTION (응답: "0명") 발생.
    // 본 flag는 applyFilter→AUTO_CROSS_MERGE→mergeSlots(intersect) call site에서만 set. LLM-explicit merge_slots 호출은 영향 X (LLM이 명시적으로 변화 의도 표명).
    if (this._suppressAutoChangeFilterOnce) {
      this._suppressAutoChangeFilterOnce = false;
      return '';
    }
    const userInput = this.getUserInput?.() || '';
    const direction = this._detectChangeDirection(userInput);
    if (!direction) return '';

    const sample = mergedItems[0] || {};
    // 메타 _change_*(aggregate가 만든 메타 필드의 변화)는 의미 없으니 제외 — buffer가 자체 추적한 메타 필드 사용
    const buf = this.buffers.get(outputSlot);
    const aggregateMeta = buf?._aggregateMetaFields || [];
    const metaChangeFields = new Set(aggregateMeta.map(f => `_change_${f}`));
    const changeFields = Object.keys(sample).filter(k =>
      k.startsWith('_change_') && !k.startsWith('_changeRate_')
      && !metaChangeFields.has(k)
    );
    if (changeFields.length === 0) return '';

    const targetField = changeFields[0];
    const before = mergedItems.length;
    const filtered = mergedItems.filter(item => {
      const val = item[targetField];
      if (typeof val !== 'number') return false;
      return direction === 'decreased' ? val < 0 : val > 0;
    });

    if (!buf) return '';
    buf.items = filtered;
    // originalItems는 자동 필터 전 mergedItems로 보존 (reset 시 복원 가능 + show_display 0건 처리)
    buf.originalItems = mergedItems.map(item => ({ ...item }));
    buf.appliedFilters = [{
      field: targetField,
      compute: undefined,
      condition: { type: direction === 'decreased' ? 'numeric_lt' : 'numeric_gt', value: 0 },
      fieldNames: [targetField],
    }];
    // [L9c-COMPARE-002 cycle 1 — AUTO-CHANGE NARROWED SNAPSHOT] dual sync with nodeAgent.mjs
    //   본질: auto-change-filter가 적용한 narrowed items(예: 51건)를 slot-level snapshot으로 보존.
    //         후속 applyFilter 호출 시 ALIVE_SCOPE FILTER / CHANGE_FILTER HALLUCINATION guard가
    //         buf.items = [...source]로 raw(265)로 reset하면 prior narrowing 손실 → merge_slots 결과 over-broad.
    //   기존 _preFilterItemsSnapshot 접근(approach 2, reverted)과의 차이:
    //     - _preFilterItemsSnapshot은 applyFilter 진입 시 [...buf.items]로 capture → buf.items가
    //       prior tool로 corrupt(0/265)된 경우 snapshot도 corrupt
    //     - _autoChangeFilterSnapshot은 _applyAutoChangeFilter 시점(narrowed 직후) capture →
    //       guard reset 시 항상 narrowed state 복원 보장
    //   회귀 risk: snapshot은 buf 자체 field이므로 turn 경계에서 buffer 재생성 시 자동 무효화.
    //              guard restore 분기에서만 참조 → 정상 filter 흐름 영향 X.
    buf._autoChangeFilterSnapshot = {
      items: filtered.map(item => ({ ...item })),
      targetField,
      direction,
      capturedAt: Date.now(),
    };

    if (TOOL_CALLING_LOG) {
      console.log(`[merge] auto-change-filter: ${targetField} ${direction === 'decreased' ? '< 0' : '> 0'} (${before} → ${filtered.length})`);
    }

    return ` [AUTO-APPLIED] "${userInput.slice(0, 30)}" 표현 → ${targetField} ${direction === 'decreased' ? '< 0' : '> 0'} 자동 필터 (${before}건 → ${filtered.length}건). 의도와 다르면 filter({reset:true})로 복원.`;
  }

  // ============================================
  // 2. 배열 추출 — API 응답에서 배열 찾기
  // ============================================

  // API 응답에서 배열(개인 데이터)과 요약(전체 통계)을 분리 추출 //
  extractArray(data) {
    if (!data) return { items: null, summary: null };
    if (Array.isArray(data)) {
      // primitive 배열([1,"a",...])은 items로 사용 불가 — 요약으로 전환
      if (data.length > 0 && typeof data[0] !== 'object') {
        return { items: [], summary: { _values: data } };
      }
      return { items: data, summary: null };
    }

    let bestArray = null;
    let bestKey = null;
    let bestLength = 0;
    let hasEmptyArray = false;

    for (const key of Object.keys(data)) {
      if (Array.isArray(data[key])) {
        // primitive 배열([1,2,"a"]) 무시 — object 배열만 후보
        if (data[key].length > 0 && typeof data[key][0] !== 'object') continue;

        if (data[key].length > bestLength) {
          bestArray = data[key];
          bestKey = key;
          bestLength = data[key].length;
        }
        else if (data[key].length === 0) {
          hasEmptyArray = true;
        }
      }
    }

    if (!bestArray && hasEmptyArray) return { items: [], summary: null };

    // 배열이 없으면 전체가 요약 데이터
    if (!bestArray && typeof data === 'object') {
      return { items: [data], summary: data };
    }

    // 상위 스칼라 필드를 요약으로 분리 (개인 행에 주입하지 않음)
    let summary = null;

    if (bestArray && bestKey) {
      const meta = {};

      for (const key of Object.keys(data)) {
        if (key === bestKey || Array.isArray(data[key])) continue;
        if (typeof data[key] !== 'object' && data[key] !== null) {
          // 1) flat scalar
          meta[key] = data[key];
        }
        else if (key === 'summary' && data[key] && typeof data[key] === 'object') {
          // 2) [B2 회귀 fix] backend convention: nested `data.summary: {...}` 평탄화
          // — linkopay-history 등 nested summary 인텐션에서 totalRevenue 등 정답 키가 buf.summary에 노출되지 않던 회귀 차단
          for (const [sk, sv] of Object.entries(data[key])) {
            if (typeof sv !== 'object' || sv === null) meta[sk] = sv;
          }
        }
      }

      if (Object.keys(meta).length > 0) {
        summary = meta;
      }
    }

    return { items: bestArray, summary };
  }

  // extractArray의 best-array 키 찾기 로직과 짝 — items 자리에 새 array 주입 //
  replaceLargestObjectArray(data, newItems) {
    if (Array.isArray(data)) return newItems;
    if (data && typeof data === 'object') {
      let bestKey = null, bestLen = -1;
      for (const key of Object.keys(data)) {
        const arr = data[key];
        if (Array.isArray(arr) && arr.length > bestLen
            && (arr.length === 0 || typeof arr[0] === 'object')) {
          bestKey = key; bestLen = arr.length;
        }
      }
      if (bestKey) return { ...data, [bestKey]: newItems };
    }
    return data;
  }

  // [CROSS-REF AUTO-FILTER] _merged + sql_* 슬롯 존재 시 회원 식별자 IN-filter 적용 후보 //
  applyCrossRefAutoFilter(items, idHint = null) {
    if (!items || items.length === 0) return null;
    if (!this.buffers.has('_merged')) return null;
    const mergedBuf = this.buffers.get('_merged');
    if (!mergedBuf?.items?.length) return null;
    const hasSqlSlots = [...this.buffers.keys()].some(k => /^sql_\d/.test(k));
    if (!hasSqlSlots) return null;
    const userInput = this.getUserInput?.() || '';
    if (/(전체|모두|모든|각각|전부)/.test(userInput)) return null;

    const candidates = idHint ? [idHint, 'memberId', 'userId'] : ['memberId', 'userId'];
    for (const idField of candidates) {
      if (items[0]?.[idField] === undefined) continue;
      const mergedIds = new Set(mergedBuf.items.map(it => it[idField]).filter(v => v != null));
      if (mergedIds.size === 0) continue;
      const filtered = items.filter(it => mergedIds.has(it[idField]));
      if (filtered.length > 0 && filtered.length < items.length) {
        return { filtered, idField, before: items.length, after: filtered.length };
      }
      return null;
    }
    return null;
  }

  // availableFields 키가 items에 없으면 하위 배열을 탐색하여 자동 flatMap //
  // depth: 재귀 제한 (최대 2단계 중첩까지)
  autoFlatten(items, availableFields, depth = 0) {
    const sample = items[0];
    if (!sample || typeof sample !== 'object') return items;

    const expectedKeys = Object.keys(availableFields);
    const missingKeys = expectedKeys.filter(k => !(k in sample));
    if (missingKeys.length === 0) return items; // 이미 플랫 — 변환 불필요

    // 대부분 필드가 현재 레벨에 있으면 flatten 불필요 (누락 비율 30% 이하)
    const presentCount = expectedKeys.length - missingKeys.length;
    if (presentCount > 0 && missingKeys.length / expectedKeys.length <= 0.3) {
      if (TOOL_CALLING_LOG) console.log(`[autoFlatten] skip: ${presentCount}/${expectedKeys.length} 필드 존재, 누락 ${missingKeys.length}건은 파생/합성 필드로 판단`);
      return items;
    }

    // 하위 배열 탐색: missingKeys가 가장 많이 존재하는 배열 선택 //
    // dot notation 지원: "reserves.name" → reserves 배열 안에 "name" 존재 여부 체크 //
    let bestSubKey = null;
    let bestMatchCount = 0;

    for (const key of Object.keys(sample)) {
      if (!Array.isArray(sample[key]) || sample[key].length === 0) continue;
      const subSample = sample[key][0];
      if (!subSample || typeof subSample !== 'object') continue;

      const matchCount = missingKeys.filter(k => {
        if (k in subSample) return true;
        if (k.startsWith(key + '.')) return k.slice(key.length + 1) in subSample;
        return false;
      }).length;
      if (matchCount > bestMatchCount) {
        bestSubKey = key;
        bestMatchCount = matchCount;
      }
    }

    if (!bestSubKey) return items; // 하위 배열에도 없으면 원본 유지

    if (TOOL_CALLING_LOG) {
      console.log(`[autoFlatten] "${bestSubKey}" 하위 배열 flatMap (missing keys: ${missingKeys.join(', ')})`);
    }

    // 상위 스칼라 필드 + 하위 배열 아이템 합성
    const flattened = items.flatMap(item => {
      const parentFields = {};
      for (const [k, v] of Object.entries(item)) {
        if (k !== bestSubKey && !Array.isArray(v) && (typeof v !== 'object' || v === null)) {
          parentFields[k] = v;
        }
      }
      return (item[bestSubKey] || []).map(sub => ({ ...parentFields, ...sub }));
    });

    // 재귀: 1단계 flatten 후에도 missing keys 남으면 한 번 더
    if (depth < 1 && flattened.length > 0) {
      const flatSample = flattened[0];
      if (flatSample && typeof flatSample === 'object') {
        const stillMissing = expectedKeys.filter(k => !(k in flatSample));
        if (stillMissing.length > 0) {
          return this.autoFlatten(flattened, availableFields, depth + 1);
        }
      }
    }

    return flattened;
  }

  // ============================================
  // 3. 필터 실행 — 구조화된 파라미터
  // ============================================

  // 버퍼 데이터를 조건으로 필터링 //
  applyFilter({ field, compute, condition, reset }) {
    const buf = this.getActiveBuf();
    if (!buf) return { success: false, message: 'No data in buffer. Call get_intentions → call_api first.' };

    // [LESSON-METRIC NORMALIZE] per-lesson aggregate intentions에서 totalSessions/attendedCount/totalReservations 동시 노출 시
    // 사용자 input semantics (참여/출석 vs 예약 vs 개설/진행)와 filter field 불일치 → redirect (generic, dual sync)
    let _lessonMetricNormalizeNote = null;
    if (!reset && field && condition?.type) {
      const _lessonNorm = normalizeLessonMetricFilterArgs(
        { field, condition },
        this.getUserInput?.() || '',
        buf
      );
      if (_lessonNorm.applied) {
        field = _lessonNorm.args.field;
        _lessonMetricNormalizeNote = _lessonNorm.note;
        if (TOOL_CALLING_LOG) console.log(`[applyFilter] ${_lessonMetricNormalizeNote}`);
      }
    }

    // [WEEKLY-FREQ NORMALIZE] "주 N회 이상/이하" 의도 + attendedCount filter → numberOfWeeks 환산 + ATTENDED min:1 보강
    // dual sync: nodeAgent.mjs에도 동일 적용
    let _weeklyNormalizeNote = null;
    if (!reset && field === 'attendedCount' && condition?.type) {
      const _normResult = normalizeAttendanceFilterArgs(
        { field, condition },
        this.getUserInput?.() || '',
        buf
      );
      if (_normResult.applied) {
        field = _normResult.args.field;
        condition = _normResult.args.condition;
        _weeklyNormalizeNote = _normResult.note;
        if (TOOL_CALLING_LOG) console.log(`[applyFilter] ${_weeklyNormalizeNote}`);
      }
    }

    // [ENUM-FILTER NORMALIZE] Korean enum value → actual enum value 자동 변환 (gender/boolean 등 universal categorical enum)
    // dual sync: nodeAgent.mjs에도 동일 적용
    // compacting/멀티턴 후 enumValues 가이드가 LLM context에서 약해져 한국어("남자/여자/활성") 그대로 전달 → 0매칭 + tool result enum hint가 finalText로 누출되는 회귀 차단
    let _enumNormalizeNote = null;
    if (!reset && field && condition?.type === 'exact') {
      const _enumNorm = normalizeEnumFilterValue({ field, condition }, buf);
      if (_enumNorm.applied) {
        condition = _enumNorm.args.condition;
        _enumNormalizeNote = _enumNorm.note;
        if (TOOL_CALLING_LOG) console.log(`[applyFilter] ${_enumNormalizeNote}`);
      }
    }

    // reset 모드: 필터 전부 초기화, 원본 데이터 복원
    if (reset) {
      // [R*] 사용자 input에 reset 의도가 없는데 LLM이 자체 reset → 차단 (filter 우회 방지)
      // 사용자 reset 의도 키워드 없을 때만 차단 (한국어 표준 패턴 — FILTER_GUIDE 동일)
      const userInput = this.getUserInput?.() || '';
      const userResetIntent = /풀어|리셋|reset|원래|전체|해제|제거|복원|취소/.test(userInput);
      if (!userResetIntent && buf.appliedFilters?.length > 0) {
        return {
          success: false,
          message: `Filter reset BLOCKED — user input "${userInput.slice(0, 40)}" does not request to remove filter. Current buffer reflects user's filter intent (${buf.appliedFilters.map(f => f.field).join(', ')}). Respond using current buffer state. To restore, user must explicitly request (풀어/리셋/전체로/원래대로/해제/복원).`,
        };
      }
      const original = buf.originalItems || buf.items;
      buf.items = [...original];
      buf.appliedFilters = [];
      buf.resolvedColumns = null;
      if (TOOL_CALLING_LOG) console.log(`[filter] reset: ${buf.items.length}건 복원`);
      return {
        success: true,
        before: buf.items.length,
        after: buf.items.length,
        message: `Filter cleared. ${buf.items.length} items restored.`,
      };
    }

    const source = buf.originalItems || buf.items;

    if (!source || source.length === 0) {
      return { success: false, message: 'No data to filter. Call get_intentions → call_api first.' };
    }

    if (!field || !condition || !condition.type) {
      return { success: false, message: 'Invalid filter. field and condition.type are required.' };
    }

    // [REDUNDANT DATE_RANGE FILTER GUARD] L11-SMS-FULL-001 cycle 1 (2026-05-31) — dual sync with nodeAgent.mjs //
    //   본질: 사용자 input에 시간 키워드 없는데 LLM이 default-window intention buffer 위에 date_range filter 자동 호출
    //         (예: "만료 예정 회원한테 문자 보내줘" → call_api 후 LLM이 endDate date_range filter 추가 → 26→1로 over-narrow)
    //         INTENTION DEFAULT WINDOW GUARD는 call_api args만 보호, 후속 filter tool 호출은 별도 차단 필요
    //   구조 기반: buf.intention (registry) + condition.type === 'date_range' + user input에 explicit period 키워드 없음
    //   결정성: registry 매칭 intention + date_range filter + user no-time-kw → BLOCKED + source 유지 + redirect
    //   회귀 risk 최소화: user에 시간 키워드 있으면 미발동 (정상 N월 filter 보존). 다른 condition.type 미발동.
    {
      const REDUNDANT_DATE_FILTER_INTENTIONS = new Set([
        'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD',
      ]);
      const _bufIntention = typeof buf.intention === 'string' ? buf.intention : '';
      if (REDUNDANT_DATE_FILTER_INTENTIONS.has(_bufIntention) && condition.type === 'date_range') {
        const _uiRedundant = this.getUserInput?.() || '';
        const _TIME_KW_EXPLICIT = /오늘|어제|내일|모레|지난\s*주|이번\s*주|다음\s*주|금주|지난\s*달|이번\s*달|다음\s*달|금월|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+\s*일\s*전|작년|올해|금년|내년|지난해|당월/;
        if (_uiRedundant && !_TIME_KW_EXPLICIT.test(_uiRedundant)) {
          if (TOOL_CALLING_LOG) console.log(`[applyFilter] REDUNDANT DATE_RANGE FILTER BLOCKED: intention=${_bufIntention}, field=${field}, user="${_uiRedundant.slice(0, 40)}" (no time keyword)`);
          return {
            success: false,
            message: `[REDUNDANT_DATE_RANGE_FILTER_BLOCKED] 사용자 input "${_uiRedundant.slice(0, 60)}"에 시간 키워드(N월/이번달/최근 등)가 없습니다. ${_bufIntention} buffer는 default-window (today~today+N) 기준으로 이미 로드되었고, ${field} date_range filter (${JSON.stringify(condition).slice(0, 80)}) 호출은 결과를 false-narrow 합니다 (LLM hallucinated current-month). 현재 buffer ${source.length}건을 그대로 show_display에 전달하세요. 사용자가 명시적 기간을 요청한 경우에만 date_range filter 사용하세요.`,
          };
        }
      }
    }

    const fieldNames = getFieldNames(field);
    const newFilter = { field, compute, condition, fieldNames };
    const otherFilters = buf.appliedFilters.filter(f => f.field !== field);
    const allFilters = [...otherFilters, newFilter];

    let items = [...source];

    for (const f of allFilters) {
      items = this.filterByCondition(items, f);
    }

    // 결과 0건 + payment_method 필터 잔존 시 자동 제거 후 재시도 //
    if (items.length === 0 && condition.type !== 'payment_method'
      && otherFilters.some(f => f.condition.type === 'payment_method')) {
      const retryFilters = allFilters.filter(f => f.condition.type !== 'payment_method');
      items = [...source];

      for (const f of retryFilters) {
        items = this.filterByCondition(items, f);
      }

      if (items.length > 0) {
        allFilters.length = 0;
        allFilters.push(...retryFilters);
        if (TOOL_CALLING_LOG) console.log(`[filter] payment_method 자동 해제 → ${items.length}건`);
      }
    }

    const before = source.length;
    let after = items.length;

    buf.items = items;
    buf.appliedFilters = allFilters;
    // [L23-FILTER-FOCUS] filter 전 resolvedColumns 보존 — filter 후 show_display에서 LLM이 columns 미명시 시 이전 turn 컬럼 유지용
    if (buf.resolvedColumns && buf.resolvedColumns.length > 0) {
      buf._preFilterColumns = [...buf.resolvedColumns];
    }
    buf.resolvedColumns = null;

    if (buf.appliedSort) {
      this.applySort(buf.appliedSort);
    }

    if (TOOL_CALLING_LOG) {
      console.log(`[filter] ${field} (${condition.type}) : ${before}건 → ${after}건`);
      console.log('[filter] 결과 데이터:', buf.items);
    }

    // Check if filter field exists in source data //
    const fieldMissing = after === 0 && !source.some(item => fieldNames.some(fn => item[fn] !== undefined));

    // fieldMissing 시 자동 무효화 — filter 자체 의미 없음, originalItems 유지하여 cross-merge 가능
    // (LLM의 자체 reset은 [R*]로 차단되지만, 시스템 내부 자동 무효화는 별개 경로)
    if (fieldMissing) {
      // [L9c-COMPARE-002 cycle 1 — AUTO-CHANGE NARROWED RESTORE on fieldMissing] dual sync with nodeAgent.mjs
      //   본질: prior auto-change-filter narrowed state (예: 51건)이 fieldMissing path에서 raw source(265)로 reset되면
      //         후속 merge_slots(_merged, alive) 결과 over-broad (93건 = full alive 출력).
      //         snapshot.targetField와 다른 field로 fieldMissing 발생 시 narrowed state 보존.
      //   structure-based: _autoChangeFilterSnapshot 존재 + 현재 field !== snapshot.targetField → snapshot restore.
      //   regression risk: snapshot 없으면 기존 [...source] 복원. snapshot.targetField와 같은 field이면 raw 복원 (의도된 reset).
      const _snap = buf._autoChangeFilterSnapshot;
      const _useSnap = _snap && Array.isArray(_snap.items) && _snap.targetField !== field;
      buf.items = _useSnap ? _snap.items.map(it => ({ ...it })) : [...source];
      buf.appliedFilters = otherFilters;
      buf.resolvedColumns = null;
      if (buf.appliedSort) {
        this.applySort(buf.appliedSort);
      }
      if (TOOL_CALLING_LOG) console.log(`[filter] fieldMissing 자동 무효화: ${field} → ${buf.items.length}건 유지${_useSnap ? ' (auto-change snapshot 복원)' : ''}`);
      this._pendingCrossMerge = { field, intention: buf.intention || null };

      // [AUTO CROSS-MERGE] buffer에 fieldMissing 필드 가진 다른 슬롯 존재 시 자동 intersect (1회 한정, 구조 기반)
      // 트리거: filter fieldMissing + active 아닌 슬롯 중 하나에 그 필드 존재 + identity matchKey 가능 + 1회 한정
      // 다른 케이스 영향 0 — 슬롯에 필드 없으면 발동 안 함
      if (!this._autoCrossMergeApplied) {
        const SKIP_SLOTS = new Set(['_auto_merged']);
        let foundSlotName = null;
        const mergedBuf = this.buffers.get('_merged');
        if (mergedBuf?.items?.[0] && fieldNames.some(fn => fn in mergedBuf.items[0])) {
          foundSlotName = '_merged';
        } else {
          for (const [slotName, otherBuf] of this.buffers.entries()) {
            if (slotName === this.activeSlot) continue;
            if (slotName.startsWith('sql_')) continue;
            if (SKIP_SLOTS.has(slotName)) continue;
            if (!otherBuf?.items?.[0]) continue;
            if (fieldNames.some(fn => fn in otherBuf.items[0])) { foundSlotName = slotName; break; }
          }
        }
        if (foundSlotName) {
          const activeSample = source[0] || {};
          const otherSample = this.buffers.get(foundSlotName).items[0] || {};
          const matchKey = ['memberId', 'userId', 'name'].find(k => k in activeSample && k in otherSample);
          if (matchKey) {
            this._autoCrossMergeApplied = true;
            const activeSlotName = this.activeSlot;
            // [L5-CROSS-002] 사용자 explicit `_change_*` filter 진행 중 — intersect 내부 _applyAutoChangeFilter heuristic field 선택을 1회 skip (recursive applyFilter가 user field로 정합 처리)
            if (field && field.startsWith('_change_')) {
              this._suppressAutoChangeFilterOnce = true;
            }
            const mergeResult = this.mergeSlots({
              slots: [{ slotName: activeSlotName }, { slotName: foundSlotName }],
              mode: 'intersect', matchKey, outputSlot: '_auto_merged',
            });
            if (mergeResult.success) {
              if (TOOL_CALLING_LOG) console.log(`[filter] AUTO CROSS-MERGE: "${activeSlotName}" ∩ "${foundSlotName}" on "${matchKey}" → "_auto_merged" (${mergeResult.mergedCount}건)`);
              this._pendingCrossMerge = null;
              return this.applyFilter({ field, compute, condition });
            }
            // mergeSlots 실패 시 flag 잔존 방지
            this._suppressAutoChangeFilterOnce = false;
          }
        }
      }

      const _restoredCount = buf.items.length;
      const _narrowedHint = _useSnap && _restoredCount < source.length
        ? ` (prior auto-change-filter 보존: ${_snap.targetField} ${_snap.direction === 'decreased' ? '< 0' : '> 0'} → ${_restoredCount}건)`
        : '';
      return {
        success: true,
        before,
        after: _restoredCount,
        filtered: 0,
        fieldMissing: true,
        autoRestored: true,
        message: `${field} 필드가 데이터에 없습니다 (필드 누락). filter 자동 무효화 — ${_restoredCount}건 유지${_narrowedHint}. 다른 카테고리에서 ${field} 포함 데이터 로드 → merge_slots(intersect) 후 merged slot에 filter 적용 권유.${this.getSlotContext()}`,
      };
    }

    // [L9c-COMPARE-002 ALIVE_SCOPE FILTER GUARD] dual sync with nodeAgent.mjs
    //   본질: chain follow-up에서 사용자가 "유효회원/활성회원" 키워드로 좁히기 요청 시
    //         LLM이 자체 추론으로 attendance-derived boolean(hasAttendance 등) exact filter 호출 →
    //         active buffer가 ALIVE_LIST가 아니면 0건 결과 + "조건에 맞는 회원은 0명이에요" false ack 발생
    //   해소: alive keyword + after=0 + condition.type=exact + buf.intention !== ALIVE_LIST → block + redirect
    //         (현재 active buffer 그대로 두고 LLM이 ALIVE_LIST 호출 + intersect 후 재시도하도록 instruct)
    //   패턴 기반 (단일 field hardcode X): user input 키워드 + 구조 조건 (after=0 + intention mismatch)
    //   회귀 risk 최소화: alive keyword 없으면 미발동. ALIVE_LIST buffer 자체에 filter면 미발동. after>0이면 미발동.
    if (after === 0 && !fieldMissing && condition.type === 'exact') {
      const _userInputAliveFilter = this.getUserInput?.() || '';
      const _wantsAliveFilter = /유효회원|활성회원|유효한 회원|활성 회원/.test(_userInputAliveFilter);
      const _intention = typeof buf.intention === 'string' ? buf.intention : '';
      if (_wantsAliveFilter && _intention !== 'CHECK_MEMBER+ALIVE_LIST') {
        // [L9c-COMPARE-002 cycle 1 — AUTO-CHANGE NARROWED RESTORE] dual sync with nodeAgent.mjs
        //   본질: buf._autoChangeFilterSnapshot 존재 + 현재 filter field가 snapshot.targetField 와 다름 →
        //         narrowed items(예: 51건)로 restore. 그렇지 않으면 raw source(예: 265건)로 restore.
        //   structure-based: snapshot은 _applyAutoChangeFilter에서만 set, slot-level 보존, field name hardcode X.
        //   regression risk: snapshot 없는 case는 기존 behavior 그대로 유지.
        const _snap = buf._autoChangeFilterSnapshot;
        const _useSnap = _snap && Array.isArray(_snap.items) && _snap.targetField !== field;
        const _restoredItems = _useSnap ? _snap.items.map(it => ({ ...it })) : [...source];
        buf.items = _restoredItems;
        buf.appliedFilters = otherFilters;
        buf.resolvedColumns = null;
        if (buf.appliedSort) this.applySort(buf.appliedSort);
        const _restoredCount = _restoredItems.length;
        const _narrowedHint = _useSnap && _restoredCount < source.length
          ? ` (prior auto-change-filter 보존: ${_snap.targetField} ${_snap.direction === 'decreased' ? '< 0' : '> 0'} → ${_restoredCount}건)`
          : '';
        return {
          success: false,
          message: `[ALIVE_SCOPE FILTER BLOCKED] 사용자 input "${_userInputAliveFilter.slice(0, 60)}"이(가) "유효회원/활성회원" 범위를 요청하지만 현재 active buffer intention="${_intention}"은(는) ALIVE_LIST가 아닙니다. filter({field:"${field}", condition:exact}) 호출은 0건 결과를 만들어 "유효회원 0명" 거짓 응답으로 이어집니다.\n\n반드시 새로 호출하세요: get_intentions(MEMBER) → call_api(CHECK_MEMBER+ALIVE_LIST, slot:"alive") → merge_slots([{slotName:"${this.activeSlot}"}, {slotName:"alive"}], mode:"intersect", matchKey:"userId" 또는 "memberId") → show_display. 현재 buffer는 ${_restoredCount}건으로 복원됨${_narrowedHint}.`,
        };
      }
    }

    // [L9c-COMPARE-002 CHANGE_FILTER HALLUCINATION GUARD] dual sync with nodeAgent.mjs
    //   본질: chain follow-up에서 사용자가 변화 방향 키워드(줄어든/늘어난/감소/증가 등) 없이 단순 좁히기 요청 시
    //         LLM이 prior turn의 "줄어든/늘어난" 의도를 자체 추론으로 chain → `_change_*` 필터 자동 적용 →
    //         freshly-merged alive intersect 결과(예 93건)에서 다시 0건으로 좁아져 "조건에 맞는 회원 0명" 거짓 응답
    //   해소: field.startsWith('_change_') + after=0 + before>0 + user input에 변화 방향 키워드 없음 → block + revert + redirect
    //   패턴 기반: _change_ prefix(파생 필드 구조) + user input change keyword 부재(generic). field name hardcode X.
    //   회귀 risk 최소화: user input에 변화 키워드 있으면 미발동(정상 흐름 보존). after>0이면 미발동. before=0이면 미발동.
    if (after === 0 && !fieldMissing && before > 0 && typeof field === 'string' && field.startsWith('_change_')) {
      const _userInputChange = this.getUserInput?.() || '';
      const _hasChangeKw = this._detectChangeDirection?.(_userInputChange);
      if (!_hasChangeKw) {
        // [L9c-COMPARE-002 cycle 1 — AUTO-CHANGE NARROWED RESTORE] dual sync with nodeAgent.mjs
        //   본질: 다른 _change_* field hallucination filter 차단 시에도 prior auto-filter narrowed state 보존.
        //         snapshot.targetField와 다른 _change_* field이면 narrowed restore (raw 복원 X).
        const _snap = buf._autoChangeFilterSnapshot;
        const _useSnap = _snap && Array.isArray(_snap.items) && _snap.targetField !== field;
        const _restoredItems = _useSnap ? _snap.items.map(it => ({ ...it })) : [...source];
        buf.items = _restoredItems;
        buf.appliedFilters = otherFilters;
        buf.resolvedColumns = null;
        if (buf.appliedSort) this.applySort(buf.appliedSort);
        const _restoredCount = _restoredItems.length;
        const _narrowedHint = _useSnap && _restoredCount < source.length
          ? ` prior auto-change-filter 보존: ${_snap.targetField} ${_snap.direction === 'decreased' ? '< 0' : '> 0'} → ${_restoredCount}건.`
          : '';
        return {
          success: false,
          message: `[CHANGE_FILTER HALLUCINATION BLOCKED] 사용자 input "${_userInputChange.slice(0, 60)}"에 변화 방향 키워드(줄어든/늘어난/감소/증가/낮은/높은/올라간/떨어진 등)가 없습니다. ${field} (변화량 파생 필드) exact/numeric filter 호출은 사용자 의도와 무관한 prior turn 의도 chain일 가능성이 높고, 현재 결과(${before}건 → 0건)는 거짓 응답으로 이어집니다. 현재 buffer는 ${_restoredCount}건으로 복원됨.${_narrowedHint} 변화 방향 좁히기가 필요하면 사용자에게 명확히 확인 후 진행하거나, 사용자 input 그대로의 의도(예: 유효회원만 / 특정 카테고리만)로 show_display 직행하세요.`,
        };
      }
    }

    // filter 0건 + 필드 존재 + exact → 실제 enum 값 제공 (LLM 재시도 유도)
    // [CLUSTER-A ALIVE-SCOPE RETENTION] dual sync with nodeAgent.mjs — 사용내역 4차 #11 무출석 + #1 출석/#3 이탈 (v3 broaden 2026-06-25)
    //   본질: 출석/이탈/무출석 명단이 attendance-stats(#38, 전체회원=만료 포함) 기반 → 만료회원 과포함.
    //         회원 기대 = 현재 유효회원(isActive=true)만 (운영 store398 피드백 3회 명시 반복: #1 주N회↑/#3 주N회↓/#11 무출석).
    //   구조 기반 트리거(언어 무관): intention=#38 + 적용 filter가 출석량 축(attendedCount 또는 weeklyFrequency, 임계·방향 무관) +
    //         items에 isActive 존재 + 아직 isActive 미적용 + opt-out(전체/모든/만료/expired/비활성) 미포함.
    //   v3 broaden 근거(baseline 실측 2026-06-25): #3="주1회↓"는 weeklyFrequency lte, #1="주3회↑"는 attendedCount gte로 LLM이 run마다 필드 변동(stochastic).
    //         normalize가 attendedCount→weeklyFrequency redirect(gte)/비영 환산(lte)해도 두 필드 모두 캐치 → 필드 선택 무관 active-scope 결과 결정적.
    //   period 가드 미도입(적대검증 2R 기각): isActive는 현재값이라 과거 시점 스코프 불가 + 현재월 경계 시스템시각 의존은 비결정(5/5 위반).
    //         과거월 단일쿼리 over-fire는 follow-up 엣지(실 사례 0, isActive 현재값 한계).
    //   동작: isActive===true 추가 좁힘 + appliedFilters 기록(idempotent). 단일버퍼, merge(intention=MERGED)는 트리거 제외.
    //   회귀 risk 최소화: 출석량 필터 없는 중립 출석현황(L1-SCHEDULE-002)/출석률(attendanceRate)은 미발동.
    let _aliveScopeNote = '';
    if (!fieldMissing
      && (typeof buf.intention === 'string' && buf.intention === 'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD')
      && Array.isArray(buf.items) && buf.items.length > 0
      && ('isActive' in buf.items[0])
      && !allFilters.some(f => (f.fieldNames || [f.field]).includes('isActive'))
    ) {
      const _isAttendanceVolumeFilter = allFilters.some(f => {
        const _fn = f.fieldNames || [f.field];
        return _fn.includes('attendedCount') || _fn.includes('weeklyFrequency');
      });
      const _caInput = this.getUserInput?.() || '';
      const _caOptOut = /전체|모든|만료|expired|비활성/.test(_caInput);
      if (_isAttendanceVolumeFilter && !_caOptOut) {
        const _beforeAlive = buf.items.length;
        buf.items = buf.items.filter(it => it.isActive === true);
        after = buf.items.length;
        buf.appliedFilters = [...allFilters, { field: 'isActive', condition: { type: 'exact', value: true }, fieldNames: ['isActive'], _autoAliveScope: true }];
        buf.resolvedColumns = null;
        if (buf.appliedSort) this.applySort(buf.appliedSort);
        _aliveScopeNote = ` [ALIVE-SCOPE] 출석 명단은 현재 유효회원(isActive=true) 기준 — 만료회원 ${_beforeAlive - after}명 제외, 유효 ${after}명. 응답·intro/outro는 ${after}명 기준으로 작성(전체 모집단 summary 인용 금지).`;
        console.log(`[applyFilter] CLUSTER-A ALIVE-SCOPE: 출석량 필터 만료회원 제외 ${_beforeAlive}→${after}`);
      }
    }

    let enumHint = '';
    if (after === 0 && !fieldMissing && condition.type === 'exact') {
      const actualValues = [...new Set(
        source.flatMap(item => fieldNames.map(fn => item[fn]).filter(v => v != null))
      )].slice(0, 10);
      if (actualValues.length > 0) {
        enumHint = ` 실제 데이터 값: [${actualValues.join(', ')}]. 이 값 중 하나로 다시 filter하세요.`;
      }
    }

    // filter 0건 + summary 존재 → summary hint 제공 (LLM이 summary 기반 답변 가능하도록)
    let summaryHint = '';
    if (after === 0 && buf.summary) {
      const relevantKeys = Object.entries(buf.summary)
        .filter(([k, v]) => v != null && typeof v !== 'object')
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');

      if (relevantKeys) {
        summaryHint = ` [SUMMARY AVAILABLE] Filter returned 0 items, but backend summary data is still accessible via {{SUMMARY:<key>}} placeholders: ${relevantKeys}. Use these to answer aggregate questions (e.g. total refund amount, net revenue) even when filtered items = 0.`;
      }
    }

    const _normalizeMessage = _weeklyNormalizeNote ? ` ${_weeklyNormalizeNote}` : '';

    return {
      success: true,
      before,
      after,
      filtered: before - after,
      fieldMissing,
      message: `${field} 필터 적용: ${before}건 → ${after}건${_normalizeMessage}${_aliveScopeNote}${enumHint}${summaryHint}${this.getSlotContext()}`,
    };
  }

  // 조건 기반 필터링 //
  filterByCondition(items, { fieldNames, compute, condition }) {
    return items.filter(item => {
      // payment_method는 별도 분류 로직 사용 (필드 존재 여부 무관) //
      if (condition.type === 'payment_method') {
        const vals = Array.isArray(condition.value) ? condition.value : [condition.value];
        const category = this.categorizePaymentMethod(item);

        return vals.some(v => {
          if (category === v) return true;

          if (item.selectedPaymentOptions && item.selectedPaymentOptions.length > 0) {
            return item.selectedPaymentOptions.some(opt => opt.paymentOptionName === v);
          }

          return false;
        });
      }

      const raw = this.getFieldValue(item, fieldNames);

      if (raw === undefined || raw === null) return false;

      switch (condition.type) {
        case 'exact': {
          const vals = Array.isArray(condition.value) ? condition.value : [condition.value];
          return vals.some(v => String(raw) === String(v));
        }
        case 'range': {
          const num = compute === 'age' ? this.computeAge(raw) : parseFloat(raw);
          if (num === null || isNaN(num)) return false;
          if (condition.min != null && condition.max != null) return num >= condition.min && num <= condition.max;
          if (condition.min != null) return num >= condition.min;
          if (condition.max != null) return num <= condition.max;
          return false;
        }
        case 'date_range': {
          const dateStr = String(raw).replace(/[-T:Z]/g, '').slice(0, 8);

          if (condition.startDate && condition.endDate) {
            return dateStr >= condition.startDate.replace(/-/g, '') && dateStr <= condition.endDate.replace(/-/g, '');
          }

          if (condition.endDate) {
            return dateStr <= condition.endDate.replace(/-/g, '');
          }

          if (condition.startDate) {
            return dateStr >= condition.startDate.replace(/-/g, '');
          }

          return false;
        }
        case 'birthday_month': {
          const date = this.parseDate(raw);
          return date ? (date.getMonth() + 1) === condition.month : false;
        }
        case 'numeric_gte': {
          const num = compute === 'age' ? this.computeAge(raw) : parseFloat(raw);
          if (num === null || isNaN(num)) return false;
          const gte = (typeof condition.value === 'string' && !/^\d/.test(condition.value)) ? parseFloat(item[condition.value]) : parseFloat(condition.value);
          return !isNaN(gte) && num >= gte;
        }
        case 'numeric_lte': {
          const num = compute === 'age' ? this.computeAge(raw) : parseFloat(raw);
          if (num === null || isNaN(num)) return false;
          const lte = (typeof condition.value === 'string' && !/^\d/.test(condition.value)) ? parseFloat(item[condition.value]) : parseFloat(condition.value);
          return !isNaN(lte) && num <= lte;
        }
        default:
          return false;
      }
    });
  }

  // ============================================
  // 4. 정렬 실행 — 구조화된 파라미터
  // ============================================

  // 버퍼 데이터를 지정 필드 기준으로 정렬 //
  applySort({ field, compute, direction = 'asc', secondary } = {}) {
    const buf = this.getActiveBuf();
    if (!buf || !buf.items || buf.items.length === 0) {
      return { success: false, message: 'No data to sort.' };
    }

    const fieldNames = getFieldNames(field);
    const isDesc = direction === 'desc';
    const multiplier = isDesc ? -1 : 1;

    // [RANKING_SORT_GUARD] tie-break secondary 필드 결정 — args > buffer.tieBreakField > intentionMeta lookup (모두 메타 기반, hardcode X)
    let tieBreakField = secondary || null;
    if (!tieBreakField && buf.tieBreakField) {
      tieBreakField = buf.tieBreakField;
    }
    if (!tieBreakField && buf.intention) {
      const _meta = this.findIntentionMeta?.(buf.intention);
      if (_meta) {
        if (_meta.default_sort && typeof _meta.default_sort === 'object' && _meta.default_sort.secondary) {
          tieBreakField = _meta.default_sort.secondary;
        } else if (typeof _meta.tie_break_field === 'string') {
          tieBreakField = _meta.tie_break_field;
        }
      }
    }
    // primary 필드와 동일하면 tie-break 무의미 → 비활성
    if (tieBreakField === field) tieBreakField = null;
    const tieBreakFieldNames = tieBreakField ? getFieldNames(tieBreakField) : null;

    // 디버그: sort 대상 필드 존재 여부 확인
    const sample = buf.items[0];
    console.log(`[sort] field: "${field}", fieldNames:`, fieldNames, ', sample keys:', sample ? Object.keys(sample) : [], ', sample value:', sample ? this.getFieldValue(sample, fieldNames) : 'N/A', tieBreakField ? `, tie-break: "${tieBreakField}"` : '');

    buf.items.sort((a, b) => {
      let valA = this.getFieldValue(a, fieldNames);
      let valB = this.getFieldValue(b, fieldNames);

      if (compute === 'age') {
        valA = this.computeAge(valA);
        valB = this.computeAge(valB);
      }

      let primaryCmp = 0;
      if (valA == null && valB == null) {
        primaryCmp = 0;
      } else if (valA == null) {
        primaryCmp = 1;
      } else if (valB == null) {
        primaryCmp = -1;
      } else {
        // ISO 날짜 문자열 감지 (YYYY-MM-DD 또는 YYYY-MM-DDTHH:mm:ss) → 문자열 비교
        const strA = String(valA);
        const strB = String(valB);
        const isDate = /^\d{4}-\d{2}-\d{2}/.test(strA) && /^\d{4}-\d{2}-\d{2}/.test(strB);
        if (isDate) {
          primaryCmp = strA.localeCompare(strB) * multiplier;
        } else {
          const numA = parseFloat(valA);
          const numB = parseFloat(valB);
          if (!isNaN(numA) && !isNaN(numB)) {
            primaryCmp = (numA - numB) * multiplier;
          } else {
            primaryCmp = strA.localeCompare(strB, 'ko') * multiplier;
          }
        }
      }

      // primary 동률 + tie-break 필드 존재 시 secondary 비교 (primary와 동일 방향 적용)
      if (primaryCmp === 0 && tieBreakFieldNames) {
        const tbA = this.getFieldValue(a, tieBreakFieldNames);
        const tbB = this.getFieldValue(b, tieBreakFieldNames);
        if (tbA == null && tbB == null) return 0;
        if (tbA == null) return 1;
        if (tbB == null) return -1;
        const tbStrA = String(tbA);
        const tbStrB = String(tbB);
        const tbIsDate = /^\d{4}-\d{2}-\d{2}/.test(tbStrA) && /^\d{4}-\d{2}-\d{2}/.test(tbStrB);
        if (tbIsDate) return tbStrA.localeCompare(tbStrB) * multiplier;
        const tbNumA = parseFloat(tbA);
        const tbNumB = parseFloat(tbB);
        if (!isNaN(tbNumA) && !isNaN(tbNumB)) return (tbNumA - tbNumB) * multiplier;
        return tbStrA.localeCompare(tbStrB, 'ko') * multiplier;
      }

      return primaryCmp;
    });

    buf.appliedSort = { field, compute, direction: isDesc ? 'desc' : 'asc' };
    if (tieBreakField) buf.appliedSort.secondary = tieBreakField;
    buf.resolvedColumns = null;

    // [DEGENERATE_SORT_GUARD] 정렬 후 primary field 모든 row 동일값 (variance=0) 검출 //
    // 본질: L9a-SCHEDULE-003 sibling — 사용자가 "출석률 낮은 수업" 물었는데 모든 수업 100% 동일. //
    //       LLM이 attendanceRate ASC sort 호출 (의도 일치) 했지만 결과는 raw row 순서 — 사용자에게 "낮은 순으로 정리했어요" 표현은 사실과 다름. //
    // 검출 후: show_display intro/outro에 uniform 사실 명시 + 다른 비교 기준 제안 권장 메시지 LLM에 전달. //
    // 범용: case ID hardcode 없음. 모든 sort에서 N>=2 + uniqueValueCount===1 시 발동. //
    let degenerateSortInfo = null;
    if (buf.items.length >= 2) {
      const _firstVal = this.getFieldValue(buf.items[0], fieldNames);
      let _allEqual = true;
      let _hasNonNull = _firstVal != null;
      for (let i = 1; i < buf.items.length; i++) {
        const _v = this.getFieldValue(buf.items[i], fieldNames);
        if (_v != null) _hasNonNull = true;
        // 동일성 비교 — null/undefined 모두 동일 취급, 숫자 string 정규화
        const _a = _firstVal == null ? null : (isNaN(parseFloat(_firstVal)) ? String(_firstVal) : parseFloat(_firstVal));
        const _b = _v == null ? null : (isNaN(parseFloat(_v)) ? String(_v) : parseFloat(_v));
        if (_a !== _b) { _allEqual = false; break; }
      }
      if (_allEqual && _hasNonNull) {
        degenerateSortInfo = { field, uniformValue: _firstVal, itemCount: buf.items.length, direction: isDesc ? 'desc' : 'asc' };
        buf.degenerateSort = degenerateSortInfo;
      } else {
        buf.degenerateSort = null;
      }
    } else {
      buf.degenerateSort = null;
    }

    if (TOOL_CALLING_LOG) {
      console.log(`[sort] ${field} ${isDesc ? 'desc' : 'asc'} (${buf.items.length}건)`);
      if (degenerateSortInfo) console.log(`[sort] DEGENERATE — all ${buf.items.length} rows have ${field}=${degenerateSortInfo.uniformValue}`);
      console.log('[sort] 결과 데이터:', buf.items);
    }

    let _msg = `${field} 기준 ${isDesc ? '내림차순' : '오름차순'} 정렬 완료 (${buf.items.length}건)${this.getSlotContext()}`;
    if (degenerateSortInfo) {
      _msg += ` [DEGENERATE_SORT — CRITICAL] 정렬 기준 field "${field}" 값이 모든 ${buf.items.length}개 row에서 ${degenerateSortInfo.uniformValue}(으)로 동일합니다. 정렬은 사실상 noop — row 순서는 raw 그대로입니다. ` +
                 `show_display intro 작성 시 MUST: (1) "${field} 값이 모든 항목에서 ${degenerateSortInfo.uniformValue}로 동일해요" 같이 uniform 사실 1줄로 명시, (2) "낮은 순/높은 순/낮은 수업/높은 수업/적은 순/많은 순/하위/상위" 같이 정렬에 의미가 있다는 표현 사용 금지 (사실과 다름), (3) 가능하면 outro에 다른 비교 기준 1개 제안 (예: "다른 기준으로 비교해 드릴까요?").`;
    }
    return {
      success: true,
      message: _msg,
      ...(degenerateSortInfo && { degenerateSort: degenerateSortInfo }),
    };
  }

  // ============================================
  // 5. 집계/계산 — 구조화된 파라미터
  // ============================================

  // 버퍼 데이터의 집계/통계 계산 //
  // [R*] 핸들러 자기-결정 — 빈 집합에서 result===null이면 수학적으로 정의 안 됨
  // 새 operation 추가 시 OP_HANDLERS에 한 줄 등록만으로 자동 통합 (분기 코드 X)
  computeData({ operation, field, compute }) {
    const buf = this.getActiveBuf();
    const items = buf?.items || [];
    const filterInfo = buf?.appliedFilters?.length > 0
      ? ` (필터 적용: ${buf.appliedFilters.map(f => f.field).join(', ')}${buf?.originalItems ? `, 원본 ${buf.originalItems.length}건` : ''})`
      : '';

    // group은 별도 처리 (buf 전체 + 문자열 필드 가능)
    if (operation === 'group') {
      if (!buf || !buf.items || buf.items.length === 0) return { success: false, message: 'No data in buffer. Call get_intentions -> call_api first to load data before compute(group).' };
      if (!field) return { success: false, message: 'Specify field to group.' };

      // [Cycle2 L1-MEMBERSHIP-006] RATIO/RATE intention + summary에 *Rate field 보유 시 group reject
      // 본질: backend가 이미 비율(예: longTermRate=0, shortTermRate=100)을 summary로 제공하는데,
      // LLM이 추가로 group(durationType) 호출하면 buf.items가 "회원권 종류별 row"였던 게
      // "유형별 종류 수 grouping" (예: "단기: 14")으로 대체됨. 14는 회원권 종류 수이지 구매 건수(45)와 불일치.
      // 사용자가 "비율" 질문 → group이 misleading row 생성. summary placeholder로 답변하도록 redirect.
      // language-agnostic (intention name + summary structure 기반, keyword 의존 X)
      const RATIO_INTENT_RE = /(RATIO_STATISTICS|RATE_STATISTICS|RETENTION\+RATE)/;
      if (buf.intention && RATIO_INTENT_RE.test(buf.intention) && buf.summary && typeof buf.summary === 'object') {
        const rateKeys = Object.keys(buf.summary).filter(k => /[Rr]ate$/.test(k) && typeof buf.summary[k] === 'number');
        if (rateKeys.length > 0) {
          const rateSummary = rateKeys.slice(0, 4).map(k => `${k}=${buf.summary[k]}`).join(', ');
          return {
            success: false,
            operation: 'group',
            field,
            message: `[RATIO_GROUP_BLOCKED] Active intention "${buf.intention}" already provides ratio metrics in summary: ${rateSummary}. compute(group, ${field}) on the raw item list produces row counts (회원권 종류 수 등) that DO NOT match the ratio the user asked for (totalPurchaseCount 기준 longTermRate/shortTermRate). Skip compute and call show_display directly — use {{SUMMARY:longTermRate}}% / {{SUMMARY:shortTermRate}}% style placeholders for the headline ratio in intro/outro, and let the raw items (회원권 종류 row) appear in the table as-is.`,
          };
        }
      }

      return this.computeGroup(buf, field, getFieldNames(field), compute);
    }

    // 핸들러: (items, values) → result | null. null = 수학적 정의 안 됨
    const OP_HANDLERS = {
      count:   (items) => items.length,
      sum:     (items, vals) => vals.reduce((a, b) => a + b, 0),
      average: (items, vals) => vals.length === 0 ? null : Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10,
      min:     (items, vals) => vals.length === 0 ? null : Math.min(...vals),
      max:     (items, vals) => vals.length === 0 ? null : Math.max(...vals),
    };

    const handler = OP_HANDLERS[operation];
    if (!handler) {
      return { success: false, message: `Unknown operation "${operation}". Use: ${Object.keys(OP_HANDLERS).join(', ')}, group.` };
    }

    // count는 field 무관 (집합 크기)
    if (operation === 'count') {
      const result = handler(items);
      return {
        success: true, operation, result,
        message: `${result}건${filterInfo}. filter 적용 상태로 답변하세요. reset 우회 금지.`,
      };
    }

    // 그 외 operation: field + 값 추출 필요
    if (!field) return { success: false, message: 'Specify field to compute.' };
    const fieldNames = getFieldNames(field);

    // [P3 anchor 11] sample value-form detect — date field + numeric op(sum/average) 거부
    // compute='age' 미지정 시 parseFloat(date)는 의미없는 숫자 산출 (예: parseFloat("19970527")=19970527 → average 19,965,253)
    // anchor 9 placeholder substitute path가 잘못된 값을 정확히 박는 부작용 본질 차단 — compute='age' 사용 강제 안내
    // min/max는 date ordering 의미 있음 (가장 어린/늙은 회원 birthDay 추출), group은 string 분류 — 통과
    const sampleRaw = items[0] ? this.getFieldValue(items[0], fieldNames) : null;
    if (compute !== 'age' && _isDateString(sampleRaw) && ['sum', 'average'].includes(operation)) {
      return {
        success: false, operation, field,
        message: `Field "${field}" contains date strings (e.g. "${sampleRaw}"). ${operation}(date field) treats parseFloat(date) as a meaningless number — for age aggregation use compute='age' explicitly: compute({operation:'${operation}', field:'${field}', compute:'age'}). For age distribution use compute(operation:'group', field:'${field}', compute:'age').`,
      };
    }

    const values = items
      .map(item => {
        const raw = this.getFieldValue(item, fieldNames);
        if (compute === 'age') return this.computeAge(raw);
        return parseFloat(raw);
      })
      .filter(v => v !== null && !isNaN(v));

    const result = handler(items, values);
    if (result === null) {
      // 수학적 정의 안 됨 (average/min/max + 빈 values)
      return {
        success: false, operation, field,
        message: `${operation} 계산 불가 (유효 값 0건)${filterInfo}. "조건에 맞는 결과 0건" 안내 또는 사용자 명시 시 filter({reset:true}).`,
      };
    }
    // [P3-3 anchor 9] compute 결과를 buffer.summary에 저장 — LLM이 outro에 {{SUMMARY:key}} placeholder 사용 시 substitute path로 정확한 값 반환 (avgAge fabrication 본질 차단)
    // key: ${op_short}${PascalCase(compute || field)} — 기존 backend summary key 패턴(totalCount/activeRate)과 일관
    const _opShort = { average: 'avg', sum: 'sum', min: 'min', max: 'max' }[operation];
    const _keyBase = compute || field;
    const _summaryKey = _opShort && _keyBase
      ? `${_opShort}${_keyBase.charAt(0).toUpperCase()}${_keyBase.slice(1)}`
      : null;
    if (buf && _summaryKey) {
      buf.summary = buf.summary || {};
      buf.summary[_summaryKey] = result;
    }
    const _phHint = _summaryKey ? ` outro/finish 작성 시 raw 수치 박지 말고 {{SUMMARY:${_summaryKey}}} placeholder 사용 (코드가 ${result}로 치환).` : '';
    return {
      success: true, operation, field, result, count: values.length,
      message: `${operation}(${field})=${result}${filterInfo}. filter 적용 상태로 답변하세요.${_phHint}`,
    };
  }

  // 그룹핑 집계 후 버퍼 재구조화 //
  computeAggregate({ field }) {
    const buf = this.getActiveBuf();
    if (!buf || !buf.items || buf.items.length === 0) {
      return { success: false, message: 'No data to aggregate.' };
    }

    if (!field) {
      return { success: false, message: 'Specify field to aggregate.' };
    }

    let fieldNames = getFieldNames(field);
    const NAME_FIELDS = ['name', 'memberName', 'userName'];

    if (NAME_FIELDS.includes(field) && buf.items.some(item => item.memberId != null)) {
      fieldNames = ['memberId'];
    }

    const DATE_FIELD_CANDIDATES = ['classDate', 'createdDate', 'registDate', 'startDate', 'endDate', 'viewStartDate', 'viewEndDate', 'paymentDate', 'date'];
    const dateField = DATE_FIELD_CANDIDATES.find(f => buf.items[0] && buf.items[0][f] != null);
    const groupMap = new Map();

    for (const item of buf.items) {
      const key = this.getFieldValue(item, fieldNames);
      const keyStr = key != null ? String(key) : '_unknown';

      if (!groupMap.has(keyStr)) {
        groupMap.set(keyStr, { representative: { ...item }, count: 0, minDate: null, maxDate: null });
      }

      const group = groupMap.get(keyStr);
      group.count++;

      if (dateField) {
        const dateVal = item[dateField];

        if (dateVal) {
          const dateStr = String(dateVal).slice(0, 10);
          if (!group.minDate || dateStr < group.minDate) group.minDate = dateStr;
          if (!group.maxDate || dateStr > group.maxDate) group.maxDate = dateStr;
        }
      }
    }

    // [L19-DISPLAY-AGGREGATE-001 fix] buf.items가 이미 그룹 키로 거의 unique한 경우 — noop + 안내
    // CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD처럼 backend가 회원별로 이미 집계해서 보낸 데이터를
    // 또 aggregate(field=name)하면 모든 _count≈1이 되어 의미 없는 표 + 원래 출석 횟수 column 손실 + SELF_CONTRADICTION.
    // 식별: avgGroupSize (items.length / groupMap.size) <= 1.05 → 95% 이상 1:1 → 그룹핑 정보 0
    //   - 1.05 임계: 동명이인/유사 ID 등 자연 중복 5% 허용. raw 1:N transactions(매출/예약)는 보통 >2
    // 처리: buf 변경 없이 success:false 반환 — LLM이 show_display 직접 호출 또는 sort/filter 사용 유도
    const _avgGroupSize = buf.items.length / groupMap.size;
    if (_avgGroupSize <= 1.05 && buf.items.length > 0) {
      const sampleFields = Object.keys(buf.items[0] || {}).filter(k => !k.startsWith('_'));
      if (TOOL_CALLING_LOG) {
        console.log(`[aggregate] already-grouped 감지: ${buf.items.length}건 → ${groupMap.size}그룹 (avg=${_avgGroupSize.toFixed(2)}, ${fieldNames[0]} 기준 1:1 근접) → noop`);
      }
      return {
        success: false,
        operation: 'aggregate',
        field,
        message: `Data is already grouped per ${field}. ${buf.items.length} items would produce ${groupMap.size} groups with avg count ≈ ${_avgGroupSize.toFixed(2)} per group — aggregation yields no new information (every _count would be 1 or 2). The data already has entity-level fields. Do NOT aggregate — call show_display directly with the existing columns (or use sort/filter to refine). Available fields: [${sampleFields.join(', ')}].`,
      };
    }

    // [MONEY_SUM_AGGREGATE] 그룹별 monetary 필드 합계 사전 산정 — 사용자 "X별 매출/금액 합계" 의도 시 _count(횟수)만으로는 응답 불가능.
    //   본질: aggregate(itemName) 호출 시 representative item만 사용 → totalPrice 등 raw rows에 있던 numeric monetary field가 그룹 내 합산 안 됨.
    //   _sum_<field>를 derive — pattern /^(.+)(Price|Amount|Fee|Revenue)$/i (case-insensitive, monetary suffix).
    //   범용 — case ID hardcode 없음, intention 무관, field name pattern 매칭만.
    //   참고: count-type field (reserveCount/attendedCount 등)는 합산 미적용 — semantically 다양 (이미 per-entity aggregate or per-row 의미 불명확).
    const MONEY_FIELD_RE = /(Price|Amount|Fee|Revenue)$/i;
    const moneyFieldCandidates = new Set();
    if (buf.items.length > 0) {
      for (const sampleItem of buf.items.slice(0, 5)) {
        for (const [k, v] of Object.entries(sampleItem || {})) {
          if (k.startsWith('_')) continue;
          if (!MONEY_FIELD_RE.test(k)) continue;
          // numeric 값 검사 — string '12000' 같은 형태도 허용 (parseFloat 가능)
          if (typeof v === 'number' && !isNaN(v)) moneyFieldCandidates.add(k);
          else if (typeof v === 'string' && v.trim() !== '' && !isNaN(parseFloat(v))) moneyFieldCandidates.add(k);
        }
      }
    }
    // _sum_<field> 계산: 각 그룹에 대해 raw items 순회하며 합산
    //   - groupMap이 representative만 보유하므로 별도 순회 필요
    //   - 빈 그룹/null 값은 0 가산 X (NaN guard)
    const moneySumByGroup = new Map();
    if (moneyFieldCandidates.size > 0) {
      for (const item of buf.items) {
        const key = this.getFieldValue(item, fieldNames);
        const keyStr = key != null ? String(key) : '_unknown';
        if (!moneySumByGroup.has(keyStr)) {
          moneySumByGroup.set(keyStr, Object.fromEntries([...moneyFieldCandidates].map(f => [f, 0])));
        }
        const sums = moneySumByGroup.get(keyStr);
        for (const f of moneyFieldCandidates) {
          const raw = item[f];
          const num = typeof raw === 'number' ? raw : (typeof raw === 'string' ? parseFloat(raw) : NaN);
          if (!isNaN(num)) sums[f] += num;
        }
      }
    }

    const aggregated = [];
    const groupKey = fieldNames[0];

    for (const [keyStr, group] of groupMap) {
      // aggregate 결과: 그룹 키 + 통계 메타(_count/_firstDate/_lastDate)만 노출
      // representative 임의 필드(viewCount/age/profile 등)는 그룹 첫 행 한 명의 값이라 의미 없음 (가짜 정보 차단)
      const item = {
        [groupKey]: group.representative[groupKey],
        _count: group.count,
        ...(group.maxDate && { _lastDate: group.maxDate }),
        ...(group.minDate && { _firstDate: group.minDate }),
      };
      // memberId 그룹핑 시 사용자가 요청한 원본 필드(name 등) 대표값 포함
      if (groupKey !== field && group.representative[field] != null) {
        item[field] = group.representative[field];
      }
      // [MONEY_SUM_AGGREGATE] _sum_<field> 부착 — 사용자 매출/금액 합계 의도 응답 가능
      const groupSums = moneySumByGroup.get(keyStr);
      if (groupSums) {
        for (const [f, sum] of Object.entries(groupSums)) {
          item[`_sum_${f}`] = sum;
        }
      }
      aggregated.push(item);
    }

    const before = buf.items.length;

    buf.items = aggregated;
    buf.originalItems = aggregated;
    buf.appliedFilters = [];
    buf.appliedSort = null;
    buf.resolvedColumns = null;
    buf._aggregateKey = groupKey;

    const derivedFields = ['_count(횟수)'];
    const aggregateMetaFields = ['_count'];

    if (aggregated[0]?._lastDate) {
      derivedFields.push('_lastDate(마지막 날짜)');
      aggregateMetaFields.push('_lastDate');
    }
    if (aggregated[0]?._firstDate) {
      derivedFields.push('_firstDate(첫 날짜)');
      aggregateMetaFields.push('_firstDate');
    }
    // [MONEY_SUM_AGGREGATE] derive/aggregateMetaFields에 _sum_<field> 등록
    for (const f of moneyFieldCandidates) {
      derivedFields.push(`_sum_${f}(${f} 합계)`);
      aggregateMetaFields.push(`_sum_${f}`);
    }
    buf._aggregateMetaFields = aggregateMetaFields;
    buf._aggregateGroupByField = field;

    const survivingFields = aggregated[0]
      ? Object.keys(aggregated[0]).filter(k => !k.startsWith('_'))
      : [];

    if (TOOL_CALLING_LOG) {
      console.log(`[aggregate] ${field} 기준: ${before}건 → ${aggregated.length}그룹 (파생: ${derivedFields.join(', ')})`);
      console.log('[aggregate] 결과 데이터:', aggregated);
    }

    // [MONEY_SUM_AGGREGATE] message hint — 사용자 매출/합계 의도 시 _sum_<field> column 선택 안내
    const moneySumHint = moneyFieldCandidates.size > 0
      ? ` Monetary sum fields available (groupwise totals): [${[...moneyFieldCandidates].map(f => `_sum_${f}`).join(', ')}]. For "X별 매출/금액 합계" intent, prefer _sum_totalPrice (or relevant sum field) over _count in show_display columns — _count is transaction frequency, _sum_<field> is aggregated revenue/amount.`
      : '';

    return {
      success: true,
      operation: 'aggregate',
      field,
      before,
      after: aggregated.length,
      derivedFields: derivedFields.map(f => f.split('(')[0]),
      survivingFields,
      message: `Aggregation complete: ${before} items grouped by ${field} into ${aggregated.length} groups. `
        + `Derived fields added: ${derivedFields.join(', ')}. `
        + `Surviving original fields: [${survivingFields.join(', ')}]. `
        + `The groupBy field "${field}" and all non-ID fields from the representative item are preserved. `
        + `Korean: ${field} 기준 집계 완료. display columns에 ${field}과 위 surviving fields를 사용하세요.${moneySumHint}`,
    };
  }

  // 그룹별 카운트 //
  computeGroup(buf, field, fieldNames, compute) {
    // [MULTI-AXIS COMPOSITION] 같은 슬롯에 연속 compute(group) (나이대 AND 성별 등 다축 구성 질문) 대응. dual sync with nodeAgent.mjs.
    //   본질: 첫 group이 buf.items를 grouped rows({field,_count})로 교체 → switch_slot(같은 슬롯)은 grouped rows 그대로 →
    //         2번째 축 group이 grouped rows 위에서 돌아 원본 필드(birthDay 등) 전무 → '알수없음' 쓰레기 산출.
    //   해소: 첫 group 직전 원본 items 보존(_preGroupOriginalItems), 이미 grouped된 buf 재group 시 원본에서 재파생.
    //   generic: 도메인/intention 무관, grouped buffer 구조 signal(_currentAxisKey='_groupBy=*')만 사용.
    const _alreadyGrouped = typeof buf._currentAxisKey === 'string' && buf._currentAxisKey.startsWith('_groupBy=');
    if (_alreadyGrouped && Array.isArray(buf._preGroupOriginalItems)) {
      buf.items = buf._preGroupOriginalItems.slice();  // 다른 축 재group — 원본 회원 list 복원
    } else if (!_alreadyGrouped) {
      buf._preGroupOriginalItems = Array.isArray(buf.items) ? buf.items.slice() : [];  // 첫 group — 원본 보존(배열만 분리, row 참조 유지)
    }
    const _prevAxisKey = buf._currentAxisKey;

    // [L4-DERIVE pattern] Layer 0 — groupBy field 존재 여부 검증 (deriveField 2713-2719 동일 패턴) //
    // 본질: backend가 동일 intention으로 다른 form 응답(DATA FORM MISMATCH) 또는 LLM이 다른 intention 메타의 field를 잘못 사용 시,
    //       모든 item이 undefined → 전부 '알수없음' bucket → 의미없는 응답 ("알수없음 | 12") 생성.
    // 본 가드: sample item에 field 부재 + compute !== 'age' (age는 자체 null fallback) → 사전 reject.
    // generic: intention/도메인 무관, buf.availableFields 기반 안내.
    // 단, age compute는 null → '알수없음' decade fallback이 의도된 동작 (생년 미상 회원 = 알수없음대)이므로 통과.
    if (compute !== 'age' && buf.items.length > 0) {
      const sample = buf.items[0];
      const sampleRaw = this.getFieldValue(sample, fieldNames);
      if (sampleRaw === undefined) {
        const availableKeys = buf.availableFields ? Object.keys(buf.availableFields) : Object.keys(sample);
        const actualKeys = Object.keys(sample);
        return {
          success: false,
          operation: 'group',
          field,
          message: `Field "${field}" does not exist in current buffer data — grouping would bucket all ${buf.items.length} items as "알수없음" (meaningless result). Actual fields in items[0]: [${actualKeys.join(', ')}]. Defined availableFields: [${availableKeys.join(', ')}]. Possible causes: (1) DATA FORM MISMATCH — backend returned a different schema variant for this intention (use one of the actual fields above); (2) wrong intention — the requested groupBy field belongs to a different intention (load that intention via get_intentions → call_api). Re-check the intention's actual response and pick a field present in items[0], or call a different intention that exposes "${field}".`,
        };
      }
    }

    const groups = {};

    for (const item of buf.items) {
      let value = this.getFieldValue(item, fieldNames);

      if (compute === 'age') {
        const age = this.computeAge(value);

        if (age === null) {
          groups['알수없음'] = (groups['알수없음'] || 0) + 1;
          continue;
        }

        const decade = Math.floor(age / 10) * 10;
        value = `${decade}대`;
      }
      else {
        value = value != null ? String(value) : '알수없음';
      }

      groups[value] = (groups[value] || 0) + 1;
    }

    // [degenerate group guard — dual sync with nodeAgent.mjs, computeAggregate 가드(3680) 미러링] //
    //   본질: name/memberId/phone 처럼 row마다 거의 unique한 field로 group → 모든 group _count=1 → 순위/집계 정보 0.
    //         운영 store398 "꾸준히 나온 회원 순위" 👎 (compute(group,name) → 전원 출석 1회) 재발 차단.
    //   generic: field/intention/case 무관, distinct group 수 ÷ row 수 구조 신호(avg group size ≤ 1.05)만 사용.
    //   age compute는 decade 버킷팅(정상 소수 그룹)이라 통과 — computeAggregate 가드와 동일 threshold.
    if (compute !== 'age' && buf.items.length > 0) {
      const _groupCount = Object.keys(groups).length;
      const _avgGroupSize = buf.items.length / _groupCount;
      if (_avgGroupSize <= 1.05) {
        const sampleFields = Object.keys(buf.items[0] || {}).filter(k => !k.startsWith('_'));
        return {
          success: false,
          operation: 'group',
          field,
          message: `Data is already near-unique per ${field}: ${buf.items.length} items -> ${_groupCount} groups (avg ≈ ${_avgGroupSize.toFixed(2)} per group), so every _count would be 1 — grouping yields NO ranking/aggregation information. Do NOT group by a per-row-unique identifier (name/id/phone). To RANK entities, sort by the relevant metric field (e.g. attendedCount) then show_display, or use show_attendance_ranking for attendance ranking. To list members, call show_display directly with the existing columns. Available fields: [${sampleFields.join(', ')}].`,
        };
      }
    }

    // Replace buffer items with grouped rows for show_display compatibility //
    const groupKey = compute === 'age' ? `${field}_group` : fieldNames[0];
    const before = buf.items.length;
    const groupedItems = Object.entries(groups).map(([key, count]) => ({
      [groupKey]: key,
      _count: count,
    }));

    buf.items = groupedItems;
    buf.originalItems = groupedItems;
    buf.appliedFilters = [];
    buf.appliedSort = null;
    buf.resolvedColumns = null;
    buf._aggregateKey = groupKey;
    buf._aggregateMetaFields = ['_count'];

    // [MULTI-AXIS COMPOSITION] grouped 축 식별자 — displaysThisTurn dedup이 축별 별개 누적(같은 슬롯 gender/age 구분) + 재group 감지 signal. dual sync with nodeAgent.mjs.
    buf._currentAxisKey = '_groupBy=' + field;
    // 직전에 다른 축 표가 이미 표출됐으면 후속 show_display(이번 축) dup-block 1회 한정 bypass 허용 (다른 축일 때만 — 같은 축 재표출 방지)
    if (this._showDisplaySucceededThisTurn && _prevAxisKey !== buf._currentAxisKey) {
      this._multiAxisShowDisplayAllowedOnce = true;
    }

    // Store original total in summary for placeholder access //
    if (!buf.summary) buf.summary = {};
    buf.summary.groupTotalCount = before;

    // [L31-COMPLEX-007] top group label + count — for "가장 많은 X은 LABEL이고 N명" outro context.
    //   LLM이 그동안 {{SUMMARY:groupTotalCount}}(=전체 entity 수)를 top group entity count로 잘못 사용 → outro 자기모순.
    //   topGroupLabel/topGroupCount 노출하여 LLM이 정확한 placeholder 선택 가능. dual sync with nodeAgent.mjs.
    //   topGroupLabel은 enum 영문값 (예: gender='male')을 한국어 display label ('남')로 변환.
    let _topLabel = null;
    let _topCount = 0;
    for (const [k, v] of Object.entries(groups)) {
      if (v > _topCount) { _topCount = v; _topLabel = k; }
    }
    if (_topLabel != null) {
      const _fieldMeta = buf.availableFields?.[field];
      const _translated = (typeof getDisplayLabel === 'function')
        ? getDisplayLabel(field, _topLabel, _fieldMeta)
        : null;
      buf.summary.topGroupLabel = _translated || _topLabel;
      buf.summary.topGroupCount = _topCount;
    }
    buf.summary.groupCount = groupedItems.length;

    if (TOOL_CALLING_LOG) {
      console.log(`[computeGroup] ${field} grouping: ${before} items -> ${groupedItems.length} groups`);
      console.log('[computeGroup] result:', groupedItems);
    }

    const groupDetail = Object.entries(groups).map(([k, v]) => `${k}: ${v}`).join(', ');

    return {
      success: true,
      operation: 'group',
      field,
      result: groups,
      totalCount: before,
      before,
      after: groupedItems.length,
      message: `${field} group complete: ${before} items -> ${groupedItems.length} groups (${groupDetail}). Buffer replaced with grouped rows ([${groupKey}, _count]). Call show_display with columns: ["${groupKey}", "_count"]. Available summary placeholders for intro/outro: {{SUMMARY:groupTotalCount}}=전체 entity 수 (${before}), {{SUMMARY:groupCount}}=group 개수 (${groupedItems.length}), {{SUMMARY:topGroupLabel}}=가장 많은 group의 label (${buf.summary.topGroupLabel ?? 'N/A'}), {{SUMMARY:topGroupCount}}=가장 많은 group의 entity count (${_topCount}). 주의: "가장 많은 ${field}은(는) X이고 N명" 같은 문장에서 N은 {{SUMMARY:topGroupCount}}를 써야 합니다 ({{SUMMARY:groupTotalCount}}는 전체 합계).`,
    };
  }

  // ============================================
  // 6. 파생 필드 생성
  // ============================================

  // 기존 숫자 필드로부터 새 파생 필드를 계산하여 추가 //
  deriveField({ name, field, operation, value }) {
    // derive 필드명에서 선행 _ 자동 제거 (display 노출 방지) //
    if (name && name.startsWith('_')) {
      name = name.slice(1);
    }

    const buf = this.getActiveBuf();
    if (!buf || !buf.items || buf.items.length === 0) {
      return { success: false, message: 'No data in buffer. Call get_intentions → call_api first.' };
    }

    if (!name || !field || !operation) {
      return { success: false, message: 'name, field, operation are required.' };
    }

    const fieldNames = getFieldNames(field);

    // [P3 anchor 11] Layer 1 — date field 산술 거부 (value-form 기반, anchor 8 패턴)
    // birthDay/startDate/endDate 등 date string 필드는 parseFloat 적용 시 의미없는 숫자 평균 산출
    // (예: parseFloat("19970527")=19970527 → average 19,965,253 — anchor 9 placeholder substitute가 정확히 박는 부작용)
    const sample = buf.items[0];
    const sampleRaw = this.getFieldValue(sample, fieldNames);

    // [L4-DERIVE] Layer 0 — operand field 존재 여부 검증 //
    // LLM이 버퍼에 없는 필드를 지정하면 computed=0 으로 무의미한 파생 필드 생성 → 거짓 응답 원인
    // tool 레벨에서 사전 차단하여 어떤 필드가 사용 가능한지 구체적으로 안내
    if (sampleRaw === undefined) {
      const availableKeys = buf.availableFields ? Object.keys(buf.availableFields) : Object.keys(sample);

      return {
        success: false,
        message: `Field "${field}" does not exist in current buffer data. Available fields: [${availableKeys.join(', ')}]. Use one of these as the "field" parameter, or load data containing "${field}" via get_intentions → call_api first.`,
      };
    }

    if (_isDateString(sampleRaw)) {
      return {
        success: false,
        message: `Field "${field}" contains date strings (e.g. "${sampleRaw}"). Arithmetic operations (add/subtract/multiply/divide) on date fields produce semantically wrong results — parseFloat treats the date as a meaningless number. For age/duration calculations, load a separate intention that provides a pre-computed age/duration field, or omit the metric and let outro infer from displayed dates.`,
      };
    }

    // [P3 anchor 11] Layer 2 — value=0/null/undefined validation (subtract/add no-op)
    // x ± 0 = x 무의미한 호출 차단. divide value=0은 기존 computeDeriveValue operand !== 0 에서 차단.
    if (['add', 'subtract'].includes(operation) && (value === 0 || value === null || value === undefined)) {
      return {
        success: false,
        message: `value=${value} is a no-op for ${operation} (x ${operation === 'add' ? '+' : '-'} 0 = x). Use a non-zero number, or another field name (string) as value (e.g. value="attendedCount" for field-field arithmetic).`,
      };
    }

    // [L4-DERIVE] value가 문자열(field-field 연산)일 때 value 필드 존재 여부 검증 //
    if (typeof value === 'string') {
      const valueFieldNames = getFieldNames(value);
      const valueSample = this.getFieldValue(sample, valueFieldNames);

      if (valueSample === undefined) {
        const availableKeys = buf.availableFields ? Object.keys(buf.availableFields) : Object.keys(sample);

        return {
          success: false,
          message: `Value field "${value}" does not exist in current buffer data. Available fields: [${availableKeys.join(', ')}]. Use one of these as the "value" parameter, or load data containing "${value}" via get_intentions → call_api → merge_slots first.`,
        };
      }
    }

    let computed = 0;

    for (const item of buf.items) {
      const result = this.computeDeriveValue(item, fieldNames, operation, value);
      item[name] = result;
      if (result !== null) computed++;
    }

    if (buf.originalItems) {
      for (const item of buf.originalItems) {
        item[name] = this.computeDeriveValue(item, fieldNames, operation, value);
      }
    }

    // derive 필드를 availableFields에 자동 등록 (라벨 생성용) //
    // [L4-DERIVE] 한국어 라벨 합성: availableFields → LABEL_OVERRIDES → null fallback
    // raw 영문 field name (attendedCount 등)을 합성 라벨에 노출하지 않음
    // null fallback 시 합성 라벨 생성 포기 → name(LLM 한국어 명명)을 그대로 사용
    //
    // [L4-DERIVE-SUBTRACT] LLM name 우선 — name이 이미 사용자 친화 한국어 의미 라벨이면 (Hangul 포함)
    // 합성식("X op Y") 대신 name 자체를 라벨로 보존. 사용자 의도(label 자체가 의미)를 reflect.
    // 예: name="노쇼 횟수" subtract field="reservationCount" value="attendedCount"
    //  - 기존: 라벨 = "예약 횟수 - 출석 횟수" (raw expression — 사용자 의도 무시)
    //  - 신규: 라벨 = "노쇼 횟수" (LLM이 부여한 의미 라벨 — 사용자 입력 "노쇼 횟수도 추가" 반영)
    if (buf.availableFields) {
      const opLabels = { add: '+', subtract: '-', multiply: '×', divide: '/' };
      const resolveDeriveLabel = (raw) => {
        if (raw == null) return null;
        if (typeof raw !== 'string') return raw; // 숫자 value는 그대로
        const fromMeta = getFieldLabel(buf.availableFields[raw]);
        if (fromMeta && fromMeta !== raw) return fromMeta;
        if (LABEL_OVERRIDES[raw]) return LABEL_OVERRIDES[raw];
        return null; // 영문 raw 노출 회피
      };
      const HANGUL_RE = /[ㄱ-ㆎ가-힣]/;
      const nameIsSemanticKorean = typeof name === 'string' && HANGUL_RE.test(name);
      let _derivedLabel;
      if (nameIsSemanticKorean) {
        // LLM이 이미 사용자 의도를 반영한 한국어 라벨 부여 — 합성식 우회
        _derivedLabel = name;
      } else {
        const fieldLabel = resolveDeriveLabel(field);
        const valueLabel = resolveDeriveLabel(value);
        const operandResolved = typeof value === 'string' ? valueLabel != null : true;
        _derivedLabel = (fieldLabel != null && operandResolved)
          ? `${fieldLabel} ${opLabels[operation] || operation} ${valueLabel}`
          : name;
      }

      // [L4-DERIVE-ADD] price-like operand propagation —
      //   add/subtract 만 currency unit 보존 (X원 + Y원 = 합산원 / X원 - Y원 = 차액원).
      //   multiply/divide 는 단위가 바뀌므로 (원 × 배수 = 원이지만 원 / 원 = 비율 무차원) propagation 제외.
      //   numeric value (typeof value === 'number') 인 경우 add/subtract: field 만 price-like 검사.
      //   operand 중 하나라도 price-like 아니면 unit 미설정.
      const _opPreservesCurrency = operation === 'add' || operation === 'subtract';
      const _fieldPriceLike = typeof field === 'string' && PRICE_FIELDS_RE.test(field);
      const _valuePriceLike = typeof value === 'string' ? PRICE_FIELDS_RE.test(value) : true; // numeric value (e.g. cardPrice + 10000) 통과
      const _operandPriceLike = _opPreservesCurrency && _fieldPriceLike && _valuePriceLike;
      buf.availableFields[name] = _operandPriceLike
        ? { label: _derivedLabel, unit: 'currency' }
        : _derivedLabel;
    }

    // derive 필드를 표시 컬럼에 자동 포함 //
    if (!buf.columnPrefs) {
      buf.columnPrefs = { include: [name], exclude: [], computedColumns: [] };
    }
    else if (!buf.columnPrefs.include?.includes(name)) {
      buf.columnPrefs.include = [...(buf.columnPrefs.include || []), name];
    }

    if (TOOL_CALLING_LOG) {
      console.log(`[derive] ${name} = ${field} ${operation} ${value} (${computed}건 계산)`);
      console.log('[derive] 결과 데이터:', buf.items);
    }

    return {
      success: true,
      field: name,
      operation,
      computed,
      message: `파생 필드 "${name}" 생성 완료 (${field} ${operation} ${value}). ${computed}건 계산됨. 이제 filter/sort에서 "${name}"을 필드명으로 사용 가능.`,
    };
  }

  // 단일 아이템의 파생값 계산 //
  computeDeriveValue(item, fieldNames, operation, value) {
    const raw = this.getFieldValue(item, fieldNames);
    const num = parseFloat(raw);

    if (isNaN(num)) return null;

    // value가 문자열이면 다른 필드의 값을 읽음 (field-field 연산), 숫자면 그대로 사용
    let operand;
    if (typeof value === 'string') {
      const valueFieldNames = getFieldNames(value);
      operand = parseFloat(this.getFieldValue(item, valueFieldNames));
      if (isNaN(operand)) return null;
    } else {
      operand = value;
    }

    switch (operation) {
      case 'divide':  return operand !== 0 ? Math.round((num / operand) * 100) / 100 : null;
      case 'multiply': return Math.round((num * operand) * 100) / 100;
      case 'add':      return num + operand;
      case 'subtract': return num - operand;
      default:         return null;
    }
  }

  // ============================================
  // 7. 컬럼 커스터마이징
  // ============================================

  // 표시 컬럼 증감 조정 //
  setColumnPrefs(include = [], exclude = [], only = null) {
    const buf = this.getActiveBuf();
    if (!buf) return { success: false, message: 'No data in buffer.' };

    // nested 데이터 컬럼 차단 — sample item에서 object/array 값인 컬럼은 표 표시 불가
    const sample = buf.items?.[0];
    const checkNested = (fields) => {
      if (!sample) return [];
      return fields.filter(f => {
        const v = sample[f];
        return v != null && typeof v === 'object';
      });
    };
    const requestedAdd = [...(include || []), ...(only || [])];
    const nestedFields = checkNested(requestedAdd);
    if (nestedFields.length > 0) {
      const sampleVal = sample[nestedFields[0]];
      const kind = Array.isArray(sampleVal) ? `배열(${sampleVal.length}개)` : 'nested 객체';
      return {
        success: false,
        message: `[NESTED COLUMN BLOCKED] 컬럼 [${nestedFields.join(', ')}]은(는) ${kind} 형태라 표로 표시할 수 없어요. 다음 중 하나를 선택하세요: (1) 별도 인텐션으로 시간대/세부 단위 데이터를 다시 call_api로 조회 → show_display, (2) derive로 nested에서 평탄화된 파생 필드(예: 평균/합계)를 만든 뒤 customize_display, (3) 다른 평탄 필드만 customize_display.`,
      };
    }

    const computedColumns = [];

    // 필드명 → 가상 컬럼 변환 (FIELD_REGISTRY 기반, 가상 필드명일 때만 변환) //
    const resolveInclude = (fieldName) => {
      // FIELD_REGISTRY에서 가상 필드명인지 확인
      for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
        if (!config.computes) continue;

        for (const [computeType, computeConfig] of Object.entries(config.computes)) {
          if (computeConfig.virtual === fieldName) {
            if (computeConfig.handler) {
              computedColumns.push({ virtual: fieldName, source: field, compute: computeType });
            }
            return fieldName;
          }
        }
      }

      // 실제 필드명은 그대로 유지
      return fieldName;
    };

    // only 모드: 지정된 컬럼만 표시
    if (only && only.length > 0) {
      const onlyFields = only.map(resolveInclude);

      buf.resolvedColumns = null;
      buf.columnPrefs = {
        only: onlyFields,
        include: onlyFields,
        exclude: [],
        computedColumns: [...computedColumns],
      };

      return {
        success: true,
        only: onlyFields,
        message: `컬럼 설정 완료. 표시: [${onlyFields.join(', ')}] (나머지 전부 제거)`,
      };
    }

    // include/exclude 모드
    const newInclude = include.map(resolveInclude);
    const newExclude = [...exclude];
    const prev = buf.columnPrefs || { include: [], exclude: [], computedColumns: [] };

    if (prev.only) {
      delete prev.only;
    }

    const mergedExclude = [...prev.exclude.filter(e => !newInclude.includes(e)), ...newExclude];
    const mergedInclude = [...prev.include.filter(i => !newExclude.includes(i)), ...newInclude];
    const mergedComputed = [...(prev.computedColumns || []).filter(c => !newExclude.includes(c.virtual)), ...computedColumns];
    const uniqueInclude = [...new Set(mergedInclude)];
    const uniqueExclude = [...new Set(mergedExclude)];
    const uniqueComputed = mergedComputed.filter((c, i, arr) => arr.findIndex(x => x.virtual === c.virtual) === i);

    buf.columnPrefs = { include: uniqueInclude, exclude: uniqueExclude, computedColumns: uniqueComputed };

    return {
      success: true,
      include: uniqueInclude,
      exclude: uniqueExclude,
      message: `컬럼 설정 완료. 추가: [${uniqueInclude.join(', ')}], 제외: [${uniqueExclude.join(', ')}]`,
    };
  }

  // ============================================
  // 8. 내부 유틸
  // ============================================

  // 링코페이 결제 판별 //
  isLinkoPayPayment(item) {
    console.log('[isLinkoPayPayment]', item.merchantUid)
    if (!item.merchantUid) return false;
    const segment = item.merchantUid.split('-')[2];
    return segment === '1' || segment === '2';
  }

  // 결제 수단 분류 //
  categorizePaymentMethod(item) {
    if (this.isLinkoPayPayment(item)) return 'linkoPay';

    if (item.selectedPaymentOptions && item.selectedPaymentOptions.length > 0) {
      const allSame = item.selectedPaymentOptions.every(
        opt => opt.paymentOptionName === item.selectedPaymentOptions[0].paymentOptionName
      );

      if (allSame) {
        const name = item.selectedPaymentOptions[0].paymentOptionName;
        if (name === '카드') return 'card';
        if (name === '현금') return 'cash';
        if (name === '포인트') return 'point';
        if (name === '링코페이') return 'linkoPay';
      }

      return 'mixed';
    }

    if (item.cardPrice === 0 && item.cashPrice === 0) return 'free';
    if (item.cardPrice === 0) return 'cash';
    if (item.cashPrice === 0) return 'card';
    return 'mixed';
  }

  // 여러 필드명 후보 중 값을 가진 필드 찾기 //
  getFieldValue(item, fieldNames) {
    for (const name of fieldNames) {
      if (name.includes('.')) {
        const parts = name.split('.');
        let current = item;

        for (const part of parts) {
          if (current == null) break;
          current = current[part];
        }

        if (current !== undefined) return current;
      }

      if (item[name] !== undefined) return item[name];
    }

    return undefined;
  }

  // 생년월일 → 나이 계산 //
  computeAge(birthValue) {
    const date = this.parseDate(birthValue);

    if (!date) return null;

    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }

    return age;
  }

  // 날짜 문자열 파싱 //
  parseDate(value) {
    if (!value) return null;

    const str = String(value);
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoMatch) {
      return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    }

    const compactMatch = str.match(/^(\d{4})(\d{2})(\d{2})$/);

    if (compactMatch) {
      return new Date(parseInt(compactMatch[1]), parseInt(compactMatch[2]) - 1, parseInt(compactMatch[3]));
    }

    return null;
  }


  // ============================================
  // Tool 실행 핸들러 (13개 도구) — from aiTools
  // ============================================

  // Tool 상태 리셋 //
  resetToolsState() {
    Object.keys(this.intentionCache).forEach(k => delete this.intentionCache[k]);
  }

  // 카테고리별 인텐션 목록 조회 //
  // categoryRules는 omo refactor B-frontend로 폐기 (DISPLAY_GUIDE + intention.description으로 분산).
  // backend가 rules를 반환해도 LLM에 노출 안 함 — tool 응답에서 drop.
  async executeGetIntentions({ category }) {
    const userInput = this.getUserInput?.() || '';

    // [HISTORY LIST REDIRECT — 은퇴 2026-07-06] mini INTENT_DRIFT 보상용 category payload 바꿔치기(MEMBERSHIP/MEMBER_DETAIL→MEMBER)였음. 유래 6a910853이 심은 프롬프트 가이드(20_intentions NEVER/WRONG)만으로 gpt-5.4가 자력 교정(off 5/5 — MEMBERSHIP 오분류 후 스스로 MEMBER 재조회) 확인돼 제거. S2b 동형: 바꿔치기는 CATEGORY-SPLIT 가이드-payload 불일치 유발 — 재추가 전 은퇴 심사 기록 확인 (dual sync with nodeAgent.mjs) //

    // [DETAIL KEYWORD REDIRECT — 은퇴 2026-07-05] mini MEMBERSHIP→CATALOG_LIST 오분류 보상 게이트였음. 5.4 자력 MEMBER_DETAIL 선택(발동 0/24)+off 원케이스 5/5·프로브 무악화로 제거, 비회원 입력('홀딩 내역 보여줘')의 이름후보 오인 오염 리스크 소거 — 재추가 전 은퇴 심사 기록 확인 (dual sync with nodeAgent.mjs) //

    // [YEARLY SALES CATEGORY REDIRECT — 은퇴 2026-07-05] mini 오분류 보상 게이트였음. 5.4 자력 라우팅 5/5 + 비매출 연간 질문(연도별 신규회원)을 SALES payload로 바꿔치기해 가이드-intention 불일치 유발(ON 0/2 vs OFF 4/5)이 실측돼 제거 — 재추가 전 은퇴 심사 기록 확인 (dual sync with nodeAgent.mjs) //

    const _routingTerm = detectTermOnly(userInput);
    if (_routingTerm) {
      const _entry = USER_TERM_DICTIONARY[_routingTerm];
      // [RE-REGISTRATION AXIS DISCLOSURE] 재등록률은 회원권/회원 차원에만 집계됨.
      // 입력이 코치/수업 axis를 명시하면 MEMBERSHIP redirect 시 회원권 재등록률로 답해 entity 불일치 발생.
      // 코치/수업 단위 재등록 데이터는 없으므로 redirect 대신 정직하게 안내(finish 유도). dual sync: nodeAgent.mjs.
      if (_entry?.metricField === 'reRegistrationRate') {
        const _reRegAxis = detectAxisFromInput(userInput);
        if (_reRegAxis === 'coach' || _reRegAxis === 'lesson') {
          console.log(`[get_intentions] re-registration axis disclosure: term=${_routingTerm}, axis=${_reRegAxis} (no coach/lesson re-registration data)`);
          return { success: true, message: resolvePrompt('RE_REGISTRATION_AXIS_DISCLOSURE', ''), intentions: [] };
        }
      }
      if (_entry?.domain && _entry.domain !== category && !_entry.alternativeAxes?.length) {
        console.log(`[get_intentions] term dictionary redirect: ${category} → ${_entry.domain} (term: ${_routingTerm})`);
        category = _entry.domain;
      }
    }
    if (this.intentionCache[category]) {
      const cached = this.intentionCache[category];
      return this.buildIntentionsResponse(cached.intentions);
    }

    try {
      const fetchModule = new FetchModule();
      const _telStart = Date.now();
      const response = await fetchModule.getDataVersion2(
        `ai-chat/intentions?category=${category}`, 'GET'
      );

      // [시임2] get_intentions 백엔드 hop 계측 (캐시/로컬 경로는 백엔드 무호출이라 비대상) //
      try {
        this._telemetryCollector?.recordBackendCall({
          type: 'get_intentions',
          intention: null,
          endpoint: `ai-chat/intentions?category=${category}`,
          method: 'GET',
          fetchMethod: 'getDataVersion2',
          params: null,
          transformApplied: false,
          attemptStatuses: [response?.status ?? null],
          attemptCount: 1,
          latencyMs: Date.now() - _telStart,
          statusCode: response?.status ?? null,
          success: response?.status === 200,
          errorMessage: response?.status === 200 ? null : (response?.error || 'get_intentions 실패'),
          rawResponse: null,
        });
      }
      catch (_e) { /* 무시 */ }

      if (response?.status === 200 && response?.data?.intentions) {
        // [RANKING_SORT_GUARD + L1-MEMBERSHIP-007 STAT_RANKING_PERIOD_INCONSISTENCY backfill]
        //   backend response가 default_sort/tie_break_field/autoFallback/defaultPeriod 미반환 시
        //   LOCAL_INTENTIONS_DATA (frontend 정의) lookup으로 보강 — applyAutoFallbackParams + RANKING_SORT_GUARD trigger 조건 충족
        //   본질: backend DB schema에 해당 column 미존재 → meta 누락 → guard/fallback 비발동 → run마다 결정성 변동
        //   범용 — intention_key 기반 lookup, 특정 case hardcode 없음. dual sync with nodeAgent.mjs executeGetIntentions.
        const _localResForBackfill = getLocalIntentionsByCategory ? getLocalIntentionsByCategory(category) : null;
        const _localIntentionsByKey = {};
        if (_localResForBackfill?.intentions) {
          for (const li of _localResForBackfill.intentions) {
            if (li.intentionKey) _localIntentionsByKey[li.intentionKey] = li;
          }
        }
        const apiIntentions = response.data.intentions.map(i => {
          let af = typeof i.availableFields === 'string'
            ? JSON.parse(i.availableFields)
            : i.availableFields;
          // 백엔드 DB에 enum 메타 미적용 시 클라이언트 override dump로 보강 (동일 키는 override 우선)
          const override = AVAILABLE_FIELDS_OVERRIDES[i.intentionKey];
          if (override && af) af = { ...af, ...override };
          // backend DB에 defaultDisplay 메타 미적용 시 클라이언트 override dump로 보강 (LOCAL_INTENTIONS_DATA의 defaultDisplay와 정합)
          const _ddOverride = DEFAULT_DISPLAY_OVERRIDES[i.intentionKey];
          const _localMatch = _localIntentionsByKey[i.intentionKey];
          const _localBackfill = {};
          if (_localMatch) {
            if (!i.default_sort && _localMatch.default_sort && typeof _localMatch.default_sort === 'object') {
              _localBackfill.default_sort = _localMatch.default_sort;
            }
            if (!i.tie_break_field && typeof _localMatch.tie_break_field === 'string' && _localMatch.tie_break_field) {
              _localBackfill.tie_break_field = _localMatch.tie_break_field;
            }
            if (i.autoFallback !== true && _localMatch.autoFallback === true) {
              _localBackfill.autoFallback = true;
            }
            if (!i.defaultPeriod && _localMatch.defaultPeriod && typeof _localMatch.defaultPeriod === 'object') {
              _localBackfill.defaultPeriod = _localMatch.defaultPeriod;
            }
            if (i.forceParams !== true && _localMatch.forceParams === true) {
              _localBackfill.forceParams = true;
            }
            // [VIZONLY BACKFILL] backend DTO 매핑이 visualizationOnly/fallback_intention 미세팅(API2AiChatService 기본 9필드 한정) —
            //   DB에 값을 넣어도 프론트에 안 실림 → 로컬 정의로 보강해 VIZ-ONLY guard + #655 fallback re-route 활성 (dual sync nodeAgent.mjs)
            if (i.visualizationOnly !== true && _localMatch.visualizationOnly === true) {
              _localBackfill.visualizationOnly = true;
            }
            if (!i.fallback_intention && !i.fallbackIntention && typeof _localMatch.fallback_intention === 'string' && _localMatch.fallback_intention) {
              _localBackfill.fallback_intention = _localMatch.fallback_intention;
            }
          }
          return {
            ...i,
            availableFields: af,
            params: typeof i.params === 'string'
              ? JSON.parse(i.params)
              : i.params,
            ...(typeof i.defaultDisplay === 'string' && i.defaultDisplay ? {} : (_ddOverride ? { defaultDisplay: _ddOverride } : {})),
            ..._localBackfill,
          };
        });

        // 모든 카테고리 응답에 클라이언트 사이드 사전 인텐션(LOOKUP_TERM_DEFINITION) 자동 노출
        const withLookup = [...apiIntentions, LOOKUP_TERM_INTENTION];
        this.intentionCache[category] = { intentions: withLookup };

        if (TOOL_CALLING_LOG) {
          console.log(`[get_intentions] ${category}: ${apiIntentions.length}개 + LOOKUP_TERM_DEFINITION`);
        }

        return this.buildIntentionsResponse(withLookup);
      }
    }
    catch (e) {}

    return { success: false, message: `Failed to fetch intentions for category "${category}".` };
  }

  buildIntentionsResponse(intentions) {
    // [INTENTIONS PROJECTION D3] endpoint/service는 LLM 페이로드에서 제외 — call_api는 intention 키만 받고 코드가 intentionCache에서 endpoint 해석(토큰 다이어트 -7%). priorEndpoint는 선행 조회 의존 신호라 유지 (dual sync with nodeAgent.mjs) //
    return {
      success: true,
      intentions: intentions.map(i => ({
        intention: i.intentionKey,
        description: i.description,
        creative: i.creative,
        params: i.params,
        availableFields: i.availableFields,
        ...(i.priorEndpoint && { priorEndpoint: i.priorEndpoint }),
        ...(i.visualizationOnly === true && { visualizationOnly: true }),
        ...(typeof i.defaultDisplay === 'string' && i.defaultDisplay && { defaultDisplay: i.defaultDisplay }),
      })),
    };
  }

  // [MULTI-PERIOD AUTO-CHAIN] trigger 검사 — 6개 AND 조건 //
  // 사용자가 "X말고 Y" 패턴으로 직전 멀티슬롯 비교 구조를 새 인텐션에 계승 요청하는 경우,
  // LLM이 단일 슬롯 호출로 처리하는 비결정성을 우회하여 자동으로 동일 period 구조의 chain 실행
  _detectMultiPeriodAutoChain(args, intentionMeta) {
    // (6) per-turn 1회 한정 — 재귀 호출 차단
    if (this._multiPeriodAutoChainFiredThisTurn) return null;

    // (4) LLM이 비교 슬롯을 이미 명시하면 LLM 자체 chain 진행 중 → 우회
    if (args.slot && (args.slot.startsWith('compare_') || args.slot === 'base')) return null;

    // (1) 사용자 input에 "X말고 Y" / "X 대신 Y" / "X 아니고 Y" / "X 아니라 Y" 패턴
    const userInput = this.getUserInput?.() || '';
    if (!/(\S+)\s*(말고|대신|아니고|아니라)\s+(\S+)/.test(userInput)) return null;
    // [운영 5차 rid69] "뿐만 아니라"/"뿐 아니라"(additive="not only X but ALSO")는 전환(X 대신 Y)이 아니라 추가 요청 —
    //   switch regex의 "아니라"에 오탐되어 AUTO-CHAIN(직전 기간 재사용+merge)이 오발동하면, 사용자의 단일 total 질의가
    //   overlapping 다기간 merge(_merged summary 없음)로 변질→finish가 stale 값 재사용(👎 rid69 "회원권만"). additive는 제외.
    //   nodeAgent.mjs와 dual sync.
    if (/뿐\s*만?\s*아니라/.test(userInput)) return null;

    // (5) 새 인텐션이 dateRange params (startDate/endDate) 보유 — period 분할이 의미 있어야
    const paramsSchema = typeof intentionMeta.params === 'string'
      ? JSON.parse(intentionMeta.params || '[]')
      : (intentionMeta.params || []);
    const hasPeriodParams = paramsSchema.some(p => p.name === 'startDate')
      && paramsSchema.some(p => p.name === 'endDate');
    if (!hasPeriodParams) return null;

    // (2) 직전 멀티슬롯 구조 추출 — _merged.items periodStart/End 우선, fallback: 동일 intention buffer pair
    const periods = this._extractPeriodStructure();
    if (!periods || periods.length < 2) return null;

    // (3) 새 인텐션 ≠ 기존 멀티슬롯 source 인텐션 (axis swap 확인)
    //   periods 정보의 sourceIntention과 비교. 동일하면 LLM이 진짜 같은 인텐션 재요청 — auto-chain 불필요
    if (periods[0].sourceIntention && periods[0].sourceIntention === args.intention) return null;

    return { periods };
  }

  // _merged.items 또는 source 슬롯에서 period 구조 추출 //
  _extractPeriodStructure() {
    // Path A: _merged.items에 periodStart/periodEnd가 있는 경우 (SUMMARY-OVERRIDE 결과)
    const merged = this.buffers.get('_merged');
    if (merged?.items?.length >= 2) {
      const periodsA = merged.items
        .filter(it => it.periodStart && it.periodEnd)
        .map(it => ({ start: it.periodStart, end: it.periodEnd }));
      if (periodsA.length >= 2) {
        // sourceIntention 추출: 동일 intention을 가진 다른 slot의 intention
        const sourceIntention = [...this.buffers.entries()]
          .find(([n, b]) => n !== '_merged' && b.intention && b.intention !== 'MERGED' && b.dateRange)?.[1]?.intention || null;
        return periodsA.map(p => ({ ...p, sourceIntention }));
      }
    }

    // Path B: fallback — 동일 intention 보유 buffer 2개+ 있고 distinct dateRange
    const byIntention = {};
    for (const [name, buf] of this.buffers) {
      if (buf.intention === 'MERGED' || !buf.intention) continue;
      if (!buf.dateRange?.startDate || !buf.dateRange?.endDate) continue;
      const key = buf.intention;
      if (!byIntention[key]) byIntention[key] = [];
      byIntention[key].push({ name, start: buf.dateRange.startDate, end: buf.dateRange.endDate });
    }
    for (const [intentionKey, slots] of Object.entries(byIntention)) {
      const distinctRangeSet = new Set(slots.map(s => `${s.start}~${s.end}`));
      if (distinctRangeSet.size >= 2) {
        const periodsB = [...distinctRangeSet].map(r => {
          const [start, end] = r.split('~');
          return { start, end, sourceIntention: intentionKey };
        }).sort((a, b) => a.start.localeCompare(b.start));  // 시간순 (이전 → 최근)
        return periodsB;
      }
    }

    return null;
  }

  // 추출된 periods로 새 인텐션을 동일 슬롯 구조로 chain 실행 //
  async _executeMultiPeriodAutoChain(intention, periods) {
    this._multiPeriodAutoChainFiredThisTurn = true;

    // 이전 비교/merge 슬롯 정리 (새 비교 세션 시작 보장)
    this._comparisonActive = false;
    this._mergeOutputSlot = null;
    this.clearComparisonSlots?.();

    // 슬롯명: 마지막=base, 나머지=compare_1, compare_2, ...
    const slotNames = periods.map((_, i) => i === periods.length - 1 ? 'base' : `compare_${i + 1}`);

    const callResults = [];
    for (let i = 0; i < periods.length; i++) {
      const period = periods[i];
      const slotName = slotNames[i];
      const result = await this.executeCallApi({
        intention,
        startDate: period.start,
        endDate: period.end,
        slot: slotName,
      });
      if (!result.success) {
        return {
          success: false,
          intention,
          message: `[MULTI-PERIOD AUTO-CHAIN] 슬롯 "${slotName}" (${period.start}~${period.end}) 로드 실패: ${result.message}`,
        };
      }
      callResults.push(result);
    }

    const mergeResult = this.mergeSlots({
      slots: slotNames.map(name => ({ slotName: name })),
      mode: 'inner',
      outputSlot: '_merged',
    });

    if (!mergeResult.success) {
      return {
        success: false,
        intention,
        message: `[MULTI-PERIOD AUTO-CHAIN] merge 실패: ${mergeResult.message}`,
      };
    }

    const periodSummary = periods.map(p => `${p.start}~${p.end}`).join(', ');
    const mergedBuf = this.buffers.get('_merged');

    // [PRUNE NON-METRIC FIELDS] auto-chain merged items에서 비교 무관 필드 제거
    // → period info 중복(startDate/endDate vs periodStart/periodEnd), 비-numeric 메타(remainingDays 등) 제거
    // → LLM이 cols=null 또는 raw 필드 요청해도 깔끔한 비교 표 생성
    if (mergedBuf?.items?.length) {
      const KEEP_BASE = new Set(['period', 'periodStart', 'periodEnd', '_label']);
      for (const item of mergedBuf.items) {
        for (const k of Object.keys(item)) {
          if (KEEP_BASE.has(k)) continue;
          if (k.startsWith('_change_') || k.startsWith('_changeRate_')) continue;
          if (typeof item[k] === 'number') continue;  // 숫자 metric 보존
          delete item[k];
        }
      }
      // originalItems도 동일 prune (filter/sort reset 시 일관)
      if (mergedBuf.originalItems?.length) {
        for (const item of mergedBuf.originalItems) {
          for (const k of Object.keys(item)) {
            if (KEEP_BASE.has(k)) continue;
            if (k.startsWith('_change_') || k.startsWith('_changeRate_')) continue;
            if (typeof item[k] === 'number') continue;
            delete item[k];
          }
        }
      }
    }

    const sampleKeys = mergedBuf?.items?.[0]
      ? Object.keys(mergedBuf.items[0])
          .filter(k => !k.startsWith('_change_') && !k.startsWith('_changeRate_') && !k.startsWith('_') && k !== 'periodStart' && k !== 'periodEnd')
      : [];
    const colHint = sampleKeys.length > 0
      ? `\n[REQUIRED columns] show_display 호출 시 정확히 이 컬럼 사용: [${sampleKeys.join(', ')}]. period를 첫 컬럼으로 두고 나머지 지표를 선택. cols=null/생략 금지. 백엔드 raw 인텐션 필드(예: rank, memberShipName)는 stack된 summary items에 존재하지 않으므로 사용 금지.`
      : '';

    this._autoChainRequiresDisplay = true;  // finish 전 show_display 필수 플래그

    return {
      success: true,
      intention,
      slot: '_merged',
      autoChain: true,
      message: `[MULTI-PERIOD AUTO-CHAIN] 사용자 input "X말고 Y" 패턴 감지. 직전 turn 기간 구조(${periodSummary})를 새 인텐션 ${intention}에 자동 적용 — ${slotNames.length}개 슬롯 로드 + merge 완료 (${mergeResult.mergedCount || mergeResult.count || '?'}건).${colHint}\n\n${mergeResult.message || ''}`,
    };
  }

  static clampDateParam(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return dateStr;
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return dateStr;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const lastDay = new Date(year, month, 0).getDate();
    if (day > lastDay) {
      const clamped = `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`;
      if (TOOL_CALLING_LOG) console.log(`[clampDateParam] ${dateStr} → ${clamped} (month ${month} has ${lastDay} days)`);
      return clamped;
    }
    return dateStr;
  }

  // [DEDUP PARAM SIG 2026-06-19] call params 시그니처 — dedup이 startDate/endDate만 비교하고 groupBy 등을 //
  //   무시해 동일 intention+기간을 다른 groupBy로 재호출 시 첫 슬롯 데이터를 잘못 복사하던 버그 차단. //
  //   날짜는 dedup에서 별도 비교하므로 제외. 나머지 params(groupBy 등 응답 form을 바꾸는 인자) 정렬 직렬화. //
  //   특정 key 하드코딩 X — 범용 params diff. dual sync with nodeAgent.mjs. //
  static _callParamsSignature(params) {
    if (!params || typeof params !== 'object') return '';
    return Object.keys(params)
      .filter(k => k !== 'startDate' && k !== 'endDate')
      .sort()
      .map(k => `${k}=${JSON.stringify(params[k])}`)
      .join('&');
  }

  // [L5-CROSS-005 fix 2026-05-29] _resolvePeriod — visualization 도구 공통 기간 해상도 helper
  // (선행 호출자: executeShowMemberStatus/executeShowClassAttendance/executeShowAttendanceRanking/executeShowLtv +
  //  test stub의 executeShowAttendanceChart/executeShowEngagementChart).
  // 입력: { month?: "YYYY-MM", startDate?: "YYYY-MM-DD"|"YYYY-MM-DDTHH:mm:ss", endDate?: 동일 }
  // 우선순위: explicit startDate+endDate > month > 오늘 기준 이번달
  // 출력: { month, startISO, endISO, label }
  // 본 helper 부재 시 show_member_status가 항상 0명 반환 (period?.startISO undefined → classify skip) — false negative
  _resolvePeriod({ month, startDate, endDate } = {}) {
    let startMoment;
    let endMoment;
    let monthStr;
    let label;
    if (startDate && endDate) {
      startMoment = moment(startDate);
      endMoment = moment(endDate);
      monthStr = startMoment.format('YYYY-MM');
      const sameMonth = startMoment.format('YYYY-MM') === endMoment.format('YYYY-MM');
      const isFullMonth = sameMonth
        && startMoment.date() === 1
        && endMoment.date() === endMoment.daysInMonth();
      label = isFullMonth
        ? `${startMoment.format('YYYY-MM')}월`
        : `${startMoment.format('YYYY-MM-DD')} ~ ${endMoment.format('YYYY-MM-DD')}`;
    } else if (month && /^\d{4}-\d{2}$/.test(month)) {
      startMoment = moment(`${month}-01`, 'YYYY-MM-DD').startOf('month');
      endMoment = startMoment.clone().endOf('month');
      monthStr = month;
      label = `${month}월`;
    } else {
      startMoment = moment().startOf('month');
      endMoment = moment().endOf('month');
      monthStr = startMoment.format('YYYY-MM');
      label = `${monthStr}월`;
    }
    return {
      month: monthStr,
      startISO: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
      endISO: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
      label,
    };
  }

  // 인텐션 키로 백엔드 API 호출 //
  async executeCallApi(args) {
    const { intention: _rawIntention, startDate, endDate, params: rawParams, ...rest } = args;
    // [anchor 15] separator normalize — LLM이 underscore variant 호출 시 canonical key로 통일
    //   buffer.intention 일관성 확보 → show_attendance_chart 등 후속 _findBufferByIntention exact-match 정상 동작
    //   INTENTION_DIMENSIONS source에 등록된 key만 normalize (invalid key는 원본 유지 → downstream guard 동작)
    let intention = resolveIntentionKey(_rawIntention) || _rawIntention;
    let params = rawParams || {};

    // LLM이 params 없이 최상위에 파라미터를 풀어놓은 경우 fallback
    if (Object.keys(params).length === 0 && Object.keys(rest).length > 0) {
      params = { ...rest };
    }

    // 클라이언트 사이드 사전 인텐션 가로채기 — 백엔드 호출 X, 클라이언트 자체 응답
    if (intention === 'LOOKUP_TERM_DEFINITION') {
      const term = params.term || rest.term;
      const modifier = params.modifier || rest.modifier;
      const definition = lookupTermDefinition(term, modifier);

      if (!definition) {
        return {
          success: false,
          intention,
          message: `사전에 등록되지 않은 어휘입니다: term="${term}". 등록된 term: ${Object.keys(USER_TERM_DICTIONARY).join(', ')}`,
        };
      }

      const lines = [];
      lines.push(`[USER TERM] "${term}${modifier ? ' ' + modifier : ''}"`);
      lines.push(`의미: ${definition.meaning}`);
      lines.push(`도메인: ${definition.domain}`);
      lines.push(`관련 인텐션: ${definition.relatedIntention}`);
      if (definition.modifier) {
        lines.push(`\n[모디파이어 처리 룰 — ${definition.modifier}]`);
        lines.push(`실행 정의: ${definition.operationalDef}`);
        lines.push(`추천 도구: ${definition.suggestedAction}`);
        lines.push(`비즈니스 맥락: ${definition.businessContext}`);
      }
      if (definition.crossReferenceRule) {
        lines.push(`\n[CROSS-REFERENCE 룰]`);
        lines.push(`패턴: ${definition.crossReferenceRule.pattern}`);
        lines.push(`실행: ${definition.crossReferenceRule.action}`);
      }

      return {
        success: true,
        intention,
        definition,
        message: lines.join('\n') + '\n\n위 룰을 따라 즉시 후속 도구 chain을 실행하세요. 모호 input 직접 해석 금지.',
      };
    }

    let intentionMeta = this.findIntentionMeta(intention);

    if (!intentionMeta) {
      // [P1-2 anchor 6] cache lazy-fill — LLM이 get_intentions 거치지 않고 직접 call_api 시 INTENTION_DIMENSIONS source에서 category 추출 후 자동 prefetch
      //   separator 변형(+/_) normalize 동반. INTENTION_DIMENSIONS에 정의된 key만 lazy-fill — 진짜 invalid key는 여전히 차단
      const resolvedKey = resolveIntentionKey(intention);
      const dims = resolvedKey ? getDimensionsByIntentionKey(resolvedKey) : null;
      if (dims?.category) {
        await this.executeGetIntentions({ category: dims.category });
        intentionMeta = this.findIntentionMeta(resolvedKey);
        if (intentionMeta) intention = resolvedKey;
      }
      // [VIZ-ATTENDANCE-CHART-DAILY-001 fix] backend get_intentions가 visualization 전용 intention을 미반환 시
      //   LOCAL_INTENTIONS_DATA에서 직접 inject — visualization 도구의 buffer source intention이 call_api 가능하도록
      if (!intentionMeta && dims?.category) {
        const localRes = getLocalIntentionsByCategory(dims.category);
        if (localRes?.intentions) {
          const localMatch = localRes.intentions.find(i => i.intentionKey === (resolvedKey || intention));
          if (localMatch) {
            if (this.intentionCache[dims.category]?.intentions) {
              this.intentionCache[dims.category].intentions.push(localMatch);
            }
            else {
              this.intentionCache[dims.category] = { intentions: [localMatch] };
            }
            intentionMeta = localMatch;
            intention = resolvedKey || intention;
            if (TOOL_CALLING_LOG) console.log(`[call_api] LOCAL_INTENTIONS fallback inject: "${intention}" → category=${dims.category}`);
          }
        }
      }
      if (!intentionMeta) {
        const suggestion = this._lastSuggestedIntention ? ` Did you mean "${this._lastSuggestedIntention}"? Copy the exact key from get_intentions.` : ' Call get_intentions first.';
        this._lastSuggestedIntention = null;
        return { success: false, message: `Intention "${intention}" not found.${suggestion}` };
      }
    }

    if (!intentionMeta.endpoint) {
      return { success: false, message: `No endpoint configured for "${intention}".` };
    }

    // [#655 VIZONLY FALLBACK RE-ROUTE] visualizationOnly intention + fallback_intention 정의 시
    //   bare list display 의도(보여줘/알려줘 + list noun)에서 viz tool keyword 부재 → fallback intention 자동 re-route
    //   본질: visualizationOnly buffer는 show_display 차단 + LLM이 finish path로 빠지면 "표가 아니라..." false ack
    //   구조 기반 — intentionMeta.fallback_intention 필드 + user input pattern (intention key hardcode 없음)
    //   dual sync with nodeAgent.mjs
    if (intentionMeta.visualizationOnly === true && intentionMeta.fallback_intention) {
      const __userInput = this.getUserInput?.() || '';
      const LIST_DISPLAY_KW = /보여|알려|확인|리스트|목록|명단|뽑/;
      const VIZ_TOOL_KW = /분류|분석|추이|순위|차트|그래프|통계|LTV|생애.?가치|연령대|engagement|distribution|랭킹|비교|이탈률|유지율|재등록률/i;
      // [L5-CROSS-005] member-status classification keyword → show_member_status path 보존 (HISTORY_LIST→ALIVE_LIST fallback 차단)
      //   "재등록 회원"/"신규 회원"/"이탈 회원" — usages-array overlap 분류 필요. ALIVE_LIST flat fields로 fallback 시 label fabrication.
      //   (2026-07-05 sync 보수: 6d5629f2가 nodeAgent.mjs만 패치한 dual sync 누락 — 운영 반영)
      const MEMBER_STATUS_KW = /(?:신규|재등록|이탈|잔존)\s*(?:회원|가입자|고객)/;
      const isMemberStatusIntent = MEMBER_STATUS_KW.test(__userInput);
      // [VIZONLY COUNT/AXIS EXCLUSION 2026-07-05] 카운트/기간축 질문 제외 — fallback(ALIVE_LIST 스냅샷)은 원 intention과 모수가 달라
      //   수치 질문에 무관 카운트 주입 (실측: "연도별 신규 등록 회원 수 알려줘" → 유효회원 90명 오답, 정답 CHECK_NEW 58명).
      //   COUNT_Q_KW는 show_display [COUNT QUESTION] 가드와 동일 패턴(몇 N/명사+수/how many, duration 부정 lookahead 포함).
      //   기간축(연도별/월별 등)은 현재 스냅샷 fallback으로 시계열 답 불가. dual sync with nodeAgent.mjs
      const COUNT_Q_KW = new RegExp(
        '몇\\s*(?:명|건|개(?!월)|회(?!차|분)|번(?!째|지|호)|가지|군데|곳|마리|장)'
        + '|(?:^|\\s)[가-힣]{2,}\\s+수\\s+(?!있|없)(?:[가-힣?]|얼마)'
        + '|how\\s+many\\s+(?!months?\\b|years?\\b|weeks?\\b|days?\\b|hours?\\b|minutes?\\b)',
        'i'
      );
      const PERIOD_AXIS_KW = /(?:연도|년도|분기|기간|개월|월|주|일|연)별/;
      const isCountOrAxisIntent = COUNT_Q_KW.test(__userInput) || PERIOD_AXIS_KW.test(__userInput);
      const isBareListIntent = LIST_DISPLAY_KW.test(__userInput) && !VIZ_TOOL_KW.test(__userInput) && !isMemberStatusIntent && !isCountOrAxisIntent;
      const isDefaultSlot = !args.slot || args.slot === 'default';
      if (TOOL_CALLING_LOG && isMemberStatusIntent) console.log(`[call_api] VIZONLY FALLBACK SKIPPED (member-status intent: "${__userInput.slice(0, 40)}") — show_member_status path 보존`);
      if (TOOL_CALLING_LOG && isCountOrAxisIntent) console.log(`[call_api] VIZONLY FALLBACK SKIPPED (count/period-axis question: "${__userInput.slice(0, 40)}") — 무관 모수 카운트 주입 차단`);
      if (isBareListIntent && isDefaultSlot && !this._vizOnlyFallbackRerouted) {
        this._vizOnlyFallbackRerouted = true;
        const _fbKey = intentionMeta.fallback_intention;
        if (TOOL_CALLING_LOG) console.log(`[call_api] VIZONLY FALLBACK RE-ROUTE: "${intention}" → "${_fbKey}" (bare list display intent on visualizationOnly intention)`);
        const _fbResult = await this.executeCallApi({ ...args, intention: _fbKey });
        if (_fbResult && typeof _fbResult === 'object') {
          return {
            ..._fbResult,
            message: `[VIZONLY FALLBACK RE-ROUTE] "${intention}"은(는) visualizationOnly buffer source — bare 리스트 표시 의도 감지 → "${_fbKey}"로 자동 re-route (flat field 표 셀 표시 가능). 사용자 응답 intro에는 "표로 정리한 결과는 이거야" 같은 자연어로 시작하세요.\n${_fbResult.message || ''}`,
          };
        }
      }
    }

    this._calledApiThisTurn = true;  // 본 턴 call_api 진입 표식 (merge_slots 우선 차단 해제)

    // [VAGUE MEMBER SEARCH] CHECK_MEMBER_SEARCH 호출 시 params.name이 사람 이름이 아닌 도메인 keyword면 차단
    //   본질: vague *_DETAIL Q ("회원 포인트 상세 알려줘") + LLM이 사람 이름 없이도 prior_endpoint=CHECK_MEMBER_SEARCH 강제 흐름 →
    //         name="회원"/"포인트"/"상세" 같은 domain noun hallucinate → 0건 → "해당 회원을 찾지 못했어" false ack
    //   harness: aiUtils._ENTITY_GUARD_PATTERNS (categoryNoun/descriptor/memberDetailSubject) 정규식 매칭
    //   dual sync with nodeAgent.mjs
    if (intention === 'CHECK_MEMBER_SEARCH') {
      const _vagueReason = detectVagueMemberSearchName(params);
      if (_vagueReason) {
        if (TOOL_CALLING_LOG) console.log(`[call_api] VAGUE MEMBER SEARCH blocked: name="${params.name}" → ${_vagueReason}`);
        // [DEICTIC BUFFER BIND 2026-07-05] 회원 deictic("이/그/해당 회원") + 직전 buffer에 회원 items 보유 시
        //   generic 되묻기 대신 buffer roster 제시 — "이 회원이 가지고 있는 회원권 정보"에서 name="회원이" 차단 후
        //   "해당 회원을 찾지 못했어" false ack로 빠지던 것(L9b deictic 멀티버퍼 2/3)을 buffer 회원 바인딩으로 회복.
        //   회원(?!권) 경계 — "해당 회원권"(entity deictic, L9b-CROSS-010)은 회원 deictic 아님. 9명 이상은 개별 조회
        //   비경제라 기존 되묻기 유지. dual sync with nodeAgent.mjs
        const _dUserInput = this.getUserInput?.() || '';
        const _MEMBER_DEICTIC_RE = /(?:^|[\s,])(?:이|그|해당|위|저|방금)\s*회원(?!권)/;
        const _dBuf = this.getBuffer?.();
        const _dMembers = (Array.isArray(_dBuf?.items) ? _dBuf.items : [])
          .filter(it => it && typeof it === 'object' && typeof it.name === 'string' && it.name.trim());
        if (_MEMBER_DEICTIC_RE.test(_dUserInput) && _dMembers.length > 0 && _dMembers.length <= 8) {
          const _roster = _dMembers.map(it => {
            const _mid = it.memberId ?? it.userId ?? null;
            return _mid != null ? `${it.name}(memberId=${_mid})` : it.name;
          }).join(', ');
          return {
            success: false,
            intention,
            message: `[VAGUE MEMBER SEARCH BLOCKED — DEICTIC BUFFER BIND] params.name="${params.name}"은(는) 회원 이름이 아니라 직전 조회 결과를 가리키는 지시어 해석입니다 (${_vagueReason}). name 재검색 금지.\n\n직전 buffer 회원 ${_dMembers.length}명: ${_roster}\n\nNEXT STEP — 이 회원들이 질문의 대상입니다:\n(1) 전원 대상 의도(다/전부/각각/모두/'회원들')면 각 회원별 후속 조회 후 통합 표출 — memberId가 표기된 경우 subject DETAIL intention(예: CHECK_MEMBER+MEMBERSHIP_DETAIL)의 params.memberId로 직접 조회, 없으면 CHECK_MEMBER_SEARCH(name="<위 실명 그대로>") → DETAIL 순서. 회원별 slot 분리(예: m1, m2...) 후 show_display로 통합.\n(2) 단수 1명 의도인데 어느 회원인지 불명확하면 finish로 되묻기: "위 ${_dMembers.length}명 중 어느 회원인가요? 전원 조회도 가능해요."\n\n"해당 회원을 찾지 못했어" 같은 false ack 금지 — 위 회원들은 실재하는 직전 조회 결과입니다.`,
          };
        }
        return {
          success: false,
          intention,
          message: `[VAGUE MEMBER SEARCH BLOCKED] params.name="${params.name}"은(는) 회원 이름이 아니라 도메인 keyword (${_vagueReason})입니다. CHECK_MEMBER_SEARCH는 user input 의 한국어 사람 이름(2-4 음절 한글) 또는 전화번호(010-XXXX-XXXX)가 명시되었을 때만 호출하세요.\n\n사용자 input에 사람 이름/전화번호가 없다면:\n(1) finish로 자연어 안내: "어느 회원의 X를 확인해드릴까요? 회원명이나 전화번호를 알려주세요." (X = user input subject — 포인트/홀딩/연장/환불/락커/카드/출석/회원권 등)\n(2) 또는 store-wide list 의도 가능성 — get_intentions(MEMBER)로 CHECK_MEMBER+<SUBJECT>_LIST (예: POINT_LIST) 같은 list 버전을 확인 후 redirect (subject이 POINT/MEMBERSHIP 등 list 변형 보유 시).\n\nshow_display 호출 금지. CHECK_MEMBER_SEARCH 재호출 시에도 사람 이름이 input에 없으면 차단됩니다.`,
        };
      }
    }

    // [AUTO USER TERM PROTECTION] 본 turn에 AUTO USER TERM이 동일 intention으로 filtered buffer를 만들었으면
    //   LLM의 동일 intention 재호출 차단 — 재호출 시 buffer 덮어쓰여 filter 해제 → HOWTO_GOT_DATA (intro는 "X보다 낮은" 약속하지만 data는 raw 전체)
    //   조건: (a) _autoUserTermAppliedThisTurn 존재 (b) intention 매칭 (c) slot이 default (cross-merge 등 다른 slot 호출은 통과)
    //         (d) params에 startDate/endDate 추가 변경 없음 — 시간 범위 변경 의도는 통과 (사용자가 다른 기간 명시했을 때만)
    //   dual sync with nodeAgent.mjs
    if (this._autoUserTermAppliedThisTurn
        && this._autoUserTermAppliedThisTurn.intention === intention
        && (!args.slot || args.slot === 'default')
        && !startDate && !endDate) {
      const _autoCtx = this._autoUserTermAppliedThisTurn;
      if (TOOL_CALLING_LOG) console.log(`[call_api] AUTO USER TERM PROTECTION: blocked re-call of "${intention}" — buffer already filtered (${_autoCtx.before}→${_autoCtx.after}건 ${_autoCtx.field} ${_autoCtx.modifier === '낮은' ? '<=' : '>='} ${_autoCtx.threshold})`);
      return {
        success: false,
        intention,
        message: `[AUTO USER TERM PROTECTION] 본 turn에 "${_autoCtx.term} ${_autoCtx.modifier}" 자동 처리로 동일 intention("${intention}")이 이미 호출되었고 buffer가 filter 적용 상태로 준비됨 (${_autoCtx.field} ${_autoCtx.modifier === '낮은' ? '<=' : '>='} ${_autoCtx.threshold}(평균) → ${_autoCtx.after}건 적재). call_api 재호출 시 buffer가 raw 데이터로 덮어쓰여 filter가 해제되고, intro에 "전체 평균보다 낮은/높은" 등 criterion을 약속해도 실제 data는 전체이므로 사용자가 불일치를 느낍니다. \n\nNEXT STEP: 추가 call_api 호출 금지. 현재 active buffer를 그대로 사용해 show_display + finish를 호출하세요. intro에는 실제 buffer 상태 (${_autoCtx.after}건, ${_autoCtx.modifier === '낮은' ? '평균 이하' : '평균 이상'})를 반영. column은 buffer의 actual item 키만 사용.`,
      };
    }

    // [운영 5차 rid162] top-level startDate/endDate를 normalize 이전에 params로 병합 —
    //   normalizeExpiringDateRange가 실제 날짜(same-day vs range)를 보고 판정하도록. 미병합 시 params에 dates 부재로
    //   MISSING 분기가 today~+14d를 채워, LLM의 명시 기간(이번달/3월/오늘만)을 소실시킴(하단 var→params 병합은 그때 no-op).
    //   이후 clampDateParam 병합(하단)은 이미 set된 값 유지 → 무영향. dual sync — nodeAgent.mjs와 동일.
    if (startDate != null && params.startDate == null) params.startDate = startDate;
    if (endDate != null && params.endDate == null) params.endDate = endDate;

    // [1-U] call_api args 한국어 단위 suffix 정규화 — backend strict match 인텐션의 param에서
    //   LLM이 user input의 "번/호/번호/호실"을 pass-through 시 매칭 실패 차단 (CALL_API_ARG_NORMALIZE 등록 intention만)
    normalizeCallApiArgs(intention, params, this.getUserInput?.() || '');

    // [L13-API-ERROR-003] history-only intention(SALES 등) + 미래 date 차단 — backend 호출 skip + finish() 안내 강제
    //   본질: 미래 기간 매출/통계 호출 시 backend 0건 or catalog fallback 응답 → "총 1건 + zero row + D-N" /
    //         "12,500,000원" 같은 fabricated 숫자 노출 → 사용자 의도 mismatch (INTENT_DRIFT).
    //   per (intention, slot) 1회 차단 — 같은 turn LLM이 다른 intention 시도 시 본 guard 재진입 무한 회전 차단.
    //   dual sync with nodeAgent.mjs.
    if (!this._futureDateBlocked) this._futureDateBlocked = new Set();
    const _fdDims = getDimensionsByIntentionKey(intention) || null;
    const _fdEval = evaluateFutureDateGuard({ intentionMeta, intentionCategory: _fdDims?.category, params, startDate, endDate });
    if (_fdEval?.block) {
      const _fdKey = `${intention}::${args.slot || '-'}`;
      if (!this._futureDateBlocked.has(_fdKey)) {
        this._futureDateBlocked.add(_fdKey);
        if (TOOL_CALLING_LOG) console.log(`[call_api] FUTURE DATE BLOCKED: ${intention} — ${_fdEval.reason}`);
        return {
          success: false,
          intention,
          message: formatFutureDateBlockedMessage({ intention }, _fdEval),
        };
      }
    }

    // [MULTI-PERIOD AUTO-CHAIN] "X말고 Y" + 직전 멀티슬롯 구조 → Y 인텐션을 동일 period 구조로 자동 chain (LLM bypass)
    const autoChain = this._detectMultiPeriodAutoChain(args, intentionMeta);
    if (autoChain) {
      return await this._executeMultiPeriodAutoChain(intention, autoChain.periods);
    }

    // [NO-TIME-DIMENSION COMPARISON GUARD] 시간 차원 없는 intention (params=null 또는 startDate/endDate 미보유) + multi-period 비교 의도 (N월 enumeration ≥2) → 단일 호출로 충분 안내 + 추가 비교 slot 호출 차단
    // 본질: PARTNER_GYM / LOCKER / COACH / SYSTEM_SETTING / MEMBERSHIP_CATALOG 등 정적 마스터 데이터는 월별/기간별 변동이 없는데, 사용자가 "X월 Y월 Z월 비교" 입력 시 LLM이 compare_1/compare_2/base 슬롯에 동일 데이터를 3번 호출 → 모순된 finalText ("3월 4월 5월 목록은 모두 같아" 등)
    // generic: intentionMeta.params 구조 (startDate/endDate 부재) + _extractMonthSet(userInput) 결과 기반 — 13개 no-time intention 일괄 cover
    {
      const _userInputForNoTime = this.getUserInput?.() || '';
      const _inputMonths = this._extractMonthSet(_userInputForNoTime);
      if (_inputMonths.size >= 2) {
        const _paramsSchema = typeof intentionMeta.params === 'string'
          ? (() => { try { return JSON.parse(intentionMeta.params || '[]'); } catch { return []; } })()
          : (Array.isArray(intentionMeta.params) ? intentionMeta.params : []);
        const _hasTimeParams = _paramsSchema.some(p => p && (p.name === 'startDate' || p.name === 'endDate' || p.name === 'searchYear' || p.name === 'searchMonth' || p.name === 'startMonth' || p.name === 'endMonth'));
        if (!_hasTimeParams) {
          // 동일 intention이 이미 buffer에 적재됐는지 확인
          const _existingSameIntentionSlots = [...this.buffers.entries()]
            .filter(([n, b]) => b.intention === intention && n !== '_merged')
            .map(([n]) => n);
          // 두 번째+ 호출 시 BLOCK (첫 호출은 hint와 함께 통과)
          if (_existingSameIntentionSlots.length >= 1) {
            const _monthsSorted = [..._inputMonths].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            if (TOOL_CALLING_LOG) console.log(`[call_api] NO-TIME-DIMENSION COMPARISON BLOCK: "${intention}" 두번째 호출 차단 — 시간 차원 없는 intention에 ${_inputMonths.size}개월 비교 의도. 단일 호출로 충분.`);
            return {
              success: false,
              intention,
              message: `[NO-TIME-DIMENSION COMPARISON BLOCK] "${intention}"은 시간/기간 파라미터가 없는 정적 마스터 데이터입니다 (params=null). 사용자가 ${_monthsSorted.join('/')}(${_inputMonths.size}개월) 비교를 요청했지만 본 intention의 데이터는 월/기간에 따라 달라지지 않습니다. 추가 call_api 호출 금지.\n\nNEXT STEP: 이미 적재된 slot "${_existingSameIntentionSlots[0]}" buffer를 그대로 사용해 show_display 또는 finish 호출. finalText/outro에는 다음 핵심 내용을 자연어로 안내:\n  1. 본 정보는 월/기간별 변동이 없는 정적(고정) 정보임을 명시\n  2. ${_monthsSorted.join('/')} 모두 동일하게 적용되는 현재 등록된 정보를 1회 제시\n  3. "월별로 다를 수 있다"는 잘못된 전제를 노출하지 말 것 (예: "모두 같아" / "비교 결과가 없어" 표현 금지)`,
            };
          }
          // 첫 호출 — 통과시키되 message 후처리로 hint 추가 (실제 응답 데이터 후 _hint_no_time_dim 플래그)
          this._noTimeDimensionHint = {
            intention,
            inputMonths: [..._inputMonths].sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
          };
        }
      }
    }

    // [1-A] MEMBER STATUS GUIDANCE multi-period 분기 부재 — wrong intention 자동 치환
    // aiPrompts.js:441이 "신규/재등록/이탈" keyword를 HISTORY_LIST + show_member_status sequence로 라우팅하지만
    // classifyMemberStatus는 single-period only. multi-period 입력 시 의도된 LIST_BY+PERIOD/STATISTICS_BY+PERIOD intention으로 치환.
    if (!this._mpmsAutoSwapped) this._mpmsAutoSwapped = new Set();
    const _mpmsHit = detectMultiPeriodMemberStatus({
      userInput: this.getUserInput?.() || '',
      intentionKey: intention,
    });
    if (_mpmsHit) {
      const _mpmsKey = `${intention}::${args.slot || '-'}`;
      if (!this._mpmsAutoSwapped.has(_mpmsKey)) {
        this._mpmsAutoSwapped.add(_mpmsKey);
        const _mpmsRecArgs = { ...args, intention: _mpmsHit.replacementIntention };
        const _mpmsRecResult = await this.executeCallApi(_mpmsRecArgs);
        const _mpmsPrefix = formatMpmsReplacement({
          originalIntention: intention,
          replacementIntention: _mpmsHit.replacementIntention,
          matchedKeyword: _mpmsHit.matchedKeyword,
          matchedLabel: _mpmsHit.matchedLabel,
          monthCount: _mpmsHit.monthCount,
        });
        if (_mpmsRecResult && typeof _mpmsRecResult === 'object') {
          return {
            ..._mpmsRecResult,
            intention: _mpmsHit.replacementIntention,
            message: `${_mpmsPrefix}\n${_mpmsRecResult.message || ''}`,
          };
        }
      }
    }

    // [rid188 NEW-MEMBER MISROUTE] single-period "이번 달 신규 회원" 요청인데 ALIVE_LIST(유효 전체)로 오라우팅 → CHECK_NEW 자동 치환
    // 운영 rid188 run2/5: "신규 회원"인데 ALIVE_LIST 92명을 "신규 92명"으로 거짓 라벨. multi-period 가드(위)는 N월 2+만 커버 → single-period 차단. dual sync with nodeAgent.mjs
    if (!this._newMisrouteAutoSwapped) this._newMisrouteAutoSwapped = new Set();
    const _nmrHit = detectNewMemberMisroute({
      userInput: this.getUserInput?.() || '',
      intentionKey: intention,
    });
    if (_nmrHit) {
      const _nmrKey = `${intention}::${args.slot || '-'}`;
      if (!this._newMisrouteAutoSwapped.has(_nmrKey)) {
        this._newMisrouteAutoSwapped.add(_nmrKey);
        const _nmrRecArgs = { ...args, intention: _nmrHit.replacementIntention };
        const _nmrRecResult = await this.executeCallApi(_nmrRecArgs);
        const _nmrPrefix = formatNewMemberMisrouteMessage({
          originalIntention: intention,
          replacementIntention: _nmrHit.replacementIntention,
          matchedKeyword: _nmrHit.matchedKeyword,
        });
        if (_nmrRecResult && typeof _nmrRecResult === 'object') {
          return {
            ..._nmrRecResult,
            intention: _nmrHit.replacementIntention,
            message: `${_nmrPrefix}\n${_nmrRecResult.message || ''}`,
          };
        }
      }
    }

    // [L5-MEMBERSHIP-TOPATTEND-001 cycle 2] ATTENDANCE RANKING GUIDANCE multi-period 분기 부재 — visualizationOnly MONTHLY → non-MONTHLY variant 자동 치환
    // aiPrompts.js [ATTENDANCE RANKING GUIDANCE]가 "출석 상위/순위/랭킹/많은" keyword를 CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD + show_attendance_ranking sequence로 라우팅하지만
    // MONTHLY variant는 visualizationOnly:true — multi-period merge_slots + show_display 시 [VISUALIZATION-ONLY REDIRECT] guard 발동 → finish abandon.
    // 본 가드: multi-period (2+) + ranking keyword + comparison verb 만족 시 non-MONTHLY 변형(per-row attendedCount/attendanceRate 보유) 자동 치환 → merge → show_display 정상.
    if (!this._mparAutoSwapped) this._mparAutoSwapped = new Set();
    const _mparHit = detectMultiPeriodAttendanceRanking({
      userInput: this.getUserInput?.() || '',
      intentionKey: intention,
    });
    if (_mparHit) {
      const _mparKey = `${intention}::${args.slot || '-'}`;
      if (!this._mparAutoSwapped.has(_mparKey)) {
        this._mparAutoSwapped.add(_mparKey);
        const _mparRecArgs = { ...args, intention: _mparHit.replacementIntention };
        const _mparRecResult = await this.executeCallApi(_mparRecArgs);
        const _mparPrefix = formatMparReplacement({
          originalIntention: intention,
          replacementIntention: _mparHit.replacementIntention,
          matchedKeyword: _mparHit.matchedKeyword,
          matchedVerb: _mparHit.matchedVerb,
          monthCount: _mparHit.monthCount,
        });
        if (_mparRecResult && typeof _mparRecResult === 'object') {
          return {
            ..._mparRecResult,
            intention: _mparHit.replacementIntention,
            message: `${_mparPrefix}\n${_mparRecResult.message || ''}`,
          };
        }
      }
    }

    // [YEARLY SALES REDIRECT — 은퇴 2026-07-05] mini 월간/기간 intention 오선택 보상 게이트였음. 5.4 YEAR_STATISTICS 자력 선택 5/5로 제거 — 재추가 전 은퇴 심사 기록 확인 (dual sync with nodeAgent.mjs) //

    // [FINANCE STATEMENT REDIRECT] "재무제표/재무/손익/재정"(오타 제무 포함) + SALES intention → CHECK_SALES+MONTHLY+TREND (매출 흐름 차트)
    // "재무제표"는 정식 인텐션 부재 — LLM이 YEAR/AVERAGE로 stochastic 분산. 월별 추이로 coerce → defaultDisplay=show_revenue_chart 결정적 라우팅.
    // Skip if explicit table/list (표로/리스트/목록/내역) — 표 요청은 차트 아님. dual sync with nodeAgent.mjs.
    {
      const _fsInput = this.getUserInput?.() || '';
      if (/재무|제무|손익|재정/.test(_fsInput) && !/표로|리스트|목록|내역/.test(_fsInput)) {
        const FINANCE_TREND_INTENTION = 'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD';
        if (intention !== FINANCE_TREND_INTENTION && intention.startsWith('CHECK_SALES')) {
          const _ftMeta = this.findIntentionMeta(FINANCE_TREND_INTENTION);
          if (_ftMeta) {
            console.log(`[call_api] FINANCE STATEMENT REDIRECT: "${intention}" → "${FINANCE_TREND_INTENTION}" (input has 재무/제무/손익/재정)`);
            intention = FINANCE_TREND_INTENTION;
            intentionMeta = _ftMeta;
            // Derive startMonth/endMonth (YYYY-MM): startDate 있으면 그대로, 없으면(YEAR-origin searchYear만) 입력 월범위 결정적 파싱
            if (!params.startMonth || !params.endMonth) {
              if (startDate) {
                params.startMonth = params.startMonth || startDate.slice(0, 7);
                params.endMonth = params.endMonth || (endDate || startDate).slice(0, 7);
              } else {
                const _yr = (params.searchYear || `${new Date().getFullYear()}`).slice(0, 4);
                const _mr = parseMonthRangeFromInput(_fsInput, _yr);
                if (_mr) { params.startMonth = _mr.startMonth; params.endMonth = _mr.endMonth; }
              }
            }
          }
        }
      }
    }

    // [RESERVATION ROSTER REDIRECT] 운영 5차 rid193~196: "예약자 명단" 의도인데 LLM이 reserves 없는 수업 시간표
    //   intention(LESSON_LIST/CATALOG)을 오선택 → 예약자 명단 intention(RESERVATION+STATUS_LIST, reserves[] 보유)으로 결정 치환.
    //   gate 2겹: (1) "예약" + roster-noun(회원|명단|누구|누가|예약자) — "예약" 단독은 "필라테스 예약 상세" 같은
    //   lesson-specific 질의까지 hijack(전체 예약을 특정수업으로 mislabel) → roster-noun이 roster-의도 vs lesson-상세를 가름.
    //   (2) _isTimetable — 정상 "수업 일정"(roster-noun 없음)·RESERVATION 정상 선택을 보호. dual sync with nodeAgent.mjs.
    {
      const _rosterInput = this.getUserInput?.() || '';
      const _isTimetable = intention === 'CHECK_SCHEDULE+LESSON_LIST_BY+PERIOD'
                        || intention === 'CHECK_SCHEDULE+CATALOG_LIST';
      if (/예약/.test(_rosterInput) && /회원|명단|누구|누가|예약자/.test(_rosterInput) && _isTimetable) {
        const ROSTER_INTENTION = 'CHECK_SCHEDULE+RESERVATION+STATUS_LIST_BY+PERIOD';
        const _rosterMeta = this.findIntentionMeta(ROSTER_INTENTION);
        if (_rosterMeta) {
          if (TOOL_CALLING_LOG) console.log(`[call_api] RESERVATION ROSTER REDIRECT: "${intention}" → "${ROSTER_INTENTION}" (input has 예약, timetable intention 오선택)`);
          intention = ROSTER_INTENTION;
          intentionMeta = _rosterMeta;
        }
      }
    }

    // [VAGUE DETAIL INTENT] DETAIL intention 회원명 미지정 시 LIST fallback / 0건 응답 차단 → finish 되묻기 강제
    //   cluster 일관성: CARD/POINT/PRODUCT/REFUND DETAIL은 VAGUE_MEMBER_SEARCH guard 로 finish 되묻기. HOLDING_DETAIL 만 LIST fallback 이상치.
    //   generic guard — userInput에 DETAIL keyword + subject + 회원명 부재 시 success:false + nudge
    //   CATALOG_LIST fallback (SYN-CHECK_MEMBER+MEMBERSHIP_DETAIL-3) — "회원권 상세 보여줘" 같이 catalog vs member-detail 모호 input catch
    //   _finishAllowedByGuard set — guard 후 LLM 자연어 응답을 finish 로 인정 (text-only nudge 무한 회전 차단)
    //   dual sync with nodeAgent.mjs
    {
      const _vagueDetailReason = detectVagueDetailIntent(this.getUserInput?.() || '', intention);
      if (_vagueDetailReason) {
        if (TOOL_CALLING_LOG) console.log(`[call_api] VAGUE DETAIL INTENT blocked: intention="${intention}" → ${_vagueDetailReason}`);
        // CATALOG_LIST path 는 catalog vs member-detail 두 옵션 모두 제시 — LLM 이 사용자 의도 명시 받도록 nudge
        const _isCatalogFallbackPath = /^CHECK_MEMBERSHIP\+CATALOG_LIST$/.test(intention);
        const _catalogMessage = `[VAGUE DETAIL INTENT BLOCKED] ${_vagueDetailReason}. 본 intention("${intention}")은 회원권 종류 catalog 입니다. 사용자가 "상세" keyword 를 명시했지만 "종류/어떤/목록" 같은 catalog 지표 keyword 도, 회원명/전화번호도 없어 catalog vs member-detail 의도 모호합니다.\n\nNEXT STEP: 추가 tool 호출 금지. finish로 자연어 되묻기 응답:\n  예: "특정 회원의 회원권 상세인가요, 회원권 종류 목록인가요? 회원명을 알려주시거나 '회원권 종류'/'회원권 목록' 으로 다시 말씀해주세요."\n  사용자가 회원명을 답하면 CHECK_MEMBER+MEMBERSHIP_DETAIL 로, catalog 의도를 명시하면 본 intention 재호출.\n\nshow_display 호출 금지. catalog 데이터 그대로 표시 시 사용자가 회원 상세를 기대했다면 mismatch 발생.`;
        const _detailMessage = `[VAGUE DETAIL INTENT BLOCKED] ${_vagueDetailReason}. 본 intention("${intention}")은 개별 회원 상세 정보로 회원명/전화번호가 명시되어야 호출 가능합니다.\n\nNEXT STEP: 추가 tool 호출 금지. finish로 자연어 되묻기 응답:\n  예: "어느 회원의 홀딩(혹은 연장/환불/카드/포인트/락커/출석/회원권) 이력을 확인해드릴까요? 회원명이나 전화번호를 알려주세요."\n  사용자가 추정 가능한 subject 단어를 input 그대로 활용.\n\nshow_display 호출 금지. LIST intention 으로 fallback 금지 — 전체 명단 표시는 사용자가 원하는 개별 상세와 다른 응답이라 mismatch 발생.`;
        // LLM 자연어 응답을 finish 로 인정 — guard 후 text-only stop path 가 nudge 무한 회전 없이 즉시 통과
        this._finishAllowedByGuard = true;
        this._finishGuardSuggestedText = null;  // LLM 자율 작성
        return {
          success: false,
          intention,
          message: _isCatalogFallbackPath ? _catalogMessage : _detailMessage,
        };
      }
    }

    // [L1-MEMBER_DETAIL-006] HOLDING LIST → DETAIL REDIRECT guard
    // Problem: user asks "{name} holding history" but LLM picks CHECK_MEMBER+HOLDING_LIST (all holding members)
    // instead of CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL (individual member holding detail, requires memberId).
    // HOLDING_LIST returns all holding members without filtering, so LLM falsely attributes other members' data
    // to the queried member (Cat4-FALSE_ACK hallucination).
    // Guard: detect Korean name (2-4 char) in user input + HOLDING_LIST intention → internal MEMBER_SEARCH
    // → if exactly 1 match, redirect to HOLDING_DETAIL with memberId. If 0 or >1 (Korean: 동명이인), abort redirect.
    if (intention === 'CHECK_MEMBER+HOLDING_LIST') {
      const _holdingUserInput = this.getUserInput?.() || '';
      const _holdingCandidates = [
        ...(_holdingUserInput.match(/[가-힣]{2,4}/g) || []),
        ...(_holdingUserInput.match(/[A-Za-z]{2,20}/g) || []),
      ];

      let _holdingMemberName = null;
      let _holdingMemberId = null;

      if (_holdingCandidates.length > 0) {
        if (!this.findIntentionMeta('CHECK_MEMBER_SEARCH')) {
          await this.executeGetIntentions({ category: 'MEMBER' });
        }

        for (const _candidate of _holdingCandidates) {
          const _holdingSearchResult = await this.executeCallApi({
            intention: 'CHECK_MEMBER_SEARCH',
            params: { name: _candidate },
            slot: '_holding_search',
          });

          if (_holdingSearchResult?.success && _holdingSearchResult?.count === 1) {
            const _holdingSearchBuf = this.buffers.get('_holding_search');
            _holdingMemberName = _candidate;
            _holdingMemberId = _holdingSearchBuf?.items?.[0]?.memberId;
            this.buffers.delete('_holding_search');
            break;
          }

          if (_holdingSearchResult?.count > 1) {
            this.buffers.delete('_holding_search');
            if (TOOL_CALLING_LOG) console.log(`[call_api] HOLDING guard: "${_candidate}" ${_holdingSearchResult.count}명 동명이인 — skip`);
            return {
              success: false,
              error: `[HOLDING_LIST BLOCKED] "${_candidate}" 이름이 ${_holdingSearchResult.count}명 있습니다. CHECK_MEMBER_SEARCH를 먼저 호출하여 회원을 특정한 뒤 다시 시도하세요.`,
              count: 0
            };
          }

          this.buffers.delete('_holding_search');
        }
      }

      if (_holdingMemberName && _holdingMemberId) {
        if (TOOL_CALLING_LOG) console.log(`[call_api] HOLDING LIST→DETAIL guard: verified member "${_holdingMemberName}" (id=${_holdingMemberId})`);

        if (!this.findIntentionMeta('CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL')) {
          await this.executeGetIntentions({ category: 'MEMBER_DETAIL' });
        }

        const _holdingDetailMeta = this.findIntentionMeta('CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL');

        if (_holdingDetailMeta) {
          console.log(`[call_api] HOLDING LIST→DETAIL REDIRECT: "${intention}" → "CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL" (memberId=${_holdingMemberId}, name="${_holdingMemberName}")`);
          intention = 'CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL';
          intentionMeta = _holdingDetailMeta;
          params.memberId = _holdingMemberId;
          this._lastMember = { memberId: _holdingMemberId, name: _holdingMemberName };
        }
      }
    }

    // [L10-DETAIL-SCHEDULE-001] CHAIN CONTEXT MEMBER_DETAIL REDIRECT guard
    // Problem: chain T1+ user input is a bare topic keyword ("출석 현황", "수업 참여", etc.) with NO new
    // member name. T0 left the buffer in MEMBER_DETAIL state with a memberId in _lastMember. LLM picks
    // a generic LIST intention (e.g. CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD) returning ALL members,
    // losing the chain's individual-member context (Cat4-CONTEXT_BREAK).
    // Guard: registry-based mapping from generic LIST intentions to their MEMBER_DETAIL counterparts.
    //   Trigger ALL of: (a) intention key matches MEMBER_DETAIL_REDIRECT_MAP entry
    //                   (b) this._lastMember.memberId exists (T0 picked a specific member)
    //                   (c) active or any persistent buffer's intention is MEMBER_DETAIL category
    //                   (d) user input has NO new Korean/Latin member-name candidate (bare keyword)
    //   On trigger: swap intention to *_DETAIL counterpart + inject memberId from _lastMember.
    // dual sync with nodeAgent.mjs
    // [CHAIN_CONTEXT_DATE_RESET flag — F6 cycle 5 NEW approach]
    // Set by MEMBER_DETAIL chain redirect below when user input has no explicit time keyword.
    // Consumed by the subsequent `if (startDate) params.startDate = ...` block to skip
    // re-injecting LLM-passed narrow dates (DETAIL scope must default to full backend window).
    let _chainContextDateReset = false;
    // [L10-DETAIL-SCHEDULE-001 CHAIN_CONTEXT_MULTI_MATCH_BLOCK] F6 cycle 5 edge case
    // F6 단일 매치 redirect 발동 조건: _lastMember.memberId 보유. 단 직전 MEMBER_SEARCH가 동명이인 2명+ 반환했고
    //   _lastMember 미저장 (다중 매치 시 1명 picking 불가) + bare keyword 입력 (새 회원명 없음) +
    //   redirect-target intention (whole-roster fallback 위험)이면 BLOCK.
    // 사용자 의도: 동명이인 중 한 명 detail. 시스템: 어떤 회원인지 알 수 없음 → 자연어 되묻기 필요.
    // _lastMemberSearchMultiMatch 존재 자체가 직전 chain MEMBER_SEARCH 발생 증거 — buffer category 확인 불필요
    //   (CHECK_MEMBER_SEARCH는 MEMBER category이며 MEMBER_DETAIL 아님 → _isChainContextMemberDetail() false)
    // 구조 기반 (registry intention + multi-match state + no new name) — case-ID hardcode 없음.
    if (MEMBER_DETAIL_REDIRECT_MAP[intention]
        && !this._lastMember?.memberId
        && this._lastMemberSearchMultiMatch
        && !this._hasNewMemberNameInInput()) {
      const _mm = this._lastMemberSearchMultiMatch;
      const _candidateNames = (_mm.candidates || []).map(c => c.name).filter(Boolean);
      const _shownNames = _candidateNames.slice(0, 3).join(', ');
      const _moreCnt = Math.max(0, _mm.count - 3);
      const _suffix = _moreCnt > 0 ? ` 외 ${_moreCnt}명` : '';
      if (TOOL_CALLING_LOG) console.log(`[call_api] CHAIN_CONTEXT_MULTI_MATCH_BLOCK: bare keyword "${(this.getUserInput?.()||'').slice(0,30)}" + 동명이인 ${_mm.count}명 (${_mm.name}) → call_api 차단, finish() 되묻기 강제`);
      return {
        success: false,
        intention,
        message: `[CHAIN_CONTEXT_MULTI_MATCH_BLOCK] 직전 회원 검색에서 "${_mm.name}" 동명이인 ${_mm.count}명이 발견되어 어느 회원의 정보인지 시스템이 특정할 수 없습니다. 사용자 입력("${(this.getUserInput?.()||'').slice(0,30)}")에는 새 회원명/전화번호가 없어 chain context로도 disambiguation 불가입니다.\n\nNEXT STEP: 추가 tool 호출 금지. finish로 자연어 되묻기:\n  예: "${_mm.name} 회원이 ${_mm.count}명이에요 (${_shownNames}${_suffix}). 어느 회원의 정보가 필요하신가요? 전화번호나 추가 정보로 알려주세요."\n\n사용자가 회원을 특정(이름+전화번호 등)하면 CHECK_MEMBER_SEARCH 재호출, 또는 전체 회원 list 의도면 사용자 명시 후 진행하세요. show_display 호출 금지 (whole-roster 데이터 그대로 노출 시 사용자가 의도하지 않은 결과 표시).`,
      };
    }

    if (MEMBER_DETAIL_REDIRECT_MAP[intention]
        && this._lastMember?.memberId
        && this._isChainContextMemberDetail()
        && !this._hasNewMemberNameInInput()) {
      const _detailIntention = MEMBER_DETAIL_REDIRECT_MAP[intention];
      if (!this.findIntentionMeta(_detailIntention)) {
        await this.executeGetIntentions({ category: 'MEMBER_DETAIL' });
      }
      const _detailMeta = this.findIntentionMeta(_detailIntention);
      if (_detailMeta) {
        console.log(`[call_api] CHAIN CONTEXT MEMBER_DETAIL REDIRECT: "${intention}" → "${_detailIntention}" (memberId=${this._lastMember.memberId}, name="${this._lastMember.name}")`);
        intention = _detailIntention;
        intentionMeta = _detailMeta;
        params.memberId = this._lastMember.memberId;
        // [CHAIN_CONTEXT_DATE_RESET — F6 cycle 5 NEW approach]
        // User input is a bare topic keyword (no new member name) — _hasNewMemberNameInInput() is false here.
        // If input ALSO has no explicit time keyword, LLM-hallucinated narrow dates (e.g. today-only) break
        // DETAIL scope ("출석 현황" → 0 records when LLM sets startDate=endDate=today).
        // Drop LLM-passed dates (both params.* and top-level via flag) so backend default window applies.
        // structural — reuses INTENTION DEFAULT WINDOW GUARD's _TIME_KW_EXPLICIT pattern; no case-ID hardcode.
        const _ui_chain = this.getUserInput?.() || '';
        const _TIME_KW_CHAIN = /오늘|어제|내일|모레|지난\s*주|이번\s*주|다음\s*주|금주|지난\s*달|이번\s*달|다음\s*달|금월|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+\s*일\s*전|작년|올해|금년|내년|지난해|당월/;
        if (!_TIME_KW_CHAIN.test(_ui_chain)) {
          const _prevDates = { s: params.startDate, e: params.endDate, ts: startDate, te: endDate };
          if (params.startDate || params.endDate || startDate || endDate) {
            console.log(`[call_api] CHAIN CONTEXT DATE RESET: drop LLM narrow dates (${JSON.stringify(_prevDates)}) — input has no explicit time keyword, fallback to backend default window`);
          }
          delete params.startDate;
          delete params.endDate;
          _chainContextDateReset = true;
        }
      }
    }

    // LLM이 직접 계산한 날짜를 params에 주입 (무효 월말 날짜 클램프 적용)
    // [CHAIN_CONTEXT_DATE_RESET] when MEMBER_DETAIL chain redirect dropped narrow dates,
    //   skip re-injecting top-level startDate/endDate so backend default window applies.
    if (!_chainContextDateReset) {
      if (startDate) params.startDate = params.startDate || AgentSession.clampDateParam(startDate);
      if (endDate) params.endDate = params.endDate || AgentSession.clampDateParam(endDate);
    }
    if (params.startDate) params.startDate = AgentSession.clampDateParam(params.startDate);
    if (params.endDate) params.endDate = AgentSession.clampDateParam(params.endDate);

    // [INTENTION DEFAULT WINDOW GUARD] L23-AUTOSORT-003 (2026-05-30) — dual sync with nodeAgent.mjs //
    //   본질: 사용자가 default-window intent ("만료 예정" 등 explicit period 없이) 표현 시 LLM이
    //   "current month" boundary로 hallucinate (예: today=05-30 → startDate=05-01 endDate=05-30 → 0건)
    //   guide에 정의된 default window (예: today~today+14) 무시 → FALSE_ACK ("없어요") 빈도 ↑
    //   구조 기반: INTENTION_DEFAULT_WINDOWS registry 정의 + EXPIRY_DEFAULT_KW 매칭 + 기존 TIME_KW로 explicit period 제외
    //   결정성: registry에 정의된 intention + default-kw 매칭 + explicit period 없음 → today~today+N 강제
    {
      const INTENTION_DEFAULT_WINDOWS = {
        'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD': { kw_re: /만료\s*예정|만료\s*임박|곧\s*만료|만료될|만료\s*되는/, daysAhead: 14 },
      };
      const _defWin = INTENTION_DEFAULT_WINDOWS[intention];
      if (_defWin) {
        const _ui = this.getUserInput?.() || '';
        // 기존 TIME_KW와 동일 — explicit period reference면 LLM 판단 존중
        const _TIME_KW_EXPLICIT = /오늘|어제|내일|지난주|이번주|다음주|지난달|이번달|다음달|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+일\s*전|작년|올해|이번\s*달|당월/;
        if (_defWin.kw_re.test(_ui) && !_TIME_KW_EXPLICIT.test(_ui)) {
          const _now = new Date();
          const _pad = (n) => String(n).padStart(2, '0');
          const _today = `${_now.getFullYear()}-${_pad(_now.getMonth() + 1)}-${_pad(_now.getDate())}`;
          const _ahead = new Date(_now.getTime() + _defWin.daysAhead * 24 * 60 * 60 * 1000);
          const _todayPlus = `${_ahead.getFullYear()}-${_pad(_ahead.getMonth() + 1)}-${_pad(_ahead.getDate())}`;
          const _prev = { s: params.startDate, e: params.endDate };
          params.startDate = _today;
          params.endDate = _todayPlus;
          if (TOOL_CALLING_LOG) console.log(`[call_api] INTENTION DEFAULT WINDOW GUARD: ${intention} | "${_ui.slice(0, 40)}" | LLM ${_prev.s}~${_prev.e} → ${_today}~${_todayPlus} (default daysAhead=${_defWin.daysAhead})`);
        }
      }
    }

    let slotName = args.slot || 'default';

    // [CROSS-MERGE GUARD] pendingCrossMerge 상태에서 slot 미명시 + 다른 인텐션 호출 시 default 덮어쓰기 차단 — 자동 슬롯 부여
    if (!args.slot && this._pendingCrossMerge && this.buffers.has('default')) {
      const defaultBuf = this.buffers.get('default');
      if (defaultBuf?.intention && defaultBuf.intention !== intention) {
        const baseName = (intention.split('+').pop() || intention).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12) || 'aux';
        let candidate = baseName;
        let suffix = 2;
        while (this.buffers.has(candidate)) {
          candidate = `${baseName}${suffix}`;
          suffix += 1;
        }
        slotName = candidate;
        if (TOOL_CALLING_LOG) console.log(`[call_api] CROSS-MERGE GUARD: default(${defaultBuf.intention}) 덮어쓰기 차단 → slot 자동 부여="${slotName}"`);
      }
    }

    // [MULTI-ASPECT DETAIL GUARD] 한 turn에 서로 다른 MEMBER_DETAIL detail intention을 slot 미지정으로 연속 호출 시 //
    //   default 덮어쓰기로 1번째 aspect(예: 출석) 데이터 silent 소실 → 멀티-aspect 회원 빠른조회("마지막 출석일이랑 //
    //   회원권 상태")에서 한 축 소실 + LLM 환각(예: 회원권 만료일을 "마지막 출석일"로 오인용). 기존/신규 둘 다 //
    //   category=MEMBER_DETAIL + 다른 intention + default 미표출/미merge 시 자동 side slot 부여로 양 버퍼 보존 → //
    //   buildUndisplayedDetailSlotOutro(aiUtils)가 미표출 축 핵심사실 결정적 보강. CROSS-MERGE GUARD 네이밍 재사용, //
    //   intention 메타(category) 구조 매칭 — case/회원명 하드코딩 X. dual sync (nodeAgent.mjs). //
    if (!args.slot && slotName === 'default' && this.buffers.has('default')) {
      const _madBuf = this.buffers.get('default');
      const _madOldCat = this.findIntentionCategory(_madBuf?.intention);
      const _madNewCat = this.findIntentionCategory(intention);
      // [CHAIN-STALE GUARD 2026-06-25] default이 "이번 turn"에 적재된 경우만 보존 대상. _turnDisplayedSlots/_turnCallApiSlots는 //
      //   매 turn resetTurnFlags로 비워지므로, guard 시점(현재 call_api의 slot add 이전)에 _turnCallApiSlots.has('default')=true면 //
      //   직전 call_api가 이번 turn에 default를 적재한 진짜 intra-turn 멀티-aspect. stale prior-turn default(예: T0 회원권 → T1 //
      //   환불 follow-up)은 _turnCallApiSlots에 없어 제외 → L9b-CROSS-007 회귀(stale 회원권표 재표출) 차단. dual sync (nodeAgent.mjs). //
      const _madThisTurn = this._turnCallApiSlots?.has('default');
      const _madUndisplayed = _madThisTurn && !this._turnDisplayedSlots?.has('default') && !this._turnMergedSourceSlots?.has('default');

      if (_madOldCat === 'MEMBER_DETAIL' && _madNewCat === 'MEMBER_DETAIL'
        && _madBuf?.intention && _madBuf.intention !== intention && _madUndisplayed) {
        const baseName = (intention.split('+').pop() || intention).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12) || 'aux';
        let candidate = baseName;
        let suffix = 2;
        while (this.buffers.has(candidate)) {
          candidate = `${baseName}${suffix}`;
          suffix += 1;
        }
        slotName = candidate;
        if (TOOL_CALLING_LOG) console.log(`[call_api] MULTI-ASPECT DETAIL GUARD: default(${_madBuf.intention}) 보존 → slot 자동 부여="${slotName}"`);
      }
    }

    // [#585-FOLLOWUP-E / PERIOD CHANGE BASE-PRESERVE] L9c-COMPARE-006 T3 (2026-05-30)
    // 사용자가 "X 기준으로 다시 비교" follow-up 시 LLM이 slot:"base"로 새 X 기간을 load하면 prior base(다른 period) 데이터 소실 →
    // merge_slots auto-union이 prior 기간 복구 못 함. 본 가드: 동일 intention + same slotName(base/compare_N) +
    // 기존 슬롯 dateRange가 새 startDate와 다른 month이면 slotName 자동 redirect (compare_{next})로 prior 데이터 보존.
    // 구조 기반 (case-ID 없음). dual sync with nodeAgent.mjs.
    {
      const userInputForBasePreserve = this.getUserInput?.() || '';
      const PERIOD_CHANGE_KW = /기준으로\s*다시|기준으로\s*비교|로\s*다시\s*비교|에\s*맞춰서?\s*(?:다시|비교)|추가해서\s*비교|도\s*같이\s*비교|도\s*포함해서\s*(?:다시|비교)/;
      if (PERIOD_CHANGE_KW.test(userInputForBasePreserve) && (slotName === 'base' || slotName.startsWith('compare_'))) {
        const existingBuf = this.buffers.get(slotName);
        const newStart = params?.startDate ? String(params.startDate) : null;
        if (existingBuf?.intention === intention && existingBuf?.dateRange?.startDate && newStart) {
          const oldM = String(existingBuf.dateRange.startDate).match(/^(\d{4}-\d{2})/);
          const newM = newStart.match(/^(\d{4}-\d{2})/);
          if (oldM && newM && oldM[1] !== newM[1]) {
            // 다른 month이면 기존 슬롯 보존 + 새 슬롯으로 redirect
            const usedCompareSlots = [...this.buffers.keys()].filter(k => k.startsWith('compare_'));
            let nextIdx = 1;
            for (const k of usedCompareSlots) {
              const m = k.match(/^compare_(\d+)$/);
              if (m) nextIdx = Math.max(nextIdx, parseInt(m[1], 10) + 1);
            }
            const newSlotName = `compare_${nextIdx}`;
            if (TOOL_CALLING_LOG) console.log(`[call_api] PERIOD CHANGE BASE-PRESERVE (#585-FOLLOWUP-E): slot "${slotName}" (prior ${oldM[1]}) → redirect "${newSlotName}" (new ${newM[1]}) — prior slot data preserved for auto-union`);
            slotName = newSlotName;
            args.slot = newSlotName;
          }
        }
      }
    }

    const isComparisonSlot = slotName === 'base' || slotName.startsWith('compare_');

    // 비교 슬롯 진입 시: 비교 세션이 아직 시작되지 않았으면 이전 비교 잔재 정리 후 락
    // merge 결과가 존재하면 새 비교 세션 시작 차단 (merge 후 데이터 보존)
    // [anchor 14] buffer에 비교 slot이 이미 존재하면 진행 중 비교 세션으로 간주 — clear skip + flag 재활성
    //   (summary 통계 다기간 비교 시 line 3347 자동 해제로 인한 slot 손실 본질 차단)
    if (isComparisonSlot && !this._mergeOutputSlot) {
      const hasExistingComparisonSlots = [...this.buffers.keys()].some(n => n === 'base' || n.startsWith('compare_'));
      if (!this._comparisonActive && !hasExistingComparisonSlots) {
        this.clearComparisonSlots();
        this._comparisonActive = true;
        if (TOOL_CALLING_LOG) console.log('[call_api] 새 비교 세션 시작 — 이전 비교 슬롯 정리');
      } else if (!this._comparisonActive && hasExistingComparisonSlots) {
        this._comparisonActive = true;
        if (TOOL_CALLING_LOG) console.log('[call_api] 비교 세션 재활성 — 기존 비교 slot 보존');
      }
    }

    // 비연관 슬롯 진입 시: API 카테고리가 다르면 기존 default 정리 (격리 보장) //
    if (!isComparisonSlot && slotName !== 'default' && this.buffers.has('default')) {
      const defaultBuf = this.buffers.get('default');
      const oldCategory = this.findIntentionCategory(defaultBuf?.intention);
      const newCategory = this.findIntentionCategory(intention);

      if (oldCategory && newCategory && oldCategory !== newCategory) {
        // [anchor 15] visualizationOnly intention buffer는 cross-category chart 도구 source — 격리 보호
        //   (예: chart 도구가 SCHEDULE ATTENDANCE + MEMBER HISTORY 둘 다 필요한 경우)
        const defaultBufMeta = this.findIntentionMeta(defaultBuf?.intention);
        if (defaultBufMeta?.visualizationOnly !== true) {
          this.buffers.delete('default');
          if (TOOL_CALLING_LOG) console.log(`[call_api] 카테고리 변경 (${oldCategory}→${newCategory}) — 기존 default 정리`);
        }
      }
    }

    // searchYear, startMonth/endMonth 파라미터 자동 파생
    if (startDate) {
      const paramsSchema = typeof intentionMeta.params === 'string'
        ? JSON.parse(intentionMeta.params || '[]')
        : (intentionMeta.params || []);

      if (!params.searchYear) {
        const needsSearchYear = paramsSchema.some(p => p.name === 'searchYear');
        if (needsSearchYear) {
          params.searchYear = startDate;
        }
      }

      const needsMonth = paramsSchema.some(p => p.name === 'startMonth');
      if (needsMonth && !params.startMonth) {
        params.startMonth = startDate.slice(0, 7);
        params.endMonth = (endDate || startDate).slice(0, 7);
      }
    }

    // fixed 파라미터 자동 주입 (status=alive 등)
    const paramsSchema = typeof intentionMeta.params === 'string'
      ? JSON.parse(intentionMeta.params || '[]')
      : (intentionMeta.params || []);
    for (const p of paramsSchema) {
      if (p.fixed && !params[p.name]) {
        params[p.name] = p.fixed;
      }
    }

    // [P-rev] 인텐션 enum 세션 메모리 — 멀티턴 호출 일관성 보장
    this._intentionEnumMemory ||= {};
    this._intentionEnumMemory[intention] ||= {};
    const memoryForIntention = this._intentionEnumMemory[intention];

    // enum default 적용 (메모리 우선)
    const defaultApplied = [];
    for (const p of paramsSchema) {
      if (p.enum && !params[p.name]) {
        if (memoryForIntention[p.name]) {
          params[p.name] = memoryForIntention[p.name];
          defaultApplied.push({ name: p.name, applied: memoryForIntention[p.name], options: p.enum, fromMemory: true });
        } else if (p.default) {
          params[p.name] = p.default;
          defaultApplied.push({ name: p.name, applied: p.default, options: p.enum });
        }
      }
    }

    // 사전 매칭 — 메모리 적용된 경우도 검사 (사용자가 새 차원 명시한 경우 메모리 교정)
    // 매칭 score가 0보다 큰 경우만 교정 (모호한 input에서 잘못 변경 방지)
    const needsMatching = defaultApplied;
    if (needsMatching.length > 0) {
      const af = this.findAvailableFields(intention);
      // nested groupBy 감지 — 첫 value가 field meta object({label, dimension, enums})이면 nested 아님 (skip)
      const _firstVal = af && Object.values(af)[0];
      if (af && typeof _firstVal === 'object' && _firstVal !== null && !isFieldMetaObject(_firstVal)) {
        const userInput = this.getUserInput();
        const rawKeywords = userInput.match(/[가-힣]+/g) || [];

        // "X별" 패턴 분해 — "코치별" → X="코치"는 그룹 힌트로 가중
        const expandedKeywords = new Set(rawKeywords);
        const groupHints = new Set();
        for (const kw of rawKeywords) {
          const m = kw.match(/^(.+?)별$/);
          if (m && m[1].length >= 1) {
            expandedKeywords.add(m[1]);
            groupHints.add(m[1]);
          }
        }

        if (expandedKeywords.size > 0) {
          let bestScore = 0, bestEnumValue = null;

          for (const [groupKey, groupFields] of Object.entries(af)) {
            const labelList = Object.values(groupFields).map(v =>
              typeof v === 'string' ? v : (v?.label || '')
            );
            let score = 0;
            for (const kw of expandedKeywords) {
              if (!kw || kw.length < 2) continue;
              const weight = groupHints.has(kw) ? 5 : 1;
              const occurrences = labelList.reduce((sum, label) => sum + (label.split(kw).length - 1), 0);
              score += occurrences * weight;
            }
            if (score > bestScore) {
              bestScore = score;
              bestEnumValue = groupKey.match(/_groupBy=(.+)/)?.[1];
            }
          }

          for (const d of needsMatching) {
            if (bestEnumValue && bestEnumValue !== d.applied && d.options.includes(bestEnumValue)) {
              params[d.name] = bestEnumValue;
              d.applied = bestEnumValue;
              d.corrected = true;
              if (TOOL_CALLING_LOG) console.log(`[call_api] enum 사전 매칭: ${d.name}="${bestEnumValue}" (교정, score=${bestScore})`);
            }
          }
        }
      }
    }

    // 최종 enum 값 메모리에 저장
    for (const p of paramsSchema) {
      if (p.enum && params[p.name]) {
        memoryForIntention[p.name] = params[p.name];
      }
    }

    if (defaultApplied.length > 0) {
      for (const d of defaultApplied) {
        const source = d.fromMemory ? '메모리' : (d.corrected ? '교정' : 'default');
        if (TOOL_CALLING_LOG) console.log(`[call_api] enum ${source}: ${d.name}="${d.applied}" (options: ${d.options.join('/')})`);
      }
    }

    // autoFallback: intention metadata flag → defaultPeriod 토큰 resolve로 missing date params 자동 채움 //
    params = applyAutoFallbackParams(intentionMeta, params);

    // [L1-MEMBERSHIP-007 STAT_RANKING_PERIOD_INCONSISTENCY] defaultPeriod 강제 override (사용자 input no time kw 시) //
    //   본질: 사용자가 explicit time keyword 미입력 시 LLM이 stochastic하게 "today~today" 등 hallucinate
    //         → run마다 dateRange 결정성 변동 (single day vs default period vs all-time)
    //   해소: USER_TIME_KW_RE 미매칭 + intention.defaultPeriod 정의 시 LLM dates 무시하고 defaultPeriod 강제
    //   dual sync with nodeAgent.mjs
    {
      const _userInputForOverride = this.getUserInput?.() || '';
      params = applyDefaultPeriodOverride(intentionMeta, params, _userInputForOverride);
    }

    // [QUARTER OVERRIDE] 분기 표현은 의미가 유일(직전/현재 달력 분기) — LLM 오계산/누락과 무관하게 코드가 date params 결정적 교정 //
    //   (DEMO-37 run7: "지난 분기"를 현재 분기(Q2)로 오산출. 분기 경계는 계산 문제라 LLM 자유도에 맡길 이유 없음).
    //   resolveQuarterOverride 미매칭 시 null — 개입 없음. dual sync 불필요 (aiDateResolver 단일 소스 함수). //
    {
      const _qInput = this.getUserInput?.() || '';
      const _qRange = resolveQuarterOverride(_qInput);

      if (_qRange) {
        const _hasDateParamsQ = paramsSchema.some(p => p.name === 'startDate' || p.name === 'endDate');

        if (_hasDateParamsQ && (params?.startDate !== _qRange.startDate || params?.endDate !== _qRange.endDate)) {
          if (TOOL_CALLING_LOG) console.log(`[call_api] QUARTER OVERRIDE: ${params?.startDate || '(none)'}~${params?.endDate || '(none)'} → ${_qRange.startDate}~${_qRange.endDate}`);
          params = { ...params, startDate: _qRange.startDate, endDate: _qRange.endDate };
        }
      }
    }

    // [DAY OVERRIDE] 단일 절대일("어제/오늘/내일/그저께/모레") LLM stochastic 오산출 결정적 교정 (QUARTER OVERRIDE 동형) //
    //   REPLAY T10: "어제 예약수업"에 LLM이 startDate=endDate=06-14 명시(정답 06-15) → 0건 "보여줄 수 없어". //
    //   적대검증 결론: LLM이 *단일일 date를 명시*(startDate===endDate)했는데 틀린 case만 교정. date 미명시 호출엔 //
    //   미개입 → 기존 slot/defaultPeriod 처리 보존(date 없는 "오늘" 호출 회귀 0). resolveDayOverride 단일 소스. //
    //   dual sync with nodeAgent.mjs //
    {
      const _dInput = this.getUserInput?.() || '';
      const _dRange = resolveDayOverride(_dInput);

      if (_dRange) {
        const _hasDateParamsD = paramsSchema.some(p => p.name === 'startDate' || p.name === 'endDate');
        // [#7 오늘 date-scope] dates 누락 시에도 단일 절대일(오늘/어제/내일) 주입 (운영 4차 #7: "오늘 수업"에 LLM이 date 누락
        //   → backend 이번주 default 16건 → "오늘" 스코프 미적용). resolveDayOverride는 비교/범위/타기간 동반 시 null(보수적)
        //   이라 순수 단일일만 발동. LLM date 행동 무관 결정적. dual sync.
        const _datesMissingD = !params?.startDate && !params?.endDate;
        const _singleDayWrongD = params?.startDate && params.startDate === params.endDate && params.startDate !== _dRange.startDate;
        if (_hasDateParamsD && (_datesMissingD || _singleDayWrongD)) {
          if (TOOL_CALLING_LOG) console.log(`[call_api] DAY OVERRIDE: ${params.startDate || '(none)'}~${params.endDate || '(none)'} → ${_dRange.startDate}~${_dRange.endDate}`);
          params = { ...params, startDate: _dRange.startDate, endDate: _dRange.endDate };
        }
      }
    }

    // [DATE PARAM GUARD] 절대 기간 표현(분기/N월/작년 등) + date params 모두 누락 → 차단 + 날짜 명시 재호출 유도 //
    //   본질: USER_TIME_KW 매칭 시 defaultPeriod override는 "LLM dates 보존"인데, LLM이 dates 자체를 누락하면
    //   빈 채로 백엔드에 가서 전체 기간(2024~) 응답 → "지난 분기" 질문에 2년 누적 수치로 답하는 사고 (DEMO-37).
    //   상대 표현(최근/지난 N일)은 defaultPeriod가 정당하므로 ABS_PERIOD_KW_RE만 검사. 2회 cap — 이후 통과 (무한 루프 방지).
    //   dual sync with nodeAgent.mjs //
    {
      const _hasDateParamsDG = paramsSchema.some(p => p.name === 'startDate' || p.name === 'endDate');
      const _userInputDG = this.getUserInput?.() || '';

      if (_hasDateParamsDG && !params?.startDate && !params?.endDate
        && ABS_PERIOD_KW_RE.test(_userInputDG)
        && (this._dateParamGuardBlockedCount || 0) < 2) {
        this._dateParamGuardBlockedCount = (this._dateParamGuardBlockedCount || 0) + 1;
        if (TOOL_CALLING_LOG) console.log(`[call_api] DATE PARAM GUARD: "${intention}" — 절대 기간 표현 있으나 startDate/endDate 누락 → 차단 (${this._dateParamGuardBlockedCount}/2)`);
        return {
          success: false,
          message: `[DATE PARAM MISSING] User input contains an explicit calendar-period expression but this call omitted startDate/endDate — the backend would return its FULL default range (wrong period, e.g. 2-year totals for a "지난 분기" question). Re-call call_api(intention="${intention}") WITH startDate and endDate (YYYY-MM-DD) computed from the user's stated period.`,
        };
      }
    }

    // Required params validation + priorEndpoint auto-fix //
    const missingRequired = paramsSchema
      .filter(p => p.required && !params[p.name])
      .map(p => p.name);

    if (missingRequired.length > 0) {
      const priorKey = intentionMeta.priorEndpoint;

      if (priorKey) {
        let priorBuffer = null;

        for (const [, buf] of this.buffers) {
          if (buf.intention === priorKey) {
            priorBuffer = buf;
            break;
          }
        }

        if (priorBuffer && priorBuffer.items) {
          if (priorBuffer.items.length === 1) {
            const item = priorBuffer.items[0];

            for (const paramName of missingRequired) {
              if (item[paramName] !== undefined) {
                params[paramName] = item[paramName];
                if (TOOL_CALLING_LOG) console.log(`[call_api] prior auto-fix: ${paramName}=${item[paramName]}`);
              }
            }
          }
          else if (priorBuffer.items.length > 1) {
            return {
              success: false,
              message: `Multiple results from ${priorKey} (${priorBuffer.items.length}). Specify which one by memberId.`,
            };
          }
        }
      }

      // _lastMember fallback: priorEndpoint로 못 찾았으면 세션 메모리의 마지막 검색 회원 활용
      const stillMissing0 = missingRequired.filter(p => !params[p]);
      if (stillMissing0.length > 0 && this._lastMember) {
        for (const paramName of stillMissing0) {
          if (this._lastMember[paramName] !== undefined) {
            params[paramName] = this._lastMember[paramName];
            if (TOOL_CALLING_LOG) console.log(`[call_api] _lastMember auto-fix: ${paramName}=${this._lastMember[paramName]}`);
          }
        }
      }

      const stillMissing = missingRequired.filter(p => !params[p]);

      if (stillMissing.length > 0) {
        return {
          success: false,
          message: `Missing required params: ${stillMissing.join(', ')}. Try again with the correct params.`,
        };
      }
    }

    // atLeastOne validation (at least one of name/phone required) //
    const atLeastOneGroup = paramsSchema.filter(p => p.condition === 'atLeastOne');

    if (atLeastOneGroup.length > 0) {
      const hasAny = atLeastOneGroup.some(p => params[p.name]);

      if (!hasAny) {
        // Auto-fix: extract name/phone from userInput //
        const userInput = this.getUserInput();
        const nameMatch = userInput.match(/([가-힣]{2,4})\s/);
        const phoneMatch = userInput.match(/(01[0-9][-\s]?\d{3,4}[-\s]?\d{4})/);

        if (phoneMatch && atLeastOneGroup.some(p => p.name === 'phone')) {
          params.phone = phoneMatch[1].replace(/[-\s]/g, '');
          if (TOOL_CALLING_LOG) console.log(`[call_api] atLeastOne auto-fix: phone="${params.phone}"`);
        }
        else if (nameMatch && atLeastOneGroup.some(p => p.name === 'name')) {
          params.name = nameMatch[1];
          if (TOOL_CALLING_LOG) console.log(`[call_api] atLeastOne auto-fix: name="${params.name}"`);
        }
        else {
          const fieldNames = atLeastOneGroup.map(p => p.name).join(' or ');
          return {
            success: false,
            message: `At least one of [${fieldNames}] is required. User question: "${userInput}". Extract member name or phone from the question and include in params. Example: call_api({intention: "${intention}", params: {name: "extracted_name"}})`,
          };
        }
      }
    }

    // Duplicate detection: same intention + same date range → 기존 버퍼 재사용 (API 재호출 방지) //
    if (startDate && endDate && slotName) {
      for (const [existingSlot, buf] of this.buffers) {
        if (existingSlot === slotName) continue;

        if (buf.intention === intention && buf.dateRange
          && buf.dateRange.startDate === startDate && buf.dateRange.endDate === endDate
          && (buf.callParamsSig || '') === AgentSession._callParamsSignature(params)) {
          if (TOOL_CALLING_LOG) console.log(`[call_api] Duplicate → reuse: ${intention} ${startDate}~${endDate} from "${existingSlot}" → "${slotName}"`);
          // 현재 buffer 상태 그대로 deep copy (필터/정렬 보존, API 재호출 없음)
          this.buffers.set(slotName, {
            raw: buf.raw,
            items: buf.items ? buf.items.map(i => ({ ...i })) : null,
            originalItems: buf.originalItems ? buf.originalItems.map(i => ({ ...i })) : null,
            intention: buf.intention,
            availableFields: buf.availableFields,
            summary: buf.summary,
            dateRange: { startDate, endDate },
            matchKey: buf.matchKey || null,
            appliedFilters: [...(buf.appliedFilters || [])],
            appliedSort: buf.appliedSort ? { ...buf.appliedSort } : null,
            columnPrefs: null,
            resolvedColumns: null,
            callParamsSig: buf.callParamsSig,  // [DEDUP PARAM SIG] 동일 params 재사용이므로 source sig 승계
            _currentAxisKey: buf._currentAxisKey,  // [DEDUP AXIS PRESERVE] grouped 상태 보존 — 누락 시 _isGroupedBuf=false로 COUNT 가드 오발동(ISSUE #2)
            _axisSchemas: buf._axisSchemas,        // [DEDUP AXIS PRESERVE] sibling axis 스키마 보존 (axis-switch 감지용)
          });
          this.activeSlot = slotName;
          let copiedItems = this.buffers.get(slotName)?.items;

          // [CROSS-REF AUTO-FILTER] SQL INTERSECT 후속 reuse → _merged 회원 IN-filter
          if (slotName === 'default' && copiedItems) {
            const xref = this.applyCrossRefAutoFilter(copiedItems);
            if (xref) {
              this.buffers.get(slotName).items = xref.filtered;
              this.buffers.get(slotName).originalItems = xref.filtered.map(i => ({ ...i }));
              copiedItems = xref.filtered;
              if (TOOL_CALLING_LOG) console.log(`[call_api] CROSS-REF AUTO-FILTER (dup): ${xref.before} → ${xref.after} (by _merged ${xref.idField})`);
            }
          }

          const fieldKeys = Object.keys(buf.availableFields || {});
          return {
            success: true,
            intention,
            slot: slotName,
            count: copiedItems ? copiedItems.length : 0,
            availableFields: fieldKeys.length > 0 ? buf.availableFields : undefined,
            message: `"${existingSlot}" 슬롯의 동일 데이터를 "${slotName}" 슬롯으로 복사했습니다 (${copiedItems ? copiedItems.length : 0}건). merge_slots로 다른 슬롯과 병합하세요.${this.getSlotContext()}`,
          };
        }
      }
    }

    let result = await this.callApiInternal(intentionMeta, intention, params);

    if (process.env.NODE_ENV === 'development' && result.success && result.data) {
      console.log(`[call_api] ${intention} 전체 응답:`, result.data);
    }

    // [anchor 16] Layer 1+2:
    //   1) DETAIL fail + specifier → tool_result에 guidance append + session._detailNotFoundContext 설정
    //   2) LIST success + session context 일치 → critical warning prepend (LLM이 fallback 후에도 specifier 의도 honor)
    const _userInputForGuide = this.getUserInput?.() || '';
    result = applyDetailNotFoundGuide(result, { intention, userInput: _userInputForGuide, session: this });
    result = applyListFallbackWarning(result, { intention, session: this });

    // [CROSS-REF AUTO-FILTER] SQL INTERSECT 후속 call_api → _merged 회원 IN-filter (data 자체 교체)
    if (result.success && result.data && slotName === 'default') {
      const { items: rawItems } = this.extractArray(result.data);
      if (rawItems && rawItems.length > 0) {
        const xref = this.applyCrossRefAutoFilter(rawItems);
        if (xref) {
          result.data = this.replaceLargestObjectArray(result.data, xref.filtered);
          if (TOOL_CALLING_LOG) console.log(`[call_api] CROSS-REF AUTO-FILTER: ${xref.before} → ${xref.after} (by _merged ${xref.idField})`);
        }
      }
    }

    // 성공 시 데이터 버퍼에 저장 (개인 데이터 + 요약 분리)
    if (result.success && result.data) {
      const availableFields = this.findAvailableFields(intention);
      const callApiGuide = this.findPostToolGuide(intention);

      // summary 필드를 availableFields에 자동 주입 (LLM이 라벨로 지표 구분)
      const { summary: previewSummary } = this.extractArray(result.data);
      if (previewSummary && typeof previewSummary === 'object' && availableFields) {
        for (const [key, val] of Object.entries(previewSummary)) {
          if (!(key in availableFields) && typeof val !== 'object' && !Array.isArray(val)) {
            availableFields[key] = key;
          }
        }
      }

      // [ENUM AUTO-FILTER] binary enum 필드 + user input에 enum label 포함 시 initBuffer 전 data pre-filter (harness)
      let _enumAutoFilterApplied = null;
      if (availableFields) {
        const __userInput = this.getUserInput?.() || '';
        const LIST_NOUN_KW = /회원|목록|명단|리스트|보여|알려|확인/;
        if (__userInput && LIST_NOUN_KW.test(__userInput)) {
          const { items: _preItems } = this.extractArray(result.data);
          if (_preItems && _preItems.length > 1) {
            for (const [field, meta] of Object.entries(availableFields)) {
              if (typeof meta !== 'object' || !meta.enums) continue;
              const entries = Object.entries(meta.enums);
              if (entries.length !== 2) continue;
              for (const [val, label] of entries) {
                if (typeof label === 'string' && __userInput.includes(label)) {
                  const filterVal = val === 'true' ? true : val === 'false' ? false : val;
                  const filtered = _preItems.filter(item => item[field] === filterVal);
                  if (filtered.length < _preItems.length) {
                    result.data = this.replaceLargestObjectArray(result.data, filtered);
                    _enumAutoFilterApplied = { field, value: filterVal, label, before: _preItems.length, after: filtered.length };
                    if (TOOL_CALLING_LOG) console.log(`[call_api] ENUM AUTO-FILTER: ${field}=${filterVal} (${label}), ${_preItems.length} → ${filtered.length}건`);
                  }
                  break;
                }
              }
              if (_enumAutoFilterApplied) break;
            }
          }
        }
      }

      // [STATUS AUTO-FILTER] STATUS_FILTER_MAP intention + user input status keyword 매칭 시
      //   items pre-filter (>0) 또는 emptyStatusContext 기록 (=0).
      //   목적: LLM이 가이드대로 filter 호출 안 해도 결정적 status 정합 응답 (대기/거절/승인).
      //   dual sync with nodeAgent.mjs
      let _statusAutoFilterApplied = null;
      let _statusAutoFilterEmpty = null;
      const _statusFilterMeta = STATUS_FILTER_MAP[intention];
      if (_statusFilterMeta) {
        const __userInputForStatus = this.getUserInput?.() || '';
        let _matchedStatusKey = null;
        for (const { pattern, statusKey } of _statusFilterMeta.keywordPatterns) {
          if (pattern.test(__userInputForStatus)) {
            _matchedStatusKey = statusKey;
            break;
          }
        }
        if (_matchedStatusKey && _statusFilterMeta.statusValues[_matchedStatusKey] !== undefined) {
          const _targetVal = _statusFilterMeta.statusValues[_matchedStatusKey];
          const _statusField = _statusFilterMeta.field;
          const { items: _preItemsStatus } = this.extractArray(result.data);
          if (_preItemsStatus && _preItemsStatus.length > 0) {
            const _filteredStatus = _preItemsStatus.filter(item => item[_statusField] === _targetVal);
            const _label = _statusFilterMeta.statusLabels[_matchedStatusKey] || _matchedStatusKey;
            const _noun = _statusFilterMeta.nounLabel || '건';
            if (_filteredStatus.length === 0) {
              // 0건 — buffer 채우지 말고 honest 응답용 emptyStatusContext만 기록
              _statusAutoFilterEmpty = {
                field: _statusField,
                value: _targetVal,
                statusKey: _matchedStatusKey,
                label: _label,
                noun: _noun,
                before: _preItemsStatus.length,
              };
              // raw data 자체를 0건으로 교체 — finishPolicy / autoGenerate가 empty 시나리오 자연 처리
              result.data = this.replaceLargestObjectArray(result.data, []);
              if (TOOL_CALLING_LOG) console.log(`[call_api] STATUS AUTO-FILTER (empty): ${_statusField}=${_targetVal} (${_label}), ${_preItemsStatus.length} → 0건 — honest empty response 강제`);
            } else if (_filteredStatus.length < _preItemsStatus.length) {
              result.data = this.replaceLargestObjectArray(result.data, _filteredStatus);
              _statusAutoFilterApplied = {
                field: _statusField,
                value: _targetVal,
                statusKey: _matchedStatusKey,
                label: _label,
                noun: _noun,
                before: _preItemsStatus.length,
                after: _filteredStatus.length,
              };
              if (TOOL_CALLING_LOG) console.log(`[call_api] STATUS AUTO-FILTER: ${_statusField}=${_targetVal} (${_label}), ${_preItemsStatus.length} → ${_filteredStatus.length}건`);
            }
            // 모든 row가 매칭되면 변경 X (이미 정합 상태)
          }
        }
      }

      // [DATE RANGE POST-FILTER] EXPIRING_LIST — backend ignores startDate/endDate for expiry filtering //
      // User requests "N월에 만료되는 회원" → API returns ALL expiring members regardless of date params
      // Post-filter by endDate field to keep only members whose endDate falls within requested range
      // params.* 우선 — INTENTION DEFAULT WINDOW GUARD 등 상위 가드의 override 반영 (LLM args.startDate는 fallback)
      const _expiringStart = params.startDate || startDate;
      const _expiringEnd = params.endDate || endDate;

      if (intention === 'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD' && _expiringStart && _expiringEnd) {
        const { items: _preItems } = this.extractArray(result.data);

        if (_preItems && _preItems.length > 0) {
          const _startNorm = _expiringStart.replace(/-/g, '');
          const _endNorm = _expiringEnd.replace(/-/g, '');
          const _filtered = _preItems.filter(item => {
            const _raw = item.endDate || item.viewEndDate;
            if (!_raw) return false;
            const _dateStr = String(_raw).replace(/[-T:Z]/g, '').slice(0, 8);
            return _dateStr >= _startNorm && _dateStr <= _endNorm;
          });

          if (_filtered.length < _preItems.length) {
            result.data = this.replaceLargestObjectArray(result.data, _filtered);
            if (TOOL_CALLING_LOG) console.log(`[call_api] EXPIRING DATE POST-FILTER: endDate ${_expiringStart}~${_expiringEnd}, ${_preItems.length} -> ${_filtered.length}건`);
          }
        }
      }

      const { items, summary, error: slotError } = this.initBuffer(result.data, intention, availableFields, slotName);

      if (slotError) {
        return { success: false, message: slotError };
      }

      // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 call_api 적재 슬롯 추적 — autoGenerateDisplay outro 자동 보강 후보 //
      if (this._turnCallApiSlots) this._turnCallApiSlots.add(slotName);

      // [rid49 2차-b CROSS-CATEGORY STALE CLEAR] 새 카테고리 비교 로드 시 직전 턴의 다른-카테고리 stale 비교/merged 슬롯 정리 (dual sync with nodeAgent.mjs).
      //   본질: 체인(T0 재등록 비교 _merged → T1 매출 비교)에서 clearStaleSlots가 _mergeOutputSlot 존재 시 early-return → stale 재등록 base/compare_/_merged 생존 →
      //         LLM이 매출×재등록 cross-category merge 시도 → NO-JOIN → 혼입 텍스트(rid49 Run2 1/5, 적대검증 Agent3 clearStaleSlots root).
      //   해소: 이 턴 로드된 비교 슬롯의 카테고리와 다른 카테고리의 stale 슬롯(이 턴 미로드)만 삭제. 동일 카테고리(L9c 재비교)·태그없는 legacy는 보존. 304e0186 _mergedSourcePrefix 재사용.
      if (slotName === 'base' || slotName.startsWith('compare_')) {
        const _curPrefix = String(intention || '').match(/^(CHECK_[A-Z]+)/)?.[1] || null;
        if (_curPrefix) {
          for (const [_n, _b] of [...this.buffers.entries()]) {
            if (_n === slotName) continue;
            if (this._turnCallApiSlots?.has(_n)) continue;  // 이 턴 로드 = 보존
            const _isCmpSlot = _n === 'base' || _n.startsWith('compare_');
            const _isMergedSlot = _n === '_merged' || _b?.intention === 'MERGED';
            if (!_isCmpSlot && !_isMergedSlot) continue;
            const _bPrefix = _isMergedSlot
              ? (_b?._mergedSourcePrefix || null)
              : (String(_b?.intention || '').match(/^(CHECK_[A-Z]+)/)?.[1] || null);
            if (_bPrefix && _bPrefix !== _curPrefix) {
              this.buffers.delete(_n);
              if (this._mergeOutputSlot === _n) this._mergeOutputSlot = null;
              if (TOOL_CALLING_LOG) console.log(`[call_api] CROSS-CATEGORY STALE CLEAR: stale "${_n}"(${_bPrefix}) 삭제 — 현재 비교 카테고리=${_curPrefix}`);
            }
          }
          if (this.activeSlot && !this.buffers.has(this.activeSlot)) this.activeSlot = slotName;
        }
      }

      // [#10 MEMBER-DETAIL MULTI-ASPECT EARLY COMPLETION] 방금 적재한 intention이 등재 aspect(출석/회원권)이고 사용자 query가 //
      //   2+ aspect를 요청 + member 해소(_lastMember.memberId) 상태면, 요청된 미fetch aspect를 show_display 이전에 force-fetch. //
      //   → 직후 active-pref가 회원권을 표시 슬롯 고정 + finalizer가 출석 outro 결정 부착 = LLM fetch 행동 무관 결정성(양 aspect). //
      //   재귀 가드 _madForceFetchingThisTurn. detection은 aiUtils 공유 레지스트리. dual sync (nodeAgent.mjs). //
      if (!this._madForceFetchingThisTurn && this._lastMember?.memberId != null) {
        const _userInputMad = this.getUserInput?.() || '';
        const _requestedMad = detectRequestedMemberDetailAspects(_userInputMad);
        if (_requestedMad.length >= 2 && _requestedMad.some(a => a.intention === intention)) {
          this._madForceFetchingThisTurn = true;
          const _findBufMad = this.findBufferByIntention || this._findBufferByIntention;
          for (const _asp of _requestedMad) {
            const _existMad = typeof _findBufMad === 'function' ? _findBufMad.call(this, _asp.intention) : null;
            if (_existMad?.items?.length) continue;
            const _aspSlotMad = `_aspect_${_asp.intention.replace(/[^A-Za-z0-9]/g, '_').toLowerCase()}`;
            try {
              await this.executeCallApi({ intention: _asp.intention, slot: _aspSlotMad, params: { memberId: this._lastMember.memberId } });
              if (TOOL_CALLING_LOG) console.log(`[call_api] MULTI-ASPECT EARLY COMPLETION: force-fetch "${_asp.intention}" → slot="${_aspSlotMad}" (member ${this._lastMember.memberId})`);
            } catch (_eMad) { /* graceful — 한 aspect 실패해도 나머지 진행 */ }
          }
          this._madForceFetchingThisTurn = false;
        }
      }

      // [MULTI-ASPECT DETAIL DISPLAY PREFERENCE — 제거됨 2026-06-25] 과거 회원권을 active로 강제 전환했으나, LLM이 //
      //   show_display에 출석 컬럼을 넘기면 활성(회원권) 버퍼에 그 컬럼 부재 → SHOW_DISPLAY COLUMN GUARD FAIL(빈 응답) + //
      //   LLM switch_slot override 충돌. active-slot 강제 전환 폐기 — LLM 표 선택은 수용하고, 두 aspect 사실은 //
      //   augmentMemberDetailAspectsFinalText(finalText 조립부)가 결정적 outro 라인으로 항상 보장(표 형태 무관). dual sync (nodeAgent.mjs). //

      // [VIZ-ENGAGEMENT-002] visualizationOnly 플래그 buffer에 전파 — show_display guard 정상 발동용 (dual sync with nodeAgent.mjs) //
      if (intentionMeta.visualizationOnly === true) {
        const _vizBuf = this.buffers.get(slotName);

        if (_vizBuf) {
          _vizBuf.visualizationOnly = true;
        }
      }

      // enum auto-filter 적용 시 buffer에 filter 메타데이터 기록
      if (_enumAutoFilterApplied) {
        const buf = this.getBuffer(slotName);
        if (buf) {
          buf.appliedFilters = [{ field: _enumAutoFilterApplied.field, condition: { type: 'exact', value: _enumAutoFilterApplied.value }, fieldNames: [_enumAutoFilterApplied.field] }];
        }
      }

      // status auto-filter 적용 시 buffer에 filter + status context 메타데이터 기록
      if (_statusAutoFilterApplied) {
        const buf = this.getBuffer(slotName);
        if (buf) {
          buf.appliedFilters = [{ field: _statusAutoFilterApplied.field, condition: { type: 'exact', value: _statusAutoFilterApplied.value }, fieldNames: [_statusAutoFilterApplied.field] }];
          buf.statusFilterContext = _statusAutoFilterApplied;
        }
      }
      // 0건 empty 시나리오도 buffer에 context 기록 — finishPolicy / autoGenerate가 honest 응답에 사용
      if (_statusAutoFilterEmpty) {
        const buf = this.getBuffer(slotName);
        if (buf) {
          buf.appliedFilters = [{ field: _statusAutoFilterEmpty.field, condition: { type: 'exact', value: _statusAutoFilterEmpty.value }, fieldNames: [_statusAutoFilterEmpty.field] }];
          buf.statusFilterContext = { ..._statusAutoFilterEmpty, after: 0 };
        }
      }

      // 슬롯 날짜 범위 저장 (겹침 감지용)
      if (params.startDate && params.endDate) {
        this.setSlotDateRange(slotName, params.startDate, params.endDate);
      }
      else {
        // args에 명시 기간 없을 때(상대표현 "이번 달"/"오늘" 등 → LLM이 date 생략, backend 기본 기간 처리) 응답 summary 기간으로 dateRange 보강 //
        //   period mismatch 가드(_getSlotMonths)가 슬롯의 실제 기간을 알 수 있게 함 — 코치 통계 등 items에 날짜 없는 집계 커버 //
        const _periodBuf = this.buffers.get(slotName);
        const _periodSummary = _periodBuf == null ? null : _periodBuf.summary;
        if (_periodSummary && _periodSummary.startDate && _periodSummary.endDate) {
          this.setSlotDateRange(slotName, _periodSummary.startDate, _periodSummary.endDate);
        }
      }

      // [DEDUP PARAM SIG] 본 fresh call의 params 시그니처 저장 — 후속 dedup이 groupBy 등 차이를 식별 (dual sync with nodeAgent.mjs) //
      const _sigBuf = this.buffers.get(slotName);
      if (_sigBuf) _sigBuf.callParamsSig = AgentSession._callParamsSignature(params);

      // [RANKING_SORT_GUARD] intention 메타에 default_sort 정의 시 buffer 로딩 후 자동 정렬 — dual sync with nodeAgent.mjs //
      //   본질: backend가 ranking을 pre-sort 했다고 call_api_guide에 명시해도 실제 응답 순서가 깨지는 경우 다수 (L1-COMMUNICATION-003 commentCount 1위 37 vs 3위 45,
      //   L1-MEMBERSHIP-004 재등록률 1위 58.6% vs 2위 100%, L1-MEMBERSHIP-007 순위 5,6,8,7,1,2,9 — RANKING_BROKEN 7건).
      //   LLM이 sort tool을 호출하지 않으면 raw 순서가 표시되어 사용자 입장에서 "1위가 더 작은 값" 자기모순 발생.
      //   intention 메타 default_sort = { field, direction, secondary } 정의 시, buffer 초기 상태(appliedSort=null + 사용자 sort 미호출)에 한해
      //   자동 sort 적용. 사용자/LLM이 sort tool을 명시 호출하면 그 결정이 우선 (재호출 시 default 덮어쓰지 X).
      //   tie_break_field (단독)도 동일하게 buffer에 stamp — applySort에서 primary 동률 시 secondary로 tie-break.
      if (intentionMeta.default_sort && typeof intentionMeta.default_sort === 'object') {
        const _ds = intentionMeta.default_sort;
        const _dsBuf = this.buffers.get(slotName);
        if (_dsBuf && _dsBuf.items && _dsBuf.items.length > 1 && !_dsBuf.appliedSort && _ds.field) {
          const _dsField = _ds.field;
          const _dsDir = _ds.direction === 'asc' ? 'asc' : 'desc';
          // applySort는 activeSlot 기준 동작 → 현재 호출 slot이 active인 경우에만 안전 적용
          if (this.activeSlot === slotName) {
            const _dsResult = this.applySort({ field: _dsField, direction: _dsDir });
            if (_dsResult && _dsResult.success) {
              if (_ds.secondary && _dsBuf.appliedSort) {
                _dsBuf.appliedSort.secondary = _ds.secondary;
              }
              if (TOOL_CALLING_LOG) console.log(`[call_api] RANKING_SORT_GUARD default_sort applied: ${_dsField} ${_dsDir}${_ds.secondary ? ` (tie-break: ${_ds.secondary})` : ''}`);
            }
          }
        }
      } else if (intentionMeta.tie_break_field && typeof intentionMeta.tie_break_field === 'string') {
        // default_sort 없이 tie_break_field만 있는 intention (direction은 사용자 의도에 따라 가변) — buffer에 stamp만, applySort 호출 시 활용
        const _tbBuf = this.buffers.get(slotName);
        if (_tbBuf) {
          _tbBuf.tieBreakField = intentionMeta.tie_break_field;
        }
      }

      const fieldKeys = availableFields ? Object.keys(availableFields) : [];

      const activeSlots = this.getSlotNames();

      // 소수건(3건 이하)이면 items를 tool result에 포함 — LLM이 memberId 등 키 값을 직접 읽을 수 있도록
      // [L10-DETAIL-SCHEDULE-001] LLM finish path에서 raw ISO datetime / 미포맷 금액 노출 차단
      //   inlineItems의 모든 값을 formatValue 통과시켜 포맷팅된 사본 전달
      //   ID 필드(memberId 등)는 원본 유지 — LLM이 후속 call_api에 사용
      const INLINE_THRESHOLD = 3;
      const ID_FIELDS_RE = /Id$|_id$|^id$|^uid$|^userId$|^merchantUid/i;
      const inlineItems = (items && items.length > 0 && items.length <= INLINE_THRESHOLD)
        ? items.map(item => {
          const formatted = {};

          for (const [key, val] of Object.entries(item)) {
            if (ID_FIELDS_RE.test(key)) {
              formatted[key] = val;
            }
            else {
              // [L4-DERIVE-ADD] availableFields[key] meta 전달 — derive 필드의 currency unit propagation 인식 //
              const _fmeta = availableFields && typeof availableFields === 'object' ? availableFields[key] : undefined;
              const fv = formatValue(val, key, _fmeta);
              formatted[key] = fv === '' ? val : (typeof val === 'number' && typeof fv === 'string' && /^[\d,]+$/.test(fv) ? fv : fv);
            }
          }

          return formatted;
        })
        : undefined;

      // enum 필드 고유값 추출 (gender, status 등 짧은 문자열/boolean) //
      let enumValues;

      if (items && items.length > 0 && availableFields) {
        const enums = {};

        for (const field of fieldKeys) {
          const values = [...new Set(items.map(i => i[field]).filter(v => v != null))];

          if (values.length > 0 && values.length <= 10
            && values.every(v => typeof v === 'boolean' || (typeof v === 'string' && v.length <= 20))) {
            enums[field] = values;
          }
        }

        if (Object.keys(enums).length > 0) enumValues = enums;
      }

      // boolean enum 필드 감지 → 필터 안내 자동 생성
      let boolFilterHint = '';
      if (enumValues) {
        const boolFields = Object.entries(enumValues)
          .filter(([, vals]) => vals.length === 2 && vals.includes(true) && vals.includes(false));
        if (boolFields.length > 0) {
          const hints = boolFields.map(([field]) => {
            const label = getFieldLabel(availableFields?.[field]) || field;
            return `${field}(${label})=[true/false]`;
          });
          boolFilterHint = ` [FILTER CHECK] boolean 필드 감지: ${hints.join(', ')}. 사용자가 한쪽만 요청했으면 filter(field, exact, true/false)를 반드시 호출하세요.`;
        }
      }

      // [1-M α] DATA FORM MISMATCH — backend가 동일 intention으로 다른 form 응답 시 LLM 강제 인지 (도메인 무관 구조 기반)
      //   availableFields ↔ items[0] 키 intersection 비율 < 50% → form 분기 본질 차단
      //   예: CHECK_MEMBER+MEMBERSHIP_DETAIL이 회원권 본체 vs 일시정지(holding) 두 form 반환 — LLM이 form B를 회원권 본체로 일반화 라벨링하는 회귀 차단
      let formMismatchHint = '';
      if (items && items.length > 0 && availableFields && Object.keys(availableFields).length > 0) {
        const _itemKeys = new Set(Object.keys(items[0] || {}));
        const _fieldKeysArr = Object.keys(availableFields);
        const _presentCount = _fieldKeysArr.filter(k => _itemKeys.has(k)).length;
        const _presentRatio = _fieldKeysArr.length > 0 ? _presentCount / _fieldKeysArr.length : 1;
        if (_presentRatio < 0.5) {
          const _validKeys = _fieldKeysArr.filter(k => _itemKeys.has(k));
          const _validWithLabels = _validKeys.map(k => `${k}(${availableFields[k]})`);
          formMismatchHint = ` [DATA FORM MISMATCH] backend가 동일 intention("${intention}")으로 다른 form 응답 — availableFields 정의 ${_fieldKeysArr.length}개 중 실제 items[0]에 존재=${_presentCount}개 (${Math.round(_presentRatio * 100)}%). valid columns: [${_validWithLabels.join(', ')}]. 사용자 응답에서 실제 데이터 형태에 맞는 라벨 사용 (intention description의 form 분기 설명 참조). show_display columns는 valid columns 중에서만 선택.`;
        }
      }

      // 비교 슬롯인데 items ≤ 1건 → 요약 통계
      // [anchor 14] 다른 비교 slot이 buffer에 존재하면 비교 모드 유지 — 진행 중 다기간 비교 보존
      // [rid49 2차 Fix A] otherComparisonSlots>0(다기간 비교 진행 중)이면 "merge 불필요" hint 방출 금지 (dual sync with nodeAgent.mjs).
      //   기존: 요약 1행 슬롯마다 "merge 불필요/텍스트로 finish" 방출 → LLM이 단일기간만 표출+거짓 "비교" 라벨(rid49 2/5).
      //   비교 표는 기간별 행+_change_/_changeRate_ 필요 → merge_slots 안내로 분기(능동원인 억제, 적대검증 Agent3 진단).
      const isComparisonSlot = slotName === 'base' || slotName.startsWith('compare_');
      let summaryHint = '';
      if (isComparisonSlot && items && items.length <= 1) {
        const otherComparisonSlots = [...this.buffers.keys()].filter(n => n !== slotName && (n === 'base' || n.startsWith('compare_')));
        if (otherComparisonSlots.length === 0) {
          this._comparisonActive = false;
          if (TOOL_CALLING_LOG) console.log(`[call_api] 요약 통계(${items?.length || 0}건) — 비교 모드 해제`);
          summaryHint = ' [SUMMARY DATA] 이 데이터는 요약 통계(개별 목록 없음)이므로 merge 불필요. 각 기간 결과를 텍스트로 비교 요약하고 finish하세요.';
        } else {
          if (TOOL_CALLING_LOG) console.log(`[call_api] 요약 통계(${items?.length || 0}건) — 다른 비교 slot ${otherComparisonSlots.length}개 존재, 비교 모드 유지`);
          summaryHint = ' [COMPARISON IN PROGRESS] 비교 대상 기간이 2개 이상 로드됨. 각 기간이 요약 1행이라도 merge_slots({slots:[...], mode:"inner"})로 기간별 비교표(_change_/_changeRate_ 자동 파생)를 먼저 만든 뒤 show_display 하세요. 단일 기간만 표시하면 비교가 아닙니다.';
        }
      }

      // 회원 1건 + memberId 보유 시 _lastMember에 보존 (멀티턴 컨텍스트, 인텐션 무관 구조 기반)
      if (items && items.length === 1 && items[0]?.memberId) {
        const m = items[0];
        this._lastMember = { memberId: m.memberId, name: m.name || m.memberName || null };
        // 단일 매치 확정 — 직전 multi-match 상태 해제
        this._lastMemberSearchMultiMatch = null;
        if (TOOL_CALLING_LOG) console.log(`[call_api] _lastMember 저장:`, this._lastMember);
      }
      // [L10-DETAIL-SCHEDULE-001 CHAIN_CONTEXT_MULTI_MATCH_BLOCK] MEMBER_SEARCH 2명+ 동명이인 발견 시 disambiguation pending 표시
      // 사용자 의도: 한 명의 detail. 시스템: 어느 회원인지 모름 → 후속 T1 bare keyword에서 whole-roster fallback 위험.
      // 구조 기반 (intention === 'CHECK_MEMBER_SEARCH' && items.length >= 2 && every item has memberId/name) — 회원 list 응답에만 한정
      if (intention === 'CHECK_MEMBER_SEARCH' && items && items.length >= 2
          && items.every(it => it && (it.memberId != null) && (it.name || it.memberName))) {
        const _queryName = (params && (params.name || params.phone)) || null;
        this._lastMemberSearchMultiMatch = {
          name: _queryName,
          count: items.length,
          candidates: items.slice(0, 5).map(it => ({ name: it.name || it.memberName, memberId: it.memberId })),
        };
        if (TOOL_CALLING_LOG) console.log(`[call_api] CHAIN_CONTEXT_MULTI_MATCH_PENDING: MEMBER_SEARCH "${_queryName}" → ${items.length}명 동명이인. 후속 T1 bare keyword 시 disambiguation 강제.`);
      }

      // [1-P] LONG-TEXT FIELDS HINT — items[0]에 80자 초과 long-text/URL 필드 감지 시 LLM 자연어 인용 차단
      //   1-N/1-O는 표 셀(autoGenerateDisplay)만 차단, finalText 자율 영역 미커버 → 자연어 finish path (1-L)에서 raw URL/long-text dump 회귀 (예: L5-WORKOUT-PARTNERGYM-001 storeLogo/profiles)
      //   구조 기반 (isLongTextField string && length>80) — intention 무관
      let longTextHint = '';
      if (items && items.length > 0 && items[0] && typeof items[0] === 'object') {
        const _ltFields = Object.keys(items[0])
          .filter(k => !k.startsWith('_'))
          .filter(k => isLongTextField(k, items[0]));
        if (_ltFields.length > 0) {
          const _ltLabels = _ltFields.map(k => {
            const _label = availableFields && availableFields[k];
            return _label && _label !== k ? `${k}(${_label})` : k;
          });
          longTextHint = ` [LONG-TEXT FIELDS] items contain long-text/URL fields (80+ chars): [${_ltLabels.join(', ')}]. In finalText / finish() outro, DO NOT quote raw values. Summarize (e.g., "profile photo available") or omit entirely. Raw URLs and long text are noise for the user.`;
        }
      }

      // [REREG-SCOPE GUARD] 이번 턴 재등록 메타 실측 기록 — finish/display 서술 스코프·stale rate 교정용 (aiUtils.correctReRegScopeNarrative 소비, setUserInput 리셋)
      {
        // 단일율 키(reRegistrationRate)만 기록 — 스칼라율 부재 시 회원권별 통계 표(newCount/reRegistrationCount)에서 배타 모수 파생(tableDerived): 모수 혼합 문장만 교정, 행별 rate 교정은 aiUtils 조건 B에서 skip
        const _rrRate = summary && typeof summary === 'object' && typeof summary.reRegistrationRate === 'number'
          ? summary.reRegistrationRate
          : null;
        if (_rrRate != null) {
          if (this._reRegMetaThisTurn) this._reRegMetaThisTurn.multi = true;  // 같은 턴 복수 RE+REG call(비교/추이) — 기간별 상이 rate 정당 등장 → 교정 전면 skip
          else this._reRegMetaThisTurn = {
            total: typeof summary.totalPurchaseMembers === 'number' ? summary.totalPurchaseMembers : null,
            reReg: typeof summary.reRegistrationMembers === 'number' ? summary.reRegistrationMembers : null,
            rate: _rrRate,
            itemCount: items ? items.length : 0,
            multi: false,
          };
        }
        else if (!this._reRegMetaThisTurn && Array.isArray(items) && items.length > 0) {
          // [L1 tableDerived] 회원권별 재등록 통계표(스칼라율 부재) — items 배타 모수(신규 newCount / 재등록 reRegistrationCount) 합에서 파생. '신규 N명 중 재등록 M명' 거짓 부분집합 서술 교정 안전망 //
          let _newSum = 0;
          let _reRegSum = 0;
          let _fieldRows = 0;

          for (const _it of items) {
            if (_it && typeof _it.newCount === 'number' && typeof _it.reRegistrationCount === 'number') {
              _newSum += _it.newCount;
              _reRegSum += _it.reRegistrationCount;
              _fieldRows++;
            }
          }

          const _rrTotal = _newSum + _reRegSum;

          if (_fieldRows > 0 && _rrTotal > 0 && _newSum > 0 && _reRegSum > 0) {
            this._reRegMetaThisTurn = {
              total: _rrTotal,
              reReg: _reRegSum,
              rate: Math.round((_reRegSum / _rrTotal) * 1000) / 10,
              itemCount: _newSum,
              multi: false,
              tableDerived: true,
            };
          }
        }
      }
      return {
        success: true,
        intention,
        slot: slotName,
        activeSlots: activeSlots.length > 1 ? activeSlots : undefined,
        count: items ? items.length : 0,
        items: inlineItems,
        availableFields: fieldKeys.length > 0 ? availableFields : undefined,
        enumValues,
        summary: summary || undefined,
        callApiGuide: callApiGuide || undefined,
        defaultDisplay: typeof intentionMeta.defaultDisplay === 'string' && intentionMeta.defaultDisplay ? intentionMeta.defaultDisplay : undefined,
        message: (() => {
          if (!items) return '데이터를 조회했습니다.';
          let summaryInfo = '';
          if (summary && typeof summary === 'object') {
            // 갯수와 다른 메타지표만 노출 (값이 items.length와 정확히 같으면 갯수 메타로 추정 — LLM 혼동 방지)
            const itemsLen = items.length;
            // USER TERM primary metric — input 어휘와 매칭되는 백엔드 metric 우선 라벨링 (결정성 확보)
            const __userInput = this.getUserInput?.() || '';
            const __detectedTerm = detectTermOnly ? detectTermOnly(__userInput) : null;
            const __primary = __detectedTerm ? getPrimaryMetricForTerm(__detectedTerm) : null;
            let primaryStr = '';
            let primaryField = null;
            if (__primary && summary[__primary.field] !== undefined && typeof summary[__primary.field] === 'number') {
              primaryStr = `${__primary.label}=${summary[__primary.field]}${__primary.unit} (← 정답값, summary.${__primary.field})`;
              primaryField = __primary.field;
            }
            // [P1-D2] generic count keys (totalCount/count/total)는 도메인별 의미 모호 + items.length와 차원 불일치 → LLM "총 N건" 환각 야기 ⇒ 노출 제외
            const otherEntries = Object.entries(summary)
              .filter(([k, v]) => typeof v === 'number' && v !== itemsLen && k !== primaryField && !isAmbiguousCountKey(k))
              .slice(0, primaryStr ? 4 : 5)
              .map(([k, v]) => `${k}=${v}`);
            const allEntries = primaryStr ? [primaryStr, ...otherEntries] : otherEntries;
            if (allEntries.length > 0) {
              summaryInfo = ` [백엔드 메타지표: ${allEntries.join(', ')}]`;
            }
          }
          const defaultHint = defaultApplied.length > 0
            ? ` [${defaultApplied.some(d => d.corrected) ? 'AUTO-CORRECTED' : 'DEFAULT'} PARAMS: ${defaultApplied.map(d => `${d.name}="${d.applied}"`).join(', ')}${defaultApplied.some(d => d.corrected) ? '. Auto-corrected based on your question. Proceed to filter/sort/show_display.' : `. If wrong, call call_api again with correct value (options: ${defaultApplied.map(d => d.options.join('/')).join(', ')}).`}]`
            : '';
          // [POST-TOOL ACTION GUIDE — count별 분기 + visualizationOnly intention 분기]
          // helper: aiUtils.selectActionGuide (dual sync class 차단)
          // - 0건: finish 강제 (CHECK_MEMBER_SEARCH 0건 시 회원 재요청 가이드) / 1건: finish 또는 show_display 자율 / N건 visualizationOnly: call_api_guide / N건 default: 기존 가공/display 안내
          let actionGuide = selectActionGuide(items.length, intentionMeta, callApiGuide, intention);
          // [MEMBER EXISTS — DO NOT DENY] PROFILE_DETAIL 0건인데 직전 SEARCH가 회원을 찾았으면 거짓 부재 차단 (헬퍼 buildMemberExistsActionGuide — aiUtils 단일 소스, dual sync) //
          const _memberExistsGuide = buildMemberExistsActionGuide(intention, intentionMeta, items.length, this._lastMember);
          if (_memberExistsGuide) actionGuide = _memberExistsGuide;
          // [SINGLE-PERIOD AGG GUARD] items.length=1 + 시간축 row + avg/average summary key 동시 → degenerate avg
          //   helper: aiUtils.buildSinglePeriodAvgHint (dual sync with nodeAgent.mjs)
          //   CALC_INCONSISTENT 본질: 단일 월 데이터에 "월평균" 라벨 → 사용자 오해. 구조 기반 (field name + key prefix), intention/케이스 hardcode X.
          const singlePeriodAvgHint = buildSinglePeriodAvgHint(items, summary);
          // [1-E β] count=0 multi-call detection → same-category alternative hint inject (intention-agnostic) //
          // [1-E γ] _zeroCountAlternativeMeta 저장 — finishPolicy GUARD 8에서 zero-count finish 차단용
          let zeroCountHint = '';
          if (items.length === 0 && !isComparisonSlot) {
            const prev = this._zeroCountByIntention.get(intention) || 0;
            const next = prev + 1;
            this._zeroCountByIntention.set(intention, next);
            if (next >= 2) {
              const alternatives = this._findSameCategoryAlternatives(intention);
              if (alternatives.length > 0) {
                zeroCountHint = ` [ZERO COUNT HINT] Intention "${intention}" returned 0 items in ${next} consecutive calls this session. Same-category alternative intentions: [${alternatives.join(', ')}]. If user intent could match a different scope (e.g., physical PRODUCT vs overall SALES), consider calling an alternative intention. Otherwise finish with explicit "0건" acknowledgment.`;
                this._zeroCountAlternativeMeta = { intention, count: next, alternatives };
              }
            }
          }
          // [STATUS AUTO-FILTER HINT] STATUS_FILTER_MAP 매칭 시 LLM에 적용 사실 통보 + 정합 응답 표현 강제
          let statusAutoFilterHint = '';
          if (_statusAutoFilterApplied) {
            statusAutoFilterHint = ` [STATUS AUTO-FILTER APPLIED] code가 user input의 "${_statusAutoFilterApplied.label}" 의도 감지 → ${_statusAutoFilterApplied.field}=${_statusAutoFilterApplied.value} 자동 filter (${_statusAutoFilterApplied.before}건 → ${_statusAutoFilterApplied.after}건). intro/outro/finish의 status 표현은 반드시 "${_statusAutoFilterApplied.label}"만 사용 (다른 status 단어 X). count는 filtered count(${_statusAutoFilterApplied.after}건) 사용.`;
          }
          if (_statusAutoFilterEmpty) {
            statusAutoFilterHint = ` [STATUS AUTO-FILTER EMPTY] code가 user input의 "${_statusAutoFilterEmpty.label}" 의도 감지 → ${_statusAutoFilterEmpty.field}=${_statusAutoFilterEmpty.value} filter 시 0건. 전체 ${_statusAutoFilterEmpty.before}건 중 매칭 0건. IMMEDIATELY finish() with honest text matching the requested status: "현재 ${_statusAutoFilterEmpty.label} ${_statusAutoFilterEmpty.noun}은 없어" 형태 (다른 status row 대체 표시 절대 금지, show_display 호출 금지). 다른 status로 답변 변경 X — 사용자가 "${_statusAutoFilterEmpty.label}"을 명시 요청했음.`;
          }
          // [NO-TIME-DIMENSION HINT] 첫 호출 시 multi-period 비교 의도 + 시간 차원 없는 intention → outro/finalText 안내 hint
          let noTimeDimensionHint = '';
          if (this._noTimeDimensionHint && this._noTimeDimensionHint.intention === intention) {
            const _ntdMonths = this._noTimeDimensionHint.inputMonths.join('/');
            noTimeDimensionHint = ` [NO-TIME-DIMENSION INTENTION] "${intention}"은 시간/기간 파라미터가 없는 정적 마스터 데이터입니다. 사용자가 ${_ntdMonths}(${this._noTimeDimensionHint.inputMonths.length}개월) 비교를 요청했지만 본 정보는 월/기간별 변동이 없으므로 추가 call_api(compare_1, compare_2, base 등) 호출 금지. show_display 또는 finish 직접. finalText/outro 작성 시 (1) 본 정보가 월/기간별 변동이 없는 정적(고정) 정보임을 명시 (2) ${_ntdMonths} 모두 동일하게 적용되는 현재 등록 정보를 1회 제시 (3) "모두 같아" / "비교 결과가 없어" / "월별로 다를 수 있어" 같은 잘못된 전제 표현 금지.`;
            // 1회 발동 후 즉시 클리어 — 동일 turn 동일 intention 재호출 시 BLOCK가 우선 작동
          }
          return `${items.length}건 조회 완료${slotName !== 'default' ? ` (슬롯: ${slotName})` : ''}.${boolFilterHint}${summaryHint}${formMismatchHint}${longTextHint}${summaryInfo}${defaultHint} ${actionGuide}${singlePeriodAvgHint}${zeroCountHint}${statusAutoFilterHint}${noTimeDimensionHint}${this.getSlotContext()}${this.getCrossMergeHint()}`;
        })(),
      };
    }

    // [AUTO-COMPUTE] 집계 질문 감지 → 자동 compute (가이드만으로 LLM compute 결정성 확보 불가)
    if (result && result.success && result.count > 0) {
      const _acInput = this.getUserInput?.() || '';
      const _AGGREGATE_OP_MAP = [
        { regex: /평균/, operation: 'average' },
        { regex: /총[^ ]*\s|합계/, operation: 'sum' },
        { regex: /최대|가장.*많/, operation: 'max' },
        { regex: /최소|가장.*적/, operation: 'min' },
      ];
      const _acMatchedOp = _AGGREGATE_OP_MAP.find(m => m.regex.test(_acInput));
      if (_acMatchedOp) {
        const _acBuf = this.getActiveBuf();
        const _acAvailFields = _acBuf?.availableFields || {};
        let _acTargetField = null;
        for (const [key, label] of Object.entries(_acAvailFields)) {
          const _acCleanLabel = typeof label === 'string' ? label.replace(/\(.*\)/, '').trim() : '';
          if (_acCleanLabel && _acInput.includes(_acCleanLabel)) {
            _acTargetField = key;
            break;
          }
        }
        if (_acTargetField && _acBuf?.items?.length > 0) {
          const _acAutoResult = this.computeData({ operation: _acMatchedOp.operation, field: _acTargetField });
          if (_acAutoResult.success) {
            this._autoComputeResult = _acAutoResult;
            this._autoComputeField = _acTargetField;
          }
        }
      }
    }

    return result;
  }

  // 버퍼 데이터 필터링 //
  executeFilter(args) {
    const result = this.applyFilter(args);
    if (result.success) result.bufferState = this.getBufferSnapshot();
    // [BARE-COUNT AMBIGUITY] filter 성공 = active 버퍼 명시 앵커링 → bare {{COUNT}} 모호 게이트 skip 신호 (reset 제외, dual sync with nodeAgent.mjs)
    if (result.success && !args?.reset) this._turnFilterApplied = true;
    return result;
  }

  // 버퍼 데이터 정렬 //
  executeSort(args) {
    // [SORT_INTENT_GUARD] LLM이 사용자 keyword 무시하고 다른 field로 sort 호출 시 override //
    // 본질: "참여 인원 많은 수업" + LLM sort(utilizationRate, asc) — 정반대 정렬 stochastic 잔존 차단 //
    // 조건: (1) user input keyword가 FIELD_REGISTRY 등록 field에 strict 매칭 (length>=3), (2) 매칭 field가 buffer items에 존재, (3) LLM args.field와 다름 //
    // dual sync with nodeAgent.mjs //
    try {
      const _userInputSI = this.getUserInput?.() || '';
      const _bufSI = this.getActiveBuf?.() || this.getBuffer?.();
      const _sampleSI = _bufSI?.items?.[0];
      if (_sampleSI && _userInputSI && args && args.field) {
        const _intent = detectUserSortIntent(_userInputSI, _sampleSI);
        if (_intent && _intent.field && _intent.field !== args.field) {
          // LLM picked unrelated field while user keyword strongly implies different one — override
          const _origField = args.field;
          const _origDir = args.direction;
          args = { ...args, field: _intent.field, direction: _intent.direction };
          if (TOOL_CALLING_LOG) {
            console.log(`[executeSort] SORT_INTENT_GUARD override: "${_origField}/${_origDir}" → "${_intent.field}/${_intent.direction}" (keyword="${_intent.matchedKeyword}", modifier=${_intent.modifierDetected})`);
          }
        }
      }
    } catch (e) {
      if (TOOL_CALLING_LOG) console.log('[executeSort] SORT_INTENT_GUARD error:', e?.message);
    }

    const result = this.applySort(args);
    if (result.success) {
      result.bufferState = this.getBufferSnapshot();
      // [DIMENSION CHECK 안전망] sort 후 buffer의 fields/labels 노출 — LLM이 사용자 input 차원과 buffer 차원 일치 검증 가능
      const buf = this.getBuffer();
      const availableFields = buf?.availableFields || {};
      const sampleKeys = buf?.items?.[0] ? Object.keys(buf.items[0]).filter(k => !k.startsWith('_')) : [];
      const labelList = sampleKeys.map(k => {
        const meta = availableFields[k];
        const label = getFieldLabel(meta);
        return label ? `${k}(${label})` : k;
      }).join(', ');
      if (labelList) {
        result.message += ` [DIMENSION CHECK] 현재 buffer fields: [${labelList}]. 사용자 입력이 묻는 차원/엔티티가 이 fields에 표현되지 않으면 sort 결과는 잘못된 차원의 응답입니다. 우선순위: (1) 즉시 새 call_api 호출 — get_intentions 결과의 enum/params 옵션(groupBy 등)에 사용자 차원과 매칭되는 옵션이 있으면 되묻지 말고 바로 호출. (2) 백엔드에 옵션이 정말 없을 때만 안내. "원하시면 바로 X해드릴게요" 같은 동의 묻기 금지 — 사용자 의도 명확하면 바로 실행.`;
      }
    }
    return result;
  }

  // 집계/통계 계산 //
  executeCompute(args) {
    // [NATIONALITY COUNT — 운영 5차 rid109] LLM이 compute(group by countryCode 전화접두 +82)로 국적 날조하려는 경로 차단.
    //   compute 진입 시점 buffer.items는 raw member list이므로 결정적 집계 후 compute 차단 + finish 유도.
    const _natBlock = this._nationalityCountGuard();
    if (_natBlock) return _natBlock;
    return this.computeData(args);
  }

  // 그룹핑 집계 //
  executeAggregate(args) {
    // [NATIONALITY COUNT — 운영 5차 rid109] compute와 동형 — aggregate(group by countryCode) 국적 날조 경로 차단.
    const _natBlock = this._nationalityCountGuard();
    if (_natBlock) return _natBlock;
    const result = this.computeAggregate(args);
    if (result.success) result.bufferState = this.getBufferSnapshot();
    return result;
  }

  // [NATIONALITY COUNT — 운영 5차 rid109] compute/aggregate 진입 공통 가드 helper. raw member buffer에서 countryIso 결정적
  //   집계 후 group 툴 차단 + finish suggested text set. 발동 시 result 반환, 미발동 시 null. dual sync with nodeAgent.mjs.
  _nationalityCountGuard() {
    if (this._nationalityCountBlocked) return null;
    const _natIntent = detectNationalityCountIntent(this.getUserInput?.() || '');
    if (!_natIntent) return null;
    const _buf = this.getBuffer();
    const _items = _buf && Array.isArray(_buf.items) ? _buf.items : [];
    const _looksMember = _items.length > 0 && _items.some(it => it && it.name != null && (it.memberId != null || it.userId != null));
    if (!_looksMember) return null;
    const _agg = aggregateNationalityCount(_items, _natIntent.kind);
    this._nationalityCountBlocked = true;
    this._finishAllowedByGuard = true;
    this._finishGuardSuggestedText = _agg.text;
    return {
      success: false,
      message: `[NATIONALITY COUNT] 국적 카운트 질문입니다. compute/group 하지 말고, countryIso 필드로 결정적 집계했습니다: 외국인 ${_agg.foreign}명 / 한국 ${_agg.domestic}명 / 미입력 ${_agg.unknown}명. 즉시 finish하면서 정확히 다음 문장으로만 답하세요: "${_agg.text}" countryCode(전화 국가번호 +82)를 국적으로 group 하지 마세요.`,
    };
  }

  // 사칙연산 파생 필드 생성 //
  executeDeriveField(args) {
    const result = this.deriveField(args);
    if (result.success) result.bufferState = this.getBufferSnapshot();
    return result;
  }

  // SMS 템플릿 생성 준비 //
  executeRecommendSms({ mode, tone, targetDescription, targetIndex, targetNames, contentGuide } = {}) {
    const buffer = this.getBuffer();

    if (!buffer.items || buffer.items.length === 0) return { success: false, message: 'No recipients. Call get_intentions → call_api to load data first.' };

    const PHONE_KEYS = ['phone', 'phoneNumber', 'tel', 'mobile', 'contactNumber'];
    const allRecipients = buffer.items
      .filter(item => PHONE_KEYS.some(k => item[k]))
      .map(item => ({
        memberId: item.memberId || item.teacherId || null,
        name: item.name || item.memberName || item.userName || item.teacherName || '회원',
        phone: PHONE_KEYS.reduce((found, k) => found || item[k], null),
        profile: item.profile || null,
      }));

    if (allRecipients.length === 0) return { success: false, message: 'No members with phone numbers found.' };

    // [L12-SMS-REGEN-001 fix] targetIndex (1-based) 처리 — N번 다시 써줘 류
    // 사용자가 특정 row 번호를 명시하면 그 row 만 recipients로 한정 + individual mode 강제
    let recipients = allRecipients;
    let scopedToRow = false;
    if (Number.isFinite(targetIndex) && targetIndex > 0) {
      const idx = Math.floor(targetIndex) - 1;
      // [rid60 SMS-RECIPIENT-ORDER fix] targetIndex는 "표시된 표의 행번호" — 표시순(_lastDisplayedOrder)으로 resolve. //
      //   autoGenerateDisplay autoSort로 표시순 ≠ buffer.items(API순)일 때 buffer index 직접 매핑 시 정반대 회원 선택(운영 rid60: "Kim 말고 하채린만"→Kim).
      //   표시순 보존본 있으면 그 row의 memberId/name으로 allRecipients 매칭, 없으면(직전 표 부재) 기존 buffer index fallback(무회귀).
      const displayedOrder = Array.isArray(this._lastDisplayedOrder) && this._lastDisplayedOrder.length > 0
        ? this._lastDisplayedOrder
        : null;
      const rowCount = displayedOrder ? displayedOrder.length : allRecipients.length;
      if (idx < 0 || idx >= rowCount) {
        return {
          success: false,
          message: `targetIndex=${targetIndex} is out of range. The displayed table has ${rowCount} row(s). Provide a valid 1-based row number, or omit targetIndex to rewrite for all rows.`,
        };
      }
      if (displayedOrder) {
        const displayedTarget = displayedOrder[idx];
        const matched = allRecipients.find(r =>
          (displayedTarget.memberId != null && r.memberId === displayedTarget.memberId)
          || (displayedTarget.name != null && r.name === displayedTarget.name)
        );
        recipients = [matched || allRecipients[idx]];
      }
      else {
        recipients = [allRecipients[idx]];
      }
      scopedToRow = true;
    }

    // [rid170 SMS-NAMED-SUBSET fix] targetNames(이름 배열) 처리 — "김서현, 이옥주 두사람에게" / "2명한테만" 류 named 다중 subset. //
    //   본질: bulk가 buffer 전체를 수신자로 잡는 구멍(운영 rid170: 2명 지정→만료회원 36명 전원 발송 위험). targetIndex(단일 행번호)와 배타 — targetIndex 미지정 시에만 발동.
    //   buffer.items name 매칭(trim+대소문자 무시)으로 recipients 한정. mode는 사용자 요청 유지(bulk=동일문구). 표 narrowing은 아래 _smsRowScopeThisTurn(부분집합)로 처리. dual sync with nodeAgent.mjs //
    //   zero-match 시 전원 fallback 금지(엉뚱 발송 차단) — 안내 메시지 반환해 LLM이 이름 재확인/올바른 목록 로드하도록.
    let scopedToNames = false;
    if (!scopedToRow && Array.isArray(targetNames) && targetNames.length > 0) {
      const wanted = targetNames
        .map(n => (typeof n === 'string' ? n.trim().toLowerCase() : ''))
        .filter(Boolean);
      if (wanted.length > 0) {
        let matched = allRecipients.filter(r => r.name && wanted.includes(String(r.name).trim().toLowerCase()));

        // [rid188 SMS-DEICTIC-STATUS-SCOPE fix] deictic 흐름(_lastStatusScope 활성)에서 LLM이 직전 분류 subset을 targetNames(이름)로 풀었을 때 //
        //   동명이인(같은 이름 복수 매칭)을 scope.memberIds로 disambiguate — buffer 동명이인이 name 매칭으로 함께 잡혀 over 발송되는 구멍 차단(운영 rid188 run2 "조은진" 2명→1 over). //
        //   유일 이름은 그대로(scope 정제 안 함), scope 미존재(순수 rid170)는 블록 skip, scope 매칭 0인 group은 원본 유지(명시 추가 보존). dual sync with nodeAgent.mjs //
        if (matched.length > 0 && this._lastStatusScope) {
          const scopeIds = this._lastStatusScope.memberIds instanceof Set ? this._lastStatusScope.memberIds : new Set(this._lastStatusScope.memberIds || []);
          const byName = new Map();

          for (const r of matched) {
            const key = r.name ? String(r.name).trim().toLowerCase() : '';
            if (!byName.has(key)) byName.set(key, []);
            byName.get(key).push(r);
          }

          const resolved = [];

          for (const group of byName.values()) {
            if (group.length > 1) {
              const inScope = group.filter(r => r.memberId != null && scopeIds.has(r.memberId));
              resolved.push(...(inScope.length > 0 ? inScope : group));
            }
            else {
              resolved.push(...group);
            }
          }

          matched = resolved;
        }

        if (matched.length > 0) {
          recipients = matched;
          scopedToNames = true;
        } else {
          return {
            success: false,
            message: `None of the requested members (${targetNames.join(', ')}) were found in the current list. Ask the user to confirm the names, or load the correct member list first.`,
          };
        }
      }
    }

    // [rid188 SMS-DEICTIC-STATUS-SCOPE fix] 명시 단일(targetIndex)·named(targetNames) 미지정 bulk → 직전 show_member_status 분류 scope(_lastStatusScope)와 교집합으로 recipients 한정. //
    //   본질: deictic "이 사람들"(직전 표시 신규 N명) 의도인데 buffer 전체가 수신자로 잡히는 구멍(운영 rid188: 신규 43→407 전원 발송 위험). memberId(우선)/name(fallback) 매칭. //
    //   교집합 0건 또는 buffer 전체와 동일이면 미발동(stale scope·무회귀 — call_api(initBuffer) 무효화가 1차 방어). subset이면 아래 _smsRowScopeThisTurn 자동 narrowing으로 표도 동기화(미표시 동시 해소). dual sync with nodeAgent.mjs //
    let scopedToStatus = false;
    if (!scopedToRow && !scopedToNames && this._lastStatusScope) {
      const scope = this._lastStatusScope;
      const scopeIds = scope.memberIds instanceof Set ? scope.memberIds : new Set(scope.memberIds || []);
      const scopeNames = scope.names instanceof Set ? scope.names : new Set(scope.names || []);
      // memberId 보유 시 memberId로만 판단(동명이인 오매칭 차단 — name fallback은 memberId 없는 item 전용) //
      const matched = allRecipients.filter(r => {
        if (r.memberId != null) return scopeIds.has(r.memberId);

        return r.name ? scopeNames.has(String(r.name).trim().toLowerCase()) : false;
      });

      if (matched.length > 0 && matched.length < allRecipients.length) {
        recipients = matched;
        scopedToStatus = true;
      }
    }

    const resolvedTarget = targetDescription || 'GENERAL';
    const resolvedMode = scopedToRow ? 'individual' : (mode || 'bulk');
    const modeLabel = resolvedMode === 'individual' ? '개별' : '일괄';
    const scopeLabel = scopedToRow
      ? `${targetIndex}번 회원 ${recipients[0].name}님`
      : scopedToNames
        ? `${recipients.map(r => r.name).join(', ')} ${recipients.length}명`
        : scopedToStatus
          ? `${this._lastStatusScope.label || resolvedTarget} 회원 ${recipients.length}명`
          : `${recipients.length}명의 ${resolvedTarget} 회원`;

    // [SMS-INDIVIDUAL-SUBSET fix] recipients가 buffer 부분집합(targetIndex 단일 / targetNames 다중 / individual 부분) → show_display를 recipients로 narrowing (비-recipient 행 dead "문자 생성 하기" 버튼 방지 + 표 정합). dual sync with nodeAgent.mjs //
    //   전원 대상(bulk 전체 / individual "각각")은 recipients.length === allRecipients.length → 미발동(무회귀). mode 무관 — 부분집합이면 항상 narrowing(rid170 bulk subset 포함).
    if (recipients.length > 0 && recipients.length < allRecipients.length) {
      this._smsRowScopeThisTurn = {
        recipients: recipients.map(r => ({ memberId: r.memberId ?? null, name: r.name ?? null, phone: r.phone ?? null })),
        targetIndex: scopedToRow ? targetIndex : null,
      };
    }

    return {
      success: true,
      mode: resolvedMode,
      targetDescription: resolvedTarget,
      tone: tone || null,
      // [L6-SMS-CONTENT-SPEC-001 / 운영 5차 rid80] 사용자 지정 본문 내용(만료 안내·재등록·응원) → generateSmsTemplates가 FAST_MODEL 프롬프트에 반영. dual sync with nodeAgent.mjs
      contentGuide: (typeof contentGuide === 'string' && contentGuide.trim()) ? contentGuide.trim() : null,
      intention: buffer.intention,
      recipientCount: recipients.length,
      recipients,
      targetIndex: scopedToRow ? targetIndex : null,
      message: `${scopeLabel}에게 보낼 문자를 추천합니다. (${modeLabel} 모드)`,
    };
  }

  // 매출 캘린더 렌더링 준비 (버퍼 기반 — call_api로 사전 조회 필요) //
  async executeShowSalesView({ intro, startDate, endDate, paymentFilter, showSettlement }) {
    if (TOOL_CALLING_LOG) console.log('[Agent] 📅 show_sales_view params:', { startDate, endDate, paymentFilter, showSettlement });

    if (!startDate || !endDate) {
      return { success: false, message: 'startDate and endDate are required.' };
    }

    const start = moment(startDate);
    const end = moment(endDate);

    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return { success: false, message: 'Invalid date range.' };
    }

    // 버퍼에서 yearSales 읽기 //
    const activeBuffer = this.buffers.get(this.activeSlot);

    if (!activeBuffer || !activeBuffer.items) {
      return {
        success: false,
        message: '매출 데이터가 버퍼에 없습니다. call_api(CHECK_SALES_STATISTICS_BY+PERIOD)를 먼저 호출하세요.'
      };
    }

    // 버퍼 날짜와 요청 날짜의 연도 비교 //
    const bufferDateRange = activeBuffer.dateRange;

    if (bufferDateRange) {
      const bufferYear = moment(bufferDateRange.startDate).format('YYYY');
      const requestYear = start.format('YYYY');

      if (bufferYear !== requestYear) {
        return {
          success: false,
          message: `버퍼 데이터는 ${bufferYear}년이지만 요청은 ${requestYear}년입니다. call_api(CHECK_SALES_STATISTICS_BY+PERIOD)를 먼저 호출하세요.`
        };
      }
    }

    // paymentFilter 적용 //
    const rawYearSales = activeBuffer.items;

    let yearSales = (!paymentFilter || paymentFilter === 'all')
      ? rawYearSales
      : rawYearSales.filter(sale => this.categorizePaymentMethod(sale) === paymentFilter);

    let holidays = new Set();
    let settlementCycle = 10;
    let settlements = [];
    let settledMerchantUids = [];

    // holiday + 정산 조회 //
    try {
      const monthStart = start.clone().startOf('month');
      const monthEnd = end.clone().endOf('month');

      const calendarStart = monthStart.clone().startOf('week');
      const calendarEnd = monthEnd.clone().endOf('week');

      const holidayStart = calendarStart.clone().subtract(10, 'days');
      const holidayEnd = calendarEnd.clone().add(10, 'days');

      const fetchModule = new FetchModule();

      const apiCalls = [
        fetchModule.getDataVersion2(
          `holiday?startDate=${holidayStart.format('YYYY-MM-DD')}&endDate=${holidayEnd.format('YYYY-MM-DD')}`, 'GET'
        ),
      ];

      // 정산 API는 showSettlement일 때만 호출 //
      if (showSettlement) {
        apiCalls.push(
          fetchModule.postDataVersion2('sales/history/settlement', 'POST', {
            startDate: monthStart.format('YYYY-MM-DDTHH:mm:ss'),
            endDate: monthEnd.format('YYYY-MM-DDT23:59:59'),
          })
        );
      }

      const results = await Promise.all(apiCalls);
      const holidayRes = results[0];
      const settlementRes = showSettlement ? results[1] : null;

      // 공휴일 데이터 파싱 //
      if (holidayRes.status === 200 && holidayRes.data?.holidays) {
        for (const h of holidayRes.data.holidays) {
          holidays.add(moment(h.holidayDate).format('YYYY-MM-DD'));
        }
      }

      // 정산 데이터 파싱 //
      if (settlementRes && settlementRes.status === 200 && settlementRes.data) {
        const data = settlementRes.data;
        settlements = data.settlements || [];
        settledMerchantUids = data.settledMerchantUids || [];

        const cycle = data.settlementCycle || 10;
        const unit = data.settlementCycleUnit;

        if (unit === 'month') settlementCycle = cycle * 30;
        else if (unit === 'week') settlementCycle = cycle * 7;
        else settlementCycle = cycle;

        if (TOOL_CALLING_LOG) console.log('[Agent] 💰 Settlement loaded:', { cycle: settlementCycle, settlements: settlements.length, settledUids: settledMerchantUids.length });
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[Agent] ⚠️ Sales view fetch failed:', e.message);
    }

    if (yearSales.length === 0) {
      return { success: false, message: 'No sales data found for this year.' };
    }

    // 단일일 조회 (startDate === endDate) → daily 모드 //
    if (startDate === endDate) {
      const dayStr = start.format('YYYY.MM.DD');

      const daySales = yearSales.filter(sale => {
        if (!sale.approvalDate) return false;
        return moment(sale.approvalDate).format('YYYY.MM.DD') === dayStr;
      });

      return {
        success: true,
        salesViewResult: {
          mode: 'daily',
          intro: intro || null,
          date: startDate,
          daySales,
          yearSales,
          paymentFilter: paymentFilter || 'all',
        },
        count: daySales.length,
        message: `${startDate} 매출 ${daySales.length}건 준비 완료.`,
      };
    }

    // 월 전체 날짜 배열 생성 (SalesCalendar 방식: firstWeek~lastWeek) //
    const monthStart = start.clone().startOf('month');
    const monthEnd = end.clone().endOf('month');

    const calendarStart = monthStart.clone().startOf('week');
    const calendarEnd = monthEnd.clone().endOf('week');

    const dateList = [];
    const current = calendarStart.clone();

    while (current.isSameOrBefore(calendarEnd, 'day')) {
      dateList.push({
        date: current.clone(),
        salesPrice: 0,
        settlementPrice: 0,
        settlementStatus: 'SCHEDULED',
      });
      current.add(1, 'day');
    }

    // 매출 합산 + 정산 예측 계산 //
    const LINKOPAY_FEE_RATE = 0.035;
    const LINKOPAY_START = moment('2025-09-01');
    const settledUidSet = new Set(settledMerchantUids);

    // 공휴일/영업일 헬퍼 //
    function isOffDay(m) {
      const dow = m.day();
      if (dow === 0 || dow === 6) return true;
      return holidays.has(m.format('YYYY-MM-DD'));
    }

    function daysUntilBusinessDay(m) {
      const d = m.clone();
      let days = 0;

      while (isOffDay(d)) {
        d.add(1, 'day');
        days++;
      }

      return days;
    }

    for (let i = 0; i < dateList.length; i++) {
      const dateStr = dateList[i].date.format('YYYY.MM.DD');

      for (const sale of yearSales) {
        if (!sale.approvalDate) continue;
        const saleDate = moment(sale.approvalDate).format('YYYY.MM.DD');

        // 매출 합산 //
        if (dateStr === saleDate) {
          dateList[i].salesPrice += (sale.totalPrice || 0);
        }

        // 정산 예측 계산 (showSettlement 활성 시) //
        if (showSettlement) {
          const settlementTargetStr = dateList[i].date.clone().subtract(settlementCycle, 'days').format('YYYY.MM.DD');

          if (settlementTargetStr === saleDate
            && moment(sale.approvalDate).isAfter(LINKOPAY_START)
            && sale.cardPrice > 0
            && sale.merchantUid
            && (sale.merchantUid.split('-')[2] === '1' || sale.merchantUid.split('-')[2] === '2')
            && !settledUidSet.has(sale.merchantUid)) {
            const feeAmount = Math.floor(sale.cardPrice * LINKOPAY_FEE_RATE);

            if (isOffDay(dateList[i].date)) {
              const days = daysUntilBusinessDay(dateList[i].date);

              if (dateList.length > (i + days)) {
                dateList[i + days].settlementPrice += (sale.cardPrice - feeAmount);
              }
            }
            else {
              dateList[i].settlementPrice += (sale.cardPrice - feeAmount);
            }
          }
        }
      }
    }

    // 서버 정산 데이터로 예측값 덮어쓰기 //
    if (showSettlement) {
      for (let i = 0; i < dateList.length; i++) {
        const dateStr = dateList[i].date.format('YYYY.MM.DD');

        for (const s of settlements) {
          if (s.settledDate && moment(s.settledDate).format('YYYY.MM.DD') === dateStr) {
            dateList[i].settlementPrice = s.settlementAmount;
            dateList[i].settlementStatus = s.status;
            break;
          }
        }
      }
    }

    // 7일 단위로 weeksData 분리 (조회 기간과 겹치는 주만 포함) //
    const weeksData = [];

    for (let i = 0; i < dateList.length; i += 7) {
      const week = dateList.slice(i, i + 7);

      // 이 주의 날짜 중 하나라도 조회 기간(start~end)과 겹치면 포함 //
      const hasOverlap = week.some(d => d.date.isBetween(start, end, 'day', '[]'));
      if (!hasOverlap) continue;

      weeksData.push(
        week.map(d => ({
          date: d.date.format('YYYY-MM-DDTHH:mm:ss'),
          salesPrice: d.salesPrice,
          settlementPrice: d.settlementPrice,
          settlementStatus: d.settlementStatus,
        }))
      );
    }

    return {
      success: true,
      salesViewResult: { intro: intro || null, weeksData, yearSales, paymentFilter: paymentFilter || 'all', startDate, endDate },
      count: weeksData.length,
      message: `${weeksData.length}주 매출 캘린더 준비 완료.`,
    };
  }

  // 매출 차트 렌더링 준비 //
  executeShowRevenueChart({ intro, year }) {
    const currentYear = year || new Date().getFullYear();
    const prevYear = currentYear - 1;

    // 2년치 데이터가 하나의 버퍼에 들어있음 (슬롯명 무관)
    const buffer = this.buffers.get(this.activeSlot) || this.buffers.get('default');

    if (!buffer || !buffer.items || buffer.items.length === 0) {
      return { success: false, message: '매출 데이터가 버퍼에 없습니다. call_api(CHECK_SALES_STATISTICS_BY+PERIOD)를 먼저 호출하세요.' };
    }

    const allSales = Array.isArray(buffer.items) ? buffer.items : (buffer.items.sales || []);
    const sample = allSales[0];

    // [Format detection] 두 형태 지원: (A) approvalDate raw rows / (B) month 기반 pre-aggregated rows
    const hasApprovalDate = !!(sample && 'approvalDate' in sample);
    const hasMonthAggregate = !!(sample && 'month' in sample && (sample.totalSales != null || sample.totalPrice != null));

    // [GUARD] 두 형태 모두 아니면 retry 안내
    if (!hasApprovalDate && !hasMonthAggregate) {
      const _bufIntention = buffer.intention || '(unknown)';
      // [C-#6] viz 도구 buffer mismatch도 finishPolicy GUARD 14 신호로 — 회복 없이 finish 거부("답변 어려워요") 차단
      this._bufferMismatchInfo = { columns: ['월별 매출 차트 데이터(month, totalSales)'], activeSlot: this.activeSlot };
      return {
        success: false,
        message: `현재 buffer intention "${_bufIntention}" 데이터에 차트 렌더링 필수 필드(approvalDate 또는 month+totalSales)가 없습니다. call_api({intention:"CHECK_SALES_STATISTICS_BY+PERIOD"})로 raw 매출 데이터를 먼저 로드하거나 call_api({intention:"CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD"})로 월별 집계를 로드한 후 재시도하세요.`,
      };
    }

    if (TOOL_CALLING_LOG) {
      const sampleDates = allSales.slice(0, 5).map(s => s.approvalDate || s.month);
      const years = hasApprovalDate
        ? [...new Set(allSales.map(s => s.approvalDate?.split('T')[0]?.split('-')[0]).filter(Boolean))]
        : [...new Set(allSales.map(s => String(s.month || '').split('-')[0]).filter(Boolean))];
      console.log('[Agent] 📊 revenue buffer debug:', { totalItems: allSales.length, format: hasApprovalDate ? 'raw' : 'aggregated', sampleDates, uniqueYears: years, expectedCurrent: currentYear, expectedPrev: prevYear });
    }

    const months = Array.from({ length: 12 }, () => ({ totalPrice: 0, prevTotalPrice: 0 }));
    let maxPrice = 0;
    let currentCount = 0;
    let prevCount = 0;

    if (hasApprovalDate) {
      // [Format A — raw rows] approvalDate 기준 연/월 분리 누적
      for (const sale of allSales) {
        if (!sale.approvalDate) continue;
        const saleYear = parseInt(sale.approvalDate.split('T')[0].split('-')[0], 10);
        const monthIndex = parseInt(sale.approvalDate.split('T')[0].split('-')[1], 10) - 1;
        if (monthIndex < 0 || monthIndex > 11) continue;
        if (saleYear === currentYear) {
          months[monthIndex].totalPrice += sale.totalPrice || 0;
          if (months[monthIndex].totalPrice > maxPrice) maxPrice = months[monthIndex].totalPrice;
          currentCount++;
        }
        else if (saleYear === prevYear) {
          months[monthIndex].prevTotalPrice += sale.totalPrice || 0;
          prevCount++;
        }
      }
    }
    else if (hasMonthAggregate) {
      // [Format B — pre-aggregated] month "YYYY-MM" 그대로 12개월 배열에 매핑 (이미 집계됨 → 직접 할당)
      for (const row of allSales) {
        if (!row.month) continue;
        const parts = String(row.month).split('-');
        const rowYear = parseInt(parts[0], 10);
        const monthIndex = parseInt(parts[1], 10) - 1;
        if (monthIndex < 0 || monthIndex > 11) continue;
        const v = row.totalSales != null ? row.totalSales : row.totalPrice;
        if (typeof v !== 'number') continue;
        if (rowYear === currentYear) {
          months[monthIndex].totalPrice = v;
          if (v > maxPrice) maxPrice = v;
          currentCount++;
        }
        else if (rowYear === prevYear) {
          months[monthIndex].prevTotalPrice = v;
          prevCount++;
        }
      }
    }

    // [POST-AGGREGATE GUARD] 형태는 맞지만 currentYear+prevYear 데이터 0건 — year 파라미터 mismatch
    if (currentCount === 0 && prevCount === 0) {
      return {
        success: false,
        message: `${currentYear}년/${prevYear}년 매출 데이터가 buffer items에 0건입니다. year 파라미터를 확인하거나 call_api로 해당 연도 데이터를 다시 로드하세요. (현재 buffer items: ${allSales.length}건)`,
      };
    }

    if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_revenue_chart:', { currentYear, currentCount, prevCount, format: hasApprovalDate ? 'raw' : 'aggregated' });

    return {
      success: true,
      revenueChartResult: {
        intro: intro || null,
        year: currentYear,
        thisYear: months,
        thisYearMaxPrice: maxPrice,
      },
      count: months.filter(m => m.totalPrice > 0).length,
      message: `${currentYear}년 월별 매출 차트 준비 완료`,
    };
  }

  // 유효회원 증가 추이 차트 (show_member_trend) — 월별 유효회원 수, 무료체험 포함(막대)/제외(점선) 2계열, 결제액 기준. dual sync with nodeAgent.mjs //
  executeShowMemberTrend({ intro, startDate, endDate, series }) {
    const REQUIRED_INTENTION = 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST';

    let buffer = this._findBufferByIntention ? this._findBufferByIntention(REQUIRED_INTENTION) : null;
    if (!buffer) {
      const candidate = this.buffers?.get?.(this.activeSlot) || this.buffers?.get?.('default');
      if (candidate?.intention === REQUIRED_INTENTION) buffer = candidate;
    }

    const items = (buffer && Array.isArray(buffer.items)) ? buffer.items : null;
    if (!items || items.length === 0) {
      return {
        success: false,
        message: `회원 메타(usages 배열 포함) 데이터가 버퍼에 없습니다. call_api({intention:"${REQUIRED_INTENTION}"})를 먼저 호출하세요.`,
      };
    }

    // 기간 결정 — 명시 startDate/endDate 우선, 없으면 buffer.dateRange, 없으면 최근 12개월 //
    let sD = String(startDate || '').slice(0, 10);
    let eD = String(endDate || '').slice(0, 10);

    if (!sD || !eD) {
      const dr = buffer.dateRange;
      if (dr && dr.startDate && dr.endDate) {
        sD = String(dr.startDate).slice(0, 10);
        eD = String(dr.endDate).slice(0, 10);
      }
    }

    if (!sD || !eD) {
      const now = new Date();
      const s = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      sD = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-01`;
      eD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    }

    const trend = computeMemberTrend({ members: items, startDate: sD, endDate: eD });
    if (!trend || trend.buckets.length === 0) {
      return {
        success: false,
        message: `${sD}~${eD} 구간에서 월별 유효회원을 계산할 수 없습니다. 기간을 확인하세요.`,
      };
    }

    // 계열 선택 — paidOnly(제외만)/withTrial(포함만)/trialOnly(무료체험만) = 단일 막대(점선 숨김 hideLine), 그 외 both(막대=포함/점선=제외) //
    const mode = (series === 'paidOnly' || series === 'withTrial' || series === 'trialOnly') ? series : 'both';

    let thisYear;
    let thisYearMaxPrice;
    let legendLabels;
    let hideLine = false;

    if (mode === 'trialOnly') {
      thisYear = trend.buckets.map(b => ({ totalPrice: b.trialOnly, prevTotalPrice: 0, momPercent: b.momTrial }));
      thisYearMaxPrice = trend.maxTrial;
      legendLabels = { thisYear: '무료체험만', lastYear: '무료체험 제외' };
      hideLine = true;
    } else if (mode === 'paidOnly') {
      thisYear = trend.buckets.map(b => ({ totalPrice: b.paidOnly, prevTotalPrice: 0, momPercent: b.momPaid }));
      thisYearMaxPrice = trend.maxPaid;
      legendLabels = { thisYear: '무료체험 제외', lastYear: '무료체험 포함' };
      hideLine = true;
    } else if (mode === 'withTrial') {
      thisYear = trend.buckets.map(b => ({ totalPrice: b.withTrial, prevTotalPrice: 0, momPercent: b.momPercent }));
      thisYearMaxPrice = trend.maxCount;
      legendLabels = { thisYear: '무료체험 포함', lastYear: '무료체험 제외' };
      hideLine = true;
    } else {
      thisYear = trend.buckets.map(b => ({ totalPrice: b.withTrial, prevTotalPrice: b.paidOnly, momPercent: b.momPercent }));
      thisYearMaxPrice = trend.maxCount;
      legendLabels = { thisYear: '무료체험 포함', lastYear: '무료체험 제외' };
    }

    const xLabels = trend.buckets.map(b => b.label);

    return {
      success: true,
      memberTrendResult: {
        intro: intro || null,
        xLabels,
        thisYear,
        thisYearMaxPrice,
        legendLabels,
        hideBar: false,
        hideLine,
      },
      count: trend.buckets.length,
      message: `${sD.slice(0, 7)}~${eD.slice(0, 7)} 월별 유효회원 추이 준비 완료 (${trend.buckets.length}개월, ${mode})`,
    };
  }

  // 회원 증감 추이 차트 (show_member_flow_trend) — 월별 신규/재등록/이탈, 발산 막대(위=신규+재등록/아래=이탈). dual sync with nodeAgent.mjs //
  executeShowMemberFlowTrend({ intro, startDate, endDate }) {
    const REQUIRED_INTENTION = 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST';

    let buffer = this._findBufferByIntention ? this._findBufferByIntention(REQUIRED_INTENTION) : null;
    if (!buffer) {
      const candidate = this.buffers?.get?.(this.activeSlot) || this.buffers?.get?.('default');
      if (candidate?.intention === REQUIRED_INTENTION) buffer = candidate;
    }

    const items = (buffer && Array.isArray(buffer.items)) ? buffer.items : null;
    if (!items || items.length === 0) {
      return {
        success: false,
        message: `회원 메타(usages 배열 포함) 데이터가 버퍼에 없습니다. call_api({intention:"${REQUIRED_INTENTION}"})를 먼저 호출하세요.`,
      };
    }

    // 기간 결정 — 명시 startDate/endDate 우선, 없으면 buffer.dateRange, 없으면 최근 12개월 //
    let sD = String(startDate || '').slice(0, 10);
    let eD = String(endDate || '').slice(0, 10);

    if (!sD || !eD) {
      const dr = buffer.dateRange;
      if (dr && dr.startDate && dr.endDate) {
        sD = String(dr.startDate).slice(0, 10);
        eD = String(dr.endDate).slice(0, 10);
      }
    }

    if (!sD || !eD) {
      const now = new Date();
      const s = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      sD = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-01`;
      eD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    }

    const trend = computeMemberFlowTrend({ members: items, startDate: sD, endDate: eD });
    if (!trend || trend.buckets.length === 0) {
      return {
        success: false,
        message: `${sD}~${eD} 구간에서 월별 회원 증감을 계산할 수 없습니다. 기간을 확인하세요.`,
      };
    }

    return {
      success: true,
      memberFlowTrendResult: {
        intro: intro || null,
        monthlyStats: trend.buckets,
      },
      count: trend.buckets.length,
      message: `${sD.slice(0, 7)}~${eD.slice(0, 7)} 월별 회원 증감 추이 준비 완료 (${trend.buckets.length}개월, 신규/재등록/이탈)`,
    };
  }

  // 월별 신규/재등록 매출 추이 차트 (show_member_revenue_trend) — RevenueChart 재활용(막대=신규 매출/점선=재등록 매출, 원 단위). dual sync with nodeAgent.mjs //
  executeShowMemberRevenueTrend({ intro, startDate, endDate }) {
    const REQUIRED_INTENTION = 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST';

    let buffer = this._findBufferByIntention ? this._findBufferByIntention(REQUIRED_INTENTION) : null;
    if (!buffer) {
      const candidate = this.buffers?.get?.(this.activeSlot) || this.buffers?.get?.('default');
      if (candidate?.intention === REQUIRED_INTENTION) buffer = candidate;
    }

    const items = (buffer && Array.isArray(buffer.items)) ? buffer.items : null;
    if (!items || items.length === 0) {
      return {
        success: false,
        message: `회원 메타(usages 배열 포함) 데이터가 버퍼에 없습니다. call_api({intention:"${REQUIRED_INTENTION}"})를 먼저 호출하세요.`,
      };
    }

    // 기간 결정 — 명시 우선, 없으면 buffer.dateRange, 없으면 최근 12개월 //
    let sD = String(startDate || '').slice(0, 10);
    let eD = String(endDate || '').slice(0, 10);

    if (!sD || !eD) {
      const dr = buffer.dateRange;
      if (dr && dr.startDate && dr.endDate) {
        sD = String(dr.startDate).slice(0, 10);
        eD = String(dr.endDate).slice(0, 10);
      }
    }

    if (!sD || !eD) {
      const now = new Date();
      const s = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      sD = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-01`;
      eD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    }

    const trend = computeNewMemberRevenueTrend({ members: items, startDate: sD, endDate: eD });
    if (!trend || trend.buckets.length === 0) {
      return {
        success: false,
        message: `${sD}~${eD} 구간에서 월별 신규/재등록 매출을 계산할 수 없습니다. 기간을 확인하세요.`,
      };
    }

    const thisYear = trend.buckets.map(b => ({ totalPrice: b.newRevenue, prevTotalPrice: b.reRegRevenue, momPercent: b.momPercent }));
    const xLabels = trend.buckets.map(b => b.label);

    return {
      success: true,
      memberTrendResult: {
        intro: intro || null,
        xLabels,
        thisYear,
        thisYearMaxPrice: trend.maxRevenue,
        legendLabels: { thisYear: '신규 매출', lastYear: '재등록 매출' },
        hideBar: false,
        hideLine: false,
        valueUnit: '원',
      },
      count: trend.buckets.length,
      message: `${sD.slice(0, 7)}~${eD.slice(0, 7)} 월별 신규/재등록 매출 추이 준비 완료 (${trend.buckets.length}개월)`,
    };
  }

  async executeShowMemberStatus({ intro, memberType, month, startDate, endDate }) {
    const period = this._resolvePeriod({ month, startDate, endDate });

    // [Architecture B 일원화 2026-05-19] 자체 fetch 제거 — call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) buffer 사용
    // [L5-CROSS-004 fix 2026-05-25] intention 정합 강제 — usages 배열 필수. 잘못된 buffer(CHECK_NEW+MEMBER_LIST_BY+PERIOD 등 list intention)에서 silent 0건 분류 → false negative 차단
    const REQUIRED_INTENTION = 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST';
    // [rid179 MEMBER_STATUS GUARD] 단순 회원 list(EXPIRED/ALIVE/NEW) 조회에 분류 도구 오용 차단 — dual sync with nodeAgent.mjs //
    //   운영 rid179: "만료회원은?" → active=EXPIRED_LIST(423) 인데 LLM이 show_member_status(churned) 호출 → requiredSources 자동보강 HISTORY 506건에서 이탈 26명 분류 → 화면 표 26 vs final_text 423(EXPIRED count) 자기모순. //
    //   active 슬롯이 분류 source(HISTORY_LIST) 아니고 사용자 입력에 분류 의도 키워드(신규/재등록/이탈/잔존)회원 부재 시 차단 → show_display 유도. "만료"는 분류 키워드 아님(EXPIRED 의미). //
    {
      const _activeBufG = this.buffers.get(this.activeSlot) || this.buffers.get('default');
      const _activeIntentionG = _activeBufG?.intention || '';
      const _userInputG = this.getUserInput ? this.getUserInput() : '';
      const _MEMBER_STATUS_KW = /(?:신규|재등록|이탈|잔존)[가-힣\s]{0,3}(?:회원|가입자|고객)/;
      if (_activeIntentionG !== REQUIRED_INTENTION && (_activeBufG?.items?.length || 0) > 0 && !_MEMBER_STATUS_KW.test(_userInputG)) {
        return {
          success: false,
          message: `[MEMBER_STATUS GUARD] 사용자 입력 "${_userInputG.slice(0, 40)}"은(는) 회원 분류(신규/재등록/이탈)가 아니라 단순 회원 목록 조회입니다. 현재 활성 슬롯 intention="${_activeIntentionG}"(${_activeBufG.items.length}건)을 show_display({intro, columns:["name","viewMembershipName","viewEndDate","phone"]})로 그대로 표시하세요. show_member_status는 "신규/재등록/이탈 회원" 분류 명시 요청에만 사용합니다.`,
        };
      }
    }
    let buffer = this.findBufferByIntention
      ? this.findBufferByIntention(REQUIRED_INTENTION)
      : null;
    if (!buffer) {
      // fallback: activeSlot/default가 동일 intention 인 경우만 허용
      const candidate = this.buffers.get(this.activeSlot) || this.buffers.get('default');
      if (candidate?.intention === REQUIRED_INTENTION) buffer = candidate;
    }
    const items = (buffer && Array.isArray(buffer.items)) ? buffer.items : null;
    if (!items || items.length === 0) {
      // intention 불일치 / buffer 부재 — LLM이 처음부터 잘못된 sequence 탔거나 회원 list intention buffer만 있는 경우
      const activeBuf = this.buffers.get(this.activeSlot) || this.buffers.get('default');
      const activeIntention = activeBuf?.intention || '(없음)';
      const activeCount = activeBuf?.items?.length || 0;
      const hint = (activeCount > 0 && activeIntention !== REQUIRED_INTENTION)
        ? ` 현재 활성 슬롯(${this.activeSlot})은 intention="${activeIntention}" / items=${activeCount}건 — show_member_status는 사용 불가입니다. 대신 show_display({intro, columns: ["name", "registrationDate", "memberShipName", "phone"]})로 회원 목록을 그대로 표시하세요. (산정기간 신규/이탈 분류가 필요하면 call_api({intention:"${REQUIRED_INTENTION}"})를 먼저 호출 후 재시도)`
        : ` call_api({intention:"${REQUIRED_INTENTION}"})를 먼저 호출하세요.`;
      return {
        success: false,
        message: `회원 메타 데이터(usages 배열 포함)가 버퍼에 없습니다.${hint}`,
      };
    }

    // 통계 페이지 MemberStatusBox와 동일한 분류 로직 — visualizationTransforms utility로 분리
    const members = classifyMemberStatus(items, {
      memberType,
      periodStartISO: period.startISO,
      periodEndISO: period.endISO,
    });

    const typeLabel = memberType === 'new' ? '신규' : memberType === 'reregistered' ? '재등록' : '이탈';

    // [신규/재등록 매출 총액] SALES 버퍼(CHECK_SALES_STATISTICS_BY+PERIOD)가 있으면 통계 MemberStatusBox 미러 조인 합산. 이탈은 매출 부적합 → null. dual sync with nodeAgent.mjs //
    let _totalRevenue = null;
    if (memberType === 'new' || memberType === 'reregistered') {
      const _sbFn = this.findBufferByIntention || this._findBufferByIntention;
      const _salesBuf = _sbFn ? _sbFn.call(this, 'CHECK_SALES_STATISTICS_BY+PERIOD') : null;
      const _salesItems = (_salesBuf && Array.isArray(_salesBuf.items)) ? _salesBuf.items : null;
      if (_salesItems && _salesItems.length > 0) _totalRevenue = computeMemberStatusRevenue(members, _salesItems);
    }

    // [전체 컴포넌트] 월별 신규/재등록/이탈 막대 차트용 monthlyStats — 통계 MemberStatusBox 차트 재현(최근 12개월, 전체 items 기준). computeMemberFlowTrend 재사용. dual sync with nodeAgent.mjs //
    const _mfNow = new Date();
    const _mfS = new Date(_mfNow.getFullYear(), _mfNow.getMonth() - 11, 1);
    const _mfSD = `${_mfS.getFullYear()}-${String(_mfS.getMonth() + 1).padStart(2, '0')}-01`;
    const _mfED = `${_mfNow.getFullYear()}-${String(_mfNow.getMonth() + 1).padStart(2, '0')}-01`;
    const _flow = computeMemberFlowTrend({ members: items, startDate: _mfSD, endDate: _mfED });
    const _monthlyStats = (_flow && Array.isArray(_flow.buckets)) ? _flow.buckets : [];

    // [rid188 SMS-DEICTIC-STATUS-SCOPE fix] 분류 표시 대상을 표시순(_lastDisplayedOrder)·deictic SMS scope(_lastStatusScope)로 승격 — //
    //   show_member_status는 buffer(HISTORY_LIST 전체)에서 신규/재등록/이탈 subset만 화면 표시하나 buffer.items는 전체 유지 → 후속 "이 사람들에게 문자"·bulk SMS가 전체를 수신자로(운영 rid188: 신규 43 의도→407 전원 발송 위험). 화면 표시 subset을 SMS 대상 한정 기준으로 보존. dual sync with nodeAgent.mjs //
    this._lastDisplayedOrder = members.map(m => ({ memberId: m.memberId ?? null, name: m.name ?? null }));
    this._lastStatusScope = {
      memberIds: new Set(members.map(m => m.memberId).filter(id => id != null)),
      names: new Set(members.map(m => (m.name ? String(m.name).trim().toLowerCase() : '')).filter(Boolean)),
      label: typeLabel,
      count: members.length,
      sourceIntention: (buffer && buffer.intention) || REQUIRED_INTENTION,  // scope 생성 데이터 — 같은 intention 재로드는 scope 유지(initBuffer), 다른 데이터만 무효화
    };

    return {
      success: true,
      memberStatusResult: {
        intro: intro || null,
        memberType,
        members,
        totalRevenue: _totalRevenue,
        monthlyStats: _monthlyStats,
        accentColor: memberType === 'churned' ? '#6DD49E' : null,
        month: period.month,
        startDate: period.startISO,
        endDate: period.endISO,
        periodLabel: period.label,
      },
      count: members.length,
      message: `${period.label} ${typeLabel} 회원 ${members.length}명 목록 준비 완료`,
    };
  }

  // 수업별/시간대별 수강 인원 리스트 렌더링 준비 //
  // [Architecture B 일원화 2026-05-19] call_api(CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD) buffer 사용 //
  async executeShowClassAttendance({ intro, month, startDate, endDate }) {
    const period = this._resolvePeriod({ month, startDate, endDate });
    const buffer = this.buffers.get(this.activeSlot) || this.buffers.get('default');
    const items = (buffer && Array.isArray(buffer.items)) ? buffer.items : null;
    if (!items || items.length === 0) {
      return {
        success: false,
        message: '수업별 수강 인원 데이터가 버퍼에 없습니다. call_api(CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD)를 먼저 호출하세요.',
      };
    }

    const { frequencyData, stats } = sortClassAttendance(items);
    if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_class_attendance:', `${period.label} 수업 ${frequencyData.length}건, 총 예약 ${stats.totalReserves}건, 최다: ${stats.topLessonName}`);

    return {
      success: true,
      classAttendanceResult: {
        intro: intro || null,
        month: period.month,
        startDate: period.startISO,
        endDate: period.endISO,
        periodLabel: period.label,
        frequencyData,
      },
      count: frequencyData.length,
      totalReserves: stats.totalReserves,
      avgReservePerClass: stats.avgReservePerClass,
      topClass: stats.topClass ? `${stats.topClass.classTime} ${stats.topClass.lessonName} (${stats.topClass.reserveCount}명)` : null,
      topLessonName: stats.topLessonName,
      topLessonCount: stats.topLessonCount,
      topTimeSlot: stats.topTimeSlot,
      topTimeCount: stats.topTimeCount,
      message: `${period.label} 수업 슬롯 ${frequencyData.length}건, 총 예약 ${stats.totalReserves}건, 평균 ${stats.avgReservePerClass}명/슬롯`
        + (stats.topLessonName ? `, 인기 수업: '${stats.topLessonName}' ${stats.topLessonCount}명` : '')
        + (stats.topTimeSlot ? `, 인기 시간대: ${stats.topTimeSlot} ${stats.topTimeCount}명` : ''),
    };
  }

  // 회원별 출석 랭킹 렌더링 준비 //
  // [Architecture B 일원화 2026-05-19] call_api(68 + 66) buffer 사용 — multi-intention lookup //
  async executeShowAttendanceRanking({ intro, month, startDate, endDate, isCountOncePerDay, topN }) {
    const period = this._resolvePeriod({ month, startDate, endDate });
    const countOnce = isCountOncePerDay !== undefined ? isCountOncePerDay : true;
    // [B-1 과적합 수술] 명시적 표/리스트 요구 턴 감지 — 렌더 후 show_display 1회 허용 (POST-GUARD 예외 + 메시지 조건부)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');

    const attendanceBuf = this.findBufferByIntention('CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD');
    const memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    if (!attendanceBuf?.items?.length) {
      return { success: false, message: '출석 데이터가 버퍼에 없습니다. call_api(CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD)를 먼저 호출하세요.' };
    }
    // [graceful degrade — dual sync with nodeAgent.mjs] 회원 메타(HISTORY_LIST) 부재 시 hard-fail 대신 attendanceItems로 랭킹. //
    //   본질: 운영 긴 세션 8-슬롯 고갈로 HISTORY_LIST 미적재 → show_attendance_ranking 실패 → compute(group,name) degenerate fallback (store398 👎).
    //   attendanceItems(MONTHLY+ATTENDANCE_LIST)에 이미 memberId/name/gender/birthDay/phone 존재 → 출석회원 랭킹 산출 가능.
    //   잃는 것: 0회 회원(메타에만 존재) + 회원권명. "꾸준히 나온/출석 순위"엔 0회 회원 무관하므로 안전.
    const _memberSource = (memberBuf?.items?.length) ? memberBuf.items : attendanceBuf.items;
    const _degraded = !(memberBuf?.items?.length);

    const { ranking, totalAttended } = rankMemberAttendance({
      attendanceItems: attendanceBuf.items,
      memberItems: _memberSource,
      periodStartISO: period.startISO,
      periodEndISO: period.endISO,
      countOnce,
    });

    const avgAttendance = ranking.length > 0 ? parseFloat((totalAttended / ranking.length).toFixed(1)) : 0;
    const top = ranking[0] || null;
    const bottom = ranking[ranking.length - 1] || null;

    // [#12 모수/cap] 출석 회원(>0) 풀 기준 모수·평균 + 요청 topN 표시 cap. 전체 명부(0회 포함) 모수 오표현 차단. //
    const attendedCount = ranking.filter(r => r.attendCount > 0).length;
    const avgAttended = attendedCount > 0 ? parseFloat((totalAttended / attendedCount).toFixed(1)) : 0;
    const _topN = (typeof topN === 'number' && topN > 0) ? Math.floor(topN) : null;
    const displayRanking = _topN ? ranking.slice(0, _topN) : ranking;

    if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_attendance_ranking:', `${period.label} ${ranking.length}명, oncePerDay=${countOnce}, top=${top?.name}(${top?.attendCount})`);

    return {
      success: true,
      attendanceRankingResult: {
        intro: intro || null,
        month: period.month,
        startDate: period.startISO,
        endDate: period.endISO,
        periodLabel: period.label,
        isCountOncePerDay: countOnce,
        ranking: displayRanking,
      },
      count: ranking.length,
      attendedCount,
      avgAttended,
      displayedCount: displayRanking.length,
      topN: _topN,
      totalAttendance: totalAttended,
      avgAttendance,
      top: top ? `${top.name}(${top.attendCount}회)` : null,
      bottom: bottom ? `${bottom.name}(${bottom.attendCount}회)` : null,
      message: `${period.label} ${_degraded ? `출석 회원 ${ranking.length}명(0회 회원 제외 — 회원 메타 미적재)` : `회원 ${ranking.length}명(0회 포함)`}, 총 출석 ${totalAttended}회, 평균 ${avgAttendance}회`
        + (top ? `, 1위: ${top.name} ${top.attendCount}회` : '')
        + (bottom && ranking.length > 1 ? `, 최하: ${bottom.name} ${bottom.attendCount}회` : '')
        + ` (집계방식: ${countOnce ? '하루 1회' : '중복 포함'})`
        + (this._vizListRequested
          ? `. [FINISH NOW + LIST REQUESTED] Ranking rendered (${ranking.length} members) AND the user explicitly asked for a table/list — NOW call show_display on the attendance-list buffer (sort by attendance count desc; include member name + attendance count columns), then finish() with a brief Korean summary.`
          : `. [FINISH NOW] Ranking rendered successfully (${ranking.length} members). Call finish() now with a brief Korean summary mentioning total members, top rankers, and average attendance. Do NOT call show_display — ranking already displayed (the user did not explicitly ask for a table).`),
    };
  }

  // LTV(고객 생애 가치) 시각화 준비 //
  // [Architecture B 일원화 2026-05-19] call_api(69 + 70 + 66) buffer 사용 — multi-intention lookup //
  async executeShowLtv({ intro, month, startDate, endDate }) {
    const period = this._resolvePeriod({ month, startDate, endDate });
    const monthStart = moment(period.startISO);

    const salesBuf = this.findBufferByIntention('CHECK_SALES_LIST_BY+YEAR');
    const durationBuf = this.findBufferByIntention('CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD');
    const memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');

    if (!salesBuf?.items?.length) {
      return { success: false, message: '연간 매출 데이터가 버퍼에 없습니다. call_api(CHECK_SALES_LIST_BY+YEAR, standardYear)를 먼저 호출하세요.' };
    }
    if (!durationBuf?.items?.length) {
      return { success: false, message: '회원 지속 기간 데이터가 버퍼에 없습니다. call_api(CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD)를 먼저 호출하세요.' };
    }
    if (!memberBuf?.items?.length) {
      return { success: false, message: '회원 메타가 버퍼에 없습니다. call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST)도 호출하세요.' };
    }

    // 통계 페이지 DurationBox 로직과 동일 — visualizationTransforms utility로 분리
    // 단 기존 동작(산정기간 overlap filter)을 보존하기 위해 직접 처리
    const targetYear = monthStart.year();
    const lastMonth = targetYear === moment().year() ? moment().month() + 1 : 12;
    let totalSum = 0;
    for (const sale of salesBuf.items) {
      if (!sale.approvalDate) continue;
      const y = parseInt(sale.approvalDate.slice(0, 4), 10);
      const m = parseInt(sale.approvalDate.slice(5, 7), 10);
      if (y === targetYear && m >= 1 && m <= lastMonth) totalSum += sale.totalPrice || 0;
    }
    const priceAvg = lastMonth > 0 ? Math.round(totalSum / lastMonth) : 0;

    const memberList = durationBuf.items.slice().sort((a, b) => b.duration - a.duration);
    let durationSum = 0;
    for (const m of memberList) durationSum += (m.duration < 1 ? 1 : m.duration);
    const durationAvg = memberList.length > 0 ? parseFloat((durationSum / memberList.length).toFixed(1)) : 0;

    // 산정기간 회원권 overlap filter — DurationBox.filteredDurationMembers
    const startTs = moment(period.startISO).startOf('day').valueOf();
    const endTs = moment(period.endISO).endOf('day').valueOf();
    const validIds = new Set(
      memberBuf.items
        .filter(m => Array.isArray(m.usages) && m.usages.some(u =>
          moment(u.startDate).startOf('day').valueOf() <= endTs
          && moment(u.endDate).startOf('day').valueOf() >= startTs
        ))
        .map(m => m.memberId)
    );
    const filteredMembers = memberList.filter(d => validIds.has(d.memberId));
    const membersCount = filteredMembers.length;

    const revenueAvgPerMember = (membersCount > 0 && priceAvg > 0) ? Math.round(priceAvg / membersCount) : 0;
    const ltv = (membersCount > 0 && priceAvg > 0 && durationAvg > 0) ? Math.round((priceAvg / membersCount) * durationAvg) : 0;
    const maxAdSpend = Math.round(ltv * 0.2);
    const totalRevenue = ltv * membersCount;

    if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_ltv:', { priceAvg, durationAvg, membersCount, ltv, revenueAvgPerMember, maxAdSpend, totalRevenue });

    return {
      success: true,
      ltvResult: {
        intro: intro || null,
        startDate: monthStart,
        periodStart: period.startISO,
        periodEnd: period.endISO,
        periodLabel: period.label,
        month: period.month,
        priceAvg,
        durationAvg,
        durationMembers: filteredMembers,
        membersCount,
        ltv,
        revenueAvgPerMember,
        maxAdSpend,
        totalRevenue,
      },
      message: `LTV ${period.label}: ${ltv.toLocaleString()}원 (월 평균 매출 ${priceAvg.toLocaleString()}원 / 회원 ${membersCount}명 × 평균 ${durationAvg}개월). [FINISH NOW] LTV 산정 완료. 사용자에게 LTV 결과를 자연어로 요약해서 응답하세요. 추가 도구 호출 금지.`,
    };
  }

  // 출석률 차트 렌더링 준비 //
  // 통계 페이지(AttendanceBox)와 동일한 API를 직접 호출하여 chartData 생성 //
  async executeShowAttendanceChart({ intro, viewMode, isCountOncePerDay, month, startDate, endDate }) {
    // [B-1 과적합 수술] 명시적 표/리스트 요구 턴 감지 — 렌더 후 show_display 1회 허용 (POST-GUARD 예외 + 메시지 조건부)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');
    // [1-F] 시각화 keyword 부재 시 chart 차단 + RATE_STATISTICS intention 안내 (1회 한정)
    // user input "출석률 알려줘" 같은 단순 KPI 의도 case에서 chart 경로 우회 차단 (KPI text 응답 우선)
    if (!this._showAttendanceChartRedirectAttempted) {
      const _userInputChart = this.getUserInput?.() || '';
      const VIZ_KW = /차트|그래프|추이|시각화|그려|대시보드|chart|graph|trend/i;
      if (_userInputChart && !VIZ_KW.test(_userInputChart)) {
        this._showAttendanceChartRedirectAttempted = true;
        return {
          success: false,
          message: `[CHART REDIRECT — KPI INTENT] User input "${_userInputChart.slice(0, 60)}" does NOT contain visualization keyword (차트/그래프/추이/시각화/그려/대시보드). show_attendance_chart is for visualization only. For KPI intent (출석률/avg attendance):\n  1) call_api(intention="CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD", startDate, endDate)\n  2) finish with response using summary.activeRate as KPI text (e.g. Korean: "이번 달 전체 출석률은 {{SUMMARY:activeRate}}%야").\nDo NOT call show_attendance_chart unless user explicitly requests chart/graph visualization.`,
        };
      }
    }

    const mode = viewMode || 'daily';
    const countOnce = isCountOncePerDay !== undefined ? isCountOncePerDay : true;

    const now = new Date();

    // 일간 모드 기간 결정: 우선순위 startDate/endDate > month > 이번달 //
    // 버퍼 dateRange는 사용하지 않음 (이전 도구의 버퍼가 일별 차트와 무관할 수 있음 — 730일치 누적 방지) //
    let dailyStartDate;
    let dailyEndDate;

    if (startDate && endDate) {
      const rawStart = startDate.substring(0, 10);
      const rawEnd = endDate.substring(0, 10);
      dailyStartDate = rawStart + 'T00:00:00';
      dailyEndDate = rawEnd + 'T23:59:59';
    }
    else {
      const targetMonth = (month || moment().format('YYYY-MM')).slice(0, 7);
      const [yearStr, monthStr] = targetMonth.split('-');
      const yearNum = parseInt(yearStr, 10);
      const monthNum = parseInt(monthStr, 10);
      const lastDay = new Date(yearNum, monthNum, 0).getDate();
      dailyStartDate = `${targetMonth}-01T00:00:00`;
      dailyEndDate = `${targetMonth}-${String(lastDay).padStart(2, '0')}T23:59:59`;
    }

    // daily 모드 안전장치: 일별 차트 범위가 365일을 초과하면 종료일 기준 최근 365일로 클램프 //
    // (recharts CartesianAxis가 너무 많은 tick을 렌더하면 React 업데이트 한도 초과 → 무한 루프) //
    const DAILY_MAX_TICKS = 365;

    if (mode === 'daily') {
      const startMs = new Date(dailyStartDate).getTime();
      const endMs = new Date(dailyEndDate).getTime();
      const dayMs = 24 * 60 * 60 * 1000;
      const spanDays = Math.round((endMs - startMs) / dayMs) + 1;

      if (spanDays > DAILY_MAX_TICKS) {
        const clampedStart = new Date(endMs - (DAILY_MAX_TICKS - 1) * dayMs);
        const clampedY = clampedStart.getFullYear();
        const clampedM = String(clampedStart.getMonth() + 1).padStart(2, '0');
        const clampedD = String(clampedStart.getDate()).padStart(2, '0');
        dailyStartDate = `${clampedY}-${clampedM}-${clampedD}T00:00:00`;
        if (TOOL_CALLING_LOG) console.log(`[Agent] ⚠️ show_attendance_chart daily 범위가 ${spanDays}일 → 최근 ${DAILY_MAX_TICKS}일로 클램프`);
      }
    }

    // 월간: dailyStartDate 기준 연도 1월 1일 ~ 12월 31일 //
    const targetYearMonthly = parseInt(dailyStartDate.slice(0, 4), 10);
    const yearlyStartDate = `${targetYearMonthly}-01-01T00:00:00`;
    const yearlyEndDate = `${targetYearMonthly}-12-31T23:59:59`;

    try {
      // [Architecture B 일원화 2026-05-19] 자체 fetch 제거 — call_api(68 + 66) buffer 사용
      // multi-period 처리: daily는 1 slot, monthly는 multi slot (current/previous) 필요 — current만 우선 사용, prev 미지원 (추후 phase)
      const attendanceBuf = this.findBufferByIntention('CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD');
      const memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
      if (!attendanceBuf?.items?.length) {
        return { success: false, message: '출석 데이터가 버퍼에 없습니다. call_api(CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD)를 먼저 호출하세요.' };
      }
      if (!memberBuf?.items?.length) {
        return { success: false, message: '회원 메타가 버퍼에 없습니다. call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST)도 호출하세요 (유효회원 산정용).' };
      }
      const attendanceTotal = attendanceBuf.items;
      // monthly 모드 작년 데이터는 동일 intention 2번 slot으로 분리된 경우만 사용 (LLM이 slot='previous' 명시한 경우)
      const yearlyAttendanceData = (mode === 'monthly')
        ? (this.findBufferByIntention('CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD', 'previous')?.items || attendanceTotal)
        : [];
      const allMembers = memberBuf.items;

      // 4) allMembers의 usages를 timestamp로 파싱 (AttendanceBox.parsedAllMembers와 동일) //
      const parsedAllMembers = allMembers.map(m => ({
        memberId: m.memberId,
        usages: (m.usages || []).map(u => ({
          start: new Date(u.startDate.substring(0, 10) + 'T00:00:00').getTime(),
          end: new Date(u.endDate.substring(0, 10) + 'T00:00:00').getTime(),
        }))
      }));

      // 5) 유틸 함수 (AttendanceBox 로직 그대로) //
      function isMemberValidOn(parsedMember, dayTs) {
        for (const u of parsedMember.usages) {
          if (u.start <= dayTs && u.end >= dayTs) return true;
        }
        return false;
      }

      function getMemberAttendanceOnDay(member, dayStr, oncePerDay) {
        if (oncePerDay) {
          for (const r of member.reserveAttendances) {
            if (r.classDate.substring(0, 10) === dayStr) return 1;
          }
          return 0;
        }
        let count = 0;
        for (const r of member.reserveAttendances) {
          if (r.classDate.substring(0, 10) === dayStr) count++;
        }
        return count;
      }

      function getDailyStats(attendanceMembers, dayTs, dayStr, oncePerDay) {
        let attendees = 0;
        let validMembers = 0;
        for (const m of parsedAllMembers) {
          if (isMemberValidOn(m, dayTs)) validMembers++;
        }
        for (const m of attendanceMembers) {
          attendees += getMemberAttendanceOnDay(m, dayStr, oncePerDay);
        }
        return { attendees, validMembers };
      }

      function getMonthlyValidCount(yr, mon) {
        const mStartTs = new Date(yr, mon - 1, 1).getTime();
        const lastDayOfMonth = new Date(yr, mon, 0).getDate();
        const mEndTs = new Date(yr, mon - 1, lastDayOfMonth).getTime();
        let count = 0;
        for (const m of parsedAllMembers) {
          for (const u of m.usages) {
            if (u.end >= mStartTs && u.start <= mEndTs) {
              count++;
              break;
            }
          }
        }
        return count;
      }

      function getMonthlyAttendedCount(attendanceMembers, yr, mon, oncePerDay) {
        const monthStr = String(yr) + '-' + String(mon).padStart(2, '0');
        let count = 0;
        if (oncePerDay) {
          for (const m of attendanceMembers) {
            for (const r of m.reserveAttendances) {
              if (r.classDate.substring(0, 7) === monthStr) {
                count++;
                break;
              }
            }
          }
        }
        else {
          for (const m of attendanceMembers) {
            for (const r of m.reserveAttendances) {
              if (r.classDate.substring(0, 7) === monthStr) count++;
            }
          }
        }
        return count;
      }

      // 6) chartData 생성 (AttendanceBox.chartData useMemo와 동일) //
      let chartData = [];

      if (mode === 'monthly') {
        const monthLabels = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

        for (let m = 0; m < 12; m++) {
          const validCount = getMonthlyValidCount(targetYearMonthly, m + 1);
          const attended = getMonthlyAttendedCount(yearlyAttendanceData, targetYearMonthly, m + 1, countOnce);
          const rate = validCount === 0 ? 0 : (attended / validCount) * 100;

          chartData.push({
            day: m + 1,
            label: monthLabels[m],
            tooltipLabel: monthLabels[m],
            rate: Math.round(rate * 10) / 10,
            attendees: attended,
            validMembers: validCount
          });
        }
      }
      else {
        const start = new Date(dailyStartDate);
        const end = new Date(dailyEndDate);
        const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        // [rid90] daily 차트는 오늘 이후(미래) 날짜를 렌더하지 않음 — 미래는 출석 결정적 0이라
        //   호버 툴팁이 "유효회원 0/출석자 0"으로 빈 점("안나옴")이 됨. 평균/피크 산정은 이미 미래 제외
        //   (limitTs=min(endTs,nowTs)) → 표시도 동일하게 today로 clamp해 지표-표시 일관 + 빈 점 차단.
        //   과거 전 범위면 영향 없음(endDay<today). dual sync: visualizationTransforms.aggregateAttendanceChart.
        const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const renderEndDay = endDay < todayDay ? endDay : todayDay;

        while (current <= renderEndDay) {
          const dayTs = current.getTime();
          const yr = current.getFullYear();
          const mon = String(current.getMonth() + 1).padStart(2, '0');
          const d = String(current.getDate()).padStart(2, '0');
          const dayStr = `${yr}-${mon}-${d}`;
          const { attendees, validMembers } = getDailyStats(attendanceTotal, dayTs, dayStr, countOnce);
          const rate = validMembers === 0 ? 0 : (attendees / validMembers) * 100;

          chartData.push({
            day: current.getDate(),
            label: String(current.getDate()),
            tooltipLabel: current.getDate() + '일',
            rate: Math.round(rate * 10) / 10,
            attendees: attendees,
            validMembers: validMembers
          });

          current.setDate(current.getDate() + 1);
        }
      }

      // 7) 평균 출석률 + 평균 출석 인원 계산 (AttendanceBox.calcAttendanceRate / calcAttendedCount와 동일) //
      let attendanceRate = 0;
      let attendedCount = 0;

      if (mode === 'monthly') {
        const lastMonth = now.getMonth() + 1;
        let sumOfRates = 0;
        let monthCount = 0;
        let totalAttendees = 0;

        for (let m = 1; m <= lastMonth; m++) {
          const validCount = getMonthlyValidCount(targetYearMonthly, m);
          if (validCount === 0) continue;
          const attended = getMonthlyAttendedCount(yearlyAttendanceData, targetYearMonthly, m, countOnce);
          sumOfRates += (attended / validCount) * 100;
          totalAttendees += attended;
          monthCount++;
        }

        attendanceRate = monthCount > 0 ? Math.round(sumOfRates / monthCount) : 0;
        attendedCount = monthCount > 0 ? Math.round(totalAttendees / monthCount) : 0;
      }
      else {
        const nowTs = now.getTime();
        const endTs = new Date(dailyEndDate).getTime();
        const limitTs = Math.min(endTs, nowTs);
        const current2 = new Date(dailyStartDate);
        const currentDay = new Date(current2.getFullYear(), current2.getMonth(), current2.getDate());
        const limitDay = new Date(limitTs);
        const endDay2 = new Date(limitDay.getFullYear(), limitDay.getMonth(), limitDay.getDate());
        let sumOfRates = 0;
        let dayCount = 0;
        let totalAttendees = 0;

        while (currentDay <= endDay2) {
          const dayTs = currentDay.getTime();
          const yr = currentDay.getFullYear();
          const mon = String(currentDay.getMonth() + 1).padStart(2, '0');
          const d = String(currentDay.getDate()).padStart(2, '0');
          const dayStr = `${yr}-${mon}-${d}`;
          const { attendees, validMembers } = getDailyStats(attendanceTotal, dayTs, dayStr, countOnce);

          if (validMembers > 0) {
            sumOfRates += (attendees / validMembers);
            totalAttendees += attendees;
            dayCount++;
          }

          currentDay.setDate(currentDay.getDate() + 1);
        }

        attendanceRate = dayCount > 0 ? Math.round((sumOfRates / dayCount) * 100) : 0;
        attendedCount = dayCount > 0 ? Math.round(totalAttendees / dayCount) : 0;
      }

      // 8) LLM이 "X일 출석률은?" / "언제가 가장 높아?" 같은 특정 시점 질문에 답할 수 있도록
      //    chartData에서 LLM-friendly 요약 + 일/월별 breakdown을 top-level로 노출 //
      let maxRatePoint = null;
      let minRatePoint = null;
      const dailyStats = [];

      for (const d of chartData) {
        // 데이터가 없는(예약/유효회원 0) 시점은 비교에서 제외 //
        if (d.validMembers > 0) {
          if (!maxRatePoint || d.rate > maxRatePoint.rate) maxRatePoint = d;
          if (!minRatePoint || d.rate < minRatePoint.rate) minRatePoint = d;
        }

        dailyStats.push({
          [mode === 'monthly' ? 'month' : 'day']: d.day,
          label: d.tooltipLabel || d.label,
          rate: d.rate,
          attendees: d.attendees,
          validMembers: d.validMembers,
        });
      }

      const periodLabel = mode === 'monthly' ? '월' : '일';
      const dateRangeLabel = `${dailyStartDate.slice(0, 10)} ~ ${dailyEndDate.slice(0, 10)}`;

      if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_attendance_chart:', { mode, countOnce, dataPoints: chartData.length, max: maxRatePoint?.label, maxRate: maxRatePoint?.rate });

      return {
        success: true,
        attendanceChartResult: {
          intro: intro || null,
          viewMode: mode,
          isCountOncePerDay: countOnce,
          chartData: chartData,
          attendanceRate: attendanceRate,
          attendedCount: attendedCount,
          totalMembers: allMembers.length,
          startDate: dailyStartDate,
          endDate: dailyEndDate,
        },
        count: chartData.filter(d => d.rate > 0).length,
        viewMode: mode,
        isCountOncePerDay: countOnce,
        dateRange: dateRangeLabel,
        avgRate: attendanceRate,
        avgAttendees: attendedCount,
        maxRatePoint: maxRatePoint
          ? { label: maxRatePoint.tooltipLabel || maxRatePoint.label, rate: maxRatePoint.rate, attendees: maxRatePoint.attendees, validMembers: maxRatePoint.validMembers }
          : null,
        minRatePoint: minRatePoint
          ? { label: minRatePoint.tooltipLabel || minRatePoint.label, rate: minRatePoint.rate, attendees: minRatePoint.attendees, validMembers: minRatePoint.validMembers }
          : null,
        dailyStats,
        message: `출석률 차트 준비 (${mode === 'monthly' ? '월간' : '일간'}, ${countOnce ? '중복 제외' : '중복 포함'}, 기간: ${dateRangeLabel})`
          + `, 평균 출석률 ${attendanceRate}%, 평균 출석 ${attendedCount}명`
          + (maxRatePoint ? `, 최고: ${maxRatePoint.tooltipLabel || maxRatePoint.label} ${maxRatePoint.rate}% (${maxRatePoint.attendees}명/${maxRatePoint.validMembers}명)` : '')
          + (minRatePoint && minRatePoint !== maxRatePoint ? `, 최저: ${minRatePoint.tooltipLabel || minRatePoint.label} ${minRatePoint.rate}%` : '')
          + `. 특정 ${periodLabel} 출석률은 dailyStats 배열에서 조회 가능 — 각 항목: { ${mode === 'monthly' ? 'month' : 'day'}, label, rate, attendees, validMembers }.`
          + ` [METRIC NOTE] 평균 출석률 = (일별 출석회원/유효회원 × 100) 평균. NEVER 산정: totalAttended/activeMemberCount.`
          + (this._vizListRequested
            ? ` [NEXT + LIST REQUESTED] Chart rendered AND the user explicitly asked for a table/list — NOW call show_display on the loaded buffer (period rows with attendance figures), then finish() with a brief summary using these numbers.`
            : ` [NEXT] Chart rendered successfully. Call finish() now with a brief summary using these numbers (평균 출석률 ${attendanceRate}%, 평균 출석 ${attendedCount}명${maxRatePoint ? `, 최고 ${maxRatePoint.tooltipLabel || maxRatePoint.label}` : ''}). Do NOT call show_display — chart already displayed (the user did not explicitly ask for a table).`),
      };
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[Agent] ⚠️ show_attendance_chart fetch failed:', e.message);
      return { success: false, message: `출석 차트 데이터 조회 중 오류: ${e.message}` };
    }
  }

  // 연령별 이용 분석 차트 렌더링 준비 //
  // 회원권 선호도 시각화 준비 — 회원권별 가입 랭킹 + 연령 분포 + 매출/재등록 (통계 MembershipPreferenceBox와 동일 로직) //
  // buffer: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST(회원+usages) + CHECK_SALES_LIST_BY+YEAR(매출 기여 카드용) //
  async executeShowMembershipPreference({ intro, startDate, endDate }) {
    // [B-1 과적합 수술 동형] 명시적 표/리스트 요구 턴 감지 — 렌더 후 표 경로 1회 허용 (POST-GUARD 예외 + 메시지 조건부, dual sync nodeAgent.mjs)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');
    // 기간 기본값 — 이번달 //
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const defaultStart = `${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00`;
    const defaultEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59`;
    const startParam = startDate ? `${startDate.slice(0, 10)}T00:00:00` : defaultStart;
    const endParam = endDate ? `${endDate.slice(0, 10)}T23:59:59` : defaultEnd;

    try {
      let memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
      let salesBuf = this.findBufferByIntention('CHECK_SALES_LIST_BY+YEAR');

      // 회원 회원권 이력 auto-recovery — LLM이 call_api를 skip한 경우 자동 fetch //
      if (!memberBuf?.items?.length) {
        if (TOOL_CALLING_LOG) console.log('[Agent] show_membership_preference: memberBuf missing — auto-fetching CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
        const autoResult = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_pref_members' });
        if (autoResult?.success) memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
      }

      if (!memberBuf?.items?.length) {
        return { success: false, message: '회원 회원권 이력이 버퍼에 없습니다. call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST)를 먼저 호출하세요.' };
      }

      // 매출 리스트 auto-recovery (매출 기여 카드용 — 없어도 랭킹/연령/재등록은 정상) //
      if (!salesBuf?.items?.length) {
        if (TOOL_CALLING_LOG) console.log('[Agent] show_membership_preference: salesBuf missing — auto-fetching CHECK_SALES_LIST_BY+YEAR');
        const autoResult = await this.executeCallApi({ intention: 'CHECK_SALES_LIST_BY+YEAR', slot: '_pref_sales' });
        if (autoResult?.success) salesBuf = this.findBufferByIntention('CHECK_SALES_LIST_BY+YEAR');
      }

      // 연 단위 매출 리스트를 산정기간(approvalDate)으로 필터 → 통계 페이지 salesData와 동일 의미 //
      const salesData = (salesBuf?.items || []).filter(s =>
        s && s.approvalDate
        && moment(startParam).isSameOrBefore(s.approvalDate, 'day')
        && moment(endParam).isSameOrAfter(s.approvalDate, 'day')
      );

      const result = computeMembershipPreference({
        allMembers: memberBuf.items,
        salesData,
        startDate: startParam,
        endDate: endParam,
      });

      if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_membership_preference:', `${result.rankings.length}종, 가입 ${result.totalCount}명`);

      return {
        success: true,
        membershipPreferenceResult: {
          intro: intro || null,
          startDate: startParam,
          endDate: endParam,
          ...result,
        },
        count: result.rankings.length,
        message: `회원권 선호도 ${result.rankings.length}종, 가입 회원 ${result.totalCount}명, 회원권 매출 합계 ${result.totalRevenue.toLocaleString()}원.`
          + (result.topCards?.mostSelected ? ` 최다 선택: ${result.topCards.mostSelected.name}(${result.topCards.mostSelected.count}명)` : '')
          + (this._vizListRequested
            ? `. [FINISH NOW + LIST REQUESTED] Preference chart rendered AND the user explicitly asked for a table/list — NOW call_api(intention="CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD", startDate="${startParam.slice(0, 10)}", endDate="${endParam.slice(0, 10)}") for the per-membership purchase table, then show_display (sort by 구매 건수 desc), then finish() with a brief Korean summary.`
            : `. [FINISH NOW] Rendered successfully. Call finish() now with a brief Korean summary (top membership, member count). Do NOT call show_display (the user did not explicitly ask for a table).`),
      };
    }
    catch (e) {
      return { success: false, message: `회원권 선호도 집계 실패: ${e.message}` };
    }
  }

  // 기간별 매출(월별 매출 순위) 시각화 준비 — 월별 매출 집계 + 금액순 정렬 (통계 SalesPeriodBox와 동일 로직) //
  // buffer: CHECK_SALES_LIST_BY+YEAR(approvalDate·totalPrice). 정산은 AI 소스(merchantUid/정산 API) 부재로 제외 //
  async executeShowPeriodSales({ intro, startDate, endDate }) {
    // [B-1 과적합 수술 동형] 명시적 표/리스트 요구 턴 감지 (dual sync nodeAgent.mjs)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');
    // 기간 기본값 — 올해 1/1 ~ 오늘 (미래 빈 달 제외). 명시 기간이 다년도면 연도별로 나눠 직접 호출·병합 //
    const now = new Date();
    const year = now.getFullYear();
    const startParam = startDate ? startDate.slice(0, 10) : `${year}-01-01`;
    const endParam = endDate ? endDate.slice(0, 10) : `${year}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    try {
      // 기간이 걸치는 연도별로 sales/revenue/year 직접 호출·병합 (통계 SalesPeriodBox와 동일 — forceParams 인텐트 우회) //
      const startYear = parseInt(startParam.slice(0, 4), 10);
      const endYear = parseInt(endParam.slice(0, 4), 10);
      const fetchModule = new FetchModule();
      let mergedSales = [];

      for (let y = startYear; y <= endYear; y++) {
        const resp = await fetchModule.postDataVersion2('sales/revenue/year', 'POST', { standardYear: `${y}-01-01T00:00:00` });
        if (resp?.status === 200 && Array.isArray(resp.data?.sales)) mergedSales = mergedSales.concat(resp.data.sales);
      }

      if (!mergedSales.length) {
        return { success: false, message: `${startParam}~${endParam} 매출 데이터가 없습니다.` };
      }

      const result = computeSalesPeriod({
        startDate: moment(startParam),
        endDate: moment(endParam),
        yearSales: mergedSales,
        settlements: [],
        settledMerchantUids: [],
        settlementCycle: 0,
        holidays: new Set(),
      });

      if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_period_sales:', `${startParam}~${endParam}, ${result.monthData.length}개월, 기간 매출 ${result.periodSalesPrice}`);

      const _top = result.monthData[0];

      return {
        success: true,
        periodSalesResult: {
          intro: intro || null,
          startDate: startParam,
          endDate: endParam,
          monthData: result.monthData,
          periodSalesPrice: result.periodSalesPrice,
        },
        count: result.monthData.length,
        message: `${startParam}~${endParam} 월별 매출 순위 ${result.monthData.length}개월, 기간 매출 ${result.periodSalesPrice.toLocaleString()}원.`
          + (_top ? ` 최다 매출 월: ${moment(_top.date).format('YYYY.MM')} (${_top.salesPrice.toLocaleString()}원)` : '')
          + (this._vizListRequested
            ? `. [FINISH NOW + LIST REQUESTED] Chart rendered AND the user explicitly asked for a table/list of PER-MONTH figures. Currently loaded buffers hold INDIVIDUAL transactions (one row per payment) — displaying them does NOT satisfy "월별" (no per-month axis). REQUIRED first step: call_api(intention="CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD", startDate="${startParam}", endDate="${endParam}") to load per-month rows, THEN show_display (sort by 매출 desc), then finish() with a brief Korean summary. Do NOT show_display a transaction-level buffer.`
            : `. [FINISH NOW] Rendered successfully. Call finish() now with a brief Korean summary (top month, period total). Do NOT call show_display (the user did not explicitly ask for a table).`),
      };
    }
    catch (e) {
      return { success: false, message: `기간별 매출 집계 실패: ${e.message}` };
    }
  }

  // [MANAGEMENT-REPORT] 월간 경영 리포트 executor — 여러 섹션(체험전환율/월별매출 …)을 코드 결정 계산으로 //
  //   조립해 표 여러 개(reportDisplays[])로 반환. 각 섹션: 결정적 compute → initBuffer(고유 슬롯) → //
  //   autoGenerateDisplay(displayResult). ALIVE_SCOPE 가드는 userInput "리포트"가 /유효회원/ 불일치라 자연 비발동. //
  //   non-fabrication: 섹션 데이터 부재 시 해당 섹션 skip, 전 섹션 부재면 success=false. dual sync with nodeAgent.mjs //
  async executeManagementReport({ startDate, endDate } = {}) {
    const now = new Date();
    const _fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // 기간 기본값 — 올해 1/1 ~ 오늘 //
    const _start = startDate ? startDate.slice(0, 10) : `${now.getFullYear()}-01-01`;
    const _end = endDate ? endDate.slice(0, 10) : _fmt(now);
    const _today = _fmt(now);
    const _savedActive = this.activeSlot;
    const _isTrialName = n => /체험|무료|trial|drop|드랍|드롭|증정/i.test(n || '');
    const displays = [];
    const summaryParts = [];

    // 회원 메타(usages + gender) 1회 조달 — 섹션 전반 공유 소스 (HISTORY_LIST) //
    const _findBuf = this.findBufferByIntention || this._findBufferByIntention;
    let memberBuf = _findBuf.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');

    if (!memberBuf?.items?.length) {
      const r = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_report_members' });
      if (r?.success) memberBuf = _findBuf.call(this, 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
    }

    const _members = memberBuf?.items || [];

    // 공유 스칼라 — 활성 유효회원(오늘 기준 유효 회원권 보유, 무료체험/증정 제외) · 여성 비율 //
    const _activeValidCount = _members.filter(m =>
      (m?.usages || []).some(u =>
        u?.memberShipName && !_isTrialName(u.memberShipName)
        && String(u.startDate || '').slice(0, 10) <= _today
        && String(u.endDate || '').slice(0, 10) >= _today
      )
    ).length;
    // 활성 유료회원 — 활성 유효회원 중 오늘 유효 회원권이 결제총액 보유(isPaid/totalPrice>0; 백엔드 PR#89 결제필드) //
    const _isPaidUsage = u => (u?.isPaid === true) || (Number(u?.totalPrice) > 0);
    const _activePaidCount = _members.filter(m =>
      (m?.usages || []).some(u =>
        u?.memberShipName && !_isTrialName(u.memberShipName)
        && String(u.startDate || '').slice(0, 10) <= _today
        && String(u.endDate || '').slice(0, 10) >= _today
        && _isPaidUsage(u)
      )
    ).length;
    const _femaleCount = _members.filter(m => {
      const _g = String(m?.gender || '').toLowerCase();
      return _g === 'female' || _g === 'f';
    }).length;
    const _femaleRatio = _members.length > 0 ? Math.round(_femaleCount / _members.length * 1000) / 10 : 0;

    // 전환 기점 상품 — 최다 구매 대표 회원권 (트라이얼류면 '체험 전환율' 라벨, store별 카탈로그 적응) //
    const _catCount = {};
    for (const _m of _members) {
      for (const _u of (_m?.usages || [])) {
        if (_u?.memberShipName) _catCount[_u.memberShipName] = (_catCount[_u.memberShipName] || 0) + 1;
      }
    }
    const _fromName = Object.entries(_catCount).sort((a, b) => b[1] - a[1])[0]?.[0];
    const _isTrialFrom = !!_fromName && _isTrialName(_fromName);
    const _cfLabel = _isTrialFrom ? '체험 전환율' : `'${_fromName}' 전환율`;

    let _conversionRate = null;
    let _convCohort = null, _convConverted = null;
    let _newTotal = null;
    let _periodSales = null;
    let _fFemCohort = null, _fFemConv = null, _fFemRate = null;

    // 재등록 코호트 (백엔드 churn-retention memberRenewals — per-member 만료/재등록 판정) //
    //   items=memberRenewals(만료 코호트만) · summary=expiredMembers/renewedMembers 스칼라. 코호트는 반드시 memberRenewals에서만(EXPIRED_LIST 등 타 소스 재유도 금지 — 정의 불일치). //
    let _memberRenewals = [];
    let _reExpiredTotal = 0, _reRenewedTotal = 0;
    try {
      const _churnIntent = 'CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD';
      let _churnBuf = _findBuf.call(this, _churnIntent);
      if (!Array.isArray(_churnBuf?.items) || _churnBuf.items.length === 0) {
        const _cr = await this.executeCallApi({ intention: _churnIntent, slot: '_report_churn', startDate: _start, endDate: _end });
        if (_cr?.success) _churnBuf = _findBuf.call(this, _churnIntent);
      }
      _memberRenewals = Array.isArray(_churnBuf?.items) ? _churnBuf.items.filter(x => x && x.isExpiredInPeriod) : [];
      const _cs = _churnBuf?.summary || {};
      _reExpiredTotal = typeof _cs.expiredMembers === 'number' ? _cs.expiredMembers : _memberRenewals.length;
      _reRenewedTotal = typeof _cs.renewedMembers === 'number' ? _cs.renewedMembers : _memberRenewals.filter(x => x.isRenewed === true).length;
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] churn-retention fetch failed:', e.message);
    }

    // memberId → member 조인 맵 (gender/birthDay; 05·06과 동일 단일 소스 재사용 — 이중화 금지) //
    const _memberById = new Map();
    for (const _m of _members) { if (_m?.memberId != null) _memberById.set(_m.memberId, _m); }

    // 여성 재등록 세그먼트 (ageBand decade 버킷; memberRenewals ∩ female, 07·08 공유; 버킷 산식 05·06 동일 floor/10*10) //
    const _fReByBand = new Map(); // dec(number; -1=미상) -> { target, renewed }
    let _fReTargetTotal = 0, _fReRenewedTotal = 0;
    for (const _mr of _memberRenewals) {
      const _jm = _memberById.get(_mr?.memberId);
      if (!_jm) continue; // 조인 miss — gender/birthDay 불명 → 여성 세그먼트 제외
      const _jg = String(_jm.gender || '').toLowerCase();
      if (!(_jg === 'female' || _jg === 'f')) continue; // gender exact — 빈/비표준값은 여성 필터 제외
      const _ja = this.computeAge(_jm.birthDay);
      const _jdec = _ja === null ? -1 : Math.floor(_ja / 10) * 10; // birthDay 결측 → 만나이 미상 잔차 버킷
      if (!_fReByBand.has(_jdec)) _fReByBand.set(_jdec, { target: 0, renewed: 0 });
      const _rb = _fReByBand.get(_jdec);
      _rb.target += 1; _fReTargetTotal += 1;
      if (_mr.isRenewed === true) { _rb.renewed += 1; _fReRenewedTotal += 1; }
    }

    // 섹션 A — 전환율 (전체 회원 대상 computeConversionFunnel) //
    try {
      const funnel = _fromName ? computeConversionFunnel({ members: _members, fromName: _fromName, toNames: [], startDate: _start, endDate: _end, today: _today }) : null;

      if (funnel && funnel.cohortCount > 0 && Array.isArray(funnel.rows) && funnel.rows.length > 0) {
        const _cfFields = { conversionTarget: '전환 회원권', convertedCount: '인원(명)', share: '비율(%)', medianDays: '전환 소요일(중앙값)' };
        this.initBuffer(funnel.rows, 'CONVERSION_FUNNEL', _cfFields, '_report_conversion');
        const _cfBuf = this.buffers.get('_report_conversion');
        if (_cfBuf) _cfBuf.appliedSort = { field: 'convertedCount', direction: 'desc' };
        this.setActiveSlot('_report_conversion');
        const _cfIntro = `[${_cfLabel}] ${_start}~${_end} '${_fromName}' 구매 ${funnel.cohortCount}명 중 ${funnel.convertedCount}명(${funnel.conversionRate}%)이 다른 회원권으로 전환했어요.`;
        const _cfDisplay = autoGenerateDisplay({ intro: _cfIntro, columns: ['conversionTarget', 'convertedCount', 'share', 'medianDays'], columnLabels: _cfFields }, this);
        if (_cfDisplay) {
          _cfDisplay._currentAxisKey = '__report=conversion';
          displays.push(_cfDisplay);
          _conversionRate = funnel.conversionRate;
          _convCohort = funnel.cohortCount;
          _convConverted = funnel.convertedCount;
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] conversion section failed:', e.message);
    }

    // 섹션 03 — 월별 전환율 분석 (기간을 월버킷으로 쪼개 각 월 코호트별 computeConversionFunnel 반복; 레퍼런스 image03) //
    try {
      if (_fromName) {
        const _mcRows = [];
        let _mcOrd = 0;
        const _msY = Number(_start.slice(0, 4)), _msM = Number(_start.slice(5, 7));
        const _meY = Number(_end.slice(0, 4)), _meM = Number(_end.slice(5, 7));
        let _cy = _msY, _cm = _msM;

        while (_cy < _meY || (_cy === _meY && _cm <= _meM)) {
          const _mm = String(_cm).padStart(2, '0');
          const _mStart0 = `${_cy}-${_mm}-01`;
          const _lastDay = new Date(_cy, _cm, 0).getDate();
          const _mEnd0 = `${_cy}-${_mm}-${String(_lastDay).padStart(2, '0')}`;
          const _cStart = _mStart0 < _start ? _start : _mStart0;
          const _cEnd = _mEnd0 > _end ? _end : _mEnd0;
          const _mf = computeConversionFunnel({ members: _members, fromName: _fromName, toNames: [], startDate: _cStart, endDate: _cEnd, today: _today });
          if (_mf && _mf.cohortCount > 0) {
            _mcRows.push({ _ord: _mcOrd++, month: `${_cy}.${_mm}`, cohort: _mf.cohortCount, converted: _mf.convertedCount, rate: _mf.conversionRate });
          }
          _cm++; if (_cm > 12) { _cm = 1; _cy++; }
        }

        if (_mcRows.length > 0) {
          const _mcFields = {
            month: '월',
            cohort: _isTrialFrom ? '체험자(명)' : '구매자(명)',
            converted: _isTrialFrom ? '체험전환자(명)' : '전환자(명)',
            rate: _isTrialFrom ? '체험전환율(%)' : '전환율(%)',
          };
          this.initBuffer(_mcRows, 'REPORT_MONTHLY_CONVERSION', _mcFields, '_report_monthlyconv');
          const _mcBuf = this.buffers.get('_report_monthlyconv');
          if (_mcBuf) _mcBuf.appliedSort = { field: '_ord', direction: 'asc' };
          this.setActiveSlot('_report_monthlyconv');
          const _mcIntro = `[월별 ${_cfLabel}] ${_start.slice(0, 7)}~${_end.slice(0, 7)} '${_fromName}' 기준 월별 전환 현황이에요.`;
          const _mcDisplay = autoGenerateDisplay({ intro: _mcIntro, columns: ['month', 'cohort', 'converted', 'rate'], columnLabels: _mcFields }, this);
          if (_mcDisplay) {
            _mcDisplay._currentAxisKey = '__report=monthlyconv';
            displays.push(_mcDisplay);
          }
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] monthly conversion section failed:', e.message);
    }

    // 섹션 04 — 월별 신규 유료회원 (회원별 최초 '유료' usage 승인일 월버킷; isPaid·결제총액>0 기준, 레퍼런스 image04) //
    try {
      const _monthNewMap = new Map();

      for (const m of _members) {
        let _firstPaid = null;

        for (const u of (m?.usages || [])) {
          if (!_isPaidUsage(u)) continue;
          const _d = String(u?.approvalDate || u?.startDate || '').slice(0, 10);
          if (_d && (!_firstPaid || _d < _firstPaid)) _firstPaid = _d;
        }

        if (!_firstPaid || _firstPaid < _start || _firstPaid > _end) continue;

        const _mk = _firstPaid.slice(0, 7).replace('-', '.');
        _monthNewMap.set(_mk, (_monthNewMap.get(_mk) || 0) + 1);
      }

      const _newRows = [...(_monthNewMap.entries())].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([month, newCount]) => ({ month, newCount }));

      if (_newRows.length > 0) {
        _newTotal = _newRows.reduce((s, r) => s + r.newCount, 0);
        const _nmFields = { month: '월', newCount: '신규 유료회원(명)' };
        this.initBuffer(_newRows, 'REPORT_NEW_MEMBERS', _nmFields, '_report_newmem');
        const _nmBuf = this.buffers.get('_report_newmem');
        if (_nmBuf) _nmBuf.appliedSort = { field: 'month', direction: 'asc' };
        this.setActiveSlot('_report_newmem');
        const _nmIntro = `[월별 신규 유료회원] ${_start.slice(0, 7)}~${_end.slice(0, 7)} 신규 유료 가입 ${_newTotal}명이에요.`;
        const _nmDisplay = autoGenerateDisplay({ intro: _nmIntro, columns: ['month', 'newCount'], columnLabels: _nmFields }, this);
        if (_nmDisplay) {
          _nmDisplay._currentAxisKey = '__report=newmem';
          displays.push(_nmDisplay);
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] new members section failed:', e.message);
    }

    // 섹션 B — 월별 매출 (executeShowPeriodSales 재사용) //
    try {
      const _ps = await this.executeShowPeriodSales({ startDate: _start, endDate: _end });

      if (_ps?.success && _ps.periodSalesResult?.monthData?.length) {
        const _psr = _ps.periodSalesResult;
        _periodSales = _psr.periodSalesPrice || 0;
        const _monthRows = _psr.monthData.map(m => ({
          month: (m.date && typeof m.date.format === 'function') ? m.date.format('YYYY.MM') : String(m.date || ''),
          salesPrice: m.salesPrice || 0,
        }));
        const _msFields = { month: '월', salesPrice: '매출(원)' };
        this.initBuffer(_monthRows, 'REPORT_SALES', _msFields, '_report_sales');
        const _msBuf = this.buffers.get('_report_sales');
        if (_msBuf) _msBuf.appliedSort = { field: 'salesPrice', direction: 'desc' };
        this.setActiveSlot('_report_sales');
        const _msIntro = `[월별 매출] ${_start.slice(0, 7)}~${_end.slice(0, 7)} 기간 매출 합계는 ${_periodSales.toLocaleString('ko-KR')}원이에요.`;
        const _msDisplay = autoGenerateDisplay({ intro: _msIntro, columns: ['month', 'salesPrice'], columnLabels: _msFields }, this);
        if (_msDisplay) {
          _msDisplay._currentAxisKey = '__report=sales';
          displays.push(_msDisplay);
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] sales section failed:', e.message);
    }

    // 섹션 08 — 여자 회원 전환율 (gender=female 사전필터 후 동일 기점 funnel) //
    try {
      const _femaleMembers = _members.filter(m => {
        const _g = String(m?.gender || '').toLowerCase();
        return _g === 'female' || _g === 'f';
      });
      const _fFunnel = _fromName ? computeConversionFunnel({ members: _femaleMembers, fromName: _fromName, toNames: [], startDate: _start, endDate: _end, today: _today }) : null;
      if (_fFunnel) { _fFemCohort = _fFunnel.cohortCount; _fFemConv = _fFunnel.convertedCount; _fFemRate = _fFunnel.conversionRate; }

      if (_fFunnel && _fFunnel.cohortCount > 0 && Array.isArray(_fFunnel.rows) && _fFunnel.rows.length > 0) {
        const _ffFields = { conversionTarget: '전환 회원권', convertedCount: '인원(명)', share: '비율(%)', medianDays: '전환 소요일(중앙값)' };
        this.initBuffer(_fFunnel.rows, 'CONVERSION_FUNNEL', _ffFields, '_report_female');
        const _ffBuf = this.buffers.get('_report_female');
        if (_ffBuf) _ffBuf.appliedSort = { field: 'convertedCount', direction: 'desc' };
        this.setActiveSlot('_report_female');
        const _ffIntro = `[여자 회원 전환율] 여성 중 '${_fromName}' 구매 ${_fFunnel.cohortCount}명 중 ${_fFunnel.convertedCount}명(${_fFunnel.conversionRate}%)이 전환했어요.`;
        const _ffDisplay = autoGenerateDisplay({ intro: _ffIntro, columns: ['conversionTarget', 'convertedCount', 'share', 'medianDays'], columnLabels: _ffFields }, this);
        if (_ffDisplay) {
          _ffDisplay._currentAxisKey = '__report=female';
          displays.push(_ffDisplay);
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] female funnel section failed:', e.message);
    }

    // 섹션 05 — 성별 × 연령대 구조 (활성 유효회원 기준; 만나이 decade 버킷 — 범용 compute='age' 경계와 동일) //
    try {
      const _isActiveValid = m => (m?.usages || []).some(u =>
        u?.memberShipName && !_isTrialName(u.memberShipName)
        && String(u.startDate || '').slice(0, 10) <= _today
        && String(u.endDate || '').slice(0, 10) >= _today
      );
      const _bandMap = new Map(); // decadeKey(number; -1=미상) -> {male, female}
      for (const m of _members) {
        if (!_isActiveValid(m)) continue;
        const _age = this.computeAge(m?.birthDay);
        const _dec = _age === null ? -1 : Math.floor(_age / 10) * 10;
        if (!_bandMap.has(_dec)) _bandMap.set(_dec, { male: 0, female: 0 });
        const _slot = _bandMap.get(_dec);
        const _g = String(m?.gender || '').toLowerCase();
        if (_g === 'female' || _g === 'f') _slot.female += 1;
        else if (_g === 'male' || _g === 'm') _slot.male += 1;
      }
      const _decKeys = [...(_bandMap.keys())].sort((a, b) => (a < 0 ? 1 : b < 0 ? -1 : a - b));
      const _gaRows = [];
      let _ord = 0, _tM = 0, _tF = 0;
      for (const _dec of _decKeys) {
        const _t = _bandMap.get(_dec);
        const _label = _dec < 0 ? '알수없음' : `${_dec}대`;
        _gaRows.push({ _ord: _ord++, ageBand: _label, male: _t.male, female: _t.female, total: _t.male + _t.female });
        _tM += _t.male; _tF += _t.female;
      }
      if (_gaRows.length > 0) {
        _gaRows.push({ _ord: _ord++, ageBand: '합계', male: _tM, female: _tF, total: _tM + _tF });
        const _gaFields = { ageBand: '연령대', male: '남(명)', female: '여(명)', total: '합계(명)' };
        this.initBuffer(_gaRows, 'REPORT_GENDER_AGE', _gaFields, '_report_genderage');
        const _gaBuf = this.buffers.get('_report_genderage');
        if (_gaBuf) _gaBuf.appliedSort = { field: '_ord', direction: 'asc' };
        this.setActiveSlot('_report_genderage');
        const _gaIntro = `[성별 × 연령대 구조] 활성 유효회원 ${_tM + _tF}명 (남 ${_tM}명 / 여 ${_tF}명)의 연령대별 분포예요.`;
        const _gaDisplay = autoGenerateDisplay({ intro: _gaIntro, columns: ['ageBand', 'male', 'female', 'total'], columnLabels: _gaFields }, this);
        if (_gaDisplay) {
          _gaDisplay._currentAxisKey = '__report=genderage';
          displays.push(_gaDisplay);
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] gender-age section failed:', e.message);
    }

    // 섹션 06 — 연령대별 전환율 (만나이 decade 버킷별 members 필터 → computeConversionFunnel 반복) //
    try {
      if (_fromName) {
        const _bandMembers = new Map(); // decadeKey(number; -1=미상) -> [members]
        for (const m of _members) {
          const _age = this.computeAge(m?.birthDay);
          const _dec = _age === null ? -1 : Math.floor(_age / 10) * 10;
          if (!_bandMembers.has(_dec)) _bandMembers.set(_dec, []);
          _bandMembers.get(_dec).push(m);
        }
        const _decKeys = [...(_bandMembers.keys())].sort((a, b) => (a < 0 ? 1 : b < 0 ? -1 : a - b));
        const _caRows = [];
        let _ord = 0;
        for (const _dec of _decKeys) {
          const _bm = _bandMembers.get(_dec);
          const _bf = computeConversionFunnel({ members: _bm, fromName: _fromName, toNames: [], startDate: _start, endDate: _end, today: _today });
          if (!_bf || _bf.cohortCount === 0) continue;
          const _label = _dec < 0 ? '알수없음' : `${_dec}대`;
          _caRows.push({ _ord: _ord++, ageBand: _label, cohort: _bf.cohortCount, converted: _bf.convertedCount, rate: _bf.conversionRate });
        }
        if (_caRows.length > 0) {
          const _caCohortLabel = _isTrialFrom ? '체험자(명)' : '구매자(명)';
          const _caFields = { ageBand: '연령대', cohort: _caCohortLabel, converted: '전환(명)', rate: '전환율(%)' };
          this.initBuffer(_caRows, 'REPORT_CONV_AGE', _caFields, '_report_convage');
          const _caBuf = this.buffers.get('_report_convage');
          if (_caBuf) _caBuf.appliedSort = { field: '_ord', direction: 'asc' };
          this.setActiveSlot('_report_convage');
          const _caIntro = `[연령대별 ${_cfLabel}] '${_fromName}' 기준 연령대별 전환 현황이에요.`;
          const _caDisplay = autoGenerateDisplay({ intro: _caIntro, columns: ['ageBand', 'cohort', 'converted', 'rate'], columnLabels: _caFields }, this);
          if (_caDisplay) {
            _caDisplay._currentAxisKey = '__report=convage';
            displays.push(_caDisplay);
          }
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] conversion-by-age section failed:', e.message);
    }

    // 섹션 07 — 여자 연령대별 핵심 비교 (5열: 전체/유효/체험자·구매자/전환/전환율; 유료·재등록 열은 백엔드 대기로 제외) //
    try {
      const _femaleMembers07 = _members.filter(m => {
        const _g = String(m?.gender || '').toLowerCase();
        return _g === 'female' || _g === 'f';
      });
      if (_femaleMembers07.length > 0) {
        const _isActiveValid07 = m => (m?.usages || []).some(u =>
          u?.memberShipName && !_isTrialName(u.memberShipName)
          && String(u.startDate || '').slice(0, 10) <= _today
          && String(u.endDate || '').slice(0, 10) >= _today
        );
        const _isActivePaid07 = m => (m?.usages || []).some(u =>
          u?.memberShipName && !_isTrialName(u.memberShipName)
          && String(u.startDate || '').slice(0, 10) <= _today
          && String(u.endDate || '').slice(0, 10) >= _today
          && _isPaidUsage(u)
        );
        const _bandF = new Map(); // decadeKey(number; -1=미상) -> { members: [], valid: number, paid: number }
        for (const m of _femaleMembers07) {
          const _age = this.computeAge(m?.birthDay);
          const _dec = _age === null ? -1 : Math.floor(_age / 10) * 10;
          if (!_bandF.has(_dec)) _bandF.set(_dec, { members: [], valid: 0, paid: 0 });
          const _b = _bandF.get(_dec);
          _b.members.push(m);
          if (_isActiveValid07(m)) _b.valid += 1;
          if (_isActivePaid07(m)) _b.paid += 1;
        }
        const _decKeys = [...(_bandF.keys())].sort((a, b) => (a < 0 ? 1 : b < 0 ? -1 : a - b));
        const _f7Rows = [];
        let _ord = 0, _tAll = 0, _tValid = 0, _tPaid = 0, _tCohort = 0, _tConv = 0, _tReTarget = 0, _tReRenewed = 0;
        for (const _dec of _decKeys) {
          const _b = _bandF.get(_dec);
          const _bf = _fromName ? computeConversionFunnel({ members: _b.members, fromName: _fromName, toNames: [], startDate: _start, endDate: _end, today: _today }) : null;
          const _cohort = _bf ? _bf.cohortCount : 0;
          const _conv = _bf ? _bf.convertedCount : 0;
          const _rate = _bf ? _bf.conversionRate : 0;
          // 재등록 (memberRenewals ∩ female, 동일 decade 버킷) — 대상=만료 건, 재등록=만료 후 재등록 //
          const _re = _fReByBand.get(_dec) || { target: 0, renewed: 0 };
          const _reRate = _re.target > 0 ? Math.round(_re.renewed / _re.target * 1000) / 10 : 0;
          const _label = _dec < 0 ? '알수없음' : `${_dec}대`;
          _f7Rows.push({ _ord: _ord++, ageBand: _label, total: _b.members.length, valid: _b.valid, paid: _b.paid, cohort: _cohort, converted: _conv, rate: _rate, reTarget: _re.target, reRenewed: _re.renewed, reRate: _reRate });
          _tAll += _b.members.length; _tValid += _b.valid; _tPaid += _b.paid; _tCohort += _cohort; _tConv += _conv; _tReTarget += _re.target; _tReRenewed += _re.renewed;
        }
        if (_f7Rows.length > 0) {
          const _totRate = _tCohort > 0 ? Math.round(_tConv / _tCohort * 1000) / 10 : 0;
          const _totReRate = _tReTarget > 0 ? Math.round(_tReRenewed / _tReTarget * 1000) / 10 : 0;
          _f7Rows.push({ _ord: _ord++, ageBand: '합계', total: _tAll, valid: _tValid, paid: _tPaid, cohort: _tCohort, converted: _tConv, rate: _totRate, reTarget: _tReTarget, reRenewed: _tReRenewed, reRate: _totReRate });
          const _f7CohortLabel = _isTrialFrom ? '체험자(명)' : '구매자(명)';
          const _f7Fields = { ageBand: '연령대', total: '전체(명)', valid: '유효(명)', paid: '유료(명)', cohort: _f7CohortLabel, converted: '전환(명)', rate: '전환율(%)', reTarget: '재등록 대상(건)', reRenewed: '재등록자(건)', reRate: '재등록률(%)' };
          this.initBuffer(_f7Rows, 'REPORT_FEMALE_XTAB', _f7Fields, '_report_femalextab');
          const _f7Buf = this.buffers.get('_report_femalextab');
          if (_f7Buf) _f7Buf.appliedSort = { field: '_ord', direction: 'asc' };
          this.setActiveSlot('_report_femalextab');
          const _f7Intro = `[여자 연령대별 핵심 비교] 여성 전체 ${_tAll}명 (유효 ${_tValid}명 · 유료 ${_tPaid}명)의 연령대별 전환·재등록 현황이에요.`;
          const _f7Display = autoGenerateDisplay({ intro: _f7Intro, columns: ['ageBand', 'total', 'valid', 'paid', 'cohort', 'converted', 'rate', 'reTarget', 'reRenewed', 'reRate'], columnLabels: _f7Fields }, this);
          if (_f7Display) {
            _f7Display._currentAxisKey = '__report=femalextab';
            displays.push(_f7Display);
          }
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] female-xtab section failed:', e.message);
    }

    // 섹션 08b — 여자 지표 요약 (여성 전환 roll-up + 재등록 roll-up 스칼라; 레퍼런스 '여자 지표 요약') //
    if (_fReTargetTotal > 0 || _fFemCohort !== null) {
      try {
        const _fsRows = [];
        let _ord = 0;
        const _cohLabel = _isTrialFrom ? '여자 체험자' : '여자 구매자';
        const _convLabel = _isTrialFrom ? '여자 체험전환자' : '여자 전환자';
        const _rateLabel = _isTrialFrom ? '여자 체험전환율' : '여자 전환율';
        if (_fFemCohort !== null) {
          _fsRows.push({ _ord: _ord++, metric: _cohLabel, value: `${_fFemCohort.toLocaleString('ko-KR')}명` });
          _fsRows.push({ _ord: _ord++, metric: _convLabel, value: `${(_fFemConv || 0).toLocaleString('ko-KR')}명` });
          _fsRows.push({ _ord: _ord++, metric: _rateLabel, value: `${_fFemRate}%` });
        }
        if (_fReTargetTotal > 0) {
          const _fReRate = Math.round(_fReRenewedTotal / _fReTargetTotal * 1000) / 10;
          _fsRows.push({ _ord: _ord++, metric: '여자 재등록 대상자', value: `${_fReTargetTotal.toLocaleString('ko-KR')}건` });
          _fsRows.push({ _ord: _ord++, metric: '여자 재등록자', value: `${_fReRenewedTotal.toLocaleString('ko-KR')}건` });
          _fsRows.push({ _ord: _ord++, metric: '여자 재등록률', value: `${_fReRate}%` });
        }
        if (_fsRows.length > 0) {
          const _fsFields = { metric: '항목', value: `${_start.slice(0, 7)}~${_end.slice(0, 7)} 합계` };
          this.initBuffer(_fsRows, 'REPORT_FEMALE_SUMMARY', _fsFields, '_report_femsummary');
          const _fsBuf = this.buffers.get('_report_femsummary');
          if (_fsBuf) _fsBuf.appliedSort = { field: '_ord', direction: 'asc' };
          this.setActiveSlot('_report_femsummary');
          const _fsIntro = `[여자 지표 요약] 여성 회원의 전환·재등록 핵심 지표예요.`;
          const _fsDisplay = autoGenerateDisplay({ intro: _fsIntro, columns: ['metric', 'value'], columnLabels: _fsFields }, this);
          if (_fsDisplay) {
            _fsDisplay._currentAxisKey = '__report=femsummary';
            displays.push(_fsDisplay);
          }
        }
      }
      catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] female-summary section failed:', e.message);
      }
    }

    // 섹션 02 — 핵심 지표 요약 (결정적 스칼라 조립; 활성 유료회원 수는 백엔드 결제필드 도착 후 추가 예정) //
    //   맨 앞(displayItems)에 배치 — 요약 먼저, 상세 섹션은 뒤(extraDisplays) //
    if (displays.length > 0 && _members.length > 0) {
      try {
        const _sumRows = [];
        let _ord = 0;
        // 회원 규모 — 유효/유료/차이 (레퍼런스 02: 활성 유료회원·활성 유효회원·유료-유효 차이) //
        _sumRows.push({ _ord: _ord++, metric: '활성 유효회원', value: `${_activeValidCount.toLocaleString('ko-KR')}명` });
        _sumRows.push({ _ord: _ord++, metric: '활성 유료회원', value: `${_activePaidCount.toLocaleString('ko-KR')}명` });
        _sumRows.push({ _ord: _ord++, metric: '유료·유효 회원 차이', value: `${(_activeValidCount - _activePaidCount).toLocaleString('ko-KR')}명` });
        // 전환 — 기간 (체험/구매)자·전환자·전환율 (funnel roll-up; 트라이얼 카탈로그면 '체험' 라벨) //
        if (_convCohort !== null) {
          _sumRows.push({ _ord: _ord++, metric: _isTrialFrom ? '기간 체험자' : '기간 구매자', value: `${_convCohort.toLocaleString('ko-KR')}명` });
          _sumRows.push({ _ord: _ord++, metric: _isTrialFrom ? '기간 체험전환자' : '기간 전환자', value: `${(_convConverted || 0).toLocaleString('ko-KR')}명` });
        }
        if (_conversionRate !== null) _sumRows.push({ _ord: _ord++, metric: _cfLabel, value: `${_conversionRate}%` });
        // 신규 — 기간 신규·월평균 신규 (레퍼런스 02: 신규 유효회원·월평균 신규 유효회원) //
        if (_newTotal !== null) {
          _sumRows.push({ _ord: _ord++, metric: '기간 신규 유료회원', value: `${_newTotal.toLocaleString('ko-KR')}명` });
          const _sM = Number(_start.slice(0, 4)) * 12 + Number(_start.slice(5, 7));
          const _eM = Number(_end.slice(0, 4)) * 12 + Number(_end.slice(5, 7));
          const _months = Math.max(1, _eM - _sM + 1);
          const _avgNew = Math.round(_newTotal / _months * 10) / 10;
          _sumRows.push({ _ord: _ord++, metric: '월평균 신규 유료회원', value: `${_avgNew.toLocaleString('ko-KR')}명 (${_months}개월)` });
        }
        // 재등록 — 대상자·재등록자·재등록률 (레퍼런스 02: 재등록 대상자·재등록자·운영용 재등록률) //
        if (_reExpiredTotal > 0) {
          const _reRateOverall = Math.round(_reRenewedTotal / _reExpiredTotal * 1000) / 10;
          _sumRows.push({ _ord: _ord++, metric: '재등록 대상자', value: `${_reExpiredTotal.toLocaleString('ko-KR')}명` });
          _sumRows.push({ _ord: _ord++, metric: '재등록자', value: `${_reRenewedTotal.toLocaleString('ko-KR')}명` });
          _sumRows.push({ _ord: _ord++, metric: '재등록률', value: `${_reRateOverall}%` });
        }
        _sumRows.push({ _ord: _ord++, metric: '여성 회원 비율', value: `${_femaleRatio}% (${_femaleCount.toLocaleString('ko-KR')}명)` });
        if (_periodSales !== null) _sumRows.push({ _ord: _ord++, metric: '기간 매출', value: `${_periodSales.toLocaleString('ko-KR')}원` });

        const _syFields = { metric: '지표', value: '값' };
        this.initBuffer(_sumRows, 'REPORT_SUMMARY', _syFields, '_report_summary');
        const _syBuf = this.buffers.get('_report_summary');
        if (_syBuf) _syBuf.appliedSort = { field: '_ord', direction: 'asc' };
        this.setActiveSlot('_report_summary');
        const _syIntro = `[핵심 지표 요약] ${_start}~${_end}`;
        const _syDisplay = autoGenerateDisplay({ intro: _syIntro, columns: ['metric', 'value'], columnLabels: _syFields }, this);
        if (_syDisplay) {
          _syDisplay._currentAxisKey = '__report=summary';
          displays.unshift(_syDisplay);
          summaryParts.push(`활성 유효회원 ${_activeValidCount}명`);
          if (_conversionRate !== null) summaryParts.push(`${_cfLabel} ${_conversionRate}%`);
          if (_periodSales !== null) summaryParts.push(`기간 매출 ${_periodSales.toLocaleString('ko-KR')}원`);
        }
      }
      catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] summary section failed:', e.message);
      }
    }

    // 섹션 01 — 지표 정의 용어집 (결정적 상수 표; unshift로 맨 앞 displayItems 배치 — 정의 먼저, 요약은 뒤로) //
    if (displays.length > 0) {
      try {
        const _glRows = [];
        let _ord = 0;
        _glRows.push({ _ord: _ord++, term: '기준일 / 기간', def: `${_start} ~ ${_end} (오늘 ${_today} 기준)` });
        _glRows.push({ _ord: _ord++, term: '활성 유효회원', def: '기준일에 유효한 회원권을 보유한 회원 (무료체험·드랍인·증정 제외)' });
        _glRows.push({ _ord: _ord++, term: '활성 유료회원', def: '활성 유효회원 중 기준일 유효 회원권이 결제총액을 가진 회원 (isPaid·결제금액>0)' });
        if (_newTotal !== null) _glRows.push({ _ord: _ord++, term: '기간 신규 유료회원', def: '기간 내 첫 유료 회원권을 시작한 회원 (isPaid·결제금액>0 기준, 무료체험·증정 제외)' });
        if (_conversionRate !== null) _glRows.push({ _ord: _ord++, term: _cfLabel, def: `'${_fromName}' 구매 회원 중 이후 다른 유료 회원권으로 전환한 비율` });
        _glRows.push({ _ord: _ord++, term: '여성 회원 비율', def: '전체 회원 중 여성 회원이 차지하는 비율' });
        if (_periodSales !== null) _glRows.push({ _ord: _ord++, term: '기간 매출', def: '결제일 기준 기간 내 유료 회원권 결제 매출' });
        if (_reExpiredTotal > 0) {
          _glRows.push({ _ord: _ord++, term: '재등록 대상', def: '기간 내 회원권이 만료된 회원 (재등록률 분모)' });
          _glRows.push({ _ord: _ord++, term: '재등록률', def: '재등록 대상 중 만료 후 다시 등록한 회원 비율 (재등록자 ÷ 재등록 대상)' });
        }

        const _glFields = { term: '지표', def: '정의' };
        this.initBuffer(_glRows, 'REPORT_GLOSSARY', _glFields, '_report_glossary');
        const _glBuf = this.buffers.get('_report_glossary');
        if (_glBuf) _glBuf.appliedSort = { field: '_ord', direction: 'asc' };
        this.setActiveSlot('_report_glossary');
        const _glIntro = `[지표 정의] 이 리포트의 지표 계산 기준이에요.`;
        const _glDisplay = autoGenerateDisplay({ intro: _glIntro, columns: ['term', 'def'], columnLabels: _glFields }, this);
        if (_glDisplay) {
          _glDisplay._currentAxisKey = '__report=glossary';
          displays.unshift(_glDisplay);
        }
      }
      catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT] glossary section failed:', e.message);
      }
    }

    // 활성 슬롯 원복 (섹션 버퍼 swap 부작용 차단) //
    this.activeSlot = _savedActive;

    if (!displays.length) return { success: false, message: '리포트 산출에 필요한 데이터가 없습니다.' };

    const reportIntro = `${_start}~${_end} 경영 리포트예요.` + (summaryParts.length ? ` ${summaryParts.join(', ')}.` : '');
    return { success: true, reportDisplays: displays, reportIntro, startDate: _start, endDate: _end, sectionCount: displays.length };
  }

  // 포인트 관리 현황 시각화 준비 — point/statistics 직접 호출 (통계 PointManagementBox와 동일 소스) //
  // 응답: totalIssuedPoint/totalUsedPoint/totalHeldPoint(요약) + holdings(보유 랭킹) + histories(발행·사용 내역) //
  async executeShowPointManagement({ intro, startDate, endDate }) {
    // [B-1 과적합 수술 동형] 명시적 표/리스트 요구 턴 감지 (dual sync nodeAgent.mjs)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');
    // 기간 기본값 — 올해 1/1 ~ 오늘 //
    const now = new Date();
    const year = now.getFullYear();
    const startDay = startDate ? startDate.slice(0, 10) : `${year}-01-01`;
    const endDay = endDate ? endDate.slice(0, 10) : `${year}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    try {
      const fetchModule = new FetchModule();
      const resp = await fetchModule.postDataVersion2('point/statistics', 'POST', {
        startDate: `${startDay}T00:00:00`,
        endDate: `${endDay}T23:59:59`,
      });

      if (resp?.status !== 200 || !resp.data) {
        return { success: false, message: `${startDay}~${endDay} 포인트 데이터가 없습니다.` };
      }

      const data = resp.data;
      const totalIssued = data.totalIssuedPoint ?? 0;
      const totalUsed = data.totalUsedPoint ?? 0;
      const totalHeld = data.totalHeldPoint ?? 0;
      const holdings = Array.isArray(data.holdings) ? data.holdings : [];
      const histories = Array.isArray(data.histories) ? data.histories : [];

      if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_point_mange:', `${startDay}~${endDay}, 발행 ${totalIssued}/사용 ${totalUsed}/보유 ${totalHeld}, holdings ${holdings.length}, histories ${histories.length}`);

      return {
        success: true,
        pointResult: {
          intro: intro || null,
          startDate: startDay,
          endDate: endDay,
          totalIssuedPoint: totalIssued,
          totalUsedPoint: totalUsed,
          totalHeldPoint: totalHeld,
          holdings,
          histories,
        },
        count: histories.length,
        message: `${startDay}~${endDay} 포인트 관리 — 누적 발행 ${totalIssued.toLocaleString()}P, 누적 사용 ${totalUsed.toLocaleString()}P, 현재 보유 ${totalHeld.toLocaleString()}P.`
          + (this._vizListRequested
            ? ` [FINISH NOW + LIST REQUESTED] Point overview rendered AND the user explicitly asked for a table/list — NOW call_api(intention="CHECK_MEMBER+POINT_LIST") for per-member point rows, then show_display (sort by 보유 포인트 desc), then finish() with a brief Korean summary.`
            : ` [FINISH NOW] Rendered successfully. Call finish() now with a brief Korean summary (issued/used/held totals). Do NOT call show_display (the user did not explicitly ask for a table).`),
      };
    }
    catch (e) {
      return { success: false, message: `포인트 관리 집계 실패: ${e.message}` };
    }
  }

  // 통계 페이지(MemberEngagementBox)와 동일하게 member/engagement + member/all/statistics 두 API를 결합하여 //
  // Phase 0(보유 회원권 + 유효회원 집계) + Phase 1(reserve 누적) + Phase 2(정렬·확정) 구조로 처리 //
  async executeShowEngagementChart({ intro, startDate, endDate }) {
    // [B-1 과적합 수술] 명시적 표/리스트 요구 턴 감지 — 렌더 후 show_display 1회 허용 (POST-GUARD 예외 + 메시지 조건부)
    this._vizListRequested = detectExplicitListRequest(this.getUserInput?.() || '');
    // 기간 기본값 — 이번달 //
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const defaultStart = `${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00`;
    const defaultEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59`;
    const startParam = startDate ? `${startDate.slice(0, 10)}T00:00:00` : defaultStart;
    const endParam = endDate ? `${endDate.slice(0, 10)}T23:59:59` : defaultEnd;

    try {
      // [Architecture B 일원화 2026-05-19] 자체 fetch 제거 — call_api(71 + 66) buffer 사용
      const engagementBuf = this.findBufferByIntention('CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD');
      let memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');

      if (!engagementBuf?.items?.length) {
        return { success: false, message: 'engagement 데이터가 버퍼에 없습니다. call_api(CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD)를 먼저 호출하세요.' };
      }

      // [VIZ-ENGAGEMENT-002] memberBuf auto-recovery — LLM이 HISTORY_LIST call_api를 skip한 경우 자동 fetch //
      if (!memberBuf?.items?.length) {
        if (TOOL_CALLING_LOG) console.log('[Agent] show_engagement_chart: memberBuf missing — auto-fetching CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
        const autoResult = await this.executeCallApi({ intention: 'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST', slot: '_engagement_members' });

        if (autoResult?.success) {
          memberBuf = this.findBufferByIntention('CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST');
        }

        if (!memberBuf?.items?.length) {
          return { success: false, message: '회원 메타가 버퍼에 없습니다. call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST)도 호출하세요.' };
        }
      }

      const engagementReserves = engagementBuf.items;
      const allMembers = memberBuf.items;

      // 2. 산정기간 내 유효 회원권을 보유한 회원만 필터링 (MemberEngagementBox.parsedAllMembers와 동일) //
      const parsedAllMembers = allMembers
        .filter(m => m.birthDay && Array.isArray(m.usages))
        .map(m => ({
          memberId: m.memberId,
          birthYear: parseInt(String(m.birthDay).slice(0, 4), 10),
          memberships: m.usages
            .filter(u =>
              u.memberShipId
              && u.memberShipName
              && moment(u.startDate).isSameOrBefore(endParam, 'day')
              && moment(u.endDate).isSameOrAfter(startParam, 'day')
            )
            .map(u => ({
              membershipId: u.memberShipId,
              membershipName: u.memberShipName,
            })),
        }))
        .filter(m => m.memberships.length > 0);

      // 3. 연령대 그룹 생성 (MemberEngagementBox 10개 연령대와 동일) //
      const ageGroupKeys = ['under10', '10s', '20s', '30s', '40s', '50s', '60s', '70s', '80s', '90s'];
      const baseYear = parseInt(moment().format('YYYY'), 10);
      const engagementStateList = [];

      for (let i = 0; i < 10; i++) {
        let startYear, endYear;

        if (i === 0) {
          startYear = baseYear - 8;
          endYear = baseYear;
        }
        else {
          startYear = baseYear - (i * 10 + 8);
          endYear = baseYear - (i * 10 - 1);
        }

        engagementStateList.push({
          key: ageGroupKeys[i],
          startYear,
          endYear,
          totalCount: 0,        // userIds.size로 확정 (유효회원 고유 수)
          reservedSum: 0,       // reserve 누적 합 (선호 비중 분모)
          userIds: new Set(),
          usagesMap: new Map(),
        });
      }

      // 출생연도 → 연령대 인덱스 (구간 벗어나면 -1) //
      function getAgeGroupIndex(birthYear) {
        const age = baseYear - birthYear;

        if (age < 0 || age > 98) return -1;

        return Math.floor((age + 1) / 10);
      }

      // 4. Phase 0 — 보유 회원권 사전 등록(count: 0) + 유효회원 고유 집계 //
      for (const member of parsedAllMembers) {
        const idx = getAgeGroupIndex(member.birthYear);

        if (idx === -1) continue;

        const group = engagementStateList[idx];
        group.userIds.add(member.memberId);

        for (const m of member.memberships) {
          if (!group.usagesMap.has(m.membershipId)) {
            group.usagesMap.set(m.membershipId, {
              membershipId: m.membershipId,
              membershipName: m.membershipName,
              count: 0,
            });
          }
        }
      }

      // 5. Phase 1 — reserve 사용 실적 누적 //
      for (const reserve of engagementReserves) {
        const birthRaw = String(reserve.birthDay || '').slice(0, 4);
        const birthYear = parseInt(birthRaw, 10);

        if (!birthYear) continue;

        const idx = getAgeGroupIndex(birthYear);

        if (idx === -1) continue;

        const group = engagementStateList[idx];
        group.reservedSum += 1;

        const existing = group.usagesMap.get(reserve.membershipId);

        if (existing) {
          existing.count += 1;
        }
        else {
          // (안전망) Phase 0에 없는 회원권이 reserve에 있는 경우 — 탈퇴 등 //
          group.usagesMap.set(reserve.membershipId, {
            membershipId: reserve.membershipId,
            membershipName: reserve.membershipName,
            count: 1,
          });
        }
      }

      // 6. Phase 2 — Map → 정렬된 배열 변환 + totalCount 확정 + 최댓값 산정 //
      let grandTotal = 0;
      let maxKey = ageGroupKeys[0];
      let maxCount = 0;

      for (const group of engagementStateList) {
        group.usages = Array.from(group.usagesMap.values())
          .sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);
        group.totalCount = group.userIds.size;
        grandTotal += group.totalCount;

        if (group.totalCount > maxCount) {
          maxKey = group.key;
          maxCount = group.totalCount;
        }
      }

      // 7. Set/Map은 JSON 직렬화 불가 → 제거. 9개 그룹 + 데이터 있는 그룹 //
      function stripInternal(g) {
        return {
          key: g.key,
          startYear: g.startYear,
          endYear: g.endYear,
          totalCount: g.totalCount,
          reservedSum: g.reservedSum,
          usages: g.usages,
        };
      }

      const allAgeGroups = engagementStateList.slice(0, 9).map(stripInternal);
      const engagementStates = engagementStateList.filter(g => g.usages.length > 0).map(stripInternal);

      if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_engagement_chart:', { totalValidMembers: grandTotal, maxKey, maxCount, ageGroupsWithData: engagementStates.length, reserveItems: engagementReserves.length });

      this._engagementChartCompleted = true;

      return {
        success: true,
        engagementChartResult: {
          intro: intro || null,
          allAgeGroups,
          engagementStates,
          engagementTotalCount: grandTotal,
          engagementMaxCount: maxCount,
          engagementMaxKey: maxKey,
          startDate: startParam.slice(0, 10),
          endDate: endParam.slice(0, 10),
        },
        count: grandTotal,
        message: `Chart rendered successfully. Total active members: ${grandTotal}. Largest age group: ${maxKey} (${maxCount} members). Each age group includes usages array with membership details (membershipName, count). `
          + (this._vizListRequested
            ? `The user explicitly asked for a table/list — NOW call show_display on the loaded buffer (age-group rows), then finish() with a summary.`
            : `Call finish() now with a summary. Do NOT call show_display (the user did not explicitly ask for a table).`),
      };
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[Agent] ⚠️ show_engagement_chart fetch failed:', e.message);
      return { success: false, message: `연령별 이용 분석 데이터 조회 중 오류: ${e.message}` };
    }
  }

  // 테이블 렌더링 준비 //
  executeShowDisplay({ intro, outro, columns, columnLabels, max_count }) {
    // [CONVERSION-FUNNEL INTRO GUARD] 퍼널 슬롯 표시 시 코드 산출 intro 결정적 강제 — LLM의 "지원되지 않아요" 류 거짓 disclaimer/수치 변형 차단 (2c213cfe 동형, dual sync nodeAgent.mjs) //
    if ((this.activeSlot === '_conversion_funnel' || this.activeSlot === '_conversion_roster') && this._conversionFunnelIntro) {
      intro = this._conversionFunnelIntro;
      columns = undefined; // 라벨명 오지정 등 invalid columns로 인한 슬롯 보정/vizOnly 오발동 차단 — 전체 컬럼 자동
      columnLabels = undefined;
    }
    // [REREG-SCOPE GUARD] display intro/outro 재등록 서술 교정 — 배타 모수 혼합·stale rate (aiUtils 단일소스, 이번 턴 실측 있을 때만) //
    if (this._reRegMetaThisTurn) {
      if (intro) intro = correctReRegScopeNarrative(intro, this._reRegMetaThisTurn);
      if (outro) outro = correctReRegScopeNarrative(outro, this._reRegMetaThisTurn);
    }
    // [FINANCE STATEMENT CHART REDIRECT] 재무제표 요청 + 활성 버퍼 MONTHLY+TREND → show_display 대신 show_revenue_chart 결정적 전환 //
    //   본질: redirect로 MONTHLY+TREND 버퍼는 확보됐으나 LLM이 표시 도구를 stochastic하게 show_display 선택(가이드 무시) → 차트 미표출.
    //   "표로/리스트/목록/내역" 명시 시 비개입(표 의도 존중). cross-slot engagement redirect와 동형. dual sync with nodeAgent.mjs //
    {
      const _fsInput = this.getUserInput?.() || '';
      const _activeBufFS = this.buffers.get(this.activeSlot) || this.buffers.get('default');
      if (/재무|제무|손익|재정/.test(_fsInput) && !/표로|리스트|목록|내역/.test(_fsInput)
          && _activeBufFS?.intention === 'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD') {
        console.log('[show_display] FINANCE STATEMENT CHART REDIRECT: show_display → show_revenue_chart (재무제표 + MONTHLY+TREND 버퍼)');
        return this.executeShowRevenueChart({ intro });
      }
    }

    // [PRODUCT-VIA-SALES DISCLOSURE] 상품 질문을 SALES(전거래=회원권 결제 포함) 버퍼로 답할 때 intro 결정적 disclosure 주입 //
    //   본질: 1e9ae948 설계상 "상품 판매 내역/통계"는 SALES 라우팅(판매이력 0건 매장 false-ack 방지) —
    //   그 결과 회원권 결제 행이 "상품 판매 내역"으로 표출되는 거짓 라벨 발생(스윕 L1-PRODUCT-002/003).
    //   프롬프트 지시만으론 disclosure 생략 stochastic(rid109 3/5) → intro 코드 주입(2c213cfe 동형). 차단 X, 표 유지.
    //   조건: 현재 턴 질문에 '상품' + active 버퍼 CHECK_SALES 계열 + intro에 동등 disclosure 부재. dual sync with nodeAgent.mjs //
    {
      const _pvInput = this.getUserInput?.() || '';
      const _pvBuf = this.buffers.get(this.activeSlot) || this.buffers.get('default');
      if (/상품/.test(_pvInput) && /^CHECK_SALES/.test(_pvBuf?.intention || '')
          && !/회원권[^.。\n]{0,8}포함|전체\s*(판매|매출|거래)/.test(String(intro || ''))) {
        const _pvNote = '매장 전체 판매 내역(회원권 결제 포함) 기준이에요.';
        intro = intro ? `${String(intro).trim()} ${_pvNote}` : _pvNote;
        console.log('[show_display] PRODUCT-VIA-SALES DISCLOSURE: 상품 질문 + CHECK_SALES 버퍼 → intro 전체판매 disclosure 주입');
      }
    }

    // [PER-AXIS ACTIVE SLOT CORRECTION] userInput "X별" 축 토큰이 비활성 multi-row 슬롯에서만 해소되면 활성 슬롯을 축 슬롯으로 결정적 전환 //
    //   본질: per-axis 다지표 질문에서 LLM이 비축 슬롯(매출 요약 1행 등)을 active로 둔 채 show_display →
    //   축 표가 요약으로 밀리는 역전 (DEMO-37: 매출 1행 표 + 수업 요약 부착). 8d066104 sibling recovery와 동형 — LLM 회복 의존 X.
    //   활성 슬롯이 이미 축을 보유(merged 포함)하면 비개입. 1회 한정 (turn별 리셋). dual sync with nodeAgent.mjs //
    if (!this._perAxisSlotCorrected) {
      const _paInput = this.getUserInput?.() || '';
      const _paTokens = [..._paInput.matchAll(/([가-힣A-Za-z0-9]{1,12}?)\s?별(?:로|은|는|을|를)?(?=\s|$|[^가-힣])/g)].map(m => m[1]).filter(Boolean);

      let _paResolvedInActive = false;
      let _paAxisSlot = null;
      let _paToken = null;

      for (const _tk of _paTokens) {
        if (this._resolveAxisFieldInSlot(_tk, this.activeSlot)) {
          _paResolvedInActive = true;
          break;
        }

        if (!_paAxisSlot) {
          const _activeIntentionPA = this.buffers.get(this.activeSlot)?.intention;

          for (const _sn of this.buffers.keys()) {
            if (_sn === this.activeSlot) continue;
            if ((this.buffers.get(_sn)?.items?.length || 0) < 2) continue;  // 축 표는 multi-row
            // same-intention 다축(groupBy 변형 — 요일/시간대 등)은 기존 multi-axis 가드 영역 — 본 가드 비개입 (DEMO-12 회귀)
            if (_activeIntentionPA && this.buffers.get(_sn)?.intention === _activeIntentionPA) continue;
            if (this._resolveAxisFieldInSlot(_tk, _sn)) {
              _paAxisSlot = _sn;
              _paToken = _tk;
              break;
            }
          }
        }
      }

      if (!_paResolvedInActive && _paAxisSlot) {
        this._perAxisSlotCorrected = true;
        if (TOOL_CALLING_LOG) console.log(`[show_display] PER-AXIS ACTIVE SLOT CORRECTION: "${_paToken}별" 축 필드가 비활성 슬롯에만 존재 → active "${this.activeSlot}" → "${_paAxisSlot}"`);
        this.activeSlot = _paAxisSlot;
      }
    }

    // [L11-SWITCH-SLOT-001 cycle 2] POST-MERGE SHOW_DISPLAY BYPASS (dual sync with nodeAgent.mjs)
    //   본질: per-entity 비교 의도(각각/따로/별도/각자)에서 LLM이 redirect 안내 무시하고 switch+show 직행한 뒤
    //         뒤늦게 merge_slots 호출 → 후속 show_display(merged cols)가 dup-block에 막혀
    //         사용자 화면은 직전 single-slot 표 (비교축 missing). header_vs_text_mismatch 본질 재현.
    //   해소: merge_slots 성공 시점에 본 turn show_display 이미 성공이면 _postMergeShowDisplayAllowedOnce=true 세팅
    //         → 후속 show_display 1회 한정으로 dup-block bypass + _showDisplaySucceededThisTurn 재초기화
    //   범용: 모든 도메인의 post-merge re-display 적용 (case ID / 한국어 키워드 의존 X, 구조 signal만)
    //   회귀 risk 최소화: 1회 한정 + merge_slots 성공 후 첫 show_display만 bypass + 이후엔 다시 dup-block 정상 동작
    if (this._postMergeShowDisplayAllowedOnce) {
      console.log('[show_display] ✅ POST-MERGE BYPASS — merge_slots 직후 1회 한정 dup-block bypass');
      this._postMergeShowDisplayAllowedOnce = false;  // 1회 한정 소비
      this._showDisplaySucceededThisTurn = false;  // 후속 dup check 재초기화 (이번 호출은 새 display로 취급)
    }

    // [MULTI-AXIS COMPOSITION] 다축 구성(나이대 AND 성별 등) — 직전 표와 다른 축 compute(group) 후 후속 show_display 1회 한정 dup-block bypass (dual sync with nodeAgent.mjs)
    //   computeGroup이 다른 축 grouping 시 _multiAxisShowDisplayAllowedOnce=true 세팅 → 축마다 표 별도 표출 가능. 같은 축 재표출은 미허용(축 비교로 게이팅).
    if (this._multiAxisShowDisplayAllowedOnce) {
      console.log('[show_display] ✅ MULTI-AXIS BYPASS — 다축 구성 다른 축 1회 한정 dup-block bypass');
      this._multiAxisShowDisplayAllowedOnce = false;  // 1회 한정 소비
      this._showDisplaySucceededThisTurn = false;  // 이번 호출은 새 축 display로 취급
    }

    // [Weak8-DUP_RESPONSE] 동일 턴 내 show_display 중복 호출 차단 — 이미 성공했으면 2회째 호출 block (dual sync with nodeAgent.mjs)
    if (this._showDisplaySucceededThisTurn) {
      console.log('[show_display] 🚫 DUPLICATE BLOCKED — show_display already succeeded this turn');
      return {
        success: false,
        message: '[DUPLICATE show_display BLOCKED] show_display already succeeded in this turn. Do NOT call show_display again. Proceed directly to finish() with your outro text.',
      };
    }

    // [L12-COMPACT-003 CHAIN_LEAK fix] "유효회원/활성회원" keyword chain turn → 현재 buffer가 ALIVE_LIST가 아니면 show_display 차단.
    //   apply_filter 우회 path (LLM이 곧장 show_display로 직행) 도 동일하게 ALIVE scope 위반 → "유효회원 N명" 거짓 라벨 응답.
    //   dual sync: nodeAgent.mjs 동일 적용.
    //   [L9c-COMPARE-002 cycle 1] MERGED exemption 강화: alive-derived MERGED만 exempt.
    //     기존 모든 MERGED exempt → comparison merge(`_merged`, intention=MERGED, 51 출석 줄어든 items)
    //     상태에서 "유효회원만" show_display 시 가드 미발동 → 51건이 "유효회원만"으로 false-label 표시.
    //     구조 기반: alive-derived MERGED는 ALIVE_LIST 고유 field(isActive)를 보유 (raw alive 또는 alive merge 결과).
    //     comparison MERGED는 isActive 없음(period merge). field 존재 여부로 분기.
    {
      const _buf = this.getActiveBuf?.();
      const _userInputAlive = this.getUserInput?.() || '';
      const _wantsAlive = /유효회원|활성회원|유효한 회원|활성 회원/.test(_userInputAlive);
      const _sample = _buf?.items?.[0] || {};
      const _hasAliveField = 'isActive' in _sample;
      const _isAliveDerivedMerged = _buf?.intention === 'MERGED' && _hasAliveField;
      // [CLUSTER-A] #38 buffer가 ALIVE-SCOPE 가드로 이미 isActive=true 좁혀졌으면 in-place active-scoped → redirect 불필요
      const _autoAliveScoped = (_buf?.appliedFilters || []).some(f => f._autoAliveScope);
      if (_wantsAlive && _buf && typeof _buf.intention === 'string'
        && _buf.intention !== 'CHECK_MEMBER+ALIVE_LIST'
        && !_isAliveDerivedMerged
        && !_autoAliveScoped
      ) {
        return {
          success: false,
          message: `[ALIVE_SCOPE SHOW_DISPLAY BLOCKED] 사용자 input "${_userInputAlive.slice(0, 60)}"이(가) "유효회원/활성회원" 범위를 요청하지만 현재 active buffer intention="${_buf.intention}"${_buf.intention === 'MERGED' ? ' (alive-derived 아님 — isActive 필드 없음)' : ''}은(는) ALIVE_LIST가 아닙니다. 현재 buffer를 그대로 show_display하면 "유효회원 N명"이라는 거짓 라벨이 만들어집니다.\n\n반드시 새로 호출하세요: get_intentions(MEMBER) → call_api(CHECK_MEMBER+ALIVE_LIST, slot:"alive"). 이전 turn 데이터와 교차참조하려면 추가로 merge_slots([{slotName: current_active_slot}, {slotName:"alive"}], mode:"intersect"). 단순히 "유효회원 N명 보여줘" 응답이라면 ALIVE_LIST 호출만으로 show_display 진행.`,
        };
      }
    }

    // [STALE PERIOD RELABEL — REFETCH NUDGE] 운영 5차 rid63/65/68 (2026-06-29, dual sync with nodeAgent.mjs + finishPolicy GUARD 15)
    //   본질: 후속 턴이 새 절대기간("2026년 기준"/"이번달"/"올해")을 요청했는데 LLM이 신규 call_api 미트리거 →
    //         직전 다른 기간 period 버퍼(dateRange) 재사용 + finalText에서 새 기간으로 거짓 라벨(다음 턴 AI 자백=자기모순).
    //   기존 NARROW_PERIOD_HONEST_INFORM은 "만" 입자 필수 → "기준/이번달"(만 없음) 미매칭 gap.
    //   해소: detectStalePeriodRelabel — "만" 부재 + 절대기간 resolve 월 set ≠ 버퍼 dateRange 월 set + 본 turn call_api 미호출
    //         + 버퍼 items + dateRange(period intention) → show_display block + 새 기간 call_api 재호출 nudge (1회 한정).
    //   narrow LIST revert(주석 아래) 회피: "만" narrow는 기존 honest-inform에 위임(family 분리). period aggregate는 재조회 시 데이터 변동(T17 입증).
    if (!this._periodChangeRefetchBlocked && !this._calledApiThisTurn) {
      const _relabel = detectStalePeriodRelabel({
        userInput: this.getUserInput?.() || '',
        buffer: this.getBuffer?.(),
        calledApiThisTurn: this._calledApiThisTurn,
      });
      if (_relabel?.block) {
        this._periodChangeRefetchBlocked = true;
        const _rp = _relabel.requestedPeriod, _bp = _relabel.bufferPeriod;
        return {
          success: false,
          message: `[STALE PERIOD RELABEL — REFETCH REQUIRED] User requested a NEW period (resolved ${_rp.startDate}~${_rp.endDate}) but the active buffer (intention="${_relabel.intention}") holds data for a DIFFERENT period (${_bp.startDate}~${_bp.endDate}) and no call_api ran this turn. Calling show_display now would render the OLD-period data under a NEW-period label — a false relabel (the user notices and the next turn exposes the contradiction). Re-call call_api(intention="${_relabel.intention}", startDate="${_rp.startDate}", endDate="${_rp.endDate}") to fetch the requested period, THEN show_display. Do NOT reuse the existing buffer for a different period.`,
        };
      }
    }

    // [NARROW PERIOD CHAIN FOLLOW-UP — HONEST INFORM] L21-COMPARE-FOLLOWUP-001 cycle 1 (2026-05-30, dual sync with nodeAgent.mjs)
    //   본질: chain follow-up에서 사용자가 narrowing 시간 키워드 + 한정 어미("만")로 좁히기 요청 시
    //         LLM이 call_api 재호출 없이 show_display 직행 → buffer items 변경 없이 intro/outro framing만 변경 → false ack
    //         (이전 attempt — backend re-fetch 강제 — 는 backend가 narrow dateRange 무적용으로 동일 데이터 반환 → revert)
    //   해소: narrow particle "만" + time keyword + 본 turn call_api 미호출 + active buffer items 보유 → show_display block + finish 안내 (1회 한정)
    //         buffer는 wider period 기준이라 narrow filter 명령으로는 honest 응답 불가 → LLM이 finish로 user에게 "narrow 기간 데이터는 새로 요청 필요" 정직 안내
    //   패턴 기반 (단일 case hardcode X): 한국어 "만" 한정 particle + 시간 키워드 구조. backend 의존 X (frontend 단독 사용자 입장 본질 해소)
    //   회귀 risk 최소화: 본 turn call_api 호출됐으면 통과 — narrow + call_api 정상 흐름 보존. 또한 1회 한정으로 무한 retry 차단
    if (!this._showDisplayPeriodNarrowBlocked && !this._calledApiThisTurn) {
      const _userInputNarrow = this.getUserInput?.() || '';
      // narrow particle "만" 직전 time keyword. examples:
      //   "오늘만", "어제만", "이번주만", "이번 주만", "금주만", "이번달만", "금월만", "이달만",
      //   "지난주만", "지난달만", "N월만", "N주만", "N일만", "최근 N일만"
      const NARROW_PERIOD_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;
      if (NARROW_PERIOD_RE.test(_userInputNarrow)) {
        const _activeBufNarrow = this.getBuffer?.();
        const _hasItemsNarrow = !!(_activeBufNarrow && (_activeBufNarrow.items?.length || 0) >= 1);
        if (_hasItemsNarrow) {
          this._showDisplayPeriodNarrowBlocked = true;
          const _narrowMatch = (_userInputNarrow.match(NARROW_PERIOD_RE) || [])[0] || '';
          const _bufIntentionNarrow = _activeBufNarrow.intention || 'active';
          const _bufCountNarrow = _activeBufNarrow.items?.length || 0;
          return {
            success: false,
            message: `[NARROW PERIOD CHAIN FOLLOW-UP — HONEST INFORM] User said "${_userInputNarrow.slice(0, 50)}" (narrowing time keyword "${_narrowMatch}"). Active buffer (intention=${_bufIntentionNarrow}, ${_bufCountNarrow} items) was loaded for a wider/different time period — calling show_display with the EXISTING buffer would render the SAME items with only a renamed intro/outro ("${_narrowMatch} 기준 ${_bufCountNarrow}명..."), which is a false acknowledgment, not a real narrow filter. Backend data for the narrow period may be unavailable or return the same dataset, so silently re-running call_api is unreliable. Instead, call finish with text like: "${_narrowMatch} 기준 데이터로 좁히려면 새로 조회가 필요해요. 정확한 기간(예: 시작일~종료일)을 다시 알려주시면 새로 분석해 드릴게요." This honestly informs the user that the previous buffer cannot be reused for a narrower period, rather than silently re-labeling stale data as if filtered.`,
          };
        }
      }
    }

    // [NARROW PERIOD CHAIN FOLLOW-UP — COMPARE INTENT-DRIFT BLOCK] L21-COMPARE-FOLLOWUP-001 cycle 3 (2026-05-31, dual sync with nodeAgent.mjs)
    //   본질: chain T0='줄어든/늘어난' (compare-direction) → T1 narrow particle '이번주만' 진입 시 두 종류 INTENT-DRIFT 발생:
    //     Case A — LLM이 call_api 단일 period 호출 → active buffer가 fresh single-period (intention !== 'MERGED', no _change_* fields)로 교체 →
    //              이전 compare framing 상실. baseline (commit 345203d2 cycle 2 잔존) R1/R2/R4 패턴.
    //     Case B — LLM이 compare buffer (MERGED/_change_* 보유) 유지하지만 show_display columns에 compare-prefix field
    //              (_change_*/previous_*/recent_*) 누락 → plain 'attended' 등 base column만 render → 표면적 compare 손실.
    //   해소: (a) narrow particle "만" + time keyword (b) 본 turn call_api 호출됨 (Cycle 1 가드 미발동 path)
    //         (c) buffers에 _change_* fields 보유한 slot 존재 (compare chain history 증거)
    //         (d-A) active buf 단일 period 또는 (d-B) compare buf지만 columns에 compare-prefix 누락 → show_display block + HONEST INFORM
    //   패턴 기반 (generic, no case literal): buffer state inspection (_change_*/previous_/recent_ prefix detection) + narrow particle regex. intention key hardcode X.
    //   회귀 risk 최소화: prior buffers에 _change_* 부재면 미발동 (non-compare chain 정상). compare buf + columns에 compare-prefix 1개 이상 포함이면 미발동 (legit re-compare 보존). 1회 한정.
    if (this._compareIntentDriftNarrowBlockCount < 3 && this._calledApiThisTurn) {
      const _userInputCID = this.getUserInput?.() || '';
      const NARROW_PERIOD_CID_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;
      if (NARROW_PERIOD_CID_RE.test(_userInputCID)) {
        // prior compare chain history evidence — buffers 중 _change_* 필드 보유 slot 검색
        let _priorCompareSlot = null;
        if (this.buffers && typeof this.buffers.values === 'function') {
          for (const [_slotName, _buf] of this.buffers) {
            if (!_buf?.items?.length) continue;
            const _hasChange = _buf.items.some(it => it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_')));
            if (_hasChange) { _priorCompareSlot = _slotName; break; }
          }
        }
        if (_priorCompareSlot) {
          const _activeBufCID = this.getActiveBuf?.() || this.getBuffer?.();
          const _activeIntention = String(_activeBufCID?.intention || '');
          const _activeSample = _activeBufCID?.items?.[0] && typeof _activeBufCID.items[0] === 'object' ? _activeBufCID.items[0] : null;
          const _activeHasChange = _activeSample ? Object.keys(_activeSample).some(k => k.startsWith('_change_')) : false;
          const _activeIsCompare = _activeIntention === 'MERGED' || _activeHasChange;
          // Case A: active single-period (no compare framing preserved)
          // Case B: active compare but columns spec drops compare-prefix → plain rendering
          const _colsArr = Array.isArray(columns) ? columns : [];
          const _hasCompareCol = _colsArr.some(c => typeof c === 'string' && (c.startsWith('_change_') || c.startsWith('_changeRate_') || c.startsWith('previous_') || c.startsWith('recent_')));
          const _isCaseA = !_activeIsCompare;
          const _isCaseB = _activeIsCompare && _colsArr.length > 0 && !_hasCompareCol;
          if (_isCaseA || _isCaseB) {
            this._compareIntentDriftNarrowBlockCount += 1;
            const _narrowMatchCID = (_userInputCID.match(NARROW_PERIOD_CID_RE) || [])[0] || '';
            const _activeCountCID = _activeBufCID?.items?.length || 0;
            const _caseLabel = _isCaseA ? 'A (fresh single-period buffer)' : 'B (compare buffer but columns drop compare-prefix fields)';
            const _bufHint = _isCaseA
              ? `This turn's call_api loaded a FRESH single-period buffer (intention="${_activeIntention}", ${_activeCountCID} items, no _change_* fields), which DROPS the compare framing`
              : `Active buffer (intention="${_activeIntention}", ${_activeCountCID} items) HAS compare _change_* fields but show_display columns=[${_colsArr.join(', ')}] lack any compare-prefix field (_change_*/previous_*/recent_*), so the rendered table would show only base values without the compare semantics — surface-level compare loss`;
            const _retryHint = this._compareIntentDriftNarrowBlockCount > 1
              ? ` THIS IS BLOCK #${this._compareIntentDriftNarrowBlockCount} of max 3 — STOP retrying show_display and CALL finish() with the honest-inform text below. Further retries waste tokens; the data fundamentally cannot answer this narrow-period compare question.`
              : '';
            return {
              success: false,
              message: `[NARROW PERIOD CHAIN FOLLOW-UP — COMPARE INTENT-DRIFT BLOCK case=${_caseLabel}] User said "${_userInputCID.slice(0, 50)}" (narrowing time keyword "${_narrowMatchCID}") in a chain where prior turn produced a compare result (slot="${_priorCompareSlot}" has _change_* fields — e.g. "출석 줄어든 회원" compare). ${_bufHint}. show_display would render plain "${_narrowMatchCID} 기준 ${_activeCountCID}명" without the original "줄어든/늘어난" semantics, an INTENT-DRIFT false acknowledgment. A single narrow period CANNOT answer a compare question ("how much it changed") — comparison requires two periods.${_retryHint} Instead, call finish with text like: "${_narrowMatchCID} 한 기간만 보면 '줄어든/늘어난' 비교는 불가능해요. 비교 대상 기간(예: 지난주 대비 ${_narrowMatchCID})을 알려주시면 새로 비교해 드릴게요. 아니면 ${_narrowMatchCID} 단순 현황만 원하시는 거면 그렇게 말씀해 주세요." This honestly clarifies the user's intent rather than silently dropping the compare framing.`,
            };
          }
        }
      }
    }

    // [OUTRO REPROPOSAL SANITIZE] show_display intro/outro 인자 공손 재제안 제거 (결정성)
    const _origIntro = intro, _origOutro = outro;
    intro = stripPoliteReproposal(intro);
    outro = stripPoliteReproposal(outro);
    intro = stripMarkdownBold(intro);
    outro = stripMarkdownBold(outro);

    // URL strip — profileImage 등 raw URL이 intro/outro 자연어에 포함될 경우 차단 (도메인 무관)
    const _urlRe = /https?:\/\/[^\s)"']*/g;
    if (intro) intro = intro.replace(_urlRe, '(사진 등록됨)');
    if (outro) outro = outro.replace(_urlRe, '(사진 등록됨)');
    if (intro !== _origIntro || outro !== _origOutro) {
      console.log('[show_display] intro/outro reproposal sanitize');
    }

    // [RAW_DATETIME] intro/outro에 남은 raw ISO datetime → 날짜만 추출 (dual sync with nodeAgent.mjs)
    if (intro) intro = stripRawIsoDatetime(intro);
    if (outro) outro = stripRawIsoDatetime(outro);

    // [L10-DETAIL-SCHEDULE-001 INTRO_HALLUCINATION_DATE_REFLECT] intro narrow date kw vs buffer item range mismatch strip (dual sync with nodeAgent.mjs)
    //   본질: F6 CHAIN_CONTEXT_DATE_RESET가 backend에 full window data를 받게 했어도 LLM planning context bias로
    //         intro에 "이번달/이번주/오늘" narrow date kw hallucinate ("박은정 회원의 이번달 출석 현황은...")
    //         → user input에는 narrow date kw 없음에도 data와 intro mismatch (false ack).
    //   해소: user input에 explicit time kw 없고 + intro에 narrow date kw 있고 + buffer item date range가 그 window보다 넓으면 narrow kw phrase strip.
    {
      const _buf = this.getBuffer();
      const _userInputIDH = this.getUserInput?.() || '';
      const _introBeforeIDH = intro;
      intro = stripIntroDateHallucination(intro, _buf, _userInputIDH);
      if (intro !== _introBeforeIDH) {
        console.log('[show_display] [L10-DETAIL-SCHEDULE-001] intro narrow date kw stripped (buffer item range > narrow window, user input no explicit time kw)');
      }
    }

    // [ROOT_CAUSE_7_META_LEAK] LLM이 intro/outro 안에 harness format(`response to=functions.finish{...}`) 또는 trailing JSON tail 출력한 경우 strip (dual sync with nodeAgent.mjs).
    if (intro) intro = stripAssistantHarnessLeak(intro);
    if (outro) outro = stripAssistantHarnessLeak(outro);

    // [UNMET HISTORY-COND DISCLOSURE] 운영 store445 T3/T7: "X 쓴 사람 중 Y 산 사람"/"추가 구매한 사람" 이력 조건이
    //   실제 filter로 커버되지 않았는데 intro가 조건 적용된 척 날조("체험권 쓴 사람 중 139명") → intro를 결정적
    //   disclosure로 교체 (차단 X — 표는 유지, 거짓 주장만 제거. rid198 canonical 라벨 강제 동형). 탐지는
    //   detectUnmetHistoryCondition(aiUtils 단일소스). dual sync with nodeAgent.mjs.
    {
      const _uhInput = this.getUserInput?.() || '';
      const _uhBuf = this.getBuffer?.();
      // [CONVERSION-FUNNEL 예외] 퍼널 슬롯은 이력 조건이 코드로 '충족된' 결과물 — UNMET 오발동 시 정답 intro를 거짓 disclaimer로 교체하는 역효과 (dual sync nodeAgent.mjs)
      if (this.activeSlot !== '_conversion_funnel' && this.activeSlot !== '_conversion_roster' && _uhInput && _uhBuf?.items?.length) {
        // active 버퍼(표시 대상) lineage의 filter만 수집 — merge 버퍼는 mergeSlots가 _collectSlotFilters로 소스 filter 상속.
        // 전 슬롯 스캔은 버려진 슬롯(0단계 SQL 등)의 filter 토큰이 covered 오판을 만들어 가드 침묵 (store445 회귀 R2 실측).
        const _uhFilterVals = [];
        for (const f of (_uhBuf?.appliedFilters || [])) {
          const v = f?.condition?.value;
          if (Array.isArray(v)) _uhFilterVals.push(...v.map(String));
          else if (v != null) _uhFilterVals.push(String(v));
        }
        const _uh = detectUnmetHistoryCondition(_uhInput, { filterValues: _uhFilterVals, activeIntention: _uhBuf?.intention || '', activeSample: _uhBuf?.items?.[0] || null, activeItems: _uhBuf?.items || null });
        if (_uh) {
          const _uhIntention = String(_uhBuf?.intention || '');
          const _uhBasis = /ALIVE/.test(_uhIntention) ? '현재 유효회원'
            : /ALL_LIST/.test(_uhIntention) ? '전체 회원'
            : /EXPIRED/.test(_uhIntention) ? '만료 회원'
            : '현재 조회된 데이터';
          intro = _uh.mode === 'subset'
            ? `요청하신 "${_uh.tokens.join(', ')}" 사용/구매 이력 기준 조회는 아직 지원되지 않아요. 아래는 ${_uhBasis} 기준 {{COUNT}}건이에요.`
            : `회원권 추가 구매(재구매) 이력 기준 조회는 아직 지원되지 않아요. 아래는 ${_uhBasis} 기준 {{COUNT}}건이에요.`;
          outro = '';
          this._unmetHistoryCondApplied = { mode: _uh.mode, tokens: _uh.tokens };
          console.log(`[show_display] [UNMET HISTORY-COND] intro replaced with disclosure — mode=${_uh.mode} tokens=[${_uh.tokens.join(',')}]`);
        }
      }
    }

    // [GUARD 12] show_display intro/outro 컬럼명 나열 제거 (dual sync with nodeAgent.mjs)
    {
      const _buf = this.getBuffer();
      const _introBeforeEnum = intro;
      const _outroBeforeEnum = outro;
      intro = stripColumnEnumLeak(intro, _buf);
      outro = stripColumnEnumLeak(outro, _buf);

      if (intro !== _introBeforeEnum || outro !== _outroBeforeEnum) {
        console.log('[show_display] [GUARD 12] column enum leak stripped from intro/outro');
      }
    }

    // [L9c-COMPARE-004 FALSE_CHANGE_DIRECTION_INTRO_STRIP] chain follow-up에서 LLM이 사용자 input 부재 변화-방향 keyword를 intro/outro에 fabricate 시 strip (dual sync with nodeAgent.mjs)
    //   본질: "출석 줄어든 회원" → "출석률도 보여줘" chain follow-up에서 LLM이 "출석률이 줄어든 회원" intro 작성
    //         (subject "출석" → "출석률" 갈아치우면서 "줄어든" framing 보존 → fact-false: rate는 100→100 unchanged)
    //   해소: buffer _change_* 보유 + user input change keyword 부재 + intro/outro에 "<word>(이|가) (줄어든|...)" phrase → strip
    //   구조 기반 (case ID / 한국어 keyword hardcode 적은 활용형 패턴만)
    //   회귀 risk 최소화: user input에 변화 키워드 있으면 미발동
    {
      const _buf = this.getBuffer();
      const _userInputFalseChange = this.getUserInput?.() || '';
      const _introBeforeFC = intro;
      const _outroBeforeFC = outro;
      intro = stripFalseChangeDirectionIntro(intro, _buf, _userInputFalseChange);
      outro = stripFalseChangeDirectionIntro(outro, _buf, _userInputFalseChange);
      if (intro !== _introBeforeFC || outro !== _outroBeforeFC) {
        console.log('[show_display] [L9c-COMPARE-004] false change-direction phrase stripped from intro/outro');
      }
    }

    // [L9c-COMPARE-006 cycle 1] PERIOD COUNT MISMATCH — multi-period merge 상태에서 intro/outro 내 잘못된 single count phrase 차단 (dual sync with nodeAgent.mjs)
    //   본질: PERIOD COUNTS HINT (merge_slots 후 perPeriod meta)로 outro per-period 명시는 강제하지만,
    //   intro에 "1월 거래는 39건" 같이 다른 period의 count나 outer-merge total을 single period에 mis-associate 시 차단 없음.
    //   _periodCountHintMeta.perPeriod가 ground truth — periodLabel당 expected count로 sentence-level mismatch detect.
    if (this._periodCountHintMeta?.perPeriod?.length >= 2) {
      const _introBeforePCM = intro;
      const _outroBeforePCM = outro;
      intro = stripPeriodCountMismatchSentences(intro, this._periodCountHintMeta.perPeriod);
      outro = stripPeriodCountMismatchSentences(outro, this._periodCountHintMeta.perPeriod);
      if (intro !== _introBeforePCM || outro !== _outroBeforePCM) {
        console.log('[show_display] [L9c-COMPARE-006] period count mismatch sentence stripped from intro/outro');
      }
      // [L9c-COMPARE-006 F13 Issue C cycle 1] intro per-period count 누락 시 결정적 inject (dual sync with nodeAgent.mjs)
      const _introBeforeInject = intro;
      intro = ensurePeriodCountsInIntro(intro, this._periodCountHintMeta.perPeriod);
      if (intro !== _introBeforeInject) {
        console.log('[show_display] [L9c-COMPARE-006 F13] per-period count sentence injected into intro');
      }
    }

    // [Cat8-REPEAT] show_display intro-outro 간 중복 제거 — LLM이 outro에 intro 내용 반복 시 overlap strip (dual sync with nodeAgent.mjs)
    if (intro && outro) {
      const _introClean = (intro || '').replace(/[.!?]$/, '').trim();
      const _outroFirst = (outro || '').split(/[.!?\n]/)[0].trim();

      if (_outroFirst.length > 5 && (intro.includes(_outroFirst) || _outroFirst.includes(_introClean))) {
        const _outroRemaining = stripIntroOverlap(intro, outro);
        outro = _outroRemaining || '';

        if (!outro) {
          console.log('[show_display] [Cat8-REPEAT] intro-outro overlap detected — outro cleared');
        }
        else {
          console.log('[show_display] [Cat8-REPEAT] intro-outro overlap detected — overlapping prefix stripped from outro');
        }
      }
    }

    // [1-K Layer X] LLM 원본 columns 보존 — KEYWORD_INJECT mutate 전 (catastrophic name drop GUARD용, dual sync with nodeAgent.mjs)
    const _origLlmColumns = Array.isArray(columns) ? [...columns] : null;

    // [anchor 15] visualization-only guard — visualizationOnly intention buffer는 전용 시각화 도구로 redirect (dual sync with nodeAgent.mjs)
    //   [L31-COMPLEX-005] merged buffer 확장 — visualizationOnly intention(_sourceVizIntention)이 merge source로 포함됐어도 차단.
    //   본질: MONTHLY+ATTENDANCE를 HISTORY와 merge한 후 show_display 호출 시 active intention이 'MERGED'라 가드 우회 → raw camelCase column + 의미 없는 산식 누출.
    //   propagated _sourceVizIntention 으로 tool 검출 (intention 키 hardcode 없음, source 추적 기반).
    {
      let _vizBuf = this.getBuffer();
      // [B-1 과적합 수술] 명시적 표/리스트 요구 턴 + viz-only active buffer — 표 렌더 가능한 최근 버퍼로 자동 전환 후 계속 진행
      //   (viz 렌더 → show_display 시도 → REDIRECT → viz 재렌더 루프 차단. probe L40-OPS-VIZ-RANK-LIST-001 실측, dual sync with nodeAgent.mjs)
      //   플래그는 viz 실행기에서 세팅되므로 viz 이전에 show_display가 먼저 오는 경로는 입력 직접 감지로 보강 (run1 실측)
      const _listReqNow = this._vizListRequested || detectExplicitListRequest(this.getUserInput?.() || '');
      if (_vizBuf?.visualizationOnly === true && _listReqNow) {
        let _tabularSlot = null;
        for (const [_slotName, _b] of this.buffers) {
          if (_b?.visualizationOnly !== true && _b?.items?.length) _tabularSlot = _slotName;  // 삽입순 — 마지막 매치 = 최근 적재
        }
        if (_tabularSlot && _tabularSlot !== this.activeSlot) {
          this.activeSlot = _tabularSlot;
          _vizBuf = this.getBuffer();
          if (TOOL_CALLING_LOG) console.log(`[show_display] [LIST REQUESTED] viz-only 우회 — tabular slot "${_tabularSlot}"로 자동 전환`);
        }
      }
      if (_vizBuf?.visualizationOnly === true) {
        const _vizIntention = _vizBuf._sourceVizIntention || _vizBuf.intention || '';
        let _vizTool = '';

        let _vizAltIntention = '';

        if (_vizIntention.includes('MONTHLY+ATTENDANCE')) {
          _vizTool = 'show_attendance_chart or show_attendance_ranking';
          _vizAltIntention = 'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD (attendedCount/attendanceRate 필드 포함)';
        }
        else if (_vizIntention.includes('DURATION_LIST')) {
          _vizTool = 'show_ltv';
        }
        else if (_vizIntention.includes('ENGAGEMENT_LIST')) {
          _vizTool = 'show_engagement_chart';
        }
        else if (_vizIntention.includes('MEMBERSHIP+HISTORY_LIST')) {
          _vizTool = 'show_member_status (신규/재등록/이탈 분류) or show_engagement_chart (연령대 × 회원권) or show_attendance_chart/show_attendance_ranking (출석 분석 buffer 보강) or show_ltv (LTV buffer 보강)';
          _vizAltIntention = 'CHECK_MEMBER+ALIVE_LIST (유효회원 — viewMembershipName/viewStartDate/viewEndDate flat field) 또는 CHECK_MEMBER+EXPIRED_LIST (만료회원)';
        }

        if (_vizTool) {
          const _activeKind = _vizBuf.intention === 'MERGED' ? `merged buffer (source intention "${_vizIntention}")` : `intention "${_vizIntention}"`;
          console.log(`[show_display] visualization-only redirect: ${_activeKind} -> ${_vizTool}`);

          const _altMsg = _vizAltIntention
            ? `OR re-route via a non-visualizationOnly alternative intention that provides the per-row metrics directly (예: ${_vizAltIntention})`
            : 'OR re-route via a non-visualizationOnly alternative intention that provides the per-row metrics directly';

          // [B-1 과적합 수술] 명시적 표/리스트 요구 턴인데 tabular 버퍼 부재 — viz 재호출 지시는 루프라 대안 intention call_api를 직접 지시 (dual sync with nodeAgent.mjs)
          if (_listReqNow) {
            return {
              success: false,
              message: `[VIZ-ONLY + LIST REQUESTED] The user explicitly asked for a table/list, but every loaded buffer is visualizationOnly (not tabular). Do NOT re-call the visualization tool. Instead: (1) call_api(${_vizAltIntention || 'a non-visualizationOnly LIST intention that provides the per-row metrics'}), (2) then show_display on that buffer (sort by the relevant metric), (3) then finish() with a brief summary. Do NOT abandon or reply "데이터 없어요".`,
            };
          }

          return {
            success: false,
            message: `[VISUALIZATION-ONLY REDIRECT] The active ${_activeKind} has visualizationOnly:true — its data structure is designed for visualization rendering, not tabular display. Call ${_vizTool} instead of show_display, ${_altMsg}. You do NOT need to manually pre-fetch the other source intentions first — when you call the visualization tool, any missing required source buffers are AUTO-PREFETCHED by the system before rendering. So simply call the appropriate visualization tool now (with intro and any period args), and it will load its own sources and render the result. Do NOT abandon or reply "데이터 없어요".`,
          };
        }
      }

      // [VIZ-ENGAGEMENT-002] cross-slot engagement guard — active buffer가 ENGAGEMENT가 아니어도 다른 슬롯에 ENGAGEMENT data가 있으면 show_engagement_chart로 redirect (dual sync with nodeAgent.mjs) //
      if (this.buffers instanceof Map) {
        for (const [_sn, _sb] of this.buffers.entries()) {
          if (_sn === this.activeSlot) continue;

          const _sbIntention = _sb?.intention || '';

          if (_sbIntention.includes('ENGAGEMENT_LIST') && !this._engagementChartCompleted) {
            console.log(`[show_display] cross-slot engagement redirect: slot="${_sn}" has ENGAGEMENT data — redirecting to show_engagement_chart`);

            return {
              success: false,
              message: `[ENGAGEMENT REDIRECT] Buffer slot "${_sn}" contains ENGAGEMENT data (${_sbIntention}). You MUST call show_engagement_chart instead of show_display to properly render age-group x membership cross-analysis. Do NOT use compute+show_display for engagement analysis.`,
            };
          }
        }
      }
    }

    // [anchor 17] extreme value 1건 limit 강제 — "최고/최저/가장 + entity" intent + buffer items > limit + max_count 미지정 → retry로 max_count={limit} 강제
    {
      const _userInputForExtreme = this.getUserInput?.() || '';
      const _bufForExtreme = this.getBuffer();
      const _itemsLenExtreme = _bufForExtreme?.items?.length || 0;
      const _extremeEval = evaluateExtremeShowDisplay({
        args: { intro, outro, columns, columnLabels, max_count },
        userInput: _userInputForExtreme,
        bufferItemCount: _itemsLenExtreme,
        retryAttempted: this._extremeLimitRetryAttempted,
      });
      if (_extremeEval.block) {
        this._extremeLimitRetryAttempted = true;
        return { success: false, message: _extremeEval.message };
      }
    }

    // [AXIS INTENT MISMATCH] user input이 sibling axis 식별 토큰을 포함하지만 현재 buffer axis가 다른 axis로 로드된 경우
    //   show_display 차단 + call_api(intention, axis param 변경) 제안. 1회만 차단 (재시도 후 강행 허용).
    //   generic: schema label 기반 token 매칭. multi-axis intention(_groupBy=X 등) 전반에 적용.
    {
      const _bufForAxis = this.getBuffer();
      if (_bufForAxis?._axisSchemas && _bufForAxis?._currentAxisKey && !this._axisIntentRetryAttempted) {
        const _userInputAxis = this.getUserInput?.() || '';
        const _axisMatches = detectAxisIntentMismatch(_userInputAxis, _bufForAxis._axisSchemas, _bufForAxis._currentAxisKey);
        if (_axisMatches.length > 0) {
          this._axisIntentRetryAttempted = true;
          const _intention = _bufForAxis.intention || '<intention>';
          const _curKey = _bufForAxis._currentAxisKey;
          const _sibling = _axisMatches[0];
          const _sibKey = _sibling.siblingAxisKey;
          const _curParsed = _curKey ? _curKey.match(/^_([a-zA-Z]+)=(.+)$/) : null;
          const _sibParsed = _sibKey.match(/^_([a-zA-Z]+)=(.+)$/);
          const _paramName = _sibParsed?.[1] || 'axis';
          const _sibValue = _sibParsed?.[2] || '<value>';
          const _curValue = _curParsed?.[2] || '<value>';
          return {
            success: false,
            message: `[SHOW_DISPLAY AXIS MISMATCH] 사용자 input ("${_userInputAxis}")이 "${_sibling.matchedToken}" 키워드를 포함합니다 — 이는 ${_paramName}="${_sibValue}" axis로 데이터를 다시 로드해야 한다는 신호입니다. ` +
              `현재 buffer는 ${_paramName}="${_curValue}" axis로 로드돼 있습니다.\n` +
              `call_api({ intention: "${_intention}", params: { ${_paramName}: "${_sibValue}" } })를 호출하여 axis를 변경한 후 sort/show_display를 진행하세요. ` +
              `axis 미변경 시 응답 표가 사용자 의도(${_paramName}="${_sibValue}")와 어긋납니다.`,
          };
        }
      }
    }

    // [1-M β] DATA FORM MISMATCH BLOCK — buffer items[0] 필드 vs availableFields 정의 키 intersection < 50% 시 show_display 차단
    //   call_api 단계에서 formMismatchHint로 LLM 인지시켰지만 LLM이 그대로 show_display 호출 시 강제 차단
    //   본질: backend가 동일 intention으로 다른 form 응답 (예: CHECK_SALES+YEAR_STATISTICS가 회원권 카탈로그 fallback 응답 — L13-API-ERROR-003)
    //   1회 차단 후 retry 허용 (다음 호출은 통과 — LLM 재시도 후 강행 가능)
    //   generic: 도메인/intention 무관 구조 기반. dual sync with nodeAgent.mjs
    //
    //   [L9b-CROSS-010 cycle 2] SUMMARY-KEY EXEMPTION
    //     backend가 availableFields 정의에 (a) item-row 필드 + (b) summary-level 메타 키를 함께 섞어 응답하는 케이스 (예:
    //     CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD: availableFields 13개 = 6 item field + 7 summary field).
    //     intersection 5/13 (38%) → false-positive BLOCK. 구조 기반 보정: `buf.summary`에 실제 존재하는 키들은
    //     availableFields 분모에서 제외 (item-row 스키마에 속하지 않는 메타). 도메인/intention 무관 — summary 객체의
    //     키 집합이 분류 기준 (backend response 구조 자체가 제공).
    if (!this._showDisplayFormMismatchBlocked) {
      const _bufForForm = this.getBuffer();
      const _itemsForForm = _bufForForm?.items || [];
      const _afForForm = _bufForForm?.availableFields || null;
      if (_itemsForForm.length > 0 && _afForForm && typeof _afForForm === 'object') {
        // summary-level 키는 분모에서 제외 (item-row 스키마가 아님)
        const _summaryKeysSet = _bufForForm?.summary && typeof _bufForForm.summary === 'object'
          ? new Set(Object.keys(_bufForForm.summary))
          : null;
        const _allFieldKeys = Object.keys(_afForForm);
        const _fieldKeysFormArr = _summaryKeysSet
          ? _allFieldKeys.filter(k => !_summaryKeysSet.has(k))
          : _allFieldKeys;
        if (_fieldKeysFormArr.length > 0) {
          // [L1-MEMBER_DETAIL-007 INTENT_DRIFT] MIXED-FORM SAMPLE UNION (dual sync with nodeAgent.mjs)
          //   backend가 동일 intention으로 mixed-form items 반환 시 (예: items[0]=holding row, items[1..]=membership row 혼합) —
          //   items[0] 단일 검사로는 schema 필드 누락(예: memberShipName/categoryName items[0] X, items[1..] O) → false-positive BLOCK →
          //   LLM이 fallback으로 다른 intention(ALIVE_LIST) 호출 → INTENT_DRIFT.
          //   구조 기반 보정: 9cb944d2 autoGenerateDisplay 다행 sample union 패턴 재사용. 최대 10건 sample 합집합으로 schema 매칭 검사.
          //   도메인/intention 무관 — items 다행 sample union 자체가 구조적 fact.
          const _sampleSizeForm = Math.min(_itemsForForm.length, 10);
          const _itemKeysForm = new Set();
          for (let _i = 0; _i < _sampleSizeForm; _i++) {
            const _itForm = _itemsForForm[_i];
            if (_itForm && typeof _itForm === 'object') {
              for (const _k of Object.keys(_itForm)) _itemKeysForm.add(_k);
            }
          }
          // [L5-CROSS-002 cycle 2] MERGED-SLOT PREFIX EXEMPTION (dual sync with nodeAgent.mjs)
          //   merge_slots는 items에 `<prefix>_<rawKey>` 형태로 필드를 저장하지만 mergedAvailableFields는
          //   원본 raw key 그대로 spread → 직접 intersection으로는 31~43% 매칭 → false-positive BLOCK.
          //   구조 기반 보정: items[0] 키 중 `<anyPrefix>_<rawKey>` 패턴이 있으면 raw key를 valid로 인정.
          //   prefix는 merge code 자체가 만든 convention (`previous_`, `recent_`, `_change_`, `_changeRate_`,
          //   `<month>_`, `<label>_`). 도메인/언어 무관 — suffix match만 사용.
          const _isMergedSlot = (_bufForForm?.intention === 'MERGED');
          const _itemKeysArr = [..._itemKeysForm];
          const _presentCountForm = _fieldKeysFormArr.filter(k => {
            if (_itemKeysForm.has(k)) return true;
            if (_isMergedSlot) {
              // 동일 raw key가 `<prefix>_<k>` 형태로 items에 존재하는지 확인 (prefix 구체 string 무관)
              const _suffix = `_${k}`;
              if (_itemKeysArr.some(ik => ik !== k && ik.endsWith(_suffix))) return true;
            }
            return false;
          }).length;
          const _presentRatioForm = _presentCountForm / _fieldKeysFormArr.length;
          // [L1-MEMBER_DETAIL-007 INTENT_DRIFT] IDENTITY-FIELD EXEMPTION (dual sync with nodeAgent.mjs)
          //   schema availableFields에 정의된 identity field(name류)가 items sample union에 존재하면
          //   "true fallback/catalog response (사용자 의도와 무관)"가 아니라 "domain-correct mixed-form response"라는 강한 구조적 신호.
          //   진짜 fallback(예: SALES intention이 회원권 catalog 응답)은 schema의 identity field를 가지지 않는다.
          //   도메인/intention 무관 — identity field 정의(NAME_FIELDS_K)는 구조적 fact.
          const _IDENTITY_FIELDS_FORM = ['storeName', 'name', 'memberName', 'memberShipName', 'membershipName', 'viewMembershipName', 'productName', 'itemName', 'lessonName', 'className', 'title'];
          const _hasIdentityInSchema = _fieldKeysFormArr.some(k => _IDENTITY_FIELDS_FORM.includes(k));
          const _hasIdentityInItems = _hasIdentityInSchema && _fieldKeysFormArr.some(k => _IDENTITY_FIELDS_FORM.includes(k) && _itemKeysForm.has(k));
          // [VIZ-ENGAGEMENT-001] GROUPED-BUFFER EXEMPTION (dual sync with nodeAgent.mjs)
          //   After a compute(group)/age-aggregate, buffer.items are replaced with grouped rows
          //   ([<field>_group | <field>, _count]) and the original raw availableFields (memberId/name/
          //   birthDay/...) no longer describe the buffer — they describe the PRE-group API schema.
          //   The form-mismatch denominator therefore becomes irrelevant (intersection→0% on the
          //   *_group / _count rows) and the guard fires as a FALSE POSITIVE on a legitimately grouped
          //   buffer (e.g. age distribution: items=[{birthDay_group, _count}]).
          //   Structural detection (domain/intention-independent): compute(group) sets
          //   buf.summary.groupTotalCount and buf.summary.groupCount. Those keys exist ONLY on grouped
          //   buffers — a raw API/catalog fallback response never carries them. When present, skip the
          //   form-mismatch block entirely.
          const _isGroupedBuffer = _bufForForm?.summary && typeof _bufForForm.summary === 'object'
            && (_bufForForm.summary.groupTotalCount !== undefined || _bufForForm.summary.groupCount !== undefined);
          // [DEMO-T4-REREG-MONTHLY-FLOW / 운영4차 #5] MERGED-BUFFER EXEMPTION (dual sync with nodeAgent.mjs)
          //   merged buffer는 grouped buffer처럼 변형 결과 — prefix exemption(suffix match)으로 item field는 인정되나
          //   원본 intention의 summary-level 키(startDate/endDate/total*)가 availableFields 분모에 남아 false-positive BLOCK.
          //   (예: 3기간 재등록률 merge → 분모 15 = item 7 + summary 8, suffix 인정 7개 → 47% → 오차단 → LLM 폭주.)
          //   merge_slots 결과는 사용자가 명시 호출해 만든 데이터라 backend fallback/catalog 응답일 수 없음 → 전체 skip.
          if (_presentRatioForm < 0.5 && !_hasIdentityInItems && !_isGroupedBuffer && !_isMergedSlot) {
            // [AUTO-RECOVER] schema 불일치(fallback/오염 버퍼)면 정상 item-row 형제 슬롯으로 결정적 전환 후 재시도 유도 //
            //   본질: SQL 사전단계 오라우팅 등으로 활성 버퍼가 질문과 어긋난 form일 때, LLM 회복에 의존하지 않고 구조적으로
            //         유효한 슬롯이 있으면 자동 switch → LLM이 올바른 데이터로 재시도. intent/키워드 0. dual sync with nodeAgent.mjs.
            const _recoverSlot = this._findRenderableSiblingSlot(this.activeSlot);

            if (_recoverSlot) {
              this.setActiveSlot(_recoverSlot);
              const _rb = this.getBuffer();
              const _rfields = _rb?.availableFields ? Object.keys(_rb.availableFields).slice(0, 6).join(', ') : '';
              console.log(`[show_display] [AUTO-RECOVER] schema mismatch on "${_bufForForm?.intention || ''}" → switched active slot to "${_recoverSlot}" (${_rb?.items?.length || 0}건)`);
              return {
                success: false,
                message: `[DATA AUTO-SWITCHED] 직전 활성 데이터가 질문과 맞지 않아(schema 불일치) 적합한 슬롯 "${_recoverSlot}"(${_rb?.items?.length || 0}건, fields: [${_rfields}])로 전환했어요. 이 슬롯 기준으로 질문 의도에 맞게 (필요 시 compute(group)/filter 후) show_display 하세요. 직전 데이터는 사용하지 마세요.`,
              };
            }

            this._showDisplayFormMismatchBlocked = true;
            const _intentionForm = _bufForForm?.intention || '<intention>';
            const _actualKeysForm = [...new Set(_itemsForForm.flatMap(it => Object.keys(it || {})))].filter(k => !k.startsWith('_'));
            console.log(`[show_display] [1-M β] DATA FORM MISMATCH BLOCKED — intention="${_intentionForm}" item-schema availableFields=${_fieldKeysFormArr.length} (summary keys excluded=${_summaryKeysSet ? _summaryKeysSet.size : 0}) actual_in_items_union(${_sampleSizeForm})=${_presentCountForm} (${Math.round(_presentRatioForm * 100)}%)`);
            return {
              success: false,
              message: `[DATA FORM MISMATCH — show_display BLOCKED] 현재 buffer intention "${_intentionForm}" 응답이 정의된 schema와 다른 form입니다. 정의된 item-row availableFields(${_fieldKeysFormArr.length}개) 중 실제 items sample union(${_sampleSizeForm}건)에 존재=${_presentCountForm}개 (${Math.round(_presentRatioForm * 100)}%). 실제 items 필드: [${_actualKeysForm.join(', ')}]. ` +
                `이 데이터는 사용자 질문("${this.getUserInput?.() || ''}")의 본래 의도와 일치하지 않는 fallback/catalog 응답일 수 있습니다. ` +
                `다음 중 하나를 선택하세요: ` +
                `(1) call_api로 다른 intention 호출 — 사용자 질문에 더 부합하는 intention을 get_intentions 결과에서 재선택. ` +
                `(2) finish()로 사용자에게 데이터 부재/조회 불가 안내. (예: "${_intentionForm.includes('SALES') ? '해당 기간 매출 내역이 없어' : '해당 정보가 조회되지 않아'}" 식 자연어). ` +
                `현재 buffer 데이터를 show_display로 표시 금지 — schema 불일치로 사용자 의도와 어긋난 표가 노출됩니다.`,
            };
          }
        }
      }
    }

    // [AXIS AUTO-SWITCH] multi-axis intention에서 LLM이 sibling axis 식별자 컬럼을 명시했는데 활성 buffer가 다른 axis인 경우 //
    //   해당 sibling axis로 로드된 슬롯이 이미 있으면 결정적으로 setActiveSlot 전환 → LLM이 올바른 buffer로 재표시. //
    //   본질: compound 다축 질의("요일별, 시간대별")에서 LLM이 wrong-axis 컬럼으로 manual show → 식별자 drop(익명 숫자) + intro 오라벨. //
    //         COLUMN GUARD는 single-retry 후 botched 표를 통과시키므로 axis 슬롯 전환은 결정적으로 처리(LLM 재시도 의존 X, 1회/turn). //
    //         전환 후 잔여 축은 기존 MULTI-AXIS AUTO-RENDER가 turn-end에 결정적 표출. //
    //   generic: case/field 하드코딩 X. _axisSchemas의 sibling 식별자 + _currentAxisKey 구조 매칭만 사용. dual sync with nodeAgent.mjs. //
    if (!this._axisAutoSwitchedThisTurn && Array.isArray(columns) && columns.length > 0) {
      const _bufForAxisSwitch = this.getBuffer();
      const _curAxisKeyAS = _bufForAxisSwitch?._currentAxisKey;
      const _axisSchemasAS = _bufForAxisSwitch?._axisSchemas;

      if (_axisSchemasAS && typeof _axisSchemasAS === 'object' && _curAxisKeyAS && _axisSchemasAS[_curAxisKeyAS]) {
        const _itemsAS = _bufForAxisSwitch.items || [];
        const _dataKeysAS = (_itemsAS[0] && typeof _itemsAS[0] === 'object') ? new Set(Object.keys(_itemsAS[0])) : new Set();

        // 현재 axis의 primary 식별자(첫 usable label field) — columns에 이미 있으면 LLM이 현재 axis 의도 → 전환 불필요 //
        let _curPrimaryAS = null;

        for (const [_f, _meta] of Object.entries(_axisSchemasAS[_curAxisKeyAS])) {
          const _lbl = (_meta && typeof _meta === 'object' && _meta.label) ? _meta.label : null;

          if (!_lbl || /Id$/.test(_f) || /profile|image/i.test(_f)) continue;

          _curPrimaryAS = _f;
          break;
        }

        const _curIdentifierInCols = _curPrimaryAS && columns.includes(_curPrimaryAS);

        let _targetAxisKeyAS = null;
        let _matchedColAS = null;

        if (!_curIdentifierInCols) {
          for (const _col of columns) {
            if (_dataKeysAS.has(_col)) continue;

            for (const [_axisKey, _schema] of Object.entries(_axisSchemasAS)) {
              if (_axisKey === _curAxisKeyAS) continue;

              if (_schema && typeof _schema === 'object' && Object.prototype.hasOwnProperty.call(_schema, _col)) {
                _targetAxisKeyAS = _axisKey;
                _matchedColAS = _col;
                break;
              }
            }

            if (_targetAxisKeyAS) break;
          }
        }

        if (_targetAxisKeyAS) {
          const _targetSlotAS = this._findSlotByAxisKey(_targetAxisKeyAS, this.activeSlot);

          if (_targetSlotAS) {
            this._axisAutoSwitchedThisTurn = true;
            this.setActiveSlot(_targetSlotAS);

            const _tbAS = this.getBuffer();

            console.log(`[show_display] [AXIS AUTO-SWITCH] columns의 sibling axis(${_targetAxisKeyAS}) 식별자 "${_matchedColAS}" → active slot "${_targetSlotAS}"(${_tbAS?.items?.length || 0}건) 전환`);

            return {
              success: false,
              message: `[AXIS AUTO-SWITCHED] 명시한 컬럼 "${_matchedColAS}"(${_targetAxisKeyAS} axis 식별자)인데 직전 활성 buffer는 다른 axis였습니다. 해당 axis 슬롯 "${_targetSlotAS}"(${_tbAS?.items?.length || 0}건)로 전환했어요. 이 슬롯 기준으로 같은 의도의 intro로 show_display 하세요. 다른 축도 요청됐다면 자동으로 함께 표출되니 추가 switch_slot은 불필요합니다.`,
            };
          }
        }
      }
    }

    // [1-D] columns 검증 — buffer item field에 매칭 안 되는 column 1개 이상 + valid 후보 존재 시 retry
    {
      const _bufForCols = this.getBuffer();
      const _itemsForCols = _bufForCols?.items || [];
      const _colsEval = evaluateShowDisplayColumns({
        columns,
        bufferItems: _itemsForCols,
        labelOverrides: LABEL_OVERRIDES,
        fieldRegistry: FIELD_REGISTRY,
        retryAttempted: this._showDisplayColumnRetryAttempted,
        // [AXIS SWITCH] multi-axis intention의 sibling axis 스키마 + 현재 axis + intention 전달
        axisSchemas: _bufForCols?._axisSchemas || null,
        currentAxisKey: _bufForCols?._currentAxisKey || null,
        intention: _bufForCols?.intention || null,
      });
      if (_colsEval.block) {
        this._showDisplayColumnRetryAttempted = true;
        return { success: false, message: _colsEval.message };
      }
    }

    // [L5-TERMRATIO] Multi-period TERM_RATIO merge 시 totalRevenue 컬럼 누락 자동 보충 (dual sync with nodeAgent.mjs) //
    // LLM이 비결정적으로 totalRevenue 컬럼을 show_display columns에서 빠뜨리는 문제 guard.
    // 조건: (1) merged buffer (2) source intention이 TERM+RATIO (3) columns 배열에 purchaseCount/purchaseRate prefix가 있지만 totalRevenue prefix 없음
    {
      const _bufForTermRatio = this.getBuffer();
      const _isMergedTermRatio = _bufForTermRatio?.intention === 'MERGED'
        && Array.isArray(columns) && columns.length > 0
        && _bufForTermRatio?.items?.length > 0;

      if (_isMergedTermRatio) {
        const _sourceSlots = [...this.buffers.values()].filter(b => b.intention && b.intention.includes('TERM+RATIO'));

        if (_sourceSlots.length >= 2) {
          const _hasPurchasePrefix = columns.some(c => c.includes('_purchaseCount') || c.includes('_purchaseRate'));
          const _hasRevenuePrefix = columns.some(c => c.includes('_totalRevenue'));
          const _bufferHasRevenue = _bufForTermRatio.items.some(item => {
            return Object.keys(item).some(k => k.includes('_totalRevenue'));
          });

          if (_hasPurchasePrefix && !_hasRevenuePrefix && _bufferHasRevenue) {
            const _revenueFields = Object.keys(_bufForTermRatio.items[0] || {}).filter(k => k.includes('_totalRevenue'));
            _revenueFields.sort();
            columns.push(..._revenueFields);
            console.log('[show_display] TERM_RATIO totalRevenue auto-inject:', _revenueFields);
          }
        }
      }
    }

    // [anchor 16] DETAIL NOT FOUND fallback show_display 차단 (Layer 3a)
    //   조건: session._detailNotFoundContext 존재 (이전 DETAIL fail + specifier) + buffer items >1 (LIST fallback)
    //   1회 retry: filter(specifier) 적용 후 재호출 OR finalize "찾을 수 없어요" 권장 / 2회+ 강행: finalize 강제
    if (this._detailNotFoundContext?.specifier && !this._detailFallbackShowDisplayBlocked) {
      const _bufForDetail = this.getBuffer();
      const _items = _bufForDetail?.items || [];
      if (_items.length > 1) {
        this._detailFallbackShowDisplayBlocked = true;
        this._finishAllowedByGuard = true;
        const _spec = this._detailNotFoundContext.specifier;
        this._finishGuardSuggestedText = `${_spec}을(를) 찾을 수 없어요.`;
        return {
          success: false,
          message: `[DETAIL FALLBACK BLOCKED — show_display] DETAIL("${_spec}") 호출이 실패했고 현재 LIST fallback ${_items.length}건이 buffer에 로드되어 있습니다. specifier "${_spec}"으로 filter되지 않은 전체 LIST를 표시하면 user를 오도합니다.\n\n다음 중 하나로 진행하세요:\n  (1) filter(field: <name-like>, op: exact|contains, value: "${_spec}") 호출 → 1건 이상 match 시에만 show_display 재호출.\n  (2) finish IMMEDIATELY with a response naturally stating that "${_spec}" is not in the catalog. 한국어 응답 시 "${_spec}을(를) 찾을 수 없어요" 형식 권장 (받침 정합).\nDo NOT retry show_display without applying filter first.`,
        };
      }
    }

    // [B4-2 ENTITY MISMATCH] catalog 호출 후 사용자 명사구가 items entity field에 없으면 차단
    // - 1차 retry: LLM에게 finish() 호출 안내 (재선택 기회)
    // - 2차+ 강행: 동일 메시지로 차단 (LLM이 finish 호출하도록 강제, max iter 도달 시 종료)
    {
      const _userInputForEntity = this.getUserInput?.() || '';
      const _bufForEntity = this.getBuffer();
      const _intentionForEntity = _bufForEntity?.intention;
      const _entityMismatch = detectEntityCatalogMismatch(_userInputForEntity, _bufForEntity, _intentionForEntity);
      if (_entityMismatch) {
        this._showDisplayEntityRetryCount = (this._showDisplayEntityRetryCount || 0) + 1;
        // [P2-E] _entityType 하드코딩 (수업/회원권/코치/항목) 제거 — entity name 자체로 통일
        const _entityNoun = _entityMismatch.userNouns[0] || '항목';
        console.log(`[show_display] 🚧 entity mismatch block (#${this._showDisplayEntityRetryCount}) — userNouns:`, _entityMismatch.userNouns, 'catalogPreview:', _entityMismatch.catalogPreview);
        // [받침 cosmetic — LLM 위임] 강제 한국어 finish 텍스트 폐기. LLM 이 응답 언어로 자연어 작성 + 가이드로 받침 정합 안내.
        // 1회차: retry message (LLM에게 finish 호출 안내, 응답 자율 작성)
        if (this._showDisplayEntityRetryCount === 1) {
          this._finishAllowedByGuard = true;
          this._finishGuardSuggestedText = null;  // LLM 자율 작성
          return {
            success: false,
            message: `[ENTITY MISMATCH — show_display BLOCKED] User asked for "${_entityNoun}" but the active catalog contains only [${_entityMismatch.catalogPreview.join(', ')}]. The requested entity does NOT exist.\n\nYou MUST call finish IMMEDIATELY with a response written in the user's language, naturally stating that "${_entityNoun}" is not registered. For Korean responses: use exactly ONE correct particle form (받침 검사 — '필라테스' → '는', '회원권' → '은') — NEVER output dual-disclosure like "은(는)" / "이(가)" / "을(를)". Other languages: native grammar. Do NOT call show_display or any other tool — ONLY finish() with a natural response.`,
          };
        }
        // 2회차+: LLM 미응답 시 fallback — language-neutral marker (한국어 강제 폐기)
        const _fallbackText = `${_entityNoun}: not in catalog.`;
        console.log('[show_display] 🛑 auto-finish conversion (language-neutral fallback):', _fallbackText);
        return {
          success: true,
          _autoFinish: true,
          response: _fallbackText,
          message: _fallbackText,
        };
      }
    }

    // [B1 NUMERIC HALLUCINATION] raw number detect BEFORE placeholder substitution (LLM-authored raw only)
    // - placeholder 치환 후엔 코드가 만든 raw가 들어가서 false positive 위험. 따라서 치환 전에 검출.
    // - 1차: retry message (LLM 재작성 기회) / 1회 retry 후에도 raw → 다음 단계 strip 발동
    {
      const _bufForGuard = this.getBuffer();
      const _hText = `${intro || ''} ${outro || ''}`.trim();
      if (_hText && _bufForGuard?.items?.length) {
        const _hRes = detectIntroOutroNumericHallucination(_hText, _bufForGuard);
        if (_hRes.detected) {
          if (this._showDisplayNumericRetryCount === 0) {
            this._showDisplayNumericRetryCount = 1;
            return {
              success: false,
              message: `[NUMERIC HALLUCINATION] intro/outro contains raw number "${_hRes.sample}". You MUST use placeholders: {{SUM:field}}, {{AVG:field}}, {{COUNT}}, {{COUNT_NONZERO:field}}, {{MAX:field}}, {{MIN:field}}, {{SUMMARY:key}}. Examples: "{{SUMMARY:activeRate}}%", "{{SUM:totalPrice}}원", "총 {{COUNT}}명". If no matching field/summary exists, OMIT the number entirely. Retry show_display now.`,
            };
          }
          // 1회 retry 후에도 raw → strip (LLM 무시 차단)
          const _origI = intro, _origO = outro;
          intro = stripRawNumberArtifacts(intro);
          outro = stripRawNumberArtifacts(outro);
          if (intro !== _origI || outro !== _origO) {
            console.log('[show_display] 🧹 raw number auto-strip after retry');
          }
        }
      }
    }

    // [PRIMARY RATE PLACEHOLDER GUARD] intro/outro의 secondary rate placeholder (예: avgAttendanceRate)
    // 사용 시 intention 메타의 primaryRateKey(예: activeRate)로 자동 swap.
    // 본질: 같은 intention 응답에 다중 rate field 있을 때 (activeRate vs avgAttendanceRate)
    //       LLM이 stochastic하게 secondary 선택 → final.content에 잘못된 메트릭 노출 차단.
    //       call_api_guide 5-fold 강조에도 stochastic 무시 발생 — code 단계 결정적 차단으로 보완.
    //       (dual sync with nodeAgent.mjs)
    {
      const _bufForRate = this.getBuffer();
      const _intentionMeta = _bufForRate?.intention
        ? this.findIntentionMeta(_bufForRate.intention)
        : null;
      const _primaryRateKey = (_intentionMeta && typeof _intentionMeta.primaryRateKey === 'string')
        ? _intentionMeta.primaryRateKey
        : null;
      if (_primaryRateKey && _bufForRate?.summary && _primaryRateKey in _bufForRate.summary) {
        const _summary = _bufForRate.summary;
        const _RATE_PH = /\{\{SUMMARY:([A-Za-z0-9_]+)\}\}/g;
        const _swap = (text, label) => {
          if (!text) return text;
          let swapped = false;
          let swappedKey = null;
          const next = text.replace(_RATE_PH, (m, key) => {
            if (key === _primaryRateKey) return m;
            // rate suffix key 만 대상 (다른 summary key는 swap X)
            if (!/[Rr]ate$/.test(key)) return m;
            // summary에 실제 값 있는 key만 swap (없으면 placeholder strip path가 처리)
            if (typeof _summary[key] !== 'number') return m;
            swapped = true;
            swappedKey = key;
            return `{{SUMMARY:${_primaryRateKey}}}`;
          });
          if (swapped) {
            console.log(`[show_display] 🔄 ${label} secondary rate placeholder swap: ${swappedKey} → ${_primaryRateKey} (primary)`);
          }
          return next;
        };
        intro = _swap(intro, 'intro');
        outro = _swap(outro, 'outro');
      }
    }

    // [SMS-INDIVIDUAL-SUBSET fix] _smsRowScopeThisTurn 활성 시 buffer.items를 recipients로 1차 swap → autoGenerateDisplay가 recipients만 렌더 (후 원복). dual sync with nodeAgent.mjs //
    //   본질: individual SMS가 recipients를 부분집합으로 좁혀도 표는 full buffer를 렌더 → 비-recipient 행 "문자 생성 하기" 버튼이 죽음(recipient undefined → 핸들러 silent return).
    //   해소: show_display 시 buffer.items를 recipients로 swap + max_count=recipients 수 강제 → 표가 정확히 recipients만 표시. autoGenerateDisplay 후 원복.
    //   재진입 안전: _smsRowScopeOriginalItems(세션 보존)로 double-swap 방지.
    let _smsRowScopeRestore = null;
    if (this._smsRowScopeThisTurn) {
      const _scope = this._smsRowScopeThisTurn;
      const _scopeBuf = this.getActiveBuf?.();
      if (_scopeBuf && Array.isArray(_scopeBuf.items) && _scopeBuf.items.length > 0 && Array.isArray(_scope.recipients) && _scope.recipients.length > 0) {
        const _searchSource = this._smsRowScopeOriginalItems || _scopeBuf.items;
        // 식별자 우선순위: memberId > phone > name
        const _matched = _searchSource.filter(it => _scope.recipients.some(r => {
          if (r.memberId != null && (it.memberId === r.memberId || it.teacherId === r.memberId)) return true;
          if (r.phone) {
            const _itPhone = it.phone || it.phoneNumber || it.tel || it.mobile || it.contactNumber || '';
            if (_itPhone === r.phone) return true;
          }
          if (r.name && (it.name === r.name || it.memberName === r.name || it.userName === r.name || it.teacherName === r.name)) return true;
          return false;
        }));
        if (_matched.length > 0) {
          if (!this._smsRowScopeOriginalItems) this._smsRowScopeOriginalItems = _scopeBuf.items;
          _smsRowScopeRestore = { buf: _scopeBuf };
          _scopeBuf.items = _matched;
          max_count = _matched.length;
          console.log(`[show_display] [SMS-INDIVIDUAL-SUBSET] SMS ROW-SCOPE applied: ${_scope.recipients.length} recipient(s) (targetIndex=${_scope.targetIndex}). buffer.items ${this._smsRowScopeOriginalItems.length} → ${_matched.length}`);
        } else {
          console.log(`[show_display] [SMS-INDIVIDUAL-SUBSET] SMS ROW-SCOPE skip: recipients not found in buffer items`);
        }
      }
    }

    // [MAX_COUNT GUARD] 사용자 input에 count limit 키워드가 없는데 LLM이 임의 max_count를 보냈으면 제거 (결정성 보호 — default 적용)
    // [L23-FILTER-FOCUS-001] substitute 이전 ordering 보장 — GUARD가 max_count 제거 후 substitute 시점에 실제 표 cap 반영
    // [SMS-INDIVIDUAL-SUBSET fix] row-scope 활성 시 strip skip — 위에서 max_count를 recipients 수로 강제했으므로 보존
    const _userInputMax = this.getUserInput?.() || '';
    const COUNT_LIMIT_KW = /상위|탑|top\b|\b\d+\s*[명건개]\s*만|먼저\s*\d+|처음\s*\d+|최고|최저|가장|제일|최다|최소/i;
    if (max_count != null && _userInputMax && !COUNT_LIMIT_KW.test(_userInputMax) && !this._smsRowScopeThisTurn) {
      console.log(`[show_display] MAX_COUNT_GUARD strip: userInput "${_userInputMax.slice(0,40)}" lacks count limit (was ${max_count})`);
      max_count = undefined;
    }

    // [B1 TOKEN-ANCHORED] substitute {{SUM/AVG/COUNT/SUMMARY:field}} placeholders with buffer-derived values
    // [L23-FILTER-FOCUS-001] cap-aware substitute — {{COUNT}} 치환은 표시 row 수 기준 (cap 적용 시 buffer.items.length ≠ 표 row 수)
    //   본질: filter 결과 N건 buffer + max_count로 sliced 표 시 intro/outro {{COUNT}}가 buffer.items.length로 박혀 사용자 모순 (CALC_INCONSISTENT)
    //         → shallow wrap에 _displayedCount(=min(items.length, cap)) 추가, placeholderSubstitute.js의 COUNT case가 우선 사용
    // [L3-COMPUTE-MIN-001] EXTREME LIMIT 반영 — 사용자가 "최고/최저/가장/제일/최다/최소 + 단일 entity" 명시 시 _displayedCount 계산에 extreme limit cap 적용. dual sync with nodeAgent.mjs.
    const _bufForSub = this.getBuffer();
    if (_bufForSub?.items?.length) {
      const _MAX_DEFAULT_DISPLAY = 150;
      const _userInputForExtremeSub = this.getUserInput?.() || '';
      const _extremeIntentSub = detectExtremeIntent(_userInputForExtremeSub);
      const _topNIntentSub = detectExplicitTopNIntent(_userInputForExtremeSub);
      const _rawCap = (typeof max_count === 'number' && max_count > 0)
        ? Math.min(max_count, _MAX_DEFAULT_DISPLAY)
        : _MAX_DEFAULT_DISPLAY;
      const _effectiveCap = _extremeIntentSub
        ? Math.min(_rawCap, _extremeIntentSub.limit)
        : (_topNIntentSub
          ? Math.min(_rawCap, _topNIntentSub.limit)
          : _rawCap);
      const _displayedCount = Math.min(_bufForSub.items.length, _effectiveCap);
      // [PLACEHOLDER-SUMMARY-SCOPE] scope-aware {{COUNT}} for explicit top-N requests only
      //   본질: 사용자가 "상위 5명만/탑 3개/top 10/먼저 5건/처음 5개" 같이 명시적 top-N 요청 시,
      //         "총 {{COUNT}}명 중 5명" 자연어 의미는 scope total (full buffer.items.length).
      //         _displayedCount(=5) 사용 시 "총 5명 중에서 5명" tautology 발생.
      //   distinction with extreme intent ("최고/최저/가장 + entity"):
      //         extreme = 사용자가 THE singular (예: "최소 출석 회원 1명"). intro "총 1명이야"가 정답.
      //         "총 173명이야"는 user singular intent 모순 (A-RED-2 SELF_CONTRADICTION).
      //         따라서 _extremeIntentSub는 scope-aware 발동 X.
      //   pattern: detectExplicitTopNIntent regex (상위/탑/top/먼저/처음 + N) 단일 신호.
      //   dual sync: nodeAgent.mjs show_display + aiUtils.js _substDisplayVars 동일 처리.
      const _userExplicitTopNSatisfied = !!(_topNIntentSub && _displayedCount <= _topNIntentSub.limit);
      const _scopeTotal = _bufForSub.items.length;
      const _bufForSubCapped = { ..._bufForSub, _displayedCount, _userExplicitTopNSatisfied, _scopeTotal };
      if (intro) {
        const _r = substituteOutroPlaceholders(intro, _bufForSubCapped, this.getSlotSummaryMap());
        if (_r.substituted) console.log('[show_display] 🎯 intro placeholder substituted');
        intro = _r.text;
      }
      if (outro) {
        const _r = substituteOutroPlaceholders(outro, _bufForSubCapped, this.getSlotSummaryMap());
        if (_r.substituted) console.log('[show_display] 🎯 outro placeholder substituted');
        outro = _r.text;
      }
      // [L3-COMPUTE-GROUP-002] grouped buffer headline unit normalization — placeholder 치환 후 entity classifier ("명/건/회") → "종류"
      //   buffer가 compute(group) 결과일 때만 발동 (_aggregateMetaFields 신호). dual sync with nodeAgent.mjs.
      if (intro) intro = normalizeGroupedUnits(intro, _bufForSub);
      if (outro) outro = normalizeGroupedUnits(outro, _bufForSub);
    }
    // [SAFETY NET] LLM이 placeholder 작성했는데 치환 실패 시 (field 매칭 X) clause 통째 drop
    intro = stripPlaceholderArtifacts(intro);
    outro = stripPlaceholderArtifacts(outro);

    // [L9c-COMPARE-006 cycle 1] PERIOD COUNT MISMATCH — placeholder 치환 후 재검사 (dual sync with nodeAgent.mjs)
    //   본질: LLM이 intro에 "{{COUNT}}건" placeholder 사용 시, 위 early sanitize는 패턴 매칭 X.
    //   placeholder 치환 후 "39건" 형태로 박혀야 mismatch 검출 가능 → 여기서 한 번 더 호출.
    if (this._periodCountHintMeta?.perPeriod?.length >= 2) {
      const _introBeforePCM2 = intro;
      const _outroBeforePCM2 = outro;
      intro = stripPeriodCountMismatchSentences(intro, this._periodCountHintMeta.perPeriod);
      outro = stripPeriodCountMismatchSentences(outro, this._periodCountHintMeta.perPeriod);
      if (intro !== _introBeforePCM2 || outro !== _outroBeforePCM2) {
        console.log('[show_display] [L9c-COMPARE-006] period count mismatch sentence stripped (post-placeholder substitution)');
      }
      // [L9c-COMPARE-006 F13 Issue C cycle 1] intro per-period count 누락 시 결정적 inject (post-placeholder) (dual sync with nodeAgent.mjs)
      const _introBeforeInject2 = intro;
      intro = ensurePeriodCountsInIntro(intro, this._periodCountHintMeta.perPeriod);
      if (intro !== _introBeforeInject2) {
        console.log('[show_display] [L9c-COMPARE-006 F13] per-period count sentence injected into intro (post-placeholder)');
      }
    }

    // [NO-TIME-DIMENSION OUTRO ENFORCE] 시간 차원 없는 intention + multi-period 입력 시 outro에 정적 정보 안내 자동 append (LLM이 hint 무시 시 결정적 보완)
    // 본질: call_api hint를 LLM이 자유 자연어 변환 과정에서 누락하는 stochastic 변동 차단. outro 약/부재 시 prepend.
    if (this._noTimeDimensionHint) {
      const _ntdh = this._noTimeDimensionHint;
      const _ntdhActiveBuf = this.getBuffer();
      // 동일 intention buffer 활성화 시에만 (다른 intention으로 switch 됐으면 skip)
      if (_ntdhActiveBuf && _ntdhActiveBuf.intention === _ntdh.intention) {
        const _ntdMonths = _ntdh.inputMonths.join('/');
        // outro에 이미 변동 부재 anchor (정적/고정/변동 없/모든 기간/월별) 표현이 있으면 skip — 중복 방지
        const _NTD_ALREADY_RE = /(정적|고정|변동(이)?\s*없|월별\s*변동|기간(에|에는)?\s*따라\s*달라지지\s*않|모든\s*기간\s*동일)/;
        if (!_NTD_ALREADY_RE.test(outro || '') && !_NTD_ALREADY_RE.test(intro || '')) {
          const _ntdSentence = `${_ntdMonths} 모두 동일한 정보로, 본 정보는 월/기간별 변동이 없는 정적 정보입니다.`;
          if (outro && outro.trim().length > 0) {
            outro = `${outro.trim()} ${_ntdSentence}`;
          } else {
            outro = _ntdSentence;
          }
          console.log('[show_display] [NO-TIME-DIMENSION OUTRO ENFORCE] outro에 정적 정보 안내 자동 append');
        }
      }
    }

    let buffer = this.getBuffer();

    if (!buffer.items || buffer.items.length === 0) {
      // 필터 결과 0건이지만 원본 데이터 있었던 경우 → 정당한 0건 응답 (DISPLAY_FAIL 차단)
      if (buffer.originalItems && buffer.originalItems.length > 0) {
        const filterInfo = buffer.appliedFilters?.length > 0
          ? ` (필터: ${buffer.appliedFilters.map(f => f.field).join(', ')})`
          : '';
        return {
          success: true,
          count: 0,
          displayCount: 0,
          message: `필터 결과 0건${filterInfo}. 원본 ${buffer.originalItems.length}건이 조건에 맞지 않음. 사용자에게 "조건에 맞는 결과 없음" 안내 후 필요 시 filter({reset:true})로 복원.`,
        };
      }
      return { success: false, message: 'No data in buffer. You MUST call get_intentions → call_api to load data FIRST, then retry show_display. Do NOT finish or respond without loading data.' };
    }

    // [NATIONALITY COUNT — 운영 5차 rid109] 국적 카운트 의문("외국인/내국인/국적별 몇명") 결정적 집계 (1회 한정)
    //   근본: LLM이 국적 수를 gender/countryCode(전화 +82)로 추정·날조("외국인 100명"·정답 0). backend member list가
    //   실제 국적필드 countryIso(KR=한국, 미입력 회원은 키 생략)를 반환하므로, 이 의도 감지 시 countryIso로 결정적 집계.
    //   generic COUNT QUESTION 가드는 buffer.items.length(전체수)만 세므로 국적 필터 카운트엔 부적합 → 그보다 먼저 발동.
    //   dual sync with nodeAgent.mjs.
    if (!this._nationalityCountBlocked && !this._showDisplayCountQuestionBlocked) {
      const _natInput = this.getUserInput?.() || '';
      const _natIntent = detectNationalityCountIntent(_natInput);
      // member buffer 판정 — name + memberId/userId 존재 (국적 집계는 회원 목록에만 의미) //
      const _looksMember = _natIntent && Array.isArray(buffer.items) && buffer.items.length > 0
        && buffer.items.some(it => it && it.name != null && (it.memberId != null || it.userId != null));
      if (_looksMember) {
        const _agg = aggregateNationalityCount(buffer.items, _natIntent.kind);
        this._nationalityCountBlocked = true;
        this._finishAllowedByGuard = true;
        this._finishGuardSuggestedText = _agg.text;
        return {
          success: false,
          message: `[NATIONALITY COUNT] 국적 카운트 질문입니다. countryIso 필드로 결정적 집계했습니다: 외국인 ${_agg.foreign}명 / 한국 ${_agg.domestic}명 / 미입력 ${_agg.unknown}명. show_display로 목록/표를 띄우지 말고 즉시 finish하면서 정확히 다음 문장으로만 답하세요: "${_agg.text}" 이 수치를 임의로 바꾸지 마세요. gender/countryCode(전화 국가번호 +82)로 국적을 추정하지 마세요.`,
        };
      }
    }

    // [COUNT QUESTION] 한국어 카운트 의문문 결정적 차단 (1회 한정)
    // 형태론 패턴 (도메인 무관 한국어 의문 형태):
    //   (1) "몇 + 한국어" — 의문 수량사
    //   (2) "[명사] 수 + 의문/요청 어미" — count 명사
    // NOTE: 패턴 (2)에서 "수" 앞에 \s* (optional whitespace) 사용 시 한국어 이름 끝글자 "수"
    //   (예: 서민수, 이정수, 박종수 등 "X수" 인명) + 공백 + 한국어 noun 입력에서
    //   거짓 매칭 발생 ("서민수 회원권 정보" → "서민수 회"). \s+ (필수 공백)으로 강화하여
    //   "회원 수 알려줘"(count noun 패턴, 사이 공백 명시)만 매칭, "서민수 회원권"(인명 패턴)은 제외.
    if (!this._showDisplayCountQuestionBlocked) {
      const userInput = this.getUserInput?.() || '';
      // [L32-CHAIN-005] 단위별 부정 lookahead — "개월"(duration)/"회차·회분"/"번째·번지·번호"(ordinal/identifier)는 count 의도 아님 → 제외. 미적용 시 "몇 개월" cohort trend 질문이 "총 N건" 단답으로 오차단됨 (dual sync with nodeAgent.mjs)
      // [DEMO-12-PEAK / bilingual] EN "how many" 분기 추가 — count 의도는 입력 텍스트에만 존재(intention/SQL/buffer 동일)해 구조 신호가 없으므로 언어별 패턴 불가피.
      //   EN도 KO (?!월)과 대응해 duration(months/years/weeks/days/hours/minutes) 제외. "number of/count of"는 phone/account number 오매칭 위험으로 보수적 제외.
      const COUNT_RE = new RegExp(
        '몇\\s*(?:명|건|개(?!월)|회(?!차|분)|번(?!째|지|호)|가지|군데|곳|마리|장)'
        + '|(?:^|\\s)[가-힣]{2,}\\s+수\\s+(?!있|없)(?:[가-힣?]|얼마)'
        + '|how\\s+many\\s+(?!months?\\b|years?\\b|weeks?\\b|days?\\b|hours?\\b|minutes?\\b)',
        'i'
      );
      // [GROUPED-BUFFER EXCLUSION — DEMO-12-PEAK, 언어 무관] buffer items가 개별 레코드가 아니라 그룹/버킷이면
      //   "총 N개"(=그룹 수) 답변은 무의미 → 카운트 가드 skip하고 그룹별 표 표출 허용. 데이터 구조만 판정(입력 언어 무관).
      //   call_api groupBy 축(_currentAxisKey="_groupBy=*") | aggregate(_aggregateGroupByField/_aggregateKey)
      //   | compute(group)/auto-aggregate(_aggregateMetaFields 비어있지 않음). dual sync with nodeAgent.mjs.
      const _b = buffer;
      const _isGroupedBuf = !!(
        (Array.isArray(_b?._aggregateMetaFields) && _b._aggregateMetaFields.length > 0)
        || _b?._aggregateGroupByField != null
        || _b?._aggregateKey != null
        || (typeof _b?._currentAxisKey === 'string' && _b._currentAxisKey.startsWith('_groupBy='))
      );
      // [COMPOUND COUNT+ROSTER EXCLUSION — 운영 5차 rid56] "회원 몇명이고 누구누구 있어?"처럼 count 의문("몇명")과
      //   roster/명단 의도가 한 입력에 공존하면 카운트-only가 아니다 — 사용자는 숫자와 명단을 둘 다 원한다. 가드가
      //   show_display를 막으면 "총 N명이야" 단답만 나오고 명단 표가 누락된다 (운영 store1 T6 실측: 9명 roster 차단).
      //   roster 신호 동반 시 가드 skip → count는 LLM이 intro/outro로 명시 + 명단 표 병행. roster-noun(누구/누가/명단/
      //   목록/리스트/roster/list/who)은 list 의도 신호로 입력 텍스트에만 존재(count 의도와 동일 특성)해 언어별 키워드
      //   불가피. dual sync with nodeAgent.mjs.
      const ROSTER_INTENT_RE = /누구|누가|명단|목록|리스트|roster|\blist\b|\bwho\b/i;
      // [B-2 과적합 수술] "몇명이야? 표로 보여줘" — 표 결합형은 roster 정규식 밖이라 detectExplicitListRequest(단일소스)로 보강 (dual sync with nodeAgent.mjs)
      const _hasRosterIntent = ROSTER_INTENT_RE.test(userInput) || detectExplicitListRequest(userInput);
      if (!_isGroupedBuf && !_hasRosterIntent && COUNT_RE.test(userInput)) {
        this._showDisplayCountQuestionBlocked = true;
        this._finishAllowedByGuard = true;
        // [PLACEHOLDER LEAK fallback] 가드가 카운트를 알므로 결정적 suggested text 보유 — LLM이 text-only로 미해결 placeholder({{COUNT}}) finish 시 대체 (dual sync with nodeAgent.mjs)
        const _cqUnit = (buffer.items?.[0] && buffer.items[0].name != null && (buffer.items[0].memberId != null || buffer.items[0].userId != null)) ? '명' : '건';
        this._finishGuardSuggestedText = `총 ${buffer.items.length}${_cqUnit}이에요.`;
        return {
          success: false,
          message: `[COUNT QUESTION] 사용자 입력 "${userInput.slice(0, 40)}"이 카운트만 묻는 의문문입니다. show_display로 표/목록을 띄우지 말고 즉시 finish하면서 "총 ${buffer.items.length}명/건/개입니다" 한 줄로만 답변하세요. items.length=${buffer.items.length}. 목록 노출 금지.`,
        };
      }
    }

    // [Q] numeric subset 필터 누락 결정적 차단 (retry 1회 + auto-apply fallback, 무한루프 방지)
    //   사용자 입력에 "N 이상/이하/초과/미만" 패턴이 있는데 numeric_gte/numeric_lte/range 필터 미적용 시:
    //   - 1차: 필드 hint 포함 retry 메시지 (LLM이 filter 호출하도록 유도)
    //   - 2차: buffer.availableFields에서 numeric field 추론(inferNumericFieldFromBuffer)하여 auto-apply.
    //     추론 실패 시 추가 retry 메시지 (LLM이 명시적으로 filter 호출).
    //     추론도 안 되고 LLM도 안 호출 → 3차에서 honest finish 유도.
    //   본 가드는 hardcode 없이 buffer metadata (availableFields, items 키) 와 입력 단위/단어 overlap만 사용.
    const subsetIntent = detectNumericSubsetIntent(this.getUserInput?.() || '');
    if (subsetIntent) {
      const filters = buffer.appliedFilters || [];
      const hasNumericFilter = filters.some(f =>
        ['numeric_gte', 'numeric_lte', 'range'].includes(f?.condition?.type)
      );
      if (!hasNumericFilter) {
        const retryCount = typeof this._showDisplayFilterBlocked === 'number'
          ? this._showDisplayFilterBlocked
          : (this._showDisplayFilterBlocked ? 1 : 0);
        const inferred = inferNumericFieldFromBuffer(subsetIntent, buffer);
        if (retryCount === 0) {
          this._showDisplayFilterBlocked = 1;
          const fieldHint = inferred
            ? ` Field hint (inferred from availableFields): "${inferred.field}" (label: ${inferred.label}).`
            : '';
          return {
            success: false,
            message: `[FILTER MISSING] User input contains "${subsetIntent.raw}" (numeric threshold ${subsetIntent.type}). No numeric filter is applied to current buffer (slot: ${this.activeSlot}).${fieldHint} You MUST call filter({ field: <numeric field from availableFields>, condition: { type: "${subsetIntent.type}", value: <converted to base unit, e.g. "10만원"=100000, "100만원"=1000000> } }) BEFORE show_display. Then retry show_display.`,
          };
        }
        // 2nd attempt and beyond: try auto-apply if field is inferable
        if (inferred && subsetIntent.convertedValue !== null && subsetIntent.convertedValue !== undefined) {
          try {
            const autoResult = this.applyFilter({
              field: inferred.field,
              condition: { type: subsetIntent.type, value: subsetIntent.convertedValue },
            });
            if (autoResult?.success) {
              // 표시 buffer 재조회 (filter 적용 후 items 변경)
              const refreshedBuf = this.getActiveBuf();
              if (refreshedBuf && refreshedBuf.items?.length >= 0) {
                buffer = refreshedBuf;
                this._showDisplayFilterBlocked = retryCount + 1;
                console.log(`[show_display] [FILTER AUTO-APPLY] field=${inferred.field} value=${subsetIntent.convertedValue} type=${subsetIntent.type} → ${refreshedBuf.items.length}건`);
                this._filterAutoAppliedNote = `자동 필터 적용: ${inferred.label}(${inferred.field}) ${subsetIntent.type === 'numeric_lte' ? '≤' : '≥'} ${subsetIntent.convertedValue}`;
              }
            } else {
              console.log(`[show_display] [FILTER AUTO-APPLY UNSUCCESSFUL] ${autoResult?.message || '(no msg)'}`);
              this._showDisplayFilterBlocked = retryCount + 1;
            }
          } catch (e) {
            console.log(`[show_display] [FILTER AUTO-APPLY FAIL] ${e?.message || e}`);
            this._showDisplayFilterBlocked = retryCount + 1;
          }
        } else if (retryCount < 2) {
          // 추론 실패 → 한 번 더 retry로 LLM에게 명시적 filter 요구
          this._showDisplayFilterBlocked = retryCount + 1;
          return {
            success: false,
            message: `[FILTER MISSING — RETRY] User input contains "${subsetIntent.raw}" (numeric threshold ${subsetIntent.type}). Buffer (slot: ${this.activeSlot}) lacks an obvious numeric field for auto-application. You MUST explicitly call filter({ field: <numeric field from availableFields>, condition: { type: "${subsetIntent.type}", value: <numeric> } }) BEFORE retrying show_display.`,
          };
        } else {
          // 3rd attempt 추론도 실패: honest finish 유도
          this._finishAllowedByGuard = true;
          return {
            success: false,
            message: `[FILTER MISSING — UNRESOLVED] 사용자 입력 "${subsetIntent.raw}"의 numeric threshold를 적용할 field를 buffer.availableFields에서 추론하지 못했습니다 (slot: ${this.activeSlot}). filter 적용 없이 show_display 진행 시 조건 위반 데이터가 표시됩니다. finish로 "조건을 적용할 수치 필드가 없어요" 같은 honest 안내를 사용하세요.`,
          };
        }
      }
    }

    // [Raw Key Leak 차단] intro/outro에 items의 raw camelCase 키가 노출되면 차단 (1회 한정)
    // NOTE: Entity Hallucination 차단은 검증에서 false positive 다발 + 진짜 hallucination 검출 0건으로 제거됨
    if (!this._showDisplayHallucinationBlocked) {
      const introOutroText = `${intro || ''} ${outro || ''}`;
      if (introOutroText.trim()) {
        // raw camelCase key 노출 검사 (items[0] 키 중 영문 camelCase)
        const sample = buffer.items[0];
        if (sample) {
          const rawKeys = Object.keys(sample).filter(k => /^[a-z][a-zA-Z]*[A-Z][a-zA-Z]*$/.test(k));
          const leaked = rawKeys.filter(k => introOutroText.includes(k));
          if (leaked.length > 0) {
            this._showDisplayHallucinationBlocked = true;
            return {
              success: false,
              message: `[RAW KEY LEAK] intro/outro에 영문 raw 필드명 [${leaked.join(', ')}] 노출. availableFields/columnLabels의 한국어 라벨을 사용하세요.`,
            };
          }
        }
      }
    }

    // [CHANGE CLAIM GUARD — 구조 기반] intro/outro에 변화/비교 표현이 있는데 buffer items에 비교 필드 없으면 차단
    // (거짓 변화 claim 차단. _change_/_changeRate_/prev_/이전_/최근_ 등 비교 필드 prefix 구조 기반. 1회 retry + 2회차 auto-strip)
    if (intro || outro) {
      const _changeText = `${intro || ''} ${outro || ''}`;
      const CHANGE_CLAIM_RE = /줄어|감소|줄었|덜|낮아[졌진]|떨어|적어진|늘어난|늘었|증가|많아진|높아[졌진]|올라|상승|하락|decrease|increase|drop|rise/i;
      if (CHANGE_CLAIM_RE.test(_changeText)) {
        const _sampleKeys = Object.keys(buffer.items?.[0] || {});
        const hasChangeField = _sampleKeys.some(k =>
          k.startsWith('_change_') || k.startsWith('_changeRate_') ||
          /^(prev|previous|최근|이전|작년|전년|전월|전주|이번|올해)_/.test(k) ||
          /_(prev|previous|change|changeRate|delta)$/i.test(k)
        );
        // 시계열 dimension (cohort/tenure/period/month/year/quarter/week + _label SUMMARY-SERIES stack) 있고 row 다중이면 추이 분석 자연 → 통과
        const SERIES_COL_RE = /(?:Month|Year|Quarter|Period|Cohort|Week|Tenure)$|^(_label|period|cohort|tenure|quarter|월차|연도|분기|구간|기수)/i;
        const hasSeriesDim = _sampleKeys.some(k => SERIES_COL_RE.test(k));
        const isMultiRow = (buffer.items?.length || 0) >= 2;
        if (!hasChangeField && !(hasSeriesDim && isMultiRow)) {
          // 1회차: retry message (LLM에게 change claim 제거 또는 change field 확보 안내)
          if (!this._showDisplayChangeClaimBlocked) {
            this._showDisplayChangeClaimBlocked = true;
            return {
              success: false,
              message: `[CHANGE CLAIM] intro/outro에 변화/비교/감소/증가 표현이 있으나 active buffer items에 비교/변화 필드(_change_*, _changeRate_*, prev_*, 이전_* 등)가 없습니다. (1) 직전 raw slot(예: sql_0/sql_1)으로 돌아가 merge_slots(mode:"compare", matchKey:<공통키>)로 변화 계산 후 필요시 filter → show_display 재호출 또는 (2) intro/outro에서 변화/비교 표현 제거하고 buffer가 실제로 답할 수 있는 범위로만 작성. 거짓 변화 claim 금지.`,
            };
          }
          // 2회차+: auto-strip change claim text and proceed (무한 block → text-only fallback 방지)
          const _stripRe = /줄어[든들]?\s*|감소[한했]?\s*|줄었[던]?\s*|덜\s+|낮아[졌진]?\s*|떨어[진졌]?\s*|적어진\s*|늘어[난]?\s*|늘었[던]?\s*|증가[한했]?\s*|많아진\s*|높아[졌진]?\s*|올라[간]?\s*|상승[한했]?\s*|하락[한했]?\s*|decrease[d]?\s*|increase[d]?\s*|drop(?:ped)?\s*|rise[dn]?\s*/gi;
          if (intro) intro = intro.replace(_stripRe, '').replace(/\s{2,}/g, ' ').trim();
          if (outro) outro = outro.replace(_stripRe, '').replace(/\s{2,}/g, ' ').trim();
          console.log('[show_display] [CHANGE CLAIM] auto-strip after retry — proceeding with sanitized intro/outro');
        }
      }
    }

    // [CENTER-INFO STALE RENDER GUARD] 운영 5차 rid200~202: "체육관/센터 정보(이름·주소·전화·코치·유효회원수) 표"
    //   요청인데 active buffer가 회원 등 다른 도메인 → stale 버퍼를 "체육관 정보 N건"으로 거짓 렌더("유효회원 92명이야"=체육관정보).
    //   center-info 의도 + active buffer가 시스템설정(CHECK_SYSTEM+SETTING_DETAIL) 아님 + items 보유 → 차단 + 시스템설정 유도.
    //   복합(코치=CHECK_COACH_LIST / 회원수=MEMBER)은 별도 intention — 단일 표 불가, text 응답. 1회 한정. dual sync with nodeAgent.mjs.
    {
      const _ciInput = this.getUserInput?.() || '';
      const _isCenterInfoIntent = /(?:체육관|센터)\s*정보|(?:체육관|센터)\s*(?:이름|주소|전화|연락처)/.test(_ciInput);
      // 회원 도메인 버퍼만 차단 — 코치(CHECK_COACH_LIST)·시스템설정은 "체육관 정보"에 정당 → 렌더 보존.
      // 회원 list 렌더는 center-info 의도에서 항상 거짓 라벨 → 1회 한정 X, 매 show_display 차단(재호출 누출 방지).
      const _activeIsMemberDomain = /^CHECK_MEMBER/.test(buffer?.intention || '');
      if (_isCenterInfoIntent && _activeIsMemberDomain && buffer?.items?.length > 0) {
        this._showDisplayCenterInfoBlocked = true;
        this._finishAllowedByGuard = true;  // 가드 후 LLM text 응답을 finish로 인정 (nudge 무한 회전 차단)
        this._finishGuardSuggestedText = null;
        return {
          success: false,
          message: `[CENTER-INFO STALE RENDER] User asked for 체육관/센터 정보 (center name/address/phone) but the active buffer is "${this.activeSlot}" (intention: ${buffer.intention}) — member data, a different domain. Do NOT render this member buffer as 체육관 정보; that produces false data such as "유효회원 N명" mislabeled as center info.\n\nNEXT STEP: Call get_intentions(category:"INFRA") then call_api(CHECK_SYSTEM+SETTING_DETAIL) for center name/address/contact and answer in text (system settings are text-only, no table). 코치 목록은 CHECK_COACH_LIST(INFRA), 유효회원 수는 MEMBER 카테고리 count로 별도 조회/응답(명단 표 X). NEVER show_display the member buffer as 체육관 정보.`,
        };
      }
    }

    // [BUFFER MISMATCH GUARD — 구조 기반] LLM이 지정한 columns가 active buffer에 전부 없으면 차단
    // (사용자 요청의 데이터 종류가 현재 buffer와 다름 — 새 call_api 필요한 신호. enum 0개, items/availableFields 키 기반)
    if (Array.isArray(columns) && columns.length > 0 && !this._showDisplayBufferMismatchBlocked) {
      const sampleKeys = new Set(Object.keys(buffer.items?.[0] || {}));
      const fieldKeys = new Set(Object.keys(buffer.availableFields || {}));
      const validColumns = columns.filter(c => sampleKeys.has(c) || fieldKeys.has(c));
      if (validColumns.length === 0) {
        this._showDisplayBufferMismatchBlocked = true;
        this._bufferMismatchInfo = { columns: columns.slice(), activeSlot: this.activeSlot };  // [C-#6] finishPolicy GUARD 14 retryGuide용
        return {
          success: false,
          message: `[BUFFER MISMATCH] Requested columns [${columns.join(', ')}] not found in active buffer "${this.activeSlot}" (intention: ${buffer.intention}). The active buffer's data does not match user's request dimension. You MUST call get_intentions + call_api for an intention whose availableFields contain the requested columns. Do NOT call show_display on mismatched data — it produces empty rows.`,
        };
      }
    }

    // [DIMENSION REDIRECT — show_display 시점 period mismatch 차단]
    // user input에 period 명시(X월) + 비교 의도 있는데 active buffer가 미충족 + buffers에 매칭 슬롯 존재 시 switch_slot 안내 (1회 한정)
    // CONTEXT REUSE REDIRECT(call_api 진입 시점)의 LLM 우회 경로(call_api 미호출 + show_display 직행) 차단
    // false positive 차단 4중 조건: COMPARE_INTENT_RE 매칭 + requestedMonths.size > 0 + active buffer 미매칭 + 매칭 슬롯 존재
    if (!this._showDisplayDimensionRedirectAttempted) {
      const _userInputDim = this.getUserInput?.() || '';
      const COMPARE_INTENT_RE = /비교(?:해(?:줘|봐|드려)?|했|하면|하니|해보)?|대비|차이|얼마나|(\d+)\s*월(?:.{0,10})(\d+)\s*월/;
      if (COMPARE_INTENT_RE.test(_userInputDim)) {
        const _redirectSlot = this._findPeriodMatchSlot(_userInputDim);
        if (_redirectSlot) {
          this._showDisplayDimensionRedirectAttempted = true;
          return {
            success: false,
            message: `[SHOW_DISPLAY DIMENSION REDIRECT] User input "${_userInputDim.slice(0, 40)}" requests period comparison (months in input not matched by active buffer "${this.activeSlot}"). Buffer slot "${_redirectSlot}" has matching period data. Call switch_slot({slot:"${_redirectSlot}"}) then retry show_display.`,
          };
        }
      }
    }

    // [PERIOD MISMATCH → CALL_API REDIRECT] 절대월 명시 후속질문인데 active 슬롯 기간 불일치 + 동일 intention 매칭 슬롯 부재 시 call_api 재호출 유도 (1회 한정) //
    //   본질: "5월 코치별"인데 직전 "이번 달"(6월) 코치 슬롯을 switch_slot 재사용 → 5월 답변에 6월 데이터 표출. 5월 데이터 슬롯이 없으면 call_api(5월) 필요. //
    //   기존 DIMENSION REDIRECT(매칭 슬롯 존재 → switch 유도)의 형제 가드 — 매칭 슬롯 부재 시 switch가 아니라 call_api 유도. //
    //   false positive 차단: requestedMonths>0(절대월 명시) + period 기반 intention + active 미커버 + 동일 intention 매칭 슬롯 부재 + 1회 한정. //
    if (!this._showDisplayPeriodMismatchAttempted) {
      const _pmInput = this.getUserInput?.() || '';
      const _reqMonths = this._extractMonthSet(_pmInput);
      const _pmActiveBuf = this.getBuffer?.();
      const _pmIntention = (_pmActiveBuf == null ? '' : _pmActiveBuf.intention) || '';
      const _isPeriodIntention = /_BY\+PERIOD|STATISTICS/.test(_pmIntention);
      // 단일 절대월(size===1) 비-비교 후속질문만 발동 — 다월 비교(size>=2)나 비교어("비교/대비/차이/얼마나") 포함 시 merge / DIMENSION REDIRECT 경로에 위임 (비교 case false positive 차단) //
      const _PM_COMPARE_RE = /비교(?:해(?:줘|봐|드려)?|했|하면|하니|해보)?|대비|차이|얼마나|(\d+)\s*월(?:.{0,10})(\d+)\s*월/;
      if (_reqMonths.size === 1 && !_PM_COMPARE_RE.test(_pmInput) && _isPeriodIntention) {
        const _activeMonths = this._getSlotMonths(_pmActiveBuf);
        const _activeCovers = [..._reqMonths].every(m => _activeMonths.has(m));
        if (!_activeCovers) {
          let _hasMatchingSlot = false;

          for (const _sn of this.buffers.keys()) {
            if (_sn === this.activeSlot) continue;

            const _sb = this.buffers.get(_sn);
            if (!_sb || _sb.intention !== _pmIntention) continue;

            const _sbMonths = this._getSlotMonths(_sb);
            if ([..._reqMonths].every(m => _sbMonths.has(m))) { _hasMatchingSlot = true; break; }
          }

          if (!_hasMatchingSlot) {
            this._showDisplayPeriodMismatchAttempted = true;
            const _reqLabel = [..._reqMonths].join(', ');
            const _activeLabel = [..._activeMonths].join(', ') || 'a different period';
            return {
              success: false,
              message: `[PERIOD MISMATCH → CALL_API] User asked for ${_reqLabel} but active slot "${this.activeSlot}" holds ${_activeLabel} data (intention ${_pmIntention}), and no loaded slot has ${_reqLabel} data for this intention. switch_slot would reuse wrong-period data. You MUST call call_api with startDate/endDate covering ${_reqLabel} (that month's first to last day, use the year from conversation context) for intention "${_pmIntention}", then show_display. Do NOT switch_slot to a different-period slot.`,
            };
          }
        }
      }
    }

    // [MULTI-SLOT PER-ENTITY COMPARE — MERGE REDIRECT] L11-SWITCH-SLOT-001 cycle 1 (2026-05-30, dual sync with nodeAgent.mjs)
    //   본질/조건/회귀 risk nodeAgent.mjs 동일.
    if (!this._showDisplayMultiSlotMergeRedirectAttempted) {
      const _userInputMulti = this.getUserInput?.() || '';
      const PER_ENTITY_COMPARE_KW = /각각|따로|별도|각자/;
      if (PER_ENTITY_COMPARE_KW.test(_userInputMulti)) {
        const _multiCand = this._findMultiSlotMergeCandidates?.();
        if (_multiCand && _multiCand.otherSlots.length >= 1) {
          this._showDisplayMultiSlotMergeRedirectAttempted = true;
          const _slotsArgs = [
            { slotName: _multiCand.activeSlot, label: _multiCand.activeSlot },
            ..._multiCand.otherSlots.map(s => ({ slotName: s, label: s })),
          ];
          const _slotsArgsJson = JSON.stringify(_slotsArgs);
          return {
            success: false,
            message: `[MULTI-SLOT PER-ENTITY COMPARE — MERGE REDIRECT] User input "${_userInputMulti.slice(0, 50)}" requests per-entity comparison (keyword "각각/따로/별도/각자") across multiple slots. Active slot "${_multiCand.activeSlot}" has data for one period only, but ${_multiCand.otherSlots.length} sibling slot(s) [${_multiCand.otherSlots.join(', ')}] hold matching base intention data. Calling show_display now exposes ONLY the active slot's columns — the other slots' data will be silently dropped (subsequent show_display calls are blocked as duplicates this turn).\n\nFor true per-entity comparison, call merge_slots first:\n  merge_slots({ slots: ${_slotsArgsJson}, matchKey: "userId" or "memberId" or entity-level id from buffer fields, mode: "outer" })\n\nThis produces _change_* / _changeRate_* derived columns + per-period columns prefixed by slot label. Then call show_display with those derived columns. 1회 한정 — if you genuinely want only the active slot, call show_display again to bypass.`,
          };
        }
      }
    }

    // [KEYWORD COLUMN INJECTION — 구조 기반] userInput 키워드 매칭 필드(FIELD_REGISTRY)가 LLM columns에 누락되면 강제 주입
    // (도메인 enum 0, FIELD_REGISTRY keywords + sampleItem 키 기반. LLM column 선택 자유도 제어로 결정성 ↑)
    // [L21-CROSS-MERGE COL_MISSING] alias-group 만족 체크 추가 — LLM cols에 그 alias 그룹의 어떤 form(root or slot-label prefix 변형)이라도
    //   있으면 INJECT 생략. 본질: cross-merge 결과 buffer에서 LLM이 출석_attendedCount 명시했는데 root attendedCount 부재로 endDate 같은 다른 root가
    //   인젝트되며 5cap slice가 LLM의 출석_* 명시 cols를 drop하는 회귀 차단. dual sync with nodeAgent.mjs.
    if (Array.isArray(columns) && columns.length > 0) {
      const _kwInput = this.getUserInput?.() || '';
      const _kwSample = buffer.items?.[0] || {};
      const _kwGroups = findKeywordAliasGroups(_kwInput, _kwSample);
      const _kwColsSet = new Set(columns);
      const _kwMissingPerGroup = [];
      for (const grp of _kwGroups) {
        if (!grp.forms.some(f => _kwColsSet.has(f))) {
          _kwMissingPerGroup.push(grp.forms[0]);
        }
      }
      if (_kwMissingPerGroup.length > 0) {
        // [L21-CROSS-MERGE COL_MISSING] slice(0, 5) → slice(0, 12). LLM-provided cols 보호.
        //   기존 slice(0,5)는 LLM hallucination 50+ cols 대비 안전망이지만 autoGenerateDisplay HARD_CAP=12로 이미 cap.
        //   slice(0,5)는 alias-group 만족 못한 root keyword가 inject되면 LLM의 prefix 변형 cols을 drop하는 회귀 원인.
        columns = [..._kwMissingPerGroup, ...columns].slice(0, 12);
        console.log(`[show_display] KEYWORD_INJECT: added [${_kwMissingPerGroup.join(',')}] from userInput "${_kwInput.slice(0,40)}" (alias-group level)`);
      }
    }

    // [AGGREGATE GROUPBY COLUMN GUARD] aggregate 후 groupBy 필드가 columns에 누락되면 강제 추가
    if (buffer._aggregateGroupByField) {
      const gbField = buffer._aggregateGroupByField;
      if (Array.isArray(columns) && columns.length > 0 && !columns.includes(gbField)) {
        columns = [gbField, ...columns];
        if (TOOL_CALLING_LOG) console.log(`[show_display] AGGREGATE_GROUPBY_INJECT: added "${gbField}" to columns`);
      }
    }

    // [L12-COMPACT-003 COL_MISSING fix] ATTENDANCE list intention BARE STATUS request 핵심 metric column 강제 보강.
    //   본질: CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD 같은 출석 명단 intention에서 사용자가 "출석 알려줘/현황/명단" bare request 시
    //   LLM이 call_api_guide의 [BARE STATUS REQUEST — MANDATORY DEFAULT COLUMNS]를 무시하고 attendedCount/attendanceRate를 누락하는 비결정성 패턴.
    //   intention 가이드에는 EXACTLY columns 지정되어 있지만 LLM 자유도로 인해 50% 정도만 따름.
    //   → 코드로 결정성 강제: availableFields에 핵심 metric set이 정의된 경우 (attendedCount/attendanceRate + reservationCount) MANDATORY include.
    //   조건: bare request 식별 → buffer.appliedFilters/appliedSort 없음 + buffer.availableFields에 metric set 모두 정의됨 + intention이 LIST 타입.
    //   회귀 risk: filter/sort 있을 때는 미적용 (사용자가 명시적 의도). intention 자체에 metric field 정의 없으면 미적용 (non-attendance intention 무관).
    if (Array.isArray(columns) && columns.length > 0 && buffer.availableFields) {
      const _hasNoUserRefinement = (!buffer.appliedFilters || buffer.appliedFilters.length === 0)
        && !buffer.appliedSort;
      const ATTENDANCE_METRIC_SET = ['attendedCount', 'attendanceRate', 'reservationCount'];
      const _availableFieldKeys = new Set(Object.keys(buffer.availableFields));
      const _hasAttendanceMetricSet = ATTENDANCE_METRIC_SET.every(f => _availableFieldKeys.has(f));
      const _intentionStr = typeof buffer.intention === 'string' ? buffer.intention : '';
      const _isListIntention = _intentionStr.includes('_LIST');
      if (_hasNoUserRefinement && _hasAttendanceMetricSet && _isListIntention) {
        const _sampleItem = buffer.items?.[0] || {};
        const _missing = ATTENDANCE_METRIC_SET.filter(f => !columns.includes(f) && (f in _sampleItem));
        if (_missing.length > 0) {
          const _nameIdx = columns.findIndex(c => c === 'name' || c === 'memberName');
          if (_nameIdx >= 0) {
            columns = [
              ...columns.slice(0, _nameIdx + 1),
              ..._missing,
              ...columns.slice(_nameIdx + 1).filter(c => !_missing.includes(c)),
            ];
          } else {
            columns = [..._missing, ...columns.filter(c => !_missing.includes(c))];
          }
          if (TOOL_CALLING_LOG) console.log(`[show_display] ATTENDANCE_METRIC_INJECT: added [${_missing.join(',')}] (bare request, intention=${_intentionStr})`);
        }
      }
    }

    const prevColumns = buffer.resolvedColumns ? [...buffer.resolvedColumns] : null;
    const displayResult = autoGenerateDisplay({ intro, outro, columns, columnLabels, max_count }, this);

    // [SMS-INDIVIDUAL-SUBSET fix] row-scope swap 원복 — displayResult는 recipients 기준 이미 생성됨. 후속/다음 turn buffer 재사용 영향만 제거. dual sync with nodeAgent.mjs //
    if (_smsRowScopeRestore && this._smsRowScopeOriginalItems) {
      _smsRowScopeRestore.buf.items = this._smsRowScopeOriginalItems;
      _smsRowScopeRestore = null;
    }

    // EMPTY DISPLAY guard 회복 슬롯 검색 헬퍼는 클래스 메서드 _findRecoverySlotForColumns 참조

    // [EMPTY DISPLAY ROWS GUARD] displayResult.items 있지만 모든 row의 모든 값이 빈 — 데이터 사실상 없음 (finish 허용, 1회 한정)
    // EMPTY DISPLAY 발동 시 buffers 다른 슬롯 중 명시 columns이 매칭 + 값 채워진 슬롯 있으면 switch_slot 회복 안내 (1회 한정)
    if (displayResult && Array.isArray(displayResult.items) && displayResult.items.length > 0 && !this._showDisplayEmptyRowsBlocked) {
      const _allDisplayEmpty = displayResult.items.every(it =>
        it && typeof it === 'object' &&
        Object.values(it).every(v => v == null || v === '' || (Array.isArray(v) && v.length === 0))
      );
      if (_allDisplayEmpty) {
        this._showDisplayEmptyRowsBlocked = true;
        const recoverSlot = !this._showDisplayRecoveryAttempted
          ? this._findRecoverySlotForColumns(columns)
          : null;
        if (recoverSlot) {
          this._showDisplayRecoveryAttempted = true;
          return {
            success: false,
            message: `[EMPTY DISPLAY → RECOVERY] Active buffer "${this.activeSlot}" produces empty rows for requested columns [${columns.join(', ')}]. Buffer slot "${recoverSlot}" has matching columns with actual data. Call switch_slot({slot:"${recoverSlot}"}) then retry show_display.`,
          };
        }
        this._finishAllowedByGuard = true;
        return {
          success: false,
          message: `[EMPTY DISPLAY] Generated display has ${displayResult.items.length} row(s) but all values are empty. The requested period/condition has no meaningful records. Finish immediately with a text like "조회된 데이터가 없어요. 다른 조건으로 다시 시도해주세요." (텍스트만, 추가 도구 호출 금지).`,
        };
      }
    }

    if (!displayResult) {
      // [L30-CONCEPT-006] 2026-05-31 — filter 결과 0건 path 명시 안내 (vague "Failed" message → honest 0건 guide)
      // 구조 신호: items.length===0 + (appliedFilters 존재 || originalItems 존재) → INTENTIONAL EMPTY
      // dual sync with nodeAgent.mjs (구조 기반, case-ID/literal 0)
      const _bufNull = this.getBuffer?.();
      const itemsLen = _bufNull?.items?.length ?? 0;
      const appliedFiltersLen = Array.isArray(_bufNull?.appliedFilters) ? _bufNull.appliedFilters.length : 0;
      const originalItemsLen = Array.isArray(_bufNull?.originalItems) ? _bufNull.originalItems.length : 0;
      if (itemsLen === 0 && (appliedFiltersLen > 0 || originalItemsLen > 0)) {
        this._finishAllowedByGuard = true;
        const filterFieldSummary = appliedFiltersLen > 0
          ? _bufNull.appliedFilters.map(f => f.field).join(', ')
          : '(원본 데이터 0건)';
        return {
          success: false,
          message: `[INTENTIONAL EMPTY — FILTER RESULT 0] 필터 적용 결과 0건 (필터: ${filterFieldSummary}, 원본 ${originalItemsLen}건). 사용자에게 honest 응답을 finish로 호출하세요. 권장 표현: "조건에 맞는 [회원/항목]이 0명이야" / "조건에 맞는 결과가 없어". raw 숫자 hallucination (예: "1명이야") 금지 — 0건임을 명시하거나 "없어" 표현 사용.`,
        };
      }
      return { success: false, message: 'Failed to generate display table.' };
    }

    // 컬럼 변화 없고 제외된 컬럼만 있으면 → 테이블 재렌더링 없이 안내만
    const excluded = displayResult.excludedColumns;
    if (excluded?.length > 0 && prevColumns) {
      const same = prevColumns.length === displayResult.columns.length
        && prevColumns.every((c, i) => c === displayResult.columns[i]);
      if (same) {
        return {
          success: false,
          excludedColumns: excluded,
          availableFields: [...displayResult.columns],
          message: `요청한 컬럼 [${excluded.join(', ')}]은(는) 현재 데이터에 없어 표시할 수 없습니다. 사용 가능한 컬럼: [${displayResult.columns.join(', ')}]`,
        };
      }
    }

    // [1-K Layer X] CATASTROPHIC NAME DROP GUARD — LLM이 columns에 entity name 포함 명시했지만 결과 표에 NAME 누락된 catastrophic case
    //   본질: 사용자 입장에서 어느 entity인지 식별 불가한 표 (raw value/ID만) 차단 + 자연어 finish 유도
    //   조건: LLM 의도(NAME 명시) vs 결과(NAME 빠짐) 불일치 + 소수 row (multi-row는 표로 식별 가능하므로 제외). dual sync with nodeAgent.mjs
    //   confirmed catch 패턴:
    //     - displayed=[openingHour] 단 1키 (run 11/13)
    //     - displayed=[N월_followStoreId × 3, _change_followStoreId] merge_slots 후 raw ID column (run 3)
    {
      const _displayCols = Array.isArray(displayResult.columns) ? displayResult.columns : [];
      const _displayItems = Array.isArray(displayResult.items) ? displayResult.items : [];
      const NAME_LIKE_RE = /name|title|label/i;
      const NAME_FIELDS_K = ['storeName', 'name', 'memberName', 'memberShipName', 'membershipName', 'productName', 'itemName', 'lessonName', 'className', 'title'];
      const _isNameLike = c => typeof c === 'string' && (NAME_FIELDS_K.includes(c) || NAME_LIKE_RE.test(c));
      const _llmHasName = _origLlmColumns && _origLlmColumns.some(_isNameLike);
      const _displayHasName = _displayCols.some(_isNameLike);
      if (
        !this._catastrophicNameDropRetryAttempted
        && _origLlmColumns && _origLlmColumns.length >= 2
        && _displayCols.length >= 1
        && _llmHasName
        && !_displayHasName
        && _displayItems.length <= 3
      ) {
        this._catastrophicNameDropRetryAttempted = true;
        // NOTE: _finishAllowedByGuard 의도적 미설정 — POST-GUARD TOOL BLOCK fallback ("찾을 수 없어요") 강제 차단.
        // LLM이 retry msg 받고 finish() 자율 작성 (자연어로 entity 정보 명시) OR show_display 재호출(1회 한정 skip → 1키 표) 둘 다 거짓 ack보다 나음
        const _buf = this.getBuffer?.();
        const _itemKeys = _buf?.items?.[0] ? Object.keys(_buf.items[0]).filter(k => !k.startsWith('_')) : [];
        const _omitted = _origLlmColumns.filter(_isNameLike);
        const _sample = _buf?.items?.[0] || {};
        const _sampleEntries = Object.entries(_sample)
          .filter(([k, v]) => !k.startsWith('_') && v != null && v !== '' && typeof v !== 'object')
          .slice(0, 8)
          .map(([k, v]) => `${k}=${String(v).slice(0, 60)}`);
        const _fieldLineFormat = _sampleEntries.map(e => `  - ${e}`).join('\n');
        return {
          success: false,
          message: `[CATASTROPHIC NAME DROP] You requested columns [${_origLlmColumns.join(', ')}] including entity name column(s) [${_omitted.join(', ')}], but those were excluded from the result table. The displayed column [${_displayCols.join(', ')}] alone does NOT let the user identify which entity each row represents.\n\nYou MUST call finish IMMEDIATELY with a response that lists ALL ${_sampleEntries.length} field values below on SEPARATE lines. Do NOT skip ANY field — the user expects EVERY detail (name, address, phone, hours, etc.).\n\nRequired fields (use these EXACT values, on separate lines, translate the field labels into the user's language):\n${_fieldLineFormat}\n\nDo NOT respond with "data not found" or similar — the data EXISTS and you have ${_displayItems.length} record(s) loaded; the only issue is column selection. Do NOT reference the table.`,
        };
      }
    }

    // [F11 Issue A — L9c-COMPARE-006 cycle 1] MULTI-PERIOD MERGE ANCHOR DROP GUARD
    //   본질: multi-period merge state (intention='MERGED' OR active='_merged*' OR items에 _change_*/_changeRate_* 보유) 시
    //         show_display columns에 row 식별자(NAME_LIKE OR PERIOD_LIKE OR period-prefix column) 모두 누락된 catastrophic case.
    //         displayItems.length 무관 (multi-row 시 행 식별 불가 + single-row 시도 metric-only면 사용자 입장 의미 없음).
    //   기존 [1-K Layer X]와 차이:
    //     - [1-K]: _llmHasName==true (LLM이 명시 요청한 name이 drop) — 단일 detail 영역 보호
    //     - [F11 A]: merge state + ANCHOR (name OR period 둘 다) 미포함 — multi-period 영역 보호 (LLM이 anchor 자체 안 넣음)
    //   조건 (모두 만족):
    //     1. buffer가 MERGED state (intention/slot/items signal 중 1+)
    //     2. raw source slot items에 NAME_LIKE field 존재 (entity merge 영역 — period-pivot only면 anchor=column header이므로 제외)
    //     3. displayCols에 ANCHOR 없음 (NAME_LIKE / PERIOD_LIKE / period-prefix column 모두 누락)
    //     4. displayItems.length >= 1 + displayCols.length >= 1 (출력 형태 정상)
    //     5. 1회 한정 (turn별 리셋) — _multiPeriodMergeAnchorDropRetryAttempted
    //   Generic — no case-ID/literal hardcode, dual sync with nodeAgent.mjs.
    {
      const _displayCols = Array.isArray(displayResult.columns) ? displayResult.columns : [];
      const _displayItems = Array.isArray(displayResult.items) ? displayResult.items : [];
      const _bufAnchor = this.getBuffer?.();
      // (1) merge state detection
      const _activeName = this.activeSlot || '';
      const _bufIntention = _bufAnchor?.intention || '';
      const _firstItem = _bufAnchor?.items?.[0] || null;
      const _hasChangeFields = _firstItem && typeof _firstItem === 'object'
        && Object.keys(_firstItem).some(k => k.startsWith('_change_') || k.startsWith('_changeRate_'));
      const _isMergedState = _bufIntention === 'MERGED'
        || _activeName.startsWith('_merged')
        || _hasChangeFields;
      // (2) raw source slot에 ENTITY PRIMARY NAME field 존재 검출
      //   ⚠️ 핵심 - itemName/productName/membershipName 같은 attribute name field는 row 식별자가 아니라 product info일 수 있음.
      //   row identifier로서 entity primary name (name/memberName/storeName/teacherName/className/lessonName)만 anchor로 인정.
      const _ENTITY_PRIMARY_NAME_FIELDS = ['name', 'memberName', 'storeName', 'teacherName', 'className', 'lessonName'];
      const _isEntityPrimaryName = k => typeof k === 'string' && _ENTITY_PRIMARY_NAME_FIELDS.includes(k);
      const _rawEntityNameFields = new Set();
      if (this.buffers && typeof this.buffers.forEach === 'function') {
        for (const [_sn, _sb] of this.buffers) {
          if (_sn === _activeName) continue;
          if (_sb?.intention === 'MERGED') continue;
          const _sampleItem = _sb?.items?.[0];
          if (_sampleItem && typeof _sampleItem === 'object') {
            Object.keys(_sampleItem).filter(_isEntityPrimaryName).forEach(k => _rawEntityNameFields.add(k));
          }
        }
      }
      const _rawHasEntityName = _rawEntityNameFields.size > 0;
      // (3) displayCols에 anchor 없음 검출 — entity primary name OR period anchor (둘 다 X면 row 식별 불가)
      const _ANCHOR_PERIOD_FIELDS = ['period', 'month', 'day', 'week', 'year', 'date', '_label', 'periodLabel'];
      const _PERIOD_PREFIX_RE = /^(\d+월|\d+년|\d+주|\d+일|이번달|지난달|이번주|지난주|올해|작년|어제|오늘)_/;
      const _displayHasEntityName = _displayCols.some(c => _rawEntityNameFields.has(c));
      const _displayHasPeriodAnchor = _displayCols.some(c => typeof c === 'string' && (
        _ANCHOR_PERIOD_FIELDS.includes(c) || _PERIOD_PREFIX_RE.test(c)
      ));
      const _displayHasAnchor = _displayHasEntityName || _displayHasPeriodAnchor;
      if (
        !this._multiPeriodMergeAnchorDropRetryAttempted
        && _isMergedState
        && _rawHasEntityName
        && !_displayHasAnchor
        && _displayCols.length >= 1
        && _displayItems.length >= 1
      ) {
        this._multiPeriodMergeAnchorDropRetryAttempted = true;
        // anchor 후보 — raw entity name fields + merged slot period fields
        const _anchorCandidates = new Set([..._rawEntityNameFields]);
        const _mergedItem = _firstItem;
        if (_mergedItem && typeof _mergedItem === 'object') {
          Object.keys(_mergedItem).filter(k => _ANCHOR_PERIOD_FIELDS.includes(k)).forEach(k => _anchorCandidates.add(k));
        }
        const _anchorList = [..._anchorCandidates];
        return {
          success: false,
          availableFields: _anchorList.length > 0 ? _anchorList : ['period', 'name'],
          message: `[MULTI-PERIOD MERGE ANCHOR DROP] The result table columns [${_displayCols.join(', ')}] do NOT include any row anchor (entity name field like name/memberName/title OR period field like period/month/_label OR period-prefix column like "1월_<metric>"). In multi-period merge state, every row needs an identifier so the user can tell which entity or period each row represents — currently rows are just metric values without context. Add at least one anchor column from available fields [${_anchorList.join(', ') || 'period, name'}] (entity name for entity-level merge, period/_label for period-level merge) and call show_display again with the anchor included as the first column.`,
        };
      }
    }

    // [COLUMN MISMATCH GUARD] auto-chain 컨텍스트에서만 발동 — LLM이 raw 인텐션 필드 요청 시 빈 표 노출 방지
    // → 일반 turn에서는 기존 BUFFER MISMATCH 가드(다른 인텐션 호출 유도)에 양보 — T3 등에서 회원권 fields 노출로 잘못 유도 방지
    if (!this._showDisplayColMismatchBlocked && this._multiPeriodAutoChainFiredThisTurn && excluded?.length >= 3 && displayResult.columns.length <= 1) {
      const buf = this.getBuffer?.();
      const sample = buf?.items?.[0];
      const realKeys = sample ? Object.keys(sample).filter(k => !k.startsWith('_change_') && !k.startsWith('_changeRate_') && !k.startsWith('_')) : [];
      this._showDisplayColMismatchBlocked = true;
      return {
        success: false,
        excludedColumns: excluded,
        availableFields: realKeys,
        message: `[COLUMN MISMATCH] 요청 컬럼 [${excluded.join(', ')}] 대부분 데이터에 없습니다 (남은 컬럼: [${displayResult.columns.join(', ')}]). 실제 buffer items 키: [${realKeys.join(', ')}]. 이 키들로 columns 재구성 후 show_display 재호출 (예: ['period', '<주요 지표 1>', '<주요 지표 2>']).`,
      };
    }

    const requested = max_count || 150;
    const originalCount = buffer.originalItems?.length || 0;
    const insufficient = requested < 150 && displayResult.totalCount < requested && originalCount > displayResult.totalCount;

    // [UNDISPLAYED METRIC AUTO-APPEND] 본 턴 표시 성공 슬롯 추적 — 다축(슬롯 2개 모두 표시) 시 auto-append 후보 제외 //
    if (this._turnDisplayedSlots) this._turnDisplayedSlots.add(this.activeSlot);

    const result = {
      success: true,
      displayResult,
      count: displayResult.totalCount,
      message: insufficient
        ? `${displayResult.totalCount}건 표시 (요청: ${requested}건). Buffer has ${originalCount} items before filter. Call filter({reset: true}) to restore all data, then retry show_display.`
        : displayResult.remaining > 0
          ? `${displayResult.totalCount}건 표시 (총 ${displayResult.totalCount + displayResult.remaining}건 중, ${displayResult.remaining}건 생략).`
          : `${displayResult.totalCount}건 표시 준비 완료.`,
    };

    if (excluded?.length > 0) {
      result.message += ` 데이터에 없는 컬럼 제외: [${excluded.join(', ')}]`;
    }

    // [OUTRO GUIDE] finish() outro 작성 시 표 columns 재제안 방지 — LLM이 표에 이미 노출된 정보를 다시 제안하지 않도록
    // [P1-D3] + 표 컬럼 enumeration 거짓 차단 — show_display(columns=...)로 요청한 컬럼 중 backend가 silently drop 시 mismatch 방지
    const displayedCols = displayResult?.columns;
    if (Array.isArray(displayedCols) && displayedCols.length > 0) {
      const labels = displayResult?.columnLabels || {};
      const colNames = displayedCols.map(c => labels[c] || c).join(', ');
      result.message += ` [TABLE COLUMNS — STRICT] 표에 실제로 노출된 컬럼은 정확히 [${colNames}] (총 ${displayedCols.length}개). outro/finish 작성 시 (1) 표 컬럼 enumeration이 필요하면 {{TABLE_COLS}} placeholder 사용 — 코드가 위 라벨로 치환. (2) 컬럼명을 직접 enumeration 금지 (예: "표에 X, Y, Z 정리되어 있어요" 식으로 쓰지 마세요 — show_display에 요청한 columns 중 backend가 silently drop한 컬럼이 있을 수 있어 사용자 표와 모순). (3) 비교/분석 텍스트에서도 위 ${displayedCols.length}개 컬럼만 참조 — 표에 없는 metric으로 비교 분석 금지. (4) 표에 이미 노출된 정보를 "다음으로는 X 정리해드릴게요/보여드릴게요" 식으로 재제안 금지. (5) outro/finish에 각 row 수치를 언급할 때 반드시 해당 row의 실제 numeric field 값(예: row[0]._count, row[1].totalSales)을 그대로 인용 — 표의 row 개수(${displayedCols.length}개 컬럼 × N개 row의 N) 또는 카테고리 수를 각 row의 인원/금액/횟수 값으로 사용 금지. 예: 5개 row 표가 [{x:47},{x:22},{x:12},{x:10},{x:2}]면 "A: 47, B: 22, C: 12" (각 row의 x 값) O, "A: 5, B: 5, C: 5" (row 개수 반복) X.`;
      if (excluded && excluded.length > 0) {
        result.message += ` [EXCLUDED FROM TABLE] 요청한 컬럼 중 [${excluded.join(', ')}]은(는) 데이터에 없어 표에서 제외됐습니다 — outro/finish에서 절대 언급 금지.`;
      }
    }

    // [GUARD 12 WIRING] saveResolvedColumns/Labels — finishPolicy GUARD 12 (COLUMN_ENUM_LEAK) detector가
    //   buffer.resolvedColumnLabels를 입력으로 사용. show_display 성공 시 displayResult의 columns/columnLabels를
    //   active buffer에 propagate해야 다음 finish() 시 enum leak 검출 가능. wiring 누락 시 GUARD 12 silently dead.
    //   (dual sync with nodeAgent.mjs)
    if (Array.isArray(displayResult.columns)) {
      this.saveResolvedColumns(displayResult.columns);
    }
    if (displayResult.columnLabels && typeof displayResult.columnLabels === 'object') {
      this.saveResolvedColumnLabels(displayResult.columnLabels);
    }

    // [rid60 SMS-RECIPIENT-ORDER fix] 표시된 item order 보존 — recommend_sms targetIndex가 buffer순(API순)이 아닌 표시순으로 resolve되도록 (dual sync with nodeAgent.mjs) //
    //   본질: autoGenerateDisplay autoSort로 표시순(예: 만료임박순) ≠ buffer.items(API순). targetIndex(표 행번호)를 buffer index로 풀면 정반대 회원 선택(운영 rid60: "Kim 말고 하채린만"→Kim).
    if (Array.isArray(displayResult.items)) {
      this._lastDisplayedOrder = displayResult.items.map(it => ({
        memberId: it?.memberId ?? it?.teacherId ?? null,
        name: it?.name ?? it?.memberName ?? it?.userName ?? it?.teacherName ?? null,
      }));
    }

    result.bufferState = this.getBufferSnapshot();
    this._autoChainRequiresDisplay = false;  // show_display 성공 시 auto-chain 후속 가드 해제
    this._showDisplaySucceededThisTurn = true;  // [Weak8-DUP_RESPONSE] 동일 턴 내 중복 호출 차단 플래그 (dual sync with nodeAgent.mjs)

    // [1-A β] KPI HINT INJECT — RATE_STATISTICS / RETENTION+RATE buffer 보유 시 다음 finish()에서 KPI 표현 유도 (language-agnostic, 1회 한정)
    // 본질: show_display 차단 X — list 의도 user 정상 통과 + KPI summary 누락 차단. LLM이 user 입력 언어로 KPI 자연어 표현 (한국어 keyword 의존 폐기)
    // active slot 우선, fallback buffers Map 순회 — LLM이 RATE_STATISTICS 호출 후 추가 call_api로 slot 이동해도 RATE slot 잔존 시 검출 가능
    if (!this._showDisplayKpiHintInjected) {
      const KPI_INTENT_RE = /(RATE_STATISTICS|RETENTION\+RATE)/;
      let _kpiBuf = null;
      let _kpiSlotName = null;
      const _activeBuf = this.getBuffer();
      if (_activeBuf && KPI_INTENT_RE.test(_activeBuf.intention || '') && _activeBuf.summary && typeof _activeBuf.summary === 'object') {
        _kpiBuf = _activeBuf;
        _kpiSlotName = this.activeSlot;
      } else if (this.buffers && this.buffers.size > 0) {
        for (const [_name, _buf] of this.buffers) {
          if (_buf && KPI_INTENT_RE.test(_buf.intention || '') && _buf.summary && typeof _buf.summary === 'object') {
            _kpiBuf = _buf;
            _kpiSlotName = _name;
            break;
          }
        }
      }
      if (_kpiBuf) {
        this._showDisplayKpiHintInjected = true;
        // [1-A γ] kpiHintMeta 저장 — finishPolicy GUARD에서 KPI value 누락 검증용 (caller가 evaluateFinish input으로 전달)
        // primaryRateKey: intention 메타에서 가져와 GUARD 7 strict 모드 활성 (보조 rate 단독 인용 차단)
        const _kpiIntentionMeta = this.findIntentionMeta(_kpiBuf.intention);
        const _primaryRateKey = (_kpiIntentionMeta && typeof _kpiIntentionMeta.primaryRateKey === 'string')
          ? _kpiIntentionMeta.primaryRateKey
          : null;
        this._kpiHintMeta = {
          slotName: _kpiSlotName,
          intention: _kpiBuf.intention,
          summary: { ..._kpiBuf.summary },
          ...(_primaryRateKey && _primaryRateKey in _kpiBuf.summary && { primaryRateKey: _primaryRateKey }),
        };
        const _summaryStr = Object.entries(_kpiBuf.summary)
          .filter(([k, v]) => typeof v === 'number')
          .slice(0, 8)
          .map(([k, v]) => `${k}=${v}`)
          .join(', ');
        result.message += `\n[KPI HINT] Buffer slot "${_kpiSlotName}" intention "${_kpiBuf.intention}" has aggregated KPI summary:\n  ${_summaryStr}\nYour next finish() MUST mention these KPI values (rate/total/avg) in the user's input language. Showing the row list alone leaves the user without the metric they asked for — mention KPI first, then optionally describe the list.`;
      }
    }

    return result;
  }

  // [1-A γ] RATE_STATISTICS/RETENTION+RATE buffer 자동 감지 → _kpiHintMeta set
  // show_display 미호출 path (finish 직접 호출 등)에서도 finishPolicy GUARD 7 활성 보장.
  // 이미 set되어 있으면 skip (KPI HINT INJECT path 우선).
  // (dual sync with nodeAgent.mjs)
  _ensureKpiHintMeta() {
    if (this._kpiHintMeta) return this._kpiHintMeta;
    const KPI_INTENT_RE = /(RATE_STATISTICS|RETENTION\+RATE)/;
    let _kpiBuf = null;
    let _kpiSlotName = null;
    const _activeBuf = this.getBuffer?.();
    if (_activeBuf && KPI_INTENT_RE.test(_activeBuf.intention || '') && _activeBuf.summary && typeof _activeBuf.summary === 'object') {
      _kpiBuf = _activeBuf;
      _kpiSlotName = this.activeSlot;
    }
    else if (this.buffers && this.buffers.size > 0) {
      for (const [_name, _buf] of this.buffers) {
        if (_buf && KPI_INTENT_RE.test(_buf.intention || '') && _buf.summary && typeof _buf.summary === 'object') {
          _kpiBuf = _buf;
          _kpiSlotName = _name;
          break;
        }
      }
    }
    if (!_kpiBuf) return null;
    const _meta = this.findIntentionMeta(_kpiBuf.intention);
    const _primaryRateKey = (_meta && typeof _meta.primaryRateKey === 'string') ? _meta.primaryRateKey : null;
    this._kpiHintMeta = {
      slotName: _kpiSlotName,
      intention: _kpiBuf.intention,
      summary: { ..._kpiBuf.summary },
      ...(_primaryRateKey && _primaryRateKey in _kpiBuf.summary && { primaryRateKey: _primaryRateKey }),
    };
    return this._kpiHintMeta;
  }

  // 표시 컬럼 증감 조정 //
  executeCustomizeDisplay({ only, include, exclude }) {
    const result = this.setColumnPrefs(include || [], exclude || [], only || null);

    if (result.success) {
      const preview = previewDisplayColumns(this);
      if (preview) {
        result.currentColumns = preview;
        result.message += ` 현재 표시 컬럼: [${preview.join(', ')}]`;
      }
      result.bufferState = this.getBufferSnapshot();
    }

    return result;
  }

  // N개 슬롯 병합 //
  executeMergeSlots(args) {
    const result = this.mergeSlots(args);
    // [L11-SWITCH-SLOT-001 cycle 2] POST-MERGE SHOW_DISPLAY BYPASS — merge 성공 + 본 turn show_display 이미 성공이면
    //   후속 show_display 1회 한정 dup-block bypass 허용 (executeShowDisplay 진입부에서 소비)
    //   본질: switch+show → merge_slots 순서로 LLM이 진행 시 merged 결과 표시 막힘 차단 (dual sync with nodeAgent.mjs)
    if (result && result.success && this._showDisplaySucceededThisTurn) {
      this._postMergeShowDisplayAllowedOnce = true;
      console.log('[merge_slots] 🔓 POST-MERGE BYPASS ARMED — next show_display will bypass dup-block once');
    }
    return result;
  }

  // 활성 버퍼 슬롯 전환 //
  executeSwitchSlot({ slot }) {
    if (!slot) return { success: false, message: 'Specify slot name to switch.' };
    const result = this.setActiveSlot(slot);
    if (result.success) {
      result.slotSummaries = this.getSlotSummaries();
    }
    return result;
  }

  // [VIZ-SOURCE-COMPLETENESS] viz 도구 dispatch 전 requiredSources 메타 기반 누락 source 자동 보강 (dual sync with nodeAgent.mjs) //
  //   본질: viz 도구(show_ltv 등)는 여러 intention buffer를 동시에 읽는데, LLM이 일부 source만 로드하면 핸들러가 success:false로
  //         "데이터 부족" 반환 → LLM이 show_display로 빠지거나 포기. 본 fix는 도구 정의의 requiredSources 메타를 읽어
  //         현재 buffer에 없는 source intention을 코드가 call_api(lazy-fill)로 자동 prefetch 후 핸들러가 정상 렌더하도록 보강.
  //   범용성: 도구 키/case-ID/input-string 하드코딩 X. TOOL_DEFINITIONS의 requiredSources 메타만 읽음 — requiredSources를
  //           선언한 모든 viz 도구에 동일 적용. period 파라미터는 viz args(month/startDate/endDate)를 그대로 전달하여 source 정합.
  async _prefetchVizRequiredSources(toolName, args) {
    const _toolDef = Array.isArray(TOOL_DEFINITIONS)
      ? TOOL_DEFINITIONS.find(t => t?.function?.name === toolName)
      : null;
    const _required = _toolDef?.function?.requiredSources;
    if (!Array.isArray(_required) || _required.length === 0) return;

    // [#B active-slot 오염 fix 2026-06-24] prefetch는 viz source 보강일 뿐 — 사용자 질문 대상(직전 call_api) 슬롯이
    //   active로 유지돼야 한다. executeCallApi는 slot 인자에도 무조건 this.activeSlot=slot을 설정하므로 _src_ prefetch가
    //   activeSlot을 source 슬롯(전체 모수)으로 clobber → finish placeholder({{COUNT}}/{{SUMMARY}})가 getActiveBuf()를
    //   참조할 때 거짓 수치(예: 신규 9명인데 history 170명) 노출. prefetch 전후 activeSlot 보존으로 차단.
    //   viz 핸들러는 findBufferByIntention으로 슬롯을 찾아 activeSlot 비의존 → 복원해도 정상. dual sync with nodeAgent.mjs.
    const _prevActiveSlot = this.activeSlot;

    for (const _srcIntention of _required) {
      // 이미 buffer에 해당 source가 있으면 skip — 중복 호출 방지
      const _existing = this.findBufferByIntention(_srcIntention);
      if (_existing?.items?.length) continue;

      // 누락 source를 자동 prefetch. lazy-fill(anchor 6)이 get_intentions 없이도 INTENTION_DIMENSIONS source로 메타 해석.
      //   slot은 source intention에서 파생(충돌/active 슬롯 clobber 최소화). viz args의 period를 그대로 전달.
      const _prefetchSlot = `_src_${_srcIntention.replace(/[^A-Za-z0-9]/g, '_').toLowerCase()}`;
      const _prefetchArgs = { intention: _srcIntention, slot: _prefetchSlot };
      if (args && typeof args === 'object') {
        if (args.month) _prefetchArgs.month = args.month;
        if (args.startDate) _prefetchArgs.startDate = args.startDate;
        if (args.endDate) _prefetchArgs.endDate = args.endDate;
      }
      try {
        if (TOOL_CALLING_LOG) console.log(`[${toolName}] VIZ-SOURCE auto-prefetch missing source: ${_srcIntention} → slot=${_prefetchSlot}`);
        await this.executeCallApi(_prefetchArgs);
      } catch (_e) {
        if (TOOL_CALLING_LOG) console.log(`[${toolName}] VIZ-SOURCE auto-prefetch failed for ${_srcIntention}: ${_e?.message || _e}`);
      }
    }

    // [#B fix] prefetch가 clobber한 activeSlot 복원 — 사용자 질문 대상(직전 call_api) 슬롯을 active로 유지
    this.activeSlot = _prevActiveSlot;
  }

  // [1-F] 시각화 도구 success 시 _finishAllowedByGuard set helper — dispatch에서 wrap //
  // [L5-CROSS-005 fix 2026-05-29] 시각화 도구가 성공해서 결과를 표시했는데 LLM이 finish 대신 추가 도구 호출 시
  //   POST-GUARD TOOL BLOCK이 발동하여 default fallback "데이터를 찾을 수 없어요" 텍스트가 출력되는 false-negative 차단.
  //   visualization 도구 success 시점에는 이미 데이터/시각화가 준비됐으므로 fallback 텍스트도 "결과 확인" 방향으로 set.
  // [L1-SALES-005 fix 2026-05-31] 시각화 도구 args.intro에 placeholder ({{SUMMARY:...}}/{{COUNT}}/{{SUM:...}})가 있고
  //   buffer에 resolve 가능한 summary가 있을 때 substitute한 결과를 suggested text로 사용 — 일반 "요청하신 결과 N건을 정리했어." 폴백
  //   대신 사용자 질문에 정합한 답변(예: "올해 총 매출은 12,500,000원이야.")을 보장. baseline scalar query에서 LLM이 자연어로
  //   correct answer를 만들어도 guard suggested text(count 기반 generic)가 덮어쓰는 false-positive 차단.
  //   dual sync with nodeAgent.mjs.
  _markFinishAllowed(result, args) {
    if (result?.success) {
      this._finishAllowedByGuard = true;
      // 시각화 success 후 LLM이 finish 미호출 → POST-GUARD fallback 텍스트가 "찾을 수 없어요"가 되면 안 됨.
      // 기존 suggested text가 detail-fallback 등 다른 경로에서 명시적으로 set돼있으면 보존.
      if (!this._finishGuardSuggestedText) {
        // [INTRO-BASED SUGGESTED TEXT] args.intro 우선 — 시각화 호출 시 LLM이 명시한 intro에 placeholder가 있으면
        //   active buffer로 substitute. unresolved placeholder 잔존 시 generic fallback으로 회피.
        const _introRaw = (args && typeof args.intro === 'string') ? args.intro.trim() : '';
        let _resolvedIntro = '';
        if (_introRaw) {
          try {
            const _buf = this.getActiveBuf?.() || [...(this.buffers?.values?.() || [])].find(b => b && ((b.items?.length || 0) >= 1 || b?.summary));
            if (_buf) {
              const _phResult = substituteOutroPlaceholders(_introRaw, _buf, this.getSlotSummaryMap());
              const _candidate = (_phResult && typeof _phResult.text === 'string') ? _phResult.text : _introRaw;
              // unresolved placeholder 잔존 시 거부 ({{...}} 패턴)
              if (_candidate && !/\{\{[^}]+\}\}/.test(_candidate)) {
                _resolvedIntro = _candidate.trim();
              }
            } else if (!/\{\{[^}]+\}\}/.test(_introRaw)) {
              // buffer 없어도 plain intro (placeholder 없음)는 사용 가능
              _resolvedIntro = _introRaw;
            }
          } catch (_e) {
            // 안전 fallback — substitute 실패 시 generic 경로
            _resolvedIntro = '';
          }
        }
        if (_resolvedIntro) {
          this._finishGuardSuggestedText = _resolvedIntro;
        } else {
          const _count = (typeof result.count === 'number' && result.count >= 0) ? result.count : null;
          this._finishGuardSuggestedText = (_count != null)
            ? `요청하신 결과 ${_count}건을 정리했어.`
            : `요청하신 결과를 정리했어.`;
        }
      }
    }
    return result;
  }

  // Tool 실행 디스패처 //
  async executeTool(toolName, args) {
    // [SWITCH PATTERN BLOCK — dispatch level] "X말고 Y" 패턴 + 멀티슬롯 컨텍스트에서 LLM이 call_api 우회하여 데이터 가공 도구로 직행 차단
    // → 새 차원 Y는 call_api 호출 필수, 코드가 직전 기간 구조 자동 적용
    const _ALLOWED_BEFORE_CALL_API = new Set(['get_intentions', 'call_api', 'get_guide', 'finish']);
    if (!_ALLOWED_BEFORE_CALL_API.has(toolName)
        && !this._multiPeriodAutoChainFiredThisTurn
        && !this._calledApiThisTurn) {
      const _userInputSP = this.getUserInput?.() || '';
      if (/(\S+)\s*(말고|대신|아니고|아니라)\s+(\S+)/.test(_userInputSP)) {
        const _periodsSP = this._extractPeriodStructure();
        if (_periodsSP && _periodsSP.length >= 2) {
          return {
            success: false,
            message: `[SWITCH PATTERN BLOCK] 사용자 input "${_userInputSP.slice(0,60)}"가 "X말고 Y" 패턴입니다 — 새 차원(Y)을 위해 ${toolName}이 아니라 call_api(Y intention)를 먼저 호출하세요. 코드가 직전 turn 기간 구조(${_periodsSP.map(p => p.start + '~' + p.end).join(', ')})를 새 인텐션에 자동 적용합니다. (get_intentions로 Y에 맞는 인텐션 찾기 → call_api → 자동 chain → show_display)`,
          };
        }
      }
    }

    // [NARROW PERIOD CHAIN FOLLOW-UP — DATA TOOL HONEST INFORM] L21-COMPARE-FOLLOWUP-001 cycle 2 (2026-05-31, dual sync with nodeAgent.mjs)
    //   본질: chain follow-up에서 narrow 시간 키워드 + "만" 한정 입력 시 LLM이 call_api 재호출 없이
    //         filter/sort/compute/aggregate/derive/merge_slots 등 data-processing tool로 직행 → 기간 narrow 의도와 부합 X.
    //         예: T0="최근 30일 출석 줄어든 회원" / T1="이번주만 보여줘" → LLM이 filter(recent_attendedCount <= previous_attendedCount)
    //         → buffer items 변경되거나 0건 → "이번주 기준 0명" framing → 사용자 입장 false ack.
    //   해소: narrow particle "만" + time keyword + 본 turn call_api 미호출 + active buffer items 보유 + data-processing tool
    //         → dispatch level block + finish 안내 (1회 한정)
    //   회귀 risk 최소화: 본 turn call_api 호출됐으면 통과 — narrow + call_api 정상 흐름 보존. show_display는 별도 가드(executeShowDisplay 내부)에서 처리.
    const _DATA_PROCESSING_TOOLS = new Set(['filter', 'sort', 'compute', 'aggregate', 'derive', 'merge_slots']);
    if (_DATA_PROCESSING_TOOLS.has(toolName)
        && !this._dataToolPeriodNarrowBlocked
        && !this._calledApiThisTurn
        && !this._multiPeriodAutoChainFiredThisTurn) {
      const _userInputDTN = this.getUserInput?.() || '';
      const NARROW_PERIOD_DT_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;
      if (NARROW_PERIOD_DT_RE.test(_userInputDTN)) {
        const _activeBufDTN = this.getActiveBuf?.() || this.getBuffer?.();
        const _hasItemsDTN = !!(_activeBufDTN && (_activeBufDTN.items?.length || 0) >= 1);
        if (_hasItemsDTN) {
          this._dataToolPeriodNarrowBlocked = true;
          const _narrowMatchDTN = (_userInputDTN.match(NARROW_PERIOD_DT_RE) || [])[0] || '';
          const _bufIntentionDTN = _activeBufDTN.intention || 'active';
          const _bufCountDTN = _activeBufDTN.items?.length || 0;
          if (TOOL_CALLING_LOG) console.log(`[executeTool] NARROW PERIOD DATA-TOOL BLOCKED: tool=${toolName}, narrow="${_narrowMatchDTN}", buf=${_bufIntentionDTN}(${_bufCountDTN})`);
          return {
            success: false,
            message: `[NARROW PERIOD CHAIN FOLLOW-UP — DATA TOOL HONEST INFORM] User said "${_userInputDTN.slice(0, 50)}" (narrowing time keyword "${_narrowMatchDTN}"). Active buffer (intention=${_bufIntentionDTN}, ${_bufCountDTN} items) was loaded for a wider/different time period — calling ${toolName} on this buffer would either re-render the same items with a renamed "${_narrowMatchDTN} 기준" label (false ack) or apply a non-period filter that does not actually narrow the time range (e.g. filter(recent_attendedCount <= previous_attendedCount) is a compare filter, not a period narrow). Backend data for the narrow period may be unavailable or unreliable, so silently re-running call_api is not safe. Instead, call finish with text like: "${_narrowMatchDTN} 기준 데이터로 좁히려면 새로 조회가 필요해요. 정확한 기간(예: 시작일~종료일)을 다시 알려주시면 새로 분석해 드릴게요." This honestly informs the user that the previous buffer cannot be reused for a narrower period, rather than silently re-labeling or mis-filtering stale data.`,
          };
        }
      }
    }

    // [DATE PARAM GUARD] LLM이 사용자 input에 없는 시간 키워드를 임의로 startDate/endDate에 추가했으면 정리 (결정성 보호)
    // - 시간 키워드 있음: LLM args 유지
    // - LLM이 둘 다 유효 ISO 날짜(YYYY-MM-DD)로 명시: LLM 의도 신뢰 (멀티슬롯/컨텍스트 기반 결정 보호)
    // - 시간 키워드 없음 + 부분/placeholder + buffer dateRange 있음: buffer dateRange force inherit
    // - 시간 키워드 없음 + 부분/placeholder + buffer dateRange 없음: strip (첫 turn)
    if (toolName === 'call_api' && args && (args.startDate || args.endDate)) {
      const _userInputRaw = this.getUserInput?.() || '';
      const TIME_KW_GUARD = /오늘|어제|내일|모레|지난\s*주|이번\s*주|다음\s*주|금주|지난\s*달|이번\s*달|다음\s*달|금월|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+\s*일\s*전|작년|올해|금년|내년|지난해/;
      const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
      const llmExplicitDates = ISO_DATE.test(args.startDate || '') && ISO_DATE.test(args.endDate || '');
      if (_userInputRaw && !TIME_KW_GUARD.test(_userInputRaw) && !llmExplicitDates) {
        const candidates = [...this.buffers.values()].filter(b => b.dateRange?.startDate && b.dateRange?.endDate);
        if (candidates.length > 0) {
          const r = candidates[candidates.length - 1].dateRange;
          console.log(`[call_api] DATE_GUARD inherit buffer dateRange: ${r.startDate}~${r.endDate} (input "${_userInputRaw.slice(0,40)}")`);
          args = { ...args, startDate: r.startDate, endDate: r.endDate };
        } else {
          console.log(`[call_api] DATE_GUARD strip (no buffer dateRange): "${_userInputRaw.slice(0,40)}"`);
          args = { ...args };
          delete args.startDate;
          delete args.endDate;
        }
      }
    }
    // [L9a-MEMBERSHIP-002 fix] SMS multi-turn guard — SMS 요청인데 이전 turn buffer가 0건이면 call_api 차단 //
    if (toolName === 'call_api' && /문자|sms|SMS|추천.*보내|보내.*추천/i.test(this.getUserInput?.() || '')) {
      const _prevFT = this._previousFinalText || '';
      const _prevEmpty = this._previousBufferItemCount === 0;
      const _prevNoData = /없어요|없습니다|0건|0명|데이터가 없|해당하는.*없/i.test(_prevFT);

      if (_prevEmpty && _prevNoData) {
        return {
          success: false,
          message: `[CALL_API BLOCKED — SMS NO RECIPIENTS] Previous turn returned 0 items and responded "${_prevFT.slice(0, 60)}". The user's SMS request follows this chain context — there are no recipients to send SMS to. Call finish() with a message explaining no recipients are available (Korean: "이전 조회 결과 대상이 없어서 문자를 보낼 수 없어요").`,
        };
      }
    }

    // [HOWTO/ADVICE GATE GUARD] get_guide not_found 직후 LLM이 data path(get_intentions/call_api)로 fallback 시도하는 패턴 차단 (dual sync with nodeAgent.mjs).
    // 근본 원인: get_guide 실패 메시지의 "DATA query → get_intentions" 분기를 LLM이 잘못 선택해
    //   HOWTO/ADVICE 의도 input("X 올리는 방법 추천해줘", "어떻게 해야 돼?")에 raw data dump 응답하는 본질 mismatch.
    // 조건: (1) toolName이 get_intentions 또는 call_api (2) 본 턴 get_guide가 not_found로 실패했음 (3) 본 턴 아직 buffer 없음 (첫 turn)
    //       (4) user input에 HOWTO/ADVICE 패턴 (5) 최대 3회 차단 (LLM stochastic 재시도해도 끝까지 finish 강제, 무한루프 방지)
    // multi-turn follow-up(buffers에 데이터 있음)은 이전 데이터 컨텍스트가 있으니 bypass.
    if ((toolName === 'get_intentions' || toolName === 'call_api')
        && this._getGuideFailedThisTurn
        && this._howtoAdviceGateBlockedCount < 3) {
      const _userInputHA = this.getUserInput?.() || '';
      const _hasBufferDataHA = this.buffers && this.buffers.size > 0
        && [...this.buffers.values()].some(b => b?.items?.length > 0);
      // HOWTO/ADVICE 패턴: (a) 명시적 HOWTO 키워드 (b) ADVICE — 동작 동사 + "방법|법|전략|추천|어떻게|제안|아이디어|팁|조언|비결|꿀팁|노하우" 조합
      // 단순 "추천" 단독은 mismatch 위험 (예: "운동 회원 추천" — DATA 의도) → 동작 동사 결합 패턴만 사용
      const _howtoKwHA = /어떻게|방법|하는\s*법|사용법|설정하는\s*법|처리하는\s*법|계산법/;
      const _adviceKwHA = /(올리|개선|향상|증가|성장|늘리|줄이|낮추|높이|키우|확대|확장|유치|유지|이탈\s*방지)\S*\s*(방법|법|전략|추천|어떻게|제안|아이디어|팁|조언|비결|꿀팁|노하우)|(전략|아이디어|팁|조언|제안|비결|꿀팁|노하우)\s*(추천|줘|알려)/;
      const _isHowtoAdvice = _howtoKwHA.test(_userInputHA) || _adviceKwHA.test(_userInputHA);
      // [B-2 과적합 수술] 데이터 기반 자문 명시("우리 데이터/현황/수치 보고·기반으로 조언") — 데이터 적재 허용이 정답이라 차단 제외.
      //   적재 후엔 ADVICE FOLLOW-UP GATE가 표-only 재렌더를 막고 자문 텍스트로 유도(기존 메커니즘 합성). generic 자문은 기존대로 즉시 자문 finish. (dual sync with nodeAgent.mjs)
      const _dataGroundedHA = /(데이터|현황|수치|실적|지표|기록|통계)\s*(를|을|도)?\s*(보고|보면서|기반|참고|토대|가지고|활용|분석)/.test(_userInputHA);
      if (_isHowtoAdvice && !_hasBufferDataHA && !_dataGroundedHA) {
        this._howtoAdviceGateBlockedCount++;  // max 3회 — LLM stochastic 재시도 시 끝까지 finish 강제 (무한루프 방지)
        console.log(`[executeTool] HOWTO/ADVICE GATE GUARD: ${toolName} blocked (${this._howtoAdviceGateBlockedCount}/3) — get_guide not_found + HOWTO/ADVICE input "${_userInputHA.slice(0, 60)}"`);
        return {
          success: false,
          message: `[HOWTO/ADVICE BLOCKED ${this._howtoAdviceGateBlockedCount}/3] get_guide returned no matching guide and the user's input "${_userInputHA.slice(0, 80)}" is a HOWTO/ADVICE request (contains 방법/어떻게/추천/올리는 등). Data APIs (get_intentions/call_api) cannot answer how-to or strategic-advice questions — raw data dump would be a content mismatch from the user's perspective.\n\nSTOP retrying data tools. REQUIRED NEXT STEP: call finish() with a natural-language Korean answer that DIRECTLY HELPS the user. You MUST NOT refuse with phrases like "안내 어렵습니다", "여기서는 안내가 어려워요", "직접 제공해 드리기는 어렵습니다", "안내해 드릴 수 없습니다", or "도와드리기 어렵습니다" — these refusals do NOT satisfy the user.\n\nResponse rules by input type:\n  - ADVICE inputs (예: "매출 올리는 방법", "회원 유치 전략 추천"): MUST give 3-5 concrete, actionable suggestions based on common gym/fitness operational levers. For 매출(revenue) advice: 신규 회원 유치(체험권/지인추천/SNS 광고), 재등록률 개선(만료 전 알림/연장 할인), 회원권 다양화(단기/장기/PT 패키지), 출석률 향상(클래스 다양화/이벤트), 추가 매출(PT/용품/보충제). Use natural Korean tone with bullet-style structure or numbered list. NEVER say "안내 어려움" or similar.\n  - HOWTO inputs (예: "수업 등록 어떻게?", "회원 추가 방법"): give a polite guide pointing to manager UI (예: "관리자 페이지/설정 메뉴에서 직접 가능합니다" + 구체 메뉴 경로 추측). Do NOT refuse.\n\nDo NOT call get_intentions/call_api/merge_slots/filter/sort/aggregate/compute. Call finish() immediately with the helpful answer.`,
        };
      }
    }

    // [ADVICE FOLLOW-UP GATE GUARD] 자문 의도 후속턴(버퍼 데이터 존재)에서 LLM이 데이터 조회/렌더로 라우팅하는 패턴 차단 (dual sync with nodeAgent.mjs).
    // 근본: "이 사람들 붙잡는 방법", "어떻게 하면 유지할까" 같은 전략 자문을 LLM이 show_display/call_api/merge_slots로 라우팅
    //       → 데이터 표만 출력(자문 텍스트 mismatch). 위 HOWTO/ADVICE GATE는 !buffer && get_guide_not_found 한정이라 후속 자문 사각.
    // 조건: (1) toolName ∈ 데이터 조회/렌더 도구 (2) _isAdviceFollowupIntent(정밀 — bare "어떻게 돼?" 제외) (3) 버퍼 데이터 존재 (4) max 3회.
    // 운영 5차 rid100/101 (store323): T7 만료 회원 로드 후 "이 사람들 붙잡는 방법?" 자문에 데이터 표 재렌더 → 사용자 👎.
    if (['get_intentions', 'call_api', 'merge_slots', 'show_display'].includes(toolName)
        && this._adviceFollowupGateBlockedCount < 3) {
      const _uiAF = this.getUserInput?.() || '';
      const _hasBufAF = this.buffers && this.buffers.size > 0
        && [...this.buffers.values()].some(b => b?.items?.length > 0);
      if (this._isAdviceFollowupIntent(_uiAF) && _hasBufAF) {
        this._adviceFollowupGateBlockedCount++;  // max 3회 — LLM stochastic 재시도 시 끝까지 finish 강제 (무한루프 방지)
        console.log(`[executeTool] ADVICE FOLLOW-UP GATE GUARD: ${toolName} blocked (${this._adviceFollowupGateBlockedCount}/3) — advice intent + buffer data "${_uiAF.slice(0, 60)}"`);
        return {
          success: false,
          message: `[ADVICE FOLLOW-UP — DATA TOOL BLOCKED ${this._adviceFollowupGateBlockedCount}/3] The user's input "${_uiAF.slice(0, 80)}" is a strategy/retention ADVICE request about data already loaded this session. Rendering another table or re-fetching data is a content mismatch — the user wants actionable advice, not a table.\n\nSTOP calling data tools (show_display/call_api/merge_slots/get_intentions). REQUIRED NEXT STEP: call finish() with a natural Korean answer giving 3-5 concrete, actionable suggestions referencing the members already shown. Common gym retention levers: 만료 전 연장 안내(개인 맞춤 메시지/연장 할인), 저출석 회원 재방문 유도(이벤트/클래스 추천/PT 체험), 정기 소통(생일·기념일 축하/운동 피드백), 커뮤니티(그룹 챌린지/소모임), 가격 유연화(단기권/홀딩). You MUST NOT refuse with "안내 어렵습니다" or similar, and MUST NOT re-render the table — give the advice directly in finish().`,
        };
      }
    }

    // [INTENTION GATE GUARD] LLM이 get_intentions 미호출 + 첫 턴(buffer 없음) 상태에서 call_api 직행 차단 (dual sync with nodeAgent.mjs)
    // 근본 원인: LLM이 stochastic하게 get_intentions을 건너뛰고 call_api를 직접 호출하면
    //   사용 가능한 인텐션 목록을 보지 않고 잘못된 인텐션을 선택하여 intent drift 발생.
    // 조건: (1) 본 턴 get_intentions 미호출 (2) buffers에 기존 데이터 없음(첫 턴) (3) 1회 한정 차단 미발동
    // multi-turn follow-up(buffers에 데이터 있음)은 이전 턴에서 이미 인텐션 확인 → bypass.
    // 1회 한정: 차단 후 LLM이 get_intentions 대신 동일 call_api를 재시도하면 lazy-fill fallback 허용.
    if (toolName === 'call_api' && !this._getIntentionsCalledThisTurn && !this._intentionGateBlocked) {
      const _hasExistingBuffer = this.buffers && this.buffers.size > 0
        && [...this.buffers.values()].some(b => b?.items?.length > 0);
      if (!_hasExistingBuffer) {
        this._intentionGateBlocked = true;  // 1회 한정 — 다음 call_api는 통과
        console.log(`[executeTool] INTENTION GATE GUARD: call_api("${args?.intention}") blocked — get_intentions not called this turn, no prior buffer data`);
        return {
          success: false,
          message: `[INTENTION GATE] call_api was called without first calling get_intentions this turn. The workflow is: (1) determine category from user question, (2) call get_intentions(category) to see available intentions, (3) pick the best matching intention, (4) call call_api with that intention. Call get_intentions first to see the available intentions for the correct category.`,
        };
      }
    }

    if (toolName === 'get_guide')                return await this.executeGetGuide(args);
    if (toolName === 'get_intentions') {
      this._getIntentionsCalledThisTurn = true;  // [INTENTION GATE] LLM이 get_intentions 호출 기록 (dual sync with nodeAgent.mjs)
      return await this.executeGetIntentions(args);
    }
    if (toolName === 'call_api')                 return await this.executeCallApi(args);
    if (toolName === 'filter')                   return this.executeFilter(args);
    if (toolName === 'sort')                     return this.executeSort(args);
    if (toolName === 'compute')                  return this.executeCompute(args);
    if (toolName === 'aggregate')                return this.executeAggregate(args);
    if (toolName === 'derive')                   return this.executeDeriveField(args);
    if (toolName === 'show_display')             return this.executeShowDisplay(args);
    // [VIZ-SOURCE-COMPLETENESS] viz 도구 진입 전 requiredSources 메타 기반 누락 source 자동 보강 (dual sync with nodeAgent.mjs).
    //   메타 선언한 viz 도구에만 동작 (requiredSources 없는 도구는 no-op). LTV/case 하드코딩 X.
    await this._prefetchVizRequiredSources(toolName, args);

    // [1-F] 시각화 도구 (show_display 제외) success → _finishAllowedByGuard 자동 set.
    // show_*Chart/Ranking/View/Status/Ltv 는 그 자체로 응답 종결 의미 → finishPolicy GUARD 1(display_required) 잘못 발동 차단.
    // 비-success(인자 누락 등) 시 set X — LLM이 다음 chain 진행해야 하므로.
    if (toolName === 'show_sales_view')          return this._markFinishAllowed(await this.executeShowSalesView(args), args);
    if (toolName === 'show_revenue_chart')       return this._markFinishAllowed(this.executeShowRevenueChart(args), args);
    if (toolName === 'show_member_trend')        return this._markFinishAllowed(this.executeShowMemberTrend(args), args);
    if (toolName === 'show_member_flow_trend')   return this._markFinishAllowed(this.executeShowMemberFlowTrend(args), args);
    if (toolName === 'show_member_revenue_trend') return this._markFinishAllowed(this.executeShowMemberRevenueTrend(args), args);
    if (toolName === 'show_attendance_chart')    return this._markFinishAllowed(await this.executeShowAttendanceChart(args), args);
    if (toolName === 'show_class_attendance')    return this._markFinishAllowed(await this.executeShowClassAttendance(args), args);
    if (toolName === 'show_attendance_ranking')  return this._markFinishAllowed(await this.executeShowAttendanceRanking(args), args);
    if (toolName === 'show_member_status')       return this._markFinishAllowed(await this.executeShowMemberStatus(args), args);
    if (toolName === 'show_ltv')                 return this._markFinishAllowed(await this.executeShowLtv(args), args);
    if (toolName === 'show_engagement_chart')    return this._markFinishAllowed(await this.executeShowEngagementChart(args), args);
    if (toolName === 'show_membership_preference') return this._markFinishAllowed(await this.executeShowMembershipPreference(args), args);
    if (toolName === 'show_period_sales')        return this._markFinishAllowed(await this.executeShowPeriodSales(args), args);
    if (toolName === 'show_point_mange')         return this._markFinishAllowed(await this.executeShowPointManagement(args), args);
    if (toolName === 'customize_display')        return this.executeCustomizeDisplay(args);
    if (toolName === 'recommend_sms')            return this.executeRecommendSms(args);
    if (toolName === 'merge_slots')              return this.executeMergeSlots(args);
    if (toolName === 'switch_slot')              return this.executeSwitchSlot(args);
    return { success: false, message: `Unknown tool: ${toolName}` };
  }

  // ============================================
  // 플랫폼 가이드 조회 (get_guide)
  // ============================================

  // 가이드 검색 실행 //
  async executeGetGuide(args) {
    const { query, slug } = args;

    if (!query) {
      return { success: false, message: 'query parameter is required.' };
    }

    if (TOOL_CALLING_LOG) console.log(`[Guide] 검색: "${query}"${slug ? ` slug: "${slug}"` : ''}`);

    const result = await executeGuideSearch(query, slug);

    if (TOOL_CALLING_LOG) console.log(`[Guide] ${result.type}: ${result.success ? 'found' : 'not found'}`);

    // [HOWTO/ADVICE GATE] get_guide가 not_found(success:false, type:'not_found')로 실패하면 flag set.
    // 다음 call_api/get_intentions 호출 시 input이 HOWTO/ADVICE 패턴이면 차단 (data dump 회피, dual sync with nodeAgent.mjs)
    if (!result?.success && result?.type === 'not_found') {
      this._getGuideFailedThisTurn = true;
    }

    return result;
  }

  // ============================================
  // SQL-like 쿼리 실행 엔진 (복합 조건 INTERSECT 전용)
  // ============================================

  // SQL 쿼리를 파싱하고 실행 //
  async executeSqlQuery(sql) {
    try {
      const parsed = this.parseSql(sql);

      if (!parsed) return { success: false, message: 'SQL parse failed' };

      if (TOOL_CALLING_LOG) console.log('[SQL] parsed:', JSON.stringify(parsed, null, 0).slice(0, 200));

      if (parsed.type === 'intersect') {
        return await this.executeSqlIntersect(parsed.queries);
      }

      if (parsed.type === 'except') {
        return await this.executeSqlExcept(parsed.queries);
      }

      // intersect/except 이외는 미지원 → fallback //
      return { success: false, message: 'Only INTERSECT/EXCEPT queries are executed by SQL engine' };
    }
    catch (e) {
      return { success: false, message: `SQL error: ${e.message}` };
    }
  }

  // SQL 파싱 //
  parseSql(sql) {
    if (!sql || typeof sql !== 'string') return null;
    // LLM이 가이드의 {today-N}/{today}/{year-N} 토큰 형식을 그대로 출력한 경우 안전망 치환
    sql = substituteDateTemplates(sql);

    const cleaned = sql.replace(/```sql\n?/g, '').replace(/```/g, '').trim();
    const upper = cleaned.toUpperCase();

    // EXCEPT 우선 검사 — INTERSECT보다 specific (absence/exclusion 의도)
    if (upper.includes('EXCEPT')) {
      const parts = cleaned.split(/\bEXCEPT\b/i).map(s => s.trim()).filter(Boolean);

      return {
        type: 'except',
        queries: parts.map(p => this.parseSingleSelect(p)),
      };
    }

    if (upper.includes('INTERSECT')) {
      const parts = cleaned.split(/\bINTERSECT\b/i).map(s => s.trim()).filter(Boolean);

      return {
        type: 'intersect',
        queries: parts.map(p => this.parseSingleSelect(p)),
      };
    }

    return this.parseSingleSelect(cleaned);
  }

  // 단일 SELECT 파싱 //
  parseSingleSelect(sql) {
    // 파싱 커버리지 밖 잔여 구문 감지 → null (tool loop 폴백)
    const stripped = sql
      .replace(/SELECT\s+.+?\s+FROM/is, 'FROM')
      .replace(/FROM\s+[\w+]+(?:\([^)]*\))?/i, '')
      .replace(/WHERE\s+.+?(?=\s+ORDER|\s+LIMIT|$)/is, '')
      .replace(/ORDER\s+BY\s+.+?(?=\s+LIMIT|$)/is, '')
      .replace(/LIMIT\s+\d+/i, '')
      .trim();
    if (stripped.length > 0) {
      if (TOOL_CALLING_LOG) console.log(`[SQL] 미지원 구문 감지: "${stripped.slice(0, 50)}" → 파싱 스킵`);
      return null;
    }

    const result = { type: 'select', table: null, params: {}, filters: [], sort: null };

    // FROM 절: 인텐션 키 + 파라미터 //
    const fromMatch = sql.match(/FROM\s+([\w+]+)(?:\(([^)]*)\))?/i);

    if (fromMatch) {
      result.table = fromMatch[1];

      if (fromMatch[2]) {
        const paramParts = fromMatch[2].split(',').map(s => s.trim().replace(/'/g, ''));

        if (paramParts.length >= 2) {
          result.params.startDate = paramParts[0];
          result.params.endDate = paramParts[1];
        }
      }
    }

    // WHERE 절 //
    const whereMatch = sql.match(/WHERE\s+(.+?)(?:\s+ORDER\s+BY|\s+LIMIT|\s*$)/i);

    if (whereMatch) {
      const conditions = whereMatch[1].split(/\s+AND\s+/i);

      for (const cond of conditions) {
        const filter = this.parseSqlCondition(cond.trim());
        if (filter) result.filters.push(filter);
      }
    }

    // ORDER BY //
    const orderMatch = sql.match(/ORDER\s+BY\s+(.+?)(?:\s+LIMIT|\s*$)/i);

    if (orderMatch) {
      const parts = orderMatch[1].trim().split(/\s+/);
      result.sort = { field: parts[0], direction: (parts[1] || 'asc').toLowerCase() };
    }

    return result;
  }

  // SQL 조건 → filter 객체 //
  parseSqlCondition(cond) {
    let m;

    // field = value
    m = cond.match(/(\w+)\s*=\s*'?([^']+)'?/);
    if (m) return { field: m[1], condition: { type: 'exact', value: isNaN(m[2]) ? m[2] : Number(m[2]) } };

    // field <= value
    m = cond.match(/(\w+)\s*<=\s*'?([^']+)'?/);
    if (m) {
      if (m[2].match(/^\d{4}-\d{2}-\d{2}$/)) {
        return { field: m[1], condition: { type: 'date_range', endDate: m[2] } };
      }

      return { field: m[1], condition: { type: 'numeric_lte', value: Number(m[2]) } };
    }

    // field >= value
    m = cond.match(/(\w+)\s*>=\s*'?([^']+)'?/);
    if (m) {
      if (m[2].match(/^\d{4}-\d{2}-\d{2}$/)) {
        return { field: m[1], condition: { type: 'date_range', startDate: m[2] } };
      }

      return { field: m[1], condition: { type: 'numeric_gte', value: Number(m[2]) } };
    }

    return null;
  }

  // 단일 SELECT 실행 — 인텐션 키 직접 사용 //
  async executeSqlSelect(parsed, slotName = 'default') {
    const intentionKey = parsed.table;
    const meta = this.findIntentionMeta(intentionKey);

    if (!meta) {
      return { success: false, message: `Unknown intention: ${intentionKey}` };
    }

    const apiResult = await this.executeCallApi({
      intention: intentionKey,
      startDate: parsed.params?.startDate,
      endDate: parsed.params?.endDate,
      slot: slotName,
    });

    if (!apiResult.success) {
      return { success: false, message: `API failed: ${apiResult.message}` };
    }

    // 같은 필드의 date_range 필터 병합 (startDate + endDate → 1개)
    const filters = [...(parsed.filters || [])];
    for (let i = 0; i < filters.length; i++) {
      if (filters[i]?.condition?.type !== 'date_range') continue;
      for (let j = i + 1; j < filters.length; j++) {
        if (filters[j]?.field === filters[i].field && filters[j]?.condition?.type === 'date_range') {
          filters[i] = {
            field: filters[i].field,
            condition: {
              type: 'date_range',
              startDate: filters[i].condition.startDate || filters[j].condition.startDate,
              endDate: filters[i].condition.endDate || filters[j].condition.endDate,
            },
          };
          filters.splice(j, 1);
          j--;
        }
      }
    }

    // 같은 필드의 numeric_gte + numeric_lte 필터 병합 → range 타입 (AND semantics)
    // applyFilter는 같은 field filter를 replace하므로, 그대로 두면 마지막 filter만 적용되어
    // "WHERE totalPrice >= 1 AND totalPrice <= 100000" 같은 SQL의 AND가 깨짐.
    for (let i = 0; i < filters.length; i++) {
      const c0 = filters[i]?.condition;
      if (!c0 || (c0.type !== 'numeric_gte' && c0.type !== 'numeric_lte')) continue;
      for (let j = i + 1; j < filters.length; j++) {
        const c1 = filters[j]?.condition;
        if (!c1) continue;
        if (filters[j].field !== filters[i].field) continue;
        if (c1.type !== 'numeric_gte' && c1.type !== 'numeric_lte') continue;
        const a = { type: c0.type, value: c0.value };
        const b = { type: c1.type, value: c1.value };
        const minVal = a.type === 'numeric_gte' ? a.value : (b.type === 'numeric_gte' ? b.value : null);
        const maxVal = a.type === 'numeric_lte' ? a.value : (b.type === 'numeric_lte' ? b.value : null);
        if (minVal != null && maxVal != null) {
          filters[i] = {
            field: filters[i].field,
            condition: { type: 'range', min: Number(minVal), max: Number(maxVal) },
          };
          filters.splice(j, 1);
          j--;
        }
      }
    }

    // filters — 0건이면 해당 필터 제거하고 나머지 조건으로 진행 //
    for (const f of filters) {
      this.activeSlot = slotName;
      const filterResult = this.applyFilter(f);

      if (TOOL_CALLING_LOG && filterResult.success) {
        console.log(`[SQL] filter ${f.field}: ${filterResult.before} → ${filterResult.after}건`);
      }

      if (filterResult.after === 0 && !filterResult.fieldMissing) {
        this.applyFilter({ field: f.field, reset: true });
        if (TOOL_CALLING_LOG) console.log(`[SQL] filter ${f.field} 0건 → 제거, 나머지 조건으로 진행`);
      }
    }

    if (parsed.sort) {
      this.activeSlot = slotName;
      this.applySort(parsed.sort);
    }

    const buf = this.getBuffer(slotName);

    return { success: true, count: buf.items?.length || 0 };
  }

  // [PER-AXIS GRANULARITY GUARD] userInput의 "X별" 축 토큰 추출 → 소스 슬롯별 축 필드 존재 검사 //
  // 축 필드가 일부 소스에만 존재(some && !every)하면 mismatch 반환 — INTERSECT는 공유 entity 키로만 join 가능 //
  // 전 소스 보유(예: 같은 인텐션 기간비교) 또는 미해소(축 데이터 자체 부재)면 null — 기존 동작 유지 //
  _detectPerAxisGranularityMismatch(slotNames) {
    const input = this.getUserInput?.() || '';

    if (!input || !Array.isArray(slotNames) || slotNames.length < 2) return null;

    // "X별" / "X별로" / "X별은" 등 — X는 한글/영문/숫자 토큰, 별 뒤 조사 허용, "특별한" 같은 비조사 연결은 제외
    const axisTokens = [...input.matchAll(/([가-힣A-Za-z0-9]{1,12}?)\s?별(?:로|은|는|을|를)?(?=\s|$|[^가-힣])/g)].map(m => m[1]).filter(Boolean);

    if (axisTokens.length === 0) return null;

    for (const token of axisTokens) {
      const presentSlots = [];

      for (const slotName of slotNames) {
        const field = this._resolveAxisFieldInSlot(token, slotName);
        if (field) presentSlots.push({ slotName, field });
      }

      if (presentSlots.length > 0 && presentSlots.length < slotNames.length) {
        return {
          token,
          field: presentSlots[0].field,
          axisSlot: presentSlots[0].slotName,
          presentCount: presentSlots.length,
        };
      }
    }

    return null;
  }

  // 축 토큰 → 슬롯 내 필드 해소: availableFields 한국어 라벨 매칭 → 실데이터 평탄 키 확인(resolveToItemKey) //
  _resolveAxisFieldInSlot(token, slotName) {
    const buf = this.buffers.get(slotName);
    const sample = buf?.items?.[0];

    if (!buf || !sample) return null;

    for (const [fieldKey, fieldMeta] of Object.entries(buf.availableFields || {})) {
      const labelText = getFieldLabel(fieldMeta);
      if (typeof labelText !== 'string' || !labelText.includes(token)) continue;

      const resolved = resolveToItemKey(fieldKey, sample);
      if (resolved) return resolved;
    }

    // 영문 축 토큰이 필드 키 자체인 경우 (라벨 매칭 실패 시 보조 경로)
    const direct = resolveToItemKey(token, sample);

    return direct || null;
  }

  // INTERSECT 실행 — 각 SELECT 실행 후 교차 //
  async executeSqlIntersect(queries) {
    const slotNames = [];

    for (let i = 0; i < queries.length; i++) {
      const slotName = `sql_${i}`;
      const result = await this.executeSqlSelect(queries[i], slotName);

      if (!result.success) {
        return { success: false, message: `Source ${i} failed: ${result.message}` };
      }

      slotNames.push(slotName);
      const buf = this.buffers.get(slotName);

      // [CROSS-REF FILTER INHERIT] sql_i intention이 기존 filtered slot과 일치하면 filter 상속
      if (buf?.items?.length > 0) {
        const table = queries[i].table;
        for (const [existingName, existingBuf] of this.buffers) {
          if (/^sql_|^_merged$|^_auto_merged$/.test(existingName)) continue;
          if (existingBuf.intention === table && existingBuf.appliedFilters?.length > 0) {
            let filtered = [...buf.items];
            for (const f of existingBuf.appliedFilters) {
              filtered = this.filterByCondition(filtered, f);
            }
            if (filtered.length > 0 && filtered.length < buf.items.length) {
              if (TOOL_CALLING_LOG) console.log(`[SQL] inherited filter from "${existingName}": ${buf.items.length} → ${filtered.length}건 (${existingBuf.appliedFilters.map(f => f.field).join(', ')})`);
              buf.originalItems = [...buf.items];
              buf.items = filtered;
              buf.appliedFilters = existingBuf.appliedFilters.map(f => ({ ...f }));
            }
            break;
          }
        }
      }

      if (TOOL_CALLING_LOG) console.log(`[SQL] source ${i}: ${queries[i].table} → ${buf?.items?.length || 0}건`);
    }

    // [PER-AXIS GRANULARITY GUARD] userInput이 "X별" 축 요청인데 축 필드가 일부 소스에만 존재하면 intersect 차단 //
    // 본질: INTERSECT는 공유 entity 키(memberId 등) join이라 결과 행이 회원 단위 — 축 필드가 전 소스에 없으면
    // per-axis 표 생성 불가 (라이브 2026-06-10: "수업별 참여율+매출"이 memberId join → 회원 9명 표가 수업별 표로 위장).
    // fail 반환 → useAiChat 기존 fallback 경로로 tool loop 진입. 축 보유 슬롯을 active로 전환해 tool loop가 바로 활용.
    // 구조 기반: availableFields 라벨 + resolveToItemKey. 도메인명/case hardcode 없음. dual sync with nodeAgent.mjs //
    const axisMismatch = this._detectPerAxisGranularityMismatch(slotNames);

    if (axisMismatch) {
      this.activeSlot = axisMismatch.axisSlot;
      if (TOOL_CALLING_LOG) console.log(`[SQL] PER-AXIS GRANULARITY: "${axisMismatch.token}별" axis field "${axisMismatch.field}" in ${axisMismatch.presentCount}/${slotNames.length} sources — intersect cannot produce per-axis rows, falling back to tool loop (active slot → ${axisMismatch.axisSlot})`);
      return { success: false, message: `[PER-AXIS GRANULARITY] userInput requests per-"${axisMismatch.token}" rows but axis field "${axisMismatch.field}" exists in only ${axisMismatch.presentCount}/${slotNames.length} sources — INTERSECT joins on a shared entity key (member-level rows), not the requested axis. Falling back to tool loop.` };
    }

    // same-intention period comparison 감지 → inner merge + intersect 2단계 처리 //
    // startDate 비교로 이전 → 최근 순서 보장 (LLM이 반대로 생성한 경우 자동 정렬) //
    const tables = queries.map(q => q.table);

    if (tables.length >= 3) {
      // 3+ queries: same-intention 쌍 감지 후 Phase 1 inner merge, Phase 2 intersect //
      const tableCount = {};

      for (let i = 0; i < tables.length; i++) {
        if (!tableCount[tables[i]]) tableCount[tables[i]] = [];
        tableCount[tables[i]].push(i);
      }

      // same-intention 쌍 찾기 (같은 intention이 2회 등장하는 것) //
      let pairIndices = null;
      let pairTable = null;

      for (const [tbl, indices] of Object.entries(tableCount)) {
        if (indices.length === 2) {
          pairIndices = indices;
          pairTable = tbl;
          break;
        }
      }

      if (pairIndices) {
        // Phase 1: same-intention 쌍을 inner merge (날짜 순서 보장) → _change_ 필드 생성 //
        const [idx0, idx1] = pairIndices;
        const sd0 = queries[idx0].params?.startDate;
        const sd1 = queries[idx1].params?.startDate;
        let orderedPairSlots = [slotNames[idx0], slotNames[idx1]];

        if (sd0 && sd1 && sd0 > sd1) {
          orderedPairSlots = [slotNames[idx1], slotNames[idx0]];
        }

        if (TOOL_CALLING_LOG) console.log(`[SQL] same-intention pair detected: ${pairTable} (indices ${pairIndices.join(',')})`);

        const phase1Result = this.mergeSlots({
          slots: orderedPairSlots.map((name, i) => ({ slotName: name, label: i === 0 ? 'previous' : 'recent' })),
          mode: 'inner',
          outputSlot: '_compared',
        });

        if (!phase1Result.success) {
          return { success: false, message: `Phase 1 comparison merge failed: ${phase1Result.message}` };
        }

        if (TOOL_CALLING_LOG) console.log(`[SQL] Phase 1 inner merge: ${phase1Result.mergedCount}건 (_change_ fields generated)`);

        // Phase 2: 비교 결과(_compared)와 나머지 슬롯을 intersect //
        const remainingIndices = [];

        for (let i = 0; i < tables.length; i++) {
          if (!pairIndices.includes(i)) remainingIndices.push(i);
        }

        const phase2Slots = [
          { slotName: '_compared', label: 'compared' },
          ...remainingIndices.map(i => ({ slotName: slotNames[i], label: `src${i}` })),
        ];

        const phase2Result = this.mergeSlots({
          slots: phase2Slots,
          mode: 'intersect',
          outputSlot: '_merged',
        });

        if (!phase2Result.success) {
          return { success: false, message: `Phase 2 intersect failed: ${phase2Result.message}` };
        }

        if (TOOL_CALLING_LOG) console.log(`[SQL] Phase 2 intersect: ${phase2Result.mergedCount}건`);

        const mergedBuf = this.getBuffer('_merged');
        const fields = mergedBuf.items?.[0]
          ? Object.keys(mergedBuf.items[0]).filter(k => typeof mergedBuf.items[0][k] !== 'object')
          : [];

        return { success: true, count: phase2Result.mergedCount, fields };
      }
    }

    // 2-source same-intention period comparison 감지 //
    if (tables.length === 2 && new Set(tables).size === 1) {
      const sd0 = queries[0].params?.startDate;
      const sd1 = queries[1].params?.startDate;
      let orderedSlots = [slotNames[0], slotNames[1]];

      if (sd0 && sd1 && sd0 > sd1) {
        orderedSlots = [slotNames[1], slotNames[0]];
      }

      const mergeResult = this.mergeSlots({
        slots: orderedSlots.map((name, i) => ({ slotName: name, label: i === 0 ? 'previous' : 'recent' })),
        mode: 'inner',
        outputSlot: '_merged',
      });

      if (!mergeResult.success) {
        return { success: false, message: `Merge failed: ${mergeResult.message}` };
      }

      const mergedBuf = this.getBuffer('_merged');
      const fields = mergedBuf.items?.[0]
        ? Object.keys(mergedBuf.items[0]).filter(k => !k.startsWith('_') && typeof mergedBuf.items[0][k] !== 'object')
        : [];

      return { success: true, count: mergeResult.mergedCount, fields };
    }

    // default: 전체 intersect //
    const mergeResult = this.mergeSlots({
      slots: slotNames.map((name, i) => ({ slotName: name, label: `src${i}` })),
      mode: 'intersect',
      outputSlot: '_merged',
    });

    if (!mergeResult.success) {
      // [INTERSECT 0건 — 정직 응답] mergeSlots가 "공통 값 없음"으로 fail 시 모든 source 정상 load됐다면
      // 0건 intersect는 자연스러운 결과 (SQL 조건이 너무 좁아서 매칭 X). fallback to tool loop 차단해서
      // LLM이 "조건에 안 맞는 데이터"를 임시로 보여주는 self-contradiction 방지.
      const allSourcesLoaded = slotNames.every(n => {
        const buf = this.buffers.get(n);
        return buf && Array.isArray(buf.items);
      });
      const noOverlapMsg = String(mergeResult.message || '').includes('공통 값');
      if (allSourcesLoaded && noOverlapMsg) {
        if (!this.buffers.has('_merged')) this.buffers.set('_merged', { items: [], appliedFilters: [] });
        const emptyBuf = this.buffers.get('_merged');
        emptyBuf.items = [];
        emptyBuf.intention = 'MERGED';
        this.activeSlot = '_merged';
        if (TOOL_CALLING_LOG) console.log('[SQL] intersect 0건 (모든 source 정상 load, 공통 entity 없음) — 정직 응답 강제');
        // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] empty 시 slot 단위 breakdown 첨부 (LLM finalText 보강 inject 용도)
        const emptyBreakdown = this._buildSqlEmptyBreakdown('intersect', queries, slotNames);
        this._sqlEmptyBreakdown = emptyBreakdown;
        return { success: true, count: 0, fields: [], emptyBreakdown };
      }
      return { success: false, message: `Merge failed: ${mergeResult.message}` };
    }

    // 데이터는 _merged 버퍼에 저장됨 — display는 LLM이 show_display로 생성 //
    const mergedBuf = this.getBuffer('_merged');
    const fields = mergedBuf.items?.[0]
      ? Object.keys(mergedBuf.items[0]).filter(k => !k.startsWith('_') && typeof mergedBuf.items[0][k] !== 'object')
      : [];

    // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] mergeSlots success로 count=0 떨어진 경우도 동일하게 처리
    // (예: matchKey 매칭됐지만 join 결과 0건). emptyBreakdown 첨부해서 finalText 보강.
    if (mergeResult.mergedCount === 0) {
      const emptyBreakdown = this._buildSqlEmptyBreakdown('intersect', queries, slotNames);
      this._sqlEmptyBreakdown = emptyBreakdown;
      return { success: true, count: 0, fields, emptyBreakdown };
    }
    this._sqlEmptyBreakdown = null;

    return { success: true, count: mergeResult.mergedCount, fields };
  }

  // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] INTERSECT/EXCEPT 0건일 때 LLM/finalText에 inject할
  // slot 단위 breakdown 생성. table(intention) + WHERE 요약 + slot 개수 + 연산자. 범용 helper.
  _buildSqlEmptyBreakdown(mode, queries, slotNames) {
    try {
      const slots = (queries || []).map((q, i) => {
        const buf = this.buffers.get(slotNames[i]);
        const count = Array.isArray(buf?.items) ? buf.items.length : 0;
        const where = (q?.filters || [])
          .map(f => this._formatSqlFilterForBreakdown(f))
          .filter(Boolean)
          .join(' AND ');
        return {
          slotName: slotNames[i],
          table: q?.table || `src${i}`,
          where: where || null,
          count,
        };
      });
      const operator = mode === 'except' ? 'AND NOT' : 'AND';
      return { mode, operator, slots };
    }
    catch (_e) {
      return { mode, operator: mode === 'except' ? 'AND NOT' : 'AND', slots: [] };
    }
  }

  _formatSqlFilterForBreakdown(filter) {
    if (!filter || typeof filter !== 'object') return null;
    const field = filter.field || filter.fieldName || '';
    const cond = filter.condition || filter;
    const type = cond?.type;
    if (!field) return null;
    if (type === 'exact') return `${field} = ${this._formatBreakdownValue(cond.value)}`;
    if (type === 'numeric_gte') return `${field} >= ${cond.value}`;
    if (type === 'numeric_lte') return `${field} <= ${cond.value}`;
    if (type === 'numeric_gt') return `${field} > ${cond.value}`;
    if (type === 'numeric_lt') return `${field} < ${cond.value}`;
    if (type === 'numeric_eq') return `${field} = ${cond.value}`;
    if (type === 'numeric_between') return `${field} between ${cond.min}~${cond.max}`;
    if (type === 'range') return `${field} ${cond.min}~${cond.max}`;
    if (type === 'date_gte') return `${field} >= ${cond.value}`;
    if (type === 'date_lte') return `${field} <= ${cond.value}`;
    if (type === 'date_between') return `${field} ${cond.start}~${cond.end}`;
    if (type === 'date_range') {
      const parts = [];
      if (cond.startDate) parts.push(`>= ${cond.startDate}`);
      if (cond.endDate) parts.push(`<= ${cond.endDate}`);
      return parts.length ? `${field} ${parts.join(' AND ')}` : null;
    }
    if (type === 'contains') return `${field} contains "${cond.value}"`;
    if (type === 'starts_with') return `${field} starts "${cond.value}"`;
    if (type === 'ends_with') return `${field} ends "${cond.value}"`;
    return `${field} ${type || ''} ${cond?.value ?? ''}`.trim();
  }

  _formatBreakdownValue(v) {
    if (v == null) return '';
    if (typeof v === 'string') return `'${v}'`;
    return String(v);
  }

  // EXCEPT 실행 — 첫 SELECT(base) 결과에서 나머지 SELECT(subtract)에 매칭되는 entity 제거 //
  // 의도: "X인데 Y 없는/안 한" 같은 absence 조건. 결과는 base.items 중 subtract entity와 겹치지 않는 행만.
  async executeSqlExcept(queries) {
    if (!queries || queries.length < 2) {
      return { success: false, message: 'EXCEPT requires 2+ queries' };
    }

    const slotNames = [];

    for (let i = 0; i < queries.length; i++) {
      const slotName = `sql_${i}`;
      const result = await this.executeSqlSelect(queries[i], slotName);

      if (!result.success) {
        return { success: false, message: `Source ${i} failed: ${result.message}` };
      }

      slotNames.push(slotName);

      if (TOOL_CALLING_LOG) {
        const buf = this.buffers.get(slotName);
        console.log(`[SQL EXCEPT] source ${i}: ${queries[i].table} → ${buf?.items?.length || 0}건`);
      }
    }

    const mergeResult = this.mergeSlots({
      slots: slotNames.map((name, i) => ({ slotName: name, label: i === 0 ? 'base' : `subtract${i}` })),
      mode: 'except',
      outputSlot: '_merged',
    });

    if (!mergeResult.success) {
      return { success: false, message: `Except failed: ${mergeResult.message}` };
    }

    const mergedBuf = this.getBuffer('_merged');
    const fields = mergedBuf.items?.[0]
      ? Object.keys(mergedBuf.items[0]).filter(k => !k.startsWith('_') && typeof mergedBuf.items[0][k] !== 'object')
      : [];

    if (TOOL_CALLING_LOG) console.log(`[SQL EXCEPT] result: ${mergeResult.mergedCount}건`);

    // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] EXCEPT 0건 시 slot breakdown 첨부 (INTERSECT와 동일 path)
    if (mergeResult.mergedCount === 0) {
      const emptyBreakdown = this._buildSqlEmptyBreakdown('except', queries, slotNames);
      this._sqlEmptyBreakdown = emptyBreakdown;
      return { success: true, count: 0, fields, emptyBreakdown };
    }
    this._sqlEmptyBreakdown = null;

    return { success: true, count: mergeResult.mergedCount, fields };
  }

  // ============================================
  // 내부 헬퍼 — from aiTools
  // ============================================

  getCategoryForIntention(intentionKey) {
    if (!intentionKey || intentionKey === 'MERGED') return null;
    for (const [category, cached] of Object.entries(this.intentionCache)) {
      if (cached?.intentions?.some(i => i.intentionKey === intentionKey)) return category;
    }
    return null;
  }

  // [1-E] alternative intention keys 반환 (현재 intention 제외) //
  // count=0 multi-call 시 hint inject용 — intention-agnostic, harness pattern. dual sync with nodeAgent.mjs.
  // 우선순위: (1) same category (2) cross-category token-overlap (>=2 tokens, ranked by overlap count)
  //
  // [MODIFIER GUARD — L1-MEMBERSHIP-005 root cause]
  //   Source intention with non-empty modifier (SUBSCRIPTION/LINKOPAY/DISCOUNT/...) signals
  //   user query is scope-qualified (e.g. "구독 해지" → SUBSCRIPTION). Alternative intentions
  //   lacking the same modifier are NOT semantically substitutable — exposing them as
  //   alternatives caused LLM to wander into unrelated intentions (CHECK_MEMBER+EXPIRED_LIST,
  //   CHECK_MEMBERSHIP+APPLICATION_LIST) and produce contradictory finalText for 0건 queries.
  //   Generic structural rule (no per-case literal): require modifier overlap when source has modifier.
  //   Source with empty modifier (e.g. PRODUCT_SALES_LIST_BY+PERIOD) preserves original behavior
  //   for scope-ambiguous queries.
  _findSameCategoryAlternatives(intentionKey) {
    const tokens = String(intentionKey || '').split(/[+_]/).filter(t => t && t !== 'CHECK');
    const sourceDims = getDimensionsByIntentionKey(intentionKey);
    const sourceModifier = Array.isArray(sourceDims?.modifier) ? sourceDims.modifier : [];
    const requireModifierOverlap = sourceModifier.length > 0;
    const sameCategoryKeys = [];
    const crossCategoryRanked = [];
    for (const category of Object.keys(this.intentionCache)) {
      const items = this.intentionCache[category]?.intentions || this.intentionCache[category] || [];
      for (const i of items) {
        if (!i.intentionKey || i.intentionKey === intentionKey) continue;
        // [MODIFIER GUARD] candidate must share at least one modifier with source when source is modifier-qualified
        if (requireModifierOverlap) {
          const candDims = getDimensionsByIntentionKey(i.intentionKey);
          const candModifier = Array.isArray(candDims?.modifier) ? candDims.modifier : [];
          const modifierOverlap = candModifier.filter(m => sourceModifier.includes(m)).length;
          if (modifierOverlap === 0) continue;
        }
        if (items.some(x => x.intentionKey === intentionKey)) {
          // same category
          if (!sameCategoryKeys.includes(i.intentionKey)) sameCategoryKeys.push(i.intentionKey);
        } else {
          // cross-category token overlap
          const candidateTokens = String(i.intentionKey).split(/[+_]/).filter(t => t && t !== 'CHECK');
          const overlap = candidateTokens.filter(t => tokens.includes(t)).length;
          if (overlap >= 2) crossCategoryRanked.push({ key: i.intentionKey, overlap });
        }
      }
    }
    crossCategoryRanked.sort((a, b) => b.overlap - a.overlap);
    const crossKeys = crossCategoryRanked
      .map(c => c.key)
      .filter(k => !sameCategoryKeys.includes(k));
    return [...sameCategoryKeys, ...crossKeys].slice(0, 5);
  }

  // 인텐션 키로 메타데이터 검색 //
  // 인텐션 키의 API 카테고리 조회 //
  findIntentionCategory(intentionKey) {
    for (const category of Object.keys(this.intentionCache)) {
      const items = this.intentionCache[category]?.intentions || this.intentionCache[category] || [];
      if (items.some(i => i.intentionKey === intentionKey)) return category;
    }
    return null;
  }

  findIntentionMeta(intentionKey) {
    // Exact match //
    for (const category of Object.keys(this.intentionCache)) {
      const items = this.intentionCache[category]?.intentions || this.intentionCache[category] || [];
      const found = items.find(i => i.intentionKey === intentionKey);
      if (found) return found;
    }

    // Fuzzy match — all tokens of input key must exist in candidate key //
    const inputTokens = intentionKey.split(/[+_]/).filter(Boolean);
    const candidates = [];

    for (const category of Object.keys(this.intentionCache)) {
      const items = this.intentionCache[category]?.intentions || this.intentionCache[category] || [];
      for (const i of items) {
        const candidateTokens = i.intentionKey.split(/[+_]/).filter(Boolean);
        const allMatch = inputTokens.every(t => candidateTokens.includes(t));

        if (allMatch) candidates.push(i);
      }
    }

    if (candidates.length === 1) {
      if (TOOL_CALLING_LOG) console.log(`[findIntentionMeta] fuzzy match: "${intentionKey}" → "${candidates[0].intentionKey}"`);
      return candidates[0];
    }

    // Suggest closest match — find candidate with most token overlap //
    let bestMatch = null;
    let bestOverlap = 0;

    for (const category of Object.keys(this.intentionCache)) {
      const items = this.intentionCache[category]?.intentions || this.intentionCache[category] || [];
      for (const i of items) {
        const candidateTokens = i.intentionKey.split(/[+_]/).filter(Boolean);
        const overlap = inputTokens.filter(t => candidateTokens.includes(t)).length;

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestMatch = i.intentionKey;
        }
      }
    }

    if (bestMatch) {
      this._lastSuggestedIntention = bestMatch;
    }

    return null;
  }

  // 인텐션 키로 availableFields 조회 //
  findAvailableFields(intentionKey) {
    const meta = this.findIntentionMeta(intentionKey);

    return meta?.availableFields || null;
  }

  // 인텐션 키로 callApiGuide 조회 //
  findPostToolGuide(intentionKey) {
    const meta = this.findIntentionMeta(intentionKey);

    return meta?.callApiGuide || null;
  }

  // 백엔드 API 내부 호출 //
  async callApiInternal(intentionMeta, intention, params) {
    const parsed = parseEndpoint(intentionMeta.endpoint);
    if (!parsed) return { success: false, intention, message: "endpoint 파싱 실패" };

    const paramsSchema = typeof intentionMeta.params === 'string'
      ? JSON.parse(intentionMeta.params || '[]')
      : (intentionMeta.params || []);

    const endpoint = buildDynamicEndpoint(parsed.path, paramsSchema, params);
    const fetchMethodName = resolveFetchMethod(intentionMeta.service || 'zone', parsed.apiVersion, parsed.method);
    const parseResponse = PARSE_RESPONSE_OVERRIDES[intention] || ((data) => data);

    // [시임2] 백엔드 호출 계측 — 터미널 return 지점마다 기록, 텔레메트리 실패는 동작 무영향 (dual sync with nodeAgent.mjs) //
    const _telStart = Date.now();
    const _telAttemptStatuses = [];
    const _tel = (fields) => {
      try {
        this._telemetryCollector?.recordBackendCall({
          type: 'call_api',
          intention,
          endpoint,
          method: parsed.method || 'GET',
          fetchMethod: fetchMethodName,
          params: safeJsonCapped(params, 20000).json,
          transformApplied: !!PARSE_RESPONSE_OVERRIDES[intention],
          attemptStatuses: _telAttemptStatuses,
          attemptCount: _telAttemptStatuses.length,
          latencyMs: Date.now() - _telStart,
          ...fields,
        });
      }
      catch (_e) { /* 무시 */ }
    };

    const fetchModule = new FetchModule();
    const methodMap = {
      getDataVersion2: () => fetchModule.getDataVersion2(endpoint, parsed.method),
      postDataVersion2: () => fetchModule.postDataVersion2(endpoint, parsed.method, params),
      getData: () => fetchModule.getData(endpoint, parsed.method),
      postData: () => fetchModule.postData(endpoint, parsed.method, params),
      getIndividualData: () => fetchModule.getIndividualData(endpoint, parsed.method),
      getDataOfExerciseVersion2: () => fetchModule.getDataOfExerciseVersion2(endpoint, parsed.method),
      postDataOfExerciseVersion2: () => fetchModule.postDataOfExerciseVersion2(endpoint, parsed.method, params),
    };
    const makeRequest = methodMap[fetchMethodName] || methodMap.getDataVersion2;

    const MAX_ATTEMPTS = 3;
    const TIMEOUT_MS = 60000;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const responseData = await Promise.race([
          makeRequest(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), TIMEOUT_MS)),
        ]);
        _telAttemptStatuses.push(responseData?.status ?? null);

        if (responseData.status === 200) {
          // 백엔드가 status=200이지만 error 필드로 실패를 알리는 경우
          if (responseData.error && responseData.error !== 'Success') {
            _tel({ statusCode: 200, success: false, errorMessage: responseData.error, rawResponse: null });
            return { success: false, intention, message: responseData.error };
          }
          const _telRaw = safeJsonCapped(responseData.data, 200000);
          _tel({ statusCode: 200, success: true, errorMessage: null, rawResponse: _telRaw.json, rawResponseTruncated: _telRaw.truncated });
          return { success: true, intention, data: parseResponse(responseData.data) };
        }

        const errorType = classifyBackendError(null, responseData.status);
        const errorConfig = ERROR_CONFIG[errorType];

        if (!errorConfig?.retryable || attempt >= (errorConfig?.maxRetries || 1) - 1) {
          _tel({ statusCode: responseData?.status ?? null, success: false, errorMessage: errorConfig?.message || 'API 오류', rawResponse: null });
          return { success: false, intention, message: errorConfig?.message || "API 오류" };
        }

        await new Promise(resolve => setTimeout(resolve, getBackoffDelay(attempt)));
      }
      catch (error) {
        _telAttemptStatuses.push(null);
        const errorType = classifyBackendError(error, null);
        const errorConfig = ERROR_CONFIG[errorType];

        if (!errorConfig?.retryable || attempt >= (errorConfig?.maxRetries || 1) - 1) {
          _tel({ statusCode: null, success: false, errorMessage: String(error?.message || error), rawResponse: null });
          return { success: false, intention, message: errorConfig?.message || "API 오류" };
        }

        await new Promise(resolve => setTimeout(resolve, getBackoffDelay(attempt)));
      }
    }

    _tel({ statusCode: null, success: false, errorMessage: '최대 재시도 초과', rawResponse: null });
    return { success: false, intention, message: "API 호출 실패 (최대 재시도 초과)" };
  }

  // [시임2] 턴 텔레메트리 콜렉터 주입 (useAiChat runAgent가 턴마다 호출, null 허용) //
  setTelemetryCollector(collector) {
    this._telemetryCollector = collector || null;
  }

  // YYYY-MM-DD 날짜에 일수를 더하거나 빼기 //
  adjustDate(dateStr, days) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }
}
